import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import i18n, { setAppLanguage } from "./i18n/index.js";
import { useAuth } from "./auth/AuthContext.jsx";

import AppOnboardingBridge from
  "./features/onboarding/AppOnboardingBridge.jsx";

import {
  emitAppEvent,
  openOnboardingMenu,
  subscribeAppEvents,
} from "./features/onboarding/events.js";

import absImg from "./assets/abs.webp";
import backImg from "./assets/back.webp";
import bicepsImg from "./assets/biceps.webp";
import chestImg from "./assets/chest.webp";
import forearmsImg from "./assets/forearms.webp";
import legsImg from "./assets/legs.webp";
import shouldersImg from "./assets/shoulders.webp";
import tricepsImg from "./assets/triceps.webp";

import measurementsImg from "./assets/measurements.webp";
import stepsImg from "./assets/steps.webp";
import supplementsImg from "./assets/supplements.webp";

import bottomFoodImg from "./assets/bottom-nav/food.webp";
import bottomRationImg from "./assets/bottom-nav/ration.webp";
import bottomBodyImg from "./assets/bottom-nav/body.webp";
import bottomWorkoutImg from "./assets/bottom-nav/workout.webp";

// === Storage helpers (CloudStorage → localStorage fallback) ===
// Возврат WebApp или null — без optional chaining
// === Storage helpers (CloudStorage → localStorage fallback) ===
const getTg = () => {
  if (typeof window === 'undefined') return null;
  const w = window;
  if (!w.Telegram || !w.Telegram.WebApp) return null;
  return w.Telegram.WebApp;
};
const escapeToastHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => {
    const map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[ch] || ch;
  });

const safeAlert = (message, title = "Сообщение") => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const oldToast = document.getElementById("fitmelup-toast");
  if (oldToast) oldToast.remove();

  const oldStyle = document.getElementById("fitmelup-toast-style");
  if (oldStyle) oldStyle.remove();

  const rawTitle = String(title || "").trim();
  const rawMessage = String(message ?? "")
    .replace(/[✅✓]\s*$/g, "")
    .trim();

  const lowerTitle = rawTitle.toLowerCase();
  const lowerMessage = rawMessage.toLowerCase();

const isError =
  lowerTitle.includes("ошибка") ||
  lowerTitle.includes("недоступно") ||
  lowerMessage.includes("ошибка") ||
  lowerMessage.includes("не удалось") ||
  lowerMessage.includes("не получилось") ||
  lowerMessage.includes("не нашёл") ||
  lowerMessage.includes("нельзя") ||
  lowerMessage.includes("введите") ||
  lowerTitle.includes("error") ||
  lowerMessage.includes("error") ||
  lowerMessage.includes("failed") ||
  lowerMessage.includes("could not");

  const text = isError
    ? i18n.t("feedback.errorGeneric")
    : i18n.t("feedback.done");

  try {
    getTg()?.HapticFeedback?.notificationOccurred?.(isError ? "error" : "success");
  } catch {}

  const style = document.createElement("style");
  style.id = "fitmelup-toast-style";
  style.textContent = `
    @keyframes fitmelupToastIn {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-7px) scale(0.985);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0) scale(1);
      }
    }
  `;
  document.head.appendChild(style);

  const toast = document.createElement("div");
  toast.id = "fitmelup-toast";

  toast.style.position = "fixed";
  toast.style.left = "50%";
  toast.style.top = "calc(16px + env(safe-area-inset-top))";
  toast.style.zIndex = "999999";

  toast.style.transform = "translateX(-50%) translateY(-7px) scale(0.985)";
  toast.style.opacity = "0";

  toast.style.boxSizing = "border-box";
  toast.style.width = "auto";
  toast.style.maxWidth = "calc(100vw - 72px)";
  toast.style.minWidth = "104px";
  toast.style.height = "40px";
  toast.style.padding = "0 18px";

  toast.style.display = "flex";
  toast.style.alignItems = "center";
  toast.style.justifyContent = "center";

  toast.style.borderRadius = "18px";

  toast.style.background = isError
    ? "rgba(232, 205, 210, 0.78)"
    : "rgba(226, 234, 235, 0.78)";

  toast.style.border = "1px solid rgba(255, 255, 255, 0.42)";

  toast.style.boxShadow = `
    0 10px 24px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.46),
    inset 0 -1px 0 rgba(0, 0, 0, 0.035)
  `;

  toast.style.backdropFilter = "blur(24px) saturate(180%)";
  toast.style.WebkitBackdropFilter = "blur(24px) saturate(180%)";

  toast.style.color = "#101821";
  toast.style.fontFamily =
    "system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";
  toast.style.fontSize = "16px";
  toast.style.fontWeight = "650";
  toast.style.lineHeight = "1";
  toast.style.letterSpacing = "-0.015em";

  toast.style.pointerEvents = "none";
  toast.style.whiteSpace = "nowrap";
  toast.style.overflow = "hidden";
  toast.style.textOverflow = "ellipsis";

  toast.style.animation =
    "fitmelupToastIn 150ms cubic-bezier(.2,.8,.2,1) forwards";

  toast.textContent = text;

  document.body.appendChild(toast);

  window.setTimeout(() => {
    toast.style.transition = "opacity 150ms ease, transform 160ms ease";
    toast.style.opacity = "0";
    toast.style.transform = "translateX(-50%) translateY(-6px) scale(0.985)";

    window.setTimeout(() => {
      toast.remove();
    }, 180);
  }, 1250);
};
// Единое подтверждение действий.
// Telegram: оставляем системный confirm без изменений.
// Web/PWA: собственное окно, чтобы язык кнопок совпадал с языком FitMeUp.
const safeConfirm = (message) => {
  const text = String(message ?? "");

  if (
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return Promise.resolve(false);
  }

  if (hasTgAuth()) {
    try {
      protectNextTap(1200);

      const ok = window.confirm(text);

      protectNextTap(1200);

      return Promise.resolve(ok);
    } catch {
      protectNextTap(1200);
      return Promise.resolve(false);
    }
  }

  protectNextTap(1200);

  const oldOverlay =
    document.getElementById("fitmeup-confirm-overlay");

  if (oldOverlay) {
    oldOverlay.remove();
  }

  const language =
    String(
      i18n.resolvedLanguage ||
      i18n.language ||
      ""
    ).toLowerCase();

  const isRussian =
    language === "ru" ||
    language.startsWith("ru-");

  const isDestructive =
    /(delete|remove|удал)/i.test(text);

  const cancelLabel =
    isRussian ? "Отмена" : "Cancel";

  const confirmLabel =
    isDestructive
      ? (isRussian ? "Удалить" : "Delete")
      : (isRussian ? "ОК" : "OK");

  return new Promise((resolve) => {
    let finished = false;

    const overlay = document.createElement("div");
    overlay.id = "fitmeup-confirm-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "1000000",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      padding:
        "calc(18px + env(safe-area-inset-top, 0px)) 20px calc(18px + env(safe-area-inset-bottom, 0px))",
      background: "rgba(2, 8, 23, 0.22)",
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif",
    });

    const card = document.createElement("div");

    Object.assign(card.style, {
      width: "100%",
      maxWidth: "390px",
      boxSizing: "border-box",
      borderRadius: "26px",
      border: "1px solid rgba(148, 163, 184, 0.22)",
      background: "rgba(12, 24, 39, 0.98)",
      boxShadow:
        "0 24px 70px rgba(0,0,0,0.58), inset 0 1px 0 rgba(255,255,255,0.055)",
      padding: "20px",
      color: "#f8fafc",
    });

    const messageEl = document.createElement("div");
    messageEl.textContent = text;

    Object.assign(messageEl.style, {
      fontSize: "17px",
      lineHeight: "1.35",
      fontWeight: "650",
      letterSpacing: "-0.015em",
      color: "#f8fafc",
      wordBreak: "break-word",
    });

    const actions = document.createElement("div");

    Object.assign(actions.style, {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px",
      marginTop: "20px",
    });

    const cancelButton =
      document.createElement("button");

    cancelButton.type = "button";
    cancelButton.textContent = cancelLabel;

    Object.assign(cancelButton.style, {
      height: "48px",
      borderRadius: "16px",
      border: "1px solid rgba(148,163,184,0.20)",
      background: "rgba(255,255,255,0.055)",
      color: "#e2e8f0",
      fontSize: "15px",
      fontWeight: "700",
    });

    const confirmButton =
      document.createElement("button");

    confirmButton.type = "button";
    confirmButton.textContent = confirmLabel;

    Object.assign(confirmButton.style, {
      height: "48px",
      borderRadius: "16px",
      border: "0",
      background:
        "linear-gradient(90deg, #2dd4bf, #22d3ee)",
      color: "#03131a",
      fontSize: "15px",
      fontWeight: "800",
    });

    const finish = (value) => {
      if (finished) return;
      finished = true;

      window.removeEventListener(
        "keydown",
        onKeyDown
      );

      overlay.remove();

      protectNextTap(1200);

      resolve(Boolean(value));
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        finish(false);
      }
    };

    cancelButton.addEventListener(
      "click",
      () => finish(false)
    );

    confirmButton.addEventListener(
      "click",
      () => finish(true)
    );

    overlay.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target === overlay) {
          finish(false);
        }
      }
    );

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    actions.append(
      cancelButton,
      confirmButton
    );

    card.append(
      messageEl,
      actions
    );

    overlay.appendChild(card);
    document.body.appendChild(overlay);
  });
};
const getUid = () => {
  const tg = getTg();

  const tgId =
    tg &&
    tg.initDataUnsafe &&
    tg.initDataUnsafe.user &&
    tg.initDataUnsafe.user.id;

  // В Telegram ничего не меняем:
  // используем настоящий Telegram ID.
  if (tgId) {
    return tgId;
  }

  // В обычном браузере/PWA используем
  // внутренний users.id из нашей БД.
  if (typeof window !== "undefined") {
    const webUserId =
      window.localStorage.getItem(
        "fitmeup:webUserId"
      );

    if (webUserId) {
      return `web-${webUserId}`;
    }
  }

  // До авторизации App обычно вообще
  // не будет открыт благодаря WebAuthGate.
  return "web-guest";
};
const getInitData = () => {
  const tg = getTg();
  // initData = строка, которую Telegram подписывает
  // её сервер сможет проверить и понять, кто ты
  return (tg && tg.initData) ? tg.initData : "";
};
const hasTgAuth = () => !!getInitData();


async function tgFetch(url, options = {}) {
  const initData = getInitData();

  // добавим сразу несколько вариантов — чтобы точно “попасть” в то, что ждёт бэкенд
  const headers = {
    ...(options.headers || {}),
    "x-telegram-init-data": initData,
    "x-telegram-initdata": initData,
    ...(initData ? { Authorization: `tma ${initData}` } : {}),
  };

  // если отправляем JSON — добавим initData и в body (часто /api/auth так сделан)
  let body = options.body;
  const ct = String(headers["Content-Type"] || headers["content-type"] || "");

  if (initData && body && ct.includes("application/json")) {
    try {
      const obj = typeof body === "string" ? JSON.parse(body) : body;
      body = JSON.stringify({ ...obj, initData });
    } catch {
      // если не получилось распарсить — оставим как есть
    }
  }

  return fetch(url, {
    ...options,
    headers,
    body,
    credentials: "include",
  });
}


async function apiUpdateFoodOnboarding(
  action,
  payload = {}
) {
  const res = await tgFetch(
    "/api/onboarding",
    {
      method: "POST",

      headers: {
        "Content-Type":
          "application/json",
      },

      body: JSON.stringify({
        action,
        ...payload,
      }),
    }
  );

  const json = await res
    .json()
    .catch(() => null);

  if (
    !res.ok ||
    !json?.ok
  ) {
    throw new Error(
      json?.error ||
        "Не удалось сохранить онбординг"
    );
  }

  return json.onboarding;
}

function focusToEndAndScroll(e, scrollRootRef) {
  try {
    const el = e?.target;
    if (!el) return;

    // курсор в конец (не всегда нужно, но не мешает)
    try {
      const len = String(el.value ?? "").length;
      if (typeof el.setSelectionRange === "function") el.setSelectionRange(len, len);
    } catch {}

    const getRoot = () =>
      scrollRootRef?.current || el.closest?.('[data-modal-scroll-root="1"]') || null;

    const ensureVisible = () => {
      const root = getRoot();
      if (!root) return;

      const margin = 18;

      const elRect = el.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();

      // если поле ниже видимой зоны контейнера
      if (elRect.bottom > rootRect.bottom - margin) {
        const delta = elRect.bottom - (rootRect.bottom - margin);
        root.scrollTop += delta;
        return;
      }

      // если поле выше видимой зоны контейнера
      if (elRect.top < rootRect.top + margin) {
        const delta = (rootRect.top + margin) - elRect.top;
        root.scrollTop -= delta;
      }
    };

    // iOS/Telegram: клавиатура и viewport доезжают в несколько тиков
    requestAnimationFrame(() => requestAnimationFrame(ensureVisible));
    setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(ensureVisible)), 120);
    setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(ensureVisible)), 360);
  } catch {}
}

function focusModalInputAndOpenKeyboard(inputRef) {
  let cancelled = false;

  const focus = () => {
    if (cancelled) return;

    const el = inputRef?.current;
    if (!el) return;

    try {
      el.focus();

      const len = String(el.value ?? "").length;

      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(0, len);
      } else if (typeof el.select === "function") {
        el.select();
      }
    } catch {}
  };

  focus();

  try {
    requestAnimationFrame(focus);
    requestAnimationFrame(() => requestAnimationFrame(focus));
  } catch {}

  const timers = [0, 30, 80, 160, 320, 650].map((ms) =>
    setTimeout(focus, ms)
  );

  return () => {
    cancelled = true;
    timers.forEach(clearTimeout);
  };
}

// ✅ Чип для КБЖУ в карточках еды
function MyFoodMacroChip({ label, value }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[9px] border border-white/10 bg-white/10 px-2 py-1 text-[11px] font-semibold leading-none text-slate-100 shadow-[0_0_10px_rgba(15,23,42,0.12)]">
      {label ? (
        <span className="whitespace-nowrap text-cyan-100/90">{label}</span>
      ) : null}
      <span className="whitespace-nowrap text-slate-50">{value}</span>
    </span>
  );
}


function parseNullableNumber(v) {
  // пусто => null (чтобы показывать placeholder)
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
}

// ✅ Для полей результата фото.
// Важно: НЕ превращаем ввод сразу в Number, иначе "12," мгновенно становится "12",
// а при удалении символов окно начинает дёргаться из-за постоянной перерисовки.
function sanitizePhotoNumberInput(value, { decimal = true } = {}) {
  const raw = String(value ?? "")
    .replace(/\s+/g, "")
    .replace(/\./g, ",");

  let result = "";
  let hasComma = false;

  for (const ch of raw) {
    if (/\d/.test(ch)) {
      result += ch;
      continue;
    }

    if (decimal && ch === "," && !hasComma) {
      result += ",";
      hasComma = true;
    }
  }

  return result;
}

function readPhotoNumber(value) {
  if (value === "" || value == null) return null;

  const cleaned = String(value)
    .replace(/\s+/g, "")
    .replace(",", ".");

  if (cleaned === "" || cleaned === ".") return null;

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function photoNumberOrZero(value) {
  const n = readPhotoNumber(value);
  return Number.isFinite(n) ? n : 0;
}

function calcPhotoKcalFromMacros(total = {}) {
  const proteins = photoNumberOrZero(total.proteins);
  const fats = photoNumberOrZero(total.fats);
  const carbs = photoNumberOrZero(total.carbs);

  return Math.round(proteins * 4 + fats * 9 + carbs * 4);
}

function formatPhotoSummaryNumber(value, digits = 0) {
  const n = readPhotoNumber(value);

  if (!Number.isFinite(n)) {
    return digits > 0 ? "0,0" : "0";
  }

  try {
    return new Intl.NumberFormat("ru-RU", {
      maximumFractionDigits: digits,
    }).format(n);
  } catch {
    return digits > 0 ? String(+n.toFixed(digits)).replace(".", ",") : String(Math.round(n));
  }
}
// Черновик тренировки может сохраняться с выбранными мышцами, но без реальных данных.
// Чтобы не показывать "Продолжить тренировку" постоянно — проверяем, есть ли что продолжать.
function hasMeaningfulDraftWorkout(d) {
  if (!d) return false;

  // если пользователь уже задал название — это тоже активный черновик
  if (String(d.workoutTitle || d.title || "").trim()) return true;

  // если тренировка уже была открыта с выбранными мышцами — это тоже активный черновик
  if (Array.isArray(d.selectedMuscles) && d.selectedMuscles.length > 0) return true;
  if (d.trainMode === "cardio") return true;

  // если таймер был запущен — это тоже черновик тренировки
  if (d.workoutTimer?.isRunning || getWorkoutTimerElapsedMs(d.workoutTimer, Date.now()) > 0) {
    return true;
  }

  // есть кардио-элементы
  if (Array.isArray(d.cardioItems) && d.cardioItems.length > 0) return true;
  if (Array.isArray(d.cardio) && d.cardio.length > 0) return true;

  const isPlaceholderName = (name) => {
    const n = String(name || "").trim().toLowerCase();
    // "1 упражнение", "2 упражнение", ...
    return /^\d+\s+упражнение$/.test(n) || n === "упражнение";
  };

  const ex = Array.isArray(d.exercises) ? d.exercises : [];
  for (const e of ex) {
    const name = String(e?.name || "").trim();

    // имя “не плейсхолдер” -> значит есть смысл
    if (name && !isPlaceholderName(name)) return true;

    const sets = Array.isArray(e?.sets) ? e.sets : [];
    for (const s of sets) {
      const w = s?.weight;
      const r = s?.reps;
      if (
        (w != null && String(w).trim() !== "") ||
        (r != null && String(r).trim() !== "")
      ) {
        return true;
      }
    }
  }

  return false;
}

function createEmptyWorkoutTimer() {
  return {
    isRunning: false,
    startedAt: null,
    elapsedMs: 0,
  };
}

function normalizeWorkoutTimer(timer) {
  if (!timer) return createEmptyWorkoutTimer();

  const elapsedMs = Math.max(0, Number(timer.elapsedMs || 0));
  const startedAtNumber = Number(timer.startedAt || 0);
  const isRunning = Boolean(timer.isRunning && startedAtNumber > 0);

  return {
    isRunning,
    startedAt: isRunning ? startedAtNumber : null,
    elapsedMs,
  };
}

function getWorkoutTimerElapsedMs(timer, now = Date.now()) {
  const t = normalizeWorkoutTimer(timer);

  if (!t.isRunning || !t.startedAt) {
    return t.elapsedMs;
  }

  return t.elapsedMs + Math.max(0, Number(now) - Number(t.startedAt));
}

function formatWorkoutDuration(totalSeconds) {
  const sec = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;

  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (hours > 0) return `${hours}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}
// ключ для хранения id юзера из БД (отдельно для каждого Telegram-пользователя)
const dbUserKey = () => `fitmeup:v3:dbUserId:${getUid()}`;

function isFutureClientDate(value) {
  if (!value) return false;

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) return false;

  return date.getTime() > Date.now();
}

function buildClientAccessInfo(user) {
  const trialActive = isFutureClientDate(user?.trial_until);
  const premiumActive = isFutureClientDate(user?.premium_until);

  return {
    active: trialActive || premiumActive,
    trialActive,
    premiumActive,
    trialUntil: user?.trial_until || null,
    premiumUntil: user?.premium_until || null,
  };
}

function useDbUserId() {
  const {
    user: authUser,
    setUser: setAuthUser,
    isTelegram,
  } = useAuth();

  const [dbUserId, setDbUserId] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [userAccess, setUserAccess] = useState(() =>
    buildClientAccessInfo(null)
  );
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async ({
    silent = false,
    forceServer = false,
  } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      // WEB / PWA:
      // WebAuthGate уже получил пользователя через /api/auth.
      // Повторно сервер не вызываем.
      if (
        !forceServer &&
        !isTelegram &&
        authUser?.id
      ) {
        const user = authUser;
        const id = Number(user.id);

        if (!Number.isFinite(id) || id <= 0) {
          console.error(
            "[dbUser] bad web user id:",
            user.id
          );
          return;
        }

        setDbUserId(id);
        setDbUser(user);
        setUserAccess(
          buildClientAccessInfo(user)
        );

        if (typeof window !== "undefined") {
          window.localStorage.setItem(
            dbUserKey(),
            String(id)
          );
        }

        return;
      }

      // Telegram или явный reload:
      // получаем свежего пользователя с сервера.
      const key = dbUserKey();

      const stored =
        typeof window !== "undefined"
          ? window.localStorage.getItem(key)
          : null;

      if (stored) {
        const idFromStorage = Number(stored);

        if (
          Number.isFinite(idFromStorage) &&
          idFromStorage > 0
        ) {
          setDbUserId(idFromStorage);
        }
      }

      const res = await tgFetch("/api/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          telegramId: getUid(),
          initData: getInitData(),
          appLanguage:
            i18n.resolvedLanguage === "ru"
              ? "ru"
              : "en",
        }),
      });

      const json = await res
        .json()
        .catch(() => null);

      if (
        !res.ok ||
        !json?.ok ||
        !json.user
      ) {
        console.error(
          "[dbUser] Ошибка /api/auth:",
          json?.error || res.status
        );
        return;
      }

      const user = json.user;
      const id = Number(user.id);

      if (!Number.isFinite(id) || id <= 0) {
        console.error(
          "[dbUser] bad user id:",
          user.id
        );
        return;
      }

      setDbUserId(id);
      setDbUser(user);
      setUserAccess(
        buildClientAccessInfo(user)
      );

      // Для web обновляем также общий AuthContext,
      // если это был явный reload.
      if (!isTelegram) {
        setAuthUser(user);
      }

      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          key,
          String(id)
        );
      }
    } catch (e) {
      console.error(
        "useDbUserId error:",
        e
      );
    } finally {
      setLoading(false);
    }
  }, [
    authUser,
    isTelegram,
    setAuthUser,
  ]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return {
    dbUserId,
    dbUserLoading: loading,
    dbUser,
    userAccess,
    reloadDbUser: () =>
      loadUser({
        silent: true,
        forceServer: true,
      }),
  };
}

const k = (name) => `fitmeup:${getUid()}:${name}`;
const DRAFT_WORKOUT_KEY = k("draftWorkout");


async function storageGet(key, defVal = null) {
  if (typeof window === "undefined") return defVal;

  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return defVal;
    return JSON.parse(raw);
  } catch (e) {
    debugWarn("[storageGet] error for key", key, e);
    return defVal;
  }
}

async function storageSet(key, value) {
  if (typeof window === "undefined") return;

  try {
    const raw = JSON.stringify(value);
    window.localStorage.setItem(key, raw);
  } catch (e) {
    debugWarn("[storageSet] error for key", key, e);
  }
}

// === Простое сохранение действий пользователя (локально/в TG CloudStorage) ===
const SESSION_ID = (() => {
  if (typeof window === "undefined") return "srv";
  try {
    const ss = window.sessionStorage;
    const exist = ss.getItem("fitmeup:sid");
    if (exist) return exist;
    const sid = `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    ss.setItem("fitmeup:sid", sid);
    return sid;
  } catch (e) {
    // sessionStorage может быть недоступен в iOS/Telegram WebView
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }
})();


const isoDate = (d = new Date()) => d.toISOString().slice(0,10);
const eventsKey = (dateStr = isoDate()) => k(`events:${dateStr}`);

async function logEvent(type, payload = {}) {
  const evt = {
    userId: getUid(),
    sessionId: SESSION_ID,
    type,
    payload,
    time: Date.now()
  };
  const key = eventsKey();
  const arr = await storageGet(key, []);
  arr.push(evt);
  if (arr.length > 1000) arr.splice(0, arr.length - 1000);
  await storageSet(key, arr);
}

// === Telegram WebApp: безопасная инициализация ===


function useTelegramInitSafe() {
  useEffect(() => {
    const tg = getTg();
    if (!tg) {
      // Открыто в обычном браузере — просто идём дальше без Telegram API
      debugLog("Telegram WebApp не найден — открыто в браузере");
      return;
    }
    tg.ready?.();
    tg.expand?.();
        if (tg?.isVersionAtLeast?.("7.7")) {
      tg?.disableVerticalSwipes?.();
    }

    debugLog("User from Telegram:", tg.initDataUnsafe?.user);
  }, []);
}

// === Масштабирование/режимы под ширину экрана ===
// Логика:
// >420 — центрируем, ширина 420px, scale=1
// 390..420 — та же ширина 420px, но scale = width/420 (равномерное уменьшение/увеличение)
// <390 — ОТКЛЮЧАЕМ масштаб: ширина 100%, scale=1 (включается «логичный» адаптив)
const BASE_WIDTH = 420;
const MIN_WIDTH = 390;
function useInputFocusFlag() {
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    let timer = 0;

    const isEditable = (el) => {
      if (!el || !el.isConnected) return false;

      const tag = String(el.tagName || "").toUpperCase();

      if (tag === "TEXTAREA" || tag === "SELECT") return true;

      if (tag === "INPUT") {
        const type = String(el.type || "text").toLowerCase();

        return ![
          "button",
          "checkbox",
          "color",
          "file",
          "hidden",
          "image",
          "radio",
          "range",
          "reset",
          "submit",
        ].includes(type);
      }

      return Boolean(el.isContentEditable);
    };

    const hasEditableFocus = () => isEditable(document.activeElement);

    const showHiddenState = () => {
      clearTimeout(timer);
      setIsInputFocused(true);
    };

    const checkAndRestore = () => {
      clearTimeout(timer);

      timer = window.setTimeout(() => {
        setIsInputFocused(hasEditableFocus());
      }, 120);
    };

    const onFocusIn = (e) => {
      if (isEditable(e.target)) {
        showHiddenState();
      }
    };

    const onFocusOut = () => {
      checkAndRestore();
      window.setTimeout(checkAndRestore, 350);
    };

    const onPointerDown = (e) => {
      if (isEditable(e.target)) {
        showHiddenState();
      } else {
        checkAndRestore();
      }
    };

    const onViewportChanged = () => {
      if (!hasEditableFocus()) {
        checkAndRestore();
      }
    };

    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);

    window.addEventListener("resize", onViewportChanged);
    window.addEventListener("orientationchange", onViewportChanged);
    window.addEventListener("pageshow", onViewportChanged);
    document.addEventListener("visibilitychange", onViewportChanged);

    window.visualViewport?.addEventListener("resize", onViewportChanged);
    window.visualViewport?.addEventListener("scroll", onViewportChanged);

    return () => {
      clearTimeout(timer);

      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);

      window.removeEventListener("resize", onViewportChanged);
      window.removeEventListener("orientationchange", onViewportChanged);
      window.removeEventListener("pageshow", onViewportChanged);
      document.removeEventListener("visibilitychange", onViewportChanged);

      window.visualViewport?.removeEventListener("resize", onViewportChanged);
      window.visualViewport?.removeEventListener("scroll", onViewportChanged);
    };
  }, []);

  return isInputFocused;
}


function useScaleFactor(freeze = false) {

  const [state, setState] = useState({ scale: 1, innerWidth: "420px" });
  const lastWRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const getWidth = () =>
      Math.round((window.visualViewport?.width ?? window.innerWidth) * 100) / 100;

    const update = () => {
  // Когда открыт инпут (клавиатура) — НЕ пересчитываем scale,
  // иначе в iOS/Telegram может быть "дёрганье" интерфейса.
  if (freeze) return;

      const w = getWidth();

      // НЕ реагируем на появление клавиатуры
      if (lastWRef.current != null && Math.abs(w - lastWRef.current) < 0.5) return;
      lastWRef.current = w;

            // В Telegram/iOS масштабирование через transform даёт "мигание/подгрузку".
      // Поэтому делаем только адаптацию ширины, без scale.
      if (w > 420) {
        setState({ scale: 1, innerWidth: "420px" });
      } else {
        setState({ scale: 1, innerWidth: "100%" });
      }

    };

    const onResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      cancelAnimationFrame(rafRef.current);
    };
}, [freeze]);

  return state;
}

function useAppHeight() {
  useEffect(() => {
    const root = document.documentElement;

    // запоминаем "нормальную" высоту экрана (без клавиатуры)
    let stableH = window.innerHeight;

    const setStable = () => {
      // Берём ТОЛЬКО innerHeight (не visualViewport), иначе клавиатура будет дергать высоту
      const h = window.innerHeight;

      // Если сейчас фокус в инпуте/текстэрии и высота стала меньше — это почти точно клавиатура.
      // Игнорируем, чтобы не было общего reflow и "подвисания".
      const ae = document.activeElement;
      const isTyping =
        ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable);

      if (isTyping && h < stableH) {
        return;
      }

      stableH = h;
      root.style.setProperty("--app-height", `${stableH}px`);
    };

    setStable();

    // IMPORTANT: слушаем только window resize + orientationchange.
    // НЕ подписываемся на visualViewport.resize.
    window.addEventListener("resize", setStable);
    window.addEventListener("orientationchange", setStable);

    // когда убрали фокус (клавиатура закрылась) — можно обновить
    const onFocusOut = () => setTimeout(setStable, 50);
    window.addEventListener("focusout", onFocusOut);

    return () => {
      window.removeEventListener("resize", setStable);
      window.removeEventListener("orientationchange", setStable);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);
}




function useTelegramInit() {
  useEffect(() => {
    const tg = getTg(); // всегда берём свежий инстанс
    try {
      tg?.ready?.();
      tg?.expand?.();
      // Делает мини-апп "железным": свайпы по контенту больше не сворачивают/не минимизируют
      if (tg?.isVersionAtLeast?.("7.7")) {
        tg?.disableVerticalSwipes?.();
      }

      // тёмная шапка под Emerald Mist
      tg?.setHeaderColor?.(APP_SOLID_BG);
tg?.setBackgroundColor?.(APP_SOLID_BG);

      tg?.HapticFeedback?.impactOccurred?.("soft");
    } catch {}
  }, []);
}

const IS_DEV =
  typeof import.meta !== "undefined" &&
  Boolean(import.meta.env?.DEV);

const debugLog = (...args) => {
  if (IS_DEV) {
    console.log(...args);
  }
};

const debugWarn = (...args) => {
  if (IS_DEV) {
    console.warn(...args);
  }
};

function cn(...classes) { return classes.filter(Boolean).join(" "); }
const FOOD_WIDGET_CARD =
  "relative min-h-[138px] overflow-visible rounded-[24px] border border-[#18d7bd]/30 bg-[#051723]/78 p-3 " +
  "shadow-[0_0_0_1px_rgba(24,215,189,0.025),0_14px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl";

const FOOD_COMPACT_CARD =
  "relative overflow-visible rounded-[24px] border border-[#18d7bd]/30 bg-[#051723]/78 " +
  "shadow-[0_0_0_1px_rgba(24,215,189,0.025),0_14px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl";

const FOOD_HERO_CARD =
  "relative min-h-[154px] rounded-[28px] border border-[#18d7bd]/30 bg-[#051723]/78 pl-4 pr-4 pt-3 pb-4 " +
  "shadow-[0_0_0_1px_rgba(24,215,189,0.025),0_14px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl";

const FOOD_WIDGET_ICON =
  "grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] " +
  "text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl outline-none focus:outline-none focus:ring-0";

const FOOD_WIDGET_SMALL_BTN =
  "grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[15px] font-semibold text-[#35F3DF] " +
  "shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl outline-none focus:outline-none focus:ring-0 active:scale-[0.96]";

const FOOD_WIDGET_INFO_BTN =
  "absolute -right-[7px] -top-[7px] z-20 grid h-6 w-6 place-items-center rounded-full " +
  "border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[12px] font-black text-[#35F3DF] " +
  "shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl outline-none focus:outline-none focus:ring-0 active:scale-[0.96]";

function FoodDropIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none">
      <path
        d="M12 3.1C12 3.1 6.4 10 6.4 14.6A5.6 5.6 0 0 0 12 20.2a5.6 5.6 0 0 0 5.6-5.6C17.6 10 12 3.1 12 3.1Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FoodScaleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none">
      <rect
        x="4.5"
        y="4.5"
        width="15"
        height="15"
        rx="4"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 9l1.35-1.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function FoodMoonIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none">
      <path
        d="M19.7 14.2A7.6 7.6 0 0 1 9.8 4.3A8.2 8.2 0 1 0 19.7 14.2Z"
        fill="currentColor"
      />
      <path
        d="M18.5 4.5v2M17.5 5.5h2"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoodFace({ type = 0, active = false }) {
  const stroke = active ? "#031018" : "rgba(175,205,208,0.88)";
  const fill = active ? "#35F3DF" : "rgba(53,243,223,0.052)";
  const border = active ? "#35F3DF" : "rgba(53,243,223,0.30)";

  return (
    <svg viewBox="0 0 40 40" className="h-6 w-6" fill="none">
      <circle cx="20" cy="20" r="15" fill={fill} stroke={border} strokeWidth="2" />

      {/* 0: радостный */}
      {type === 0 && (
        <>
          <circle cx="15" cy="17" r="1.7" fill={stroke} />
          <circle cx="25" cy="17" r="1.7" fill={stroke} />
          <path
            d="M13.8 22.7C16.1 26.5 23.9 26.5 26.2 22.7"
            stroke={stroke}
            strokeWidth="2.25"
            strokeLinecap="round"
          />
        </>
      )}

      {/* 1: нейтральный */}
      {type === 1 && (
        <>
          <circle cx="15" cy="17" r="1.55" fill={stroke} />
          <circle cx="25" cy="17" r="1.55" fill={stroke} />
          <path
            d="M15.2 24H24.8"
            stroke={stroke}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </>
      )}

      {/* 2: спокойный / сонный — теперь явно другой */}
      {type === 2 && (
        <>
          <path
            d="M13.2 17.2C14.7 18.3 16.3 18.3 17.8 17.2"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M22.2 17.2C23.7 18.3 25.3 18.3 26.8 17.2"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M16.4 24.2C18.2 25.2 21.8 25.2 23.6 24.2"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M27.5 10.5H30.5L27.5 13.5H30.5"
            stroke={stroke}
            strokeWidth="1.45"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.9"
          />
        </>
      )}

      {/* 3: плохое настроение */}
      {type === 3 && (
        <>
          <path d="M13.6 15.5L17.2 18.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M17.2 15.5L13.6 18.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M22.8 15.5L26.4 18.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M26.4 15.5L22.8 18.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path
            d="M14.8 27C17.4 24.5 22.6 24.5 25.2 27"
            stroke={stroke}
            strokeWidth="2.25"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
}
// ✅ Защита от ghost-click / click-through в Telegram WebView и мобильных браузерах.
// Когда меню/модалка закрывается, браузер иногда досылает тот же тап в карточку под ним.
// Мы на короткое время блокируем следующий клик по карточкам/граммовке.
const TAP_GUARD_MS = 1200;
const TAP_MOVE_TOLERANCE = 12;

let tapGuardUntil = 0;
let tapGuardInstalled = false;

const tapStartByElement = new WeakMap();

function protectNextTap(ms = TAP_GUARD_MS) {
  tapGuardUntil = Math.max(tapGuardUntil, Date.now() + ms);
}

function isTapGuardActive() {
  return Date.now() < tapGuardUntil;
}

function stopNativeEvent(e) {
  e?.preventDefault?.();
  e?.stopPropagation?.();
  e?.nativeEvent?.stopImmediatePropagation?.();
  e?.stopImmediatePropagation?.();
}

function stopAndProtect(e, ms = TAP_GUARD_MS) {
  stopNativeEvent(e);
  protectNextTap(ms);
}

function getPointerPoint(e) {
  return {
    x: Number(e?.clientX ?? 0),
    y: Number(e?.clientY ?? 0),
  };
}

// Для кнопок граммовки.
// На pointerDown только запоминаем точку касания.
// Ничего не открываем, чтобы случайный скролл не вызывал модалку.
function rememberTapStart(e) {
  e?.stopPropagation?.();
  e?.nativeEvent?.stopImmediatePropagation?.();

  const el = e?.currentTarget;
  if (!el) return;

  const p = getPointerPoint(e);

  tapStartByElement.set(el, {
    x: p.x,
    y: p.y,
    time: Date.now(),
  });
}

function clearTapStart(e) {
  const el = e?.currentTarget;
  if (!el) return;
  tapStartByElement.delete(el);
}

// Запускаем действие только если это был настоящий тап,
// а не начало скролла.
function runIfRealTap(e, action, ms = TAP_GUARD_MS) {
  const el = e?.currentTarget;
  const start = el ? tapStartByElement.get(el) : null;

  if (el) tapStartByElement.delete(el);

  stopNativeEvent(e);

  if (!start) return;
  if (isTapGuardActive()) return;

  const p = getPointerPoint(e);
  const dx = Math.abs(p.x - start.x);
  const dy = Math.abs(p.y - start.y);
  const dt = Date.now() - start.time;

  if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) return;
  if (dt > 900) return;

  protectNextTap(ms);
  action?.();
}

// Для кнопок "...": открываем меню только на настоящий тап,
// но НЕ включаем protectNextTap, чтобы меню не ощущалось ватным.
function runMenuTap(e, action) {
  const el = e?.currentTarget;
  const start = el ? tapStartByElement.get(el) : null;

  if (el) tapStartByElement.delete(el);

  stopNativeEvent(e);

  if (!start) return;

  const p = getPointerPoint(e);
  const dx = Math.abs(p.x - start.x);
  const dy = Math.abs(p.y - start.y);
  const dt = Date.now() - start.time;

  if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) return;
  if (dt > 900) return;

  action?.();
}


function blockGuardedClick(e) {
  if (
    e.target?.closest?.("#fitmeup-confirm-overlay")
  ) {
    return;
  }

  if (!isTapGuardActive()) return;
  stopNativeEvent(e);
}


function installTapGuard() {
  if (tapGuardInstalled) return;
  if (typeof document === "undefined") return;

  tapGuardInstalled = true;

  // capture=true: ловим ghost-click ДО React onClick.
  document.addEventListener("click", blockGuardedClick, true);
  document.addEventListener("mouseup", blockGuardedClick, true);
  document.addEventListener("pointerup", blockGuardedClick, true);
  document.addEventListener("touchend", blockGuardedClick, true);
}

installTapGuard();
function useFitmeupBottomNavHidden(active) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;

    const root = document.documentElement;
    const count = Number(root.dataset.fitmeupHideBottomNavCount || 0);

    root.dataset.fitmeupHideBottomNavCount = String(count + 1);
    root.dataset.fitmeupHideBottomNav = "1";

    return () => {
      const next = Math.max(
        0,
        Number(root.dataset.fitmeupHideBottomNavCount || 1) - 1
      );

      if (next > 0) {
        root.dataset.fitmeupHideBottomNavCount = String(next);
        root.dataset.fitmeupHideBottomNav = "1";
      } else {
        delete root.dataset.fitmeupHideBottomNavCount;
        delete root.dataset.fitmeupHideBottomNav;
      }
    };
  }, [active]);
}

function keepMyFoodsSearchVisible(el) {
  if (!el || typeof window === "undefined") return;

  const run = () => {
    try {
      if (!el.isConnected) return;

      const root =
        el.closest?.('[data-my-foods-scroll-root="1"]') ||
        el.closest?.('[data-app-scroll-root="1"]');

      if (!root || typeof root.scrollBy !== "function") return;

      const rootRect = root.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const vv = window.visualViewport;
      const visibleBottom = vv
        ? Math.min(rootRect.bottom, vv.offsetTop + vv.height)
        : rootRect.bottom;

      const topLimit = rootRect.top + 18;
      const bottomLimit = visibleBottom - 18;

      if (elRect.top < topLimit) {
        root.scrollBy({
          top: elRect.top - topLimit,
          behavior: "auto",
        });
        return;
      }

      if (elRect.bottom > bottomLimit) {
        root.scrollBy({
          top: elRect.bottom - bottomLimit,
          behavior: "auto",
        });
      }
    } catch {}
  };

  requestAnimationFrame(run);
  [60, 140, 260, 420, 700].forEach((ms) => {
    window.setTimeout(run, ms);
  });
}
const DOT_MENU_BTN_CLASS =
  "grid h-6 w-6 shrink-0 place-items-center rounded-full " +
  "border border-white/20 bg-slate-700/90 " +
  "text-[14px] font-bold leading-none text-white " +
  "shadow-[0_8px_18px_rgba(0,0,0,0.26),inset_0_1px_0_rgba(255,255,255,0.10)] " +
  "backdrop-blur-md transition active:scale-[0.96]";

const DOT_MENU_WRAP_CLASS =
  "absolute -right-1 -top-1 z-[260] flex items-center";
  const MEAL_ITEM_DOT_MENU_WRAP_CLASS =
  "absolute right-1 -top-1 z-[80] flex items-center";
// ✅ Верхние кнопки-пилюли (как на твоём красивом скрине)
const TOP_PILL_BTN =
  "inline-flex items-center justify-center gap-2 " +
  "h-11 px-6 rounded-full " +
  "border border-white/30 bg-white/15 " +
  "text-[17px] font-medium text-slate-50/95 " +
  "shadow-[0_8px_22px_rgba(0,0,0,0.18)] backdrop-blur-xl " +
  "hover:bg-white/20 active:scale-[0.97] " +
  "disabled:opacity-45 disabled:cursor-not-allowed";

// если нужно чуть меньше (иногда удобнее на маленьких экранах)
const TOP_PILL_BTN_SM =
  "inline-flex items-center justify-center gap-2 " +
  "h-9 px-4 rounded-full " +
  "border border-white/30 bg-white/15 " +
  "text-[13px] font-medium text-slate-50/95 " +
  "shadow-[0_8px_22px_rgba(0,0,0,0.18)] backdrop-blur-xl " +
  "hover:bg-white/20 active:scale-[0.97] " +
  "disabled:opacity-45 disabled:cursor-not-allowed";
// ✅ Единый крестик закрытия для экранов еды/продуктов/блюд.
// Большая зона нажатия + моментальное закрытие на pointerDown/touch, чтобы в Telegram не было “тугого” крестика.
const APP_CLOSE_BUTTON_CLASS =
  "grid h-8 w-8 shrink-0 place-items-center rounded-full " +
  "border border-cyan-200/35 bg-cyan-300/[0.10] " +
  "text-[17px] font-extrabold leading-none text-cyan-50 " +
  "shadow-[0_0_16px_rgba(45,212,191,0.13),inset_0_1px_0_rgba(255,255,255,0.08)] " +
  "backdrop-blur-xl transition hover:bg-cyan-300/[0.16] active:scale-[0.96]";

function AppCloseButton({ className = "", children = "×", type = "button", onClick, style, ...props }) {
  const firedRef = useRef(0);
  const hasCustomTap = !!props.onPointerDown || !!props.onPointerUp;

  const fire = (e) => {
    const now = Date.now();
    if (now - firedRef.current < 350) return;
    firedRef.current = now;

    stopAndProtect(e, 700);
    onClick?.(e);
  };

  return (
    <button
      type={type}
      {...props}
      aria-label={props["aria-label"] || i18n.t("actions.close")}
      onPointerDown={(e) => {
        stopNativeEvent(e);
        props.onPointerDown?.(e);
        if (!hasCustomTap) fire(e);
      }}
      onPointerUp={(e) => {
        if (props.onPointerUp) {
          props.onPointerUp(e);
          return;
        }
        stopNativeEvent(e);
      }}
      onTouchStart={(e) => {
        // fallback для старых WebView без PointerEvent
        if (typeof window !== "undefined" && !window.PointerEvent && !hasCustomTap) {
          fire(e);
        }
      }}
      onClick={(e) => {
        if (hasCustomTap) {
          stopNativeEvent(e);
          onClick?.(e);
          return;
        }
        fire(e);
      }}
      style={{ touchAction: "manipulation", ...(style || {}) }}
      className={`${APP_CLOSE_BUTTON_CLASS} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

function FullNamePopup() {
  return null;
}

function FoodNameButton({ name, className = "" }) {
  const text = String(name || "Без названия");

  return (
    <div
      title={text}
      className={cn(
        "min-w-0 w-full whitespace-normal break-words text-left text-[17px] font-bold leading-snug tracking-[-0.01em] text-slate-50",
        className
      )}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </div>
  );
}
function summarizeList(items, take = 1) {
  const arr = (items || []).filter(Boolean);
  if (arr.length <= take) return { text: arr.join(", "), extra: 0, arr };
  return { text: arr.slice(0, take).join(", "), extra: arr.length - take, arr };
}

function computeWorkoutStats(workout) {
  const exercises = workout?.exercises || [];
  let setsCount = 0;
  let totalReps = 0;
  let tonnage = 0;

  for (const ex of exercises) {
    for (const s of ex?.sets || []) {
      const w = Number(s?.weight);
      const r = Number(s?.reps);

      // считаем тоннаж только когда есть и вес, и повторы
      if (Number.isFinite(w) && Number.isFinite(r)) {
        setsCount += 1;
        totalReps += r;
        tonnage += w * r;
      }
    }
  }

tonnage = Math.round(tonnage);

return { setsCount, totalReps, tonnage };
}

function computeCardioSummary(workout) {
  const items = workout?.cardio || workout?.cardioItems || [];
  const list = Array.isArray(items) ? items : [];
  let totalMinutes = 0;
  let mainTitle = "";
  let comment = "";

  for (const it of list) {
    const t = String(it?.title || "").trim();
    const m = Number(it?.minutes);
    const c = String(it?.comment || "").trim();

    if (!mainTitle && t) mainTitle = t;
    if (!comment && c) comment = c;
    if (Number.isFinite(m)) totalMinutes += m;
  }

  totalMinutes = Math.round(totalMinutes * 10) / 10;

  return {
    itemsCount: list.length,
    totalMinutes,
    mainTitle: mainTitle || (list.length ? "Кардио" : ""),
    comment,
  };
}

function formatKg(n, locale = "ru-RU") {
  const val = typeof n === "number" ? n : 0;
  const rounded = Math.round(val * 10) / 10;

  try {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(rounded);
  } catch {
    return String(rounded);
  }
}

function Portal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}


const APP_SOLID_BG = "#020817";

const MAIN_APP_BG =
  "radial-gradient(circle at 0% 0%, #14b8a6 0, transparent 55%), radial-gradient(circle at 100% 100%, #0f172a 0, transparent 55%), #020817";

// Используем один общий фирменный фон везде,
// чтобы страницы «Еда», «Мои продукты и блюда» и «Создать блюдо» не отличались по цвету.
const APP_BG = MAIN_APP_BG;
const SHEET_PREMIUM_BG = MAIN_APP_BG;


const FULLSCREEN_APP_BG_STYLE = {
  position: "fixed",
  inset: 0,
  width: "100vw",
  height: "var(--app-height, 100vh)",
  background: MAIN_APP_BG,
  pointerEvents: "none",
  zIndex: 0,
};
function BrandOverlay({ onClick }) {
  return (
    <div
      className="fixed inset-0"
      style={{
        background: APP_BG,
        opacity: 1,
        overscrollBehavior: "none",
      }}
      onClick={onClick}
    />
  );
}

function GlassCard({
  className = "",
  children,
  onClick,
  solid = false,
  ...rest
}) {
  const clickable =
    typeof onClick === "function";

  return (
    <div
      {...rest}
      role={clickable ? "button" : undefined}
      onClick={onClick}
      className={cn(
        solid
          ? "relative isolate overflow-visible rounded-3xl text-left transition"
          : "relative isolate overflow-visible rounded-3xl border border-white/20 bg-white/20 p-4 text-left shadow-lg backdrop-blur-xl transition",
        clickable
          ? solid
            ? "active:scale-[0.99] cursor-pointer"
            : "active:scale-[0.99] hover:bg-white/25 cursor-pointer"
          : "",
        className
      )}
    >
      {children}
    </div>
  );
}
const BOTTOM_NAV_ICON_SRC = {
  food: bottomFoodImg,
  ration: bottomRationImg,
  body: bottomBodyImg,
  workout: bottomWorkoutImg,
};

function BottomNavIcon({ icon, active }) {
  const src = BOTTOM_NAV_ICON_SRC[icon] || BOTTOM_NAV_ICON_SRC.food;

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable="false"
      className="block shrink-0 select-none transition-all duration-200"
      style={{
width: active ? 31 : 27,
height: active ? 31 : 27,
        objectFit: "contain",
        pointerEvents: "none",
        opacity: active ? 1 : 0.58,
        filter: active
          ? "none"
          : "grayscale(1) brightness(0.72) opacity(0.86)",
        transform: active ? "translateY(-1px)" : "translateY(0)",
      }}
    />
  );
}
function FitmeupBottomNav({ active, hidden = false, items = [] }) {
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  if (!safeItems.length) return null;

  return (
    <Portal>
      <style>{`
        html[data-fitmeup-hide-bottom-nav="1"] [data-fitmeup-bottom-nav="1"] {
          transform: translate3d(-50%, 145%, 0) !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>
<nav
  data-fitmeup-bottom-nav="1"
  aria-label={i18n.t("nav.bottomAria")}
  className="fixed left-1/2"
        style={{
          zIndex: 31000,
          width: "min(354px, calc(100vw - 44px))",
          bottom: hasTgAuth()
            ? "calc(12px + env(safe-area-inset-bottom, 0px))"
            : "max(4px, env(safe-area-inset-bottom, 0px))",
          transform: hidden
            ? "translate3d(-50%, 145%, 0)"
            : "translate3d(-50%, 0, 0)",
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : "auto",
          transition:
            "opacity 140ms ease, transform 180ms cubic-bezier(.2,.8,.2,1)",
          willChange: "transform, opacity",
          transformOrigin: "bottom center",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', Inter, system-ui, sans-serif",
        }}
      >
        <div
          className="relative grid grid-cols-4 items-center overflow-hidden"
          style={{
height: 64,
padding: "7px 6px 6px",
borderRadius: "31px",
            border: "1px solid rgba(142, 156, 172, 0.24)",
            background:
              "linear-gradient(180deg, rgba(8, 18, 30, 0.96) 0%, rgba(3, 11, 20, 0.99) 100%)",
            boxShadow:
              "0 16px 42px rgba(0,0,0,0.58), inset 0 1px 0 rgba(255,255,255,0.075), inset 0 -1px 0 rgba(45,212,191,0.07)",
            backdropFilter: "blur(22px) saturate(130%)",
            WebkitBackdropFilter: "blur(22px) saturate(130%)",
            transform: "translateZ(0)",
          }}
        >
          <div
            className="pointer-events-none absolute inset-x-[26px] top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.13), transparent)",
            }}
          />



          {safeItems.map((item) => {
            const isActive = active === item.key;

            return (
              <button
                key={item.key}
                type="button"
                aria-current={isActive ? "page" : undefined}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick?.();
                }}
                className="relative z-10 flex h-full min-w-0 select-none flex-col items-center justify-center rounded-[24px] active:scale-[0.97]"
                style={{
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <span
                  className="grid h-[33px] w-[50px] place-items-center"
                  style={{
                    transform: isActive ? "translateY(-1px)" : "translateY(0)",
                    transition: "transform 160ms ease",
                  }}
                >
                  <BottomNavIcon icon={item.icon} active={isActive} />
                </span>

                <span
                  className="mt-[3px] block whitespace-nowrap text-center leading-none"
                  style={{
                    fontSize: "12.1px",
                    fontWeight: isActive ? 560 : 430,
                    letterSpacing: "-0.02em",
                    color: isActive ? "#35F3DF" : "rgba(150, 158, 172, 0.78)",
                    textShadow: isActive
                      ? "0 0 10px rgba(53,243,223,0.28)"
                      : "none",
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </Portal>
  );
}
// === Конфиг групп мышц для экрана тренировок ===
const MUSCLE_GROUPS_TOP = [
  {
    key: "back",
    label: "Спина",
    img: backImg,
  },
  {
    key: "chest",
    label: "Грудь",
    img: chestImg,
  },
];

const MUSCLE_GROUPS_MID = [
  {
    key: "forearms",
    label: "Предплечья",
    img: forearmsImg,
  },
  {
    key: "triceps",
    label: "Трицепс",
    img: tricepsImg,
  },
  {
    key: "biceps",
    label: "Бицепс",
    img: bicepsImg,
  },
];

const MUSCLE_GROUPS_BOTTOM = [
  {
    key: "shoulders",
    label: "Плечи",
    img: shouldersImg,
  },
  {
    key: "abs",
    label: "Пресс",
    img: absImg,
  },
  {
    key: "legs",
    label: "Ноги",
    img: legsImg,
  },
];
// все группы мышц одним списком — для поиска по ключу
const ALL_MUSCLE_GROUPS = [
  ...MUSCLE_GROUPS_TOP,
  ...MUSCLE_GROUPS_MID,
  ...MUSCLE_GROUPS_BOTTOM,
];

const getMuscleLabel = (key) => {
  const found = ALL_MUSCLE_GROUPS.find((g) => g.key === key);
  return found ? found.label : key;
};
// === Новый вид карточек "Мои тренировки" ===
const normalizeWorkoutLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е");

const formatWorkoutCardDate = (value, locale = "ru-RU") => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  try {
    return d.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return d.toLocaleDateString();
  }
};

const getWorkoutDisplayTitle = (workout) => {
  const raw = String(
    workout?.title ||
      workout?.workoutTitle ||
      workout?.musclesLabel ||
      "Тренировка"
  ).trim();

  const parts = raw
    .split(/[,•]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return parts.length ? parts.join(" • ") : "Тренировка";
};

const ruPlural = (number, forms) => {
  const n = Math.abs(Number(number) || 0);
  const n10 = n % 10;
  const n100 = n % 100;

  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return forms[1];
  return forms[2];
};
const formatDishCount = (count) => {
  const n = Math.max(0, Number(count) || 0);

  return `${n} ${ruPlural(n, ["блюдо", "блюда", "блюд"])}`;
};
const makeNumberRange = (values, formatter) => {
  const nums = (values || [])
    .map((v) => Number(String(v ?? "").replace(",", ".")))
    .filter((v) => Number.isFinite(v));

  if (!nums.length) return "—";

  const min = Math.min(...nums);
  const max = Math.max(...nums);

  if (min === max) return formatter(min);
  return `${formatter(min)}–${formatter(max)}`;
};

const getExerciseRangeLabel = (
  exercise,
  {
    locale = "ru-RU",
    weightUnit = "кг",
    repsUnit = "повторений",
  } = {}
) => {
  const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];

  const weightRange = makeNumberRange(
    sets.map((s) => s?.weight),
    (n) => formatKg(n, locale)
  );

  const repsRange = makeNumberRange(
    sets.map((s) => s?.reps),
    (n) => String(Math.round(n))
  );

  return `${weightRange} ${weightUnit} • ${repsRange} ${repsUnit}`;
};

const getWorkoutMuscleBadges = (workout) => {
  const result = [];
  const used = new Set();

  const addGroup = (group) => {
    if (!group || used.has(group.key)) return;
    used.add(group.key);
    result.push(group);
  };

  if (Array.isArray(workout?.muscles)) {
    workout.muscles.forEach((key) => {
      const found = ALL_MUSCLE_GROUPS.find((g) => g.key === key);
      addGroup(found);
    });
  }

  const source = [workout?.musclesLabel, workout?.title, workout?.workoutTitle]
    .filter(Boolean)
    .join(", ");

  const tokens = source
    .split(/[,•+]+/)
    .map(normalizeWorkoutLabel)
    .filter(Boolean);

  tokens.forEach((token) => {
    const found = ALL_MUSCLE_GROUPS.find((group) => {
      const label = normalizeWorkoutLabel(group.label);
      const key = normalizeWorkoutLabel(group.key);
      return token === label || token === key || token.includes(label);
    });

    addGroup(found);
  });

  return result;
};

const splitWorkoutTitleParts = (value) =>
  String(value || "")
    .split(/[,•]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const getCompactWorkoutTitleFromLabels = (labels, fallback = "Тренировка") => {
  const arr = (labels || []).map((x) => String(x || "").trim()).filter(Boolean);

  if (arr.length === 0) return fallback || "Тренировка";
  if (arr.length === 1) return arr[0];

  return `${arr[0]} +${arr.length - 1}`;
};

const getCompactWorkoutFilterLabel = (filterName, allLabel = "Все") => {
  if (filterName === allLabel) return filterName;

  const parts = splitWorkoutTitleParts(filterName);
  return getCompactWorkoutTitleFromLabels(parts, filterName || "Тренировка");
};
const getOneWordWorkoutFilterLabel = (filterName, allLabel = "Все") => {
  const text = String(filterName || "").trim();

  if (!text) return "Тренировка";
  if (text === allLabel) return text;

  const firstWord = text.split(/\s+/)[0] || text;

  return text.length > firstWord.length ? `${firstWord}...` : firstWord;
};
const normalizeWorkoutTitleCompare = (value) =>
  normalizeWorkoutLabel(value)
    .replace(/[•]+/g, ",")
    .replace(/\s*,\s*/g, ",")
    .replace(/\s+/g, " ")
    .trim();

const getWorkoutDefaultTitleFromData = (workout) => {
  if (workout?.mode === "cardio") return "Кардио";

  const fromMuscles = Array.isArray(workout?.muscles)
    ? workout.muscles.map(getMuscleLabel).filter(Boolean).join(", ")
    : "";

  const fromLabel = String(workout?.musclesLabel || "").trim();

  return fromMuscles || fromLabel || "Тренировка";
};

const getWorkoutCustomTitle = (workout) => {
  const rawTitle = String(workout?.title || workout?.workoutTitle || "").trim();

  if (!rawTitle) return "";

  const rawNorm = normalizeWorkoutTitleCompare(rawTitle);
  const defaultNorm = normalizeWorkoutTitleCompare(
    getWorkoutDefaultTitleFromData(workout)
  );

  if (!rawNorm || rawNorm === defaultNorm) return "";

  return rawTitle;
};
// Форматирование даты "ДД.ММ"
const formatDateShort = (iso, locale = "ru-RU") => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
  });
};

// Парсинг числа с запятой/точкой
const parseNumber = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isNaN(n) ? null : +n.toFixed(1);
};

// Парсинг БЖУ с запятой/точкой
const parseMacro = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isNaN(n) ? 0 : Math.max(0, +n.toFixed(1));
};
const formatMacroPreview = (value) => {
  const n = Number(value);

  if (!Number.isFinite(n)) return "0";

  const rounded = Math.round(n * 10) / 10;

  if (Math.abs(rounded - Math.round(rounded)) < 0.000001) {
    return String(Math.round(rounded));
  }

  const isRussian =
    i18n.resolvedLanguage === "ru";

  try {
    return new Intl.NumberFormat(
      isRussian ? "ru-RU" : "en-GB",
      {
        maximumFractionDigits: 1,
      }
    ).format(rounded);
  } catch {
    return isRussian
      ? String(rounded).replace(".", ",")
      : String(rounded).replace(",", ".");
  }
};
// БЖУ вводятся на 100 г продукта.
// Каждое значение не должно быть больше 100.
const isTooHighMacrosPer100 = ({ proteins, fats, carbs }) => {
  return [proteins, fats, carbs].some(
    (value) => !Number.isFinite(value) || value > 100
  );
};

// --- Конфигурация доступных замеров ---

const METRICS = [
  { key: "glutes",       label: "Ягодицы",          group: "legs"  },
  { key: "chest",        label: "Грудь",            group: "upper" },
  { key: "back",         label: "Спина",            group: "upper" },
  { key: "shoulders",    label: "Плечи",            group: "upper" },
  { key: "bicepsLeft",   label: "Бицепс левый",     group: "arms"  },
  { key: "bicepsRight",  label: "Бицепс правый",    group: "arms"  },
  { key: "forearmLeft",  label: "Предплечье левое", group: "arms"  },
  { key: "forearmRight", label: "Предплечье правое",group: "arms"  },
  { key: "wristLeft",    label: "Кисть левая",      group: "arms"  },
  { key: "wristRight",   label: "Кисть правая",     group: "arms"  },
  { key: "hipLeft",      label: "Бедро левое",      group: "legs"  },
  { key: "hipRight",     label: "Бедро правое",     group: "legs"  },
  { key: "waist",        label: "Талия",            group: "core"  },
];

// история: массив { value, date } на каждую метрику
const createEmptyHistory = () => {
  const base = {};
  METRICS.forEach((m) => {
    base[m.key] = [];
  });
  return base;
};

const createEmptyInputs = () => {
  const base = {};
  METRICS.forEach((m) => {
    base[m.key] = "";
  });
  return base;
};
function scrollBodyInputAboveKeyboard(el) {
  if (!el || typeof window === "undefined") return;

  const scrollRoot =
    el.closest?.("[data-app-scroll-root='1']") ||
    document.scrollingElement ||
    document.documentElement;

  const metricCard =
    el.closest?.("[data-body-metric-card='1']") || el;

  const run = () => {
    const rootRect = scrollRoot.getBoundingClientRect();
    const cardRect = metricCard.getBoundingClientRect();

    const targetTop =
      scrollRoot.scrollTop + cardRect.top - rootRect.top - 14;

    scrollRoot.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });
  };

  requestAnimationFrame(run);
  window.setTimeout(run, 120);
  window.setTimeout(run, 280);
  window.setTimeout(run, 520);
}
function MetricCard({
  metric,
  history,
  valueInput,
  onChange,
  onSave,
  onRemoveMetric,
  onDeleteMeasurement,
}) {
  const { t, i18n } = useTranslation();

  const bodyLocale =
    i18n.resolvedLanguage === "ru"
      ? "ru-RU"
      : "en-GB";

  const metricLabel = t(
    `body.measurements.metrics.${metric.key}`
  );

  const last = history[history.length - 1] ?? null;
  const prev = history[history.length - 2] ?? null;
  const diff = last && prev ? +(last.value - prev.value).toFixed(1) : null;

  const hasValue = String(valueInput ?? "").trim() !== "";
  const diffArrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const diffText = diff > 0 ? `+${diff}` : `${diff}`;

  return (
    <GlassCard
      solid
      className={cn("cursor-default space-y-2 p-3 pr-10", FOOD_COMPACT_CARD)}
      data-body-metric-card="1"
    >
      {/* Крестик: убрать выбранную часть тела из списка */}
<button
  type="button"
  aria-label={t("body.measurements.removeAria", {
    name: metricLabel,
  })}
  onClick={(e) => {
    stopAndProtect(e, 700);
    onRemoveMetric?.();
  }}
className="absolute right-2 top-2 z-20 grid h-6 w-6 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[15px] font-black leading-none text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition active:scale-[0.94]" 
     >
  ×
</button>

<div className="flex items-start justify-between gap-3">
  <div>
    <div className="text-[14px] font-semibold">{metricLabel}</div>
    <div className="text-[11px] opacity-70">
      {last
        ? t("body.measurements.last", {
            date: formatDateShort(last.date, bodyLocale),
          })
        : t("body.overview.noMeasurements")}
    </div>
  </div>

  {last && (
    <div className="text-right text-[11px]">
      <div>
        {t("body.measurements.current")}{" "}
        <span className="font-semibold">
          {last.value} {t("units.centimetersShort")}
        </span>
      </div>

      {diff != null && diff !== 0 && (
        <div className="mt-0.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-[10px] font-semibold text-slate-100/80">
          <span>{diffArrow}</span>
          <span>
            {diffText} {t("units.centimetersShort")}
          </span>
        </div>
      )}
    </div>
  )}
</div>

      {/* Инпут + кнопка */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]*"
          value={valueInput}
          onFocus={(e) => {
            const el = e.currentTarget;

            try {
              const len = String(el.value ?? "").length;
              el.setSelectionRange?.(len, len);
            } catch {}

            scrollBodyInputAboveKeyboard(el);
          }}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-9 rounded-2xl bg-slate-900/80 border border-white/10 px-3 text-[14px] text-slate-50 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300"
          style={{ caretColor: "#f8fafc" }}
        />

<span className="text-[11px] opacity-70 whitespace-nowrap mr-1">
  {t("units.centimetersShort")}
</span>

<button
  type="button"
  disabled={!hasValue}
  onClick={hasValue ? onSave : undefined}
  className={cn(
    "h-9 px-3 rounded-2xl text-[12px] font-semibold transition-all active:scale-[0.97]",
    hasValue
      ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_6px_20px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
      : "bg-white/5 border border-white/10 text-slate-400 cursor-not-allowed shadow-none"
  )}
>
  {t("actions.save")}
</button>
      </div>

      {/* Мини-история до 3 последних */}
      {history.length > 0 && (
        <div className="pt-2 border-t border-white/10 mt-1">
<div className="text-[11px] opacity-70 mb-1">
  {t("body.measurements.latestMeasurements")}
</div>

<div className="flex flex-wrap gap-2 text-[11px]">
  {history
    .slice(-3)
    .reverse()
    .map((h, idx) => (
      <div
        key={`${h.date}-${h.value}-${idx}`}
        className="inline-flex items-center gap-1 rounded-full bg-slate-900/60 border border-white/10 py-1 pl-2 pr-1"
      >
        <span>
          {formatDateShort(h.date, bodyLocale)} · {h.value}{" "}
          {t("units.centimetersShort")}
        </span>

        {/* Крестик: удалить конкретный сохранённый замер */}
<button
  type="button"
  aria-label={t("body.measurements.deleteMeasurementAria", {
    date: formatDateShort(h.date, bodyLocale),
  })}
  onClick={(e) => {
    stopAndProtect(e, 700);
    onDeleteMeasurement?.(h);
  }}
  className="grid h-4 w-4 shrink-0 place-items-center rounded-full border border-[#18d7bd]/40 bg-[#18d7bd]/[0.085] p-0 text-[#35F3DF] shadow-[0_0_8px_rgba(24,215,189,0.10)] transition active:scale-[0.92]"
>
  <svg
    viewBox="0 0 16 16"
    className="h-2.5 w-2.5"
    aria-hidden="true"
    fill="none"
  >
    <path
      d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
</button>
                </div>
              ))}
          </div>
        </div>
      )}
    </GlassCard>
  );
}
// ===== Модалки тренировки (упражнения + подходы) =====
function softCloseModal(onClose) {
  try {
    const ae = document.activeElement;
    if (ae && typeof ae.blur === "function") ae.blur();
  } catch {}
  requestAnimationFrame(() => onClose?.());
}

function BaseModal({ open, onClose, children, plain = false }) {

 
 

    const baseHRef = useRef(0);
  const [zIndex, setZIndex] = useState(50000);
const softClose = () => {
  try {
    const ae = document.activeElement;
    if (ae && typeof ae.blur === "function") ae.blur();
  } catch {}

  requestAnimationFrame(() => onClose?.());
};

  const shellRef = useRef(null);
  const scrollRef = useRef(null);

  // modal stack + scroll lock
const stack =
    typeof window !== "undefined"
      ? (window.__FITMEUP_MODAL_STACK__ ||= { count: 0, z: 50000, saved: null })
      : { count: 0, z: 50000, saved: null };

  if (stack.z < 50000) stack.z = 50000;

  useEffect(() => {
     
 

    if (!open) return;
    
    baseHRef.current = window.innerHeight;

stack.z += 1;
setZIndex(stack.z);
  

    // lock body scroll (минимальный)
    if (stack.count === 0) {
      const body = document.body;
      const html = document.documentElement;

      stack.saved = {
        bodyOverflow: body.style.overflow,
        bodyOverscroll: body.style.overscrollBehavior,
        bodyTouchAction: body.style.touchAction,
        htmlOverscroll: html.style.overscrollBehavior,
      };

      body.style.overflow = "hidden";
body.style.overscrollBehavior = "none";
// iOS/Telegram: touchAction "none" ломает фокус/курсор
body.style.touchAction = "manipulation";
html.style.overscrollBehavior = "none";



    }

    stack.count += 1;

    return () => {
      // iOS/Telegram: чтобы закрытие не подвисало, сначала снимаем фокус с инпута
try {
  const ae = document.activeElement;
  if (ae && typeof ae.blur === "function") ae.blur();
} catch {}

      stack.count = Math.max(0, stack.count - 1);
      if (stack.count === 0 && stack.saved) {
  const s = stack.saved;
  stack.saved = null;

  requestAnimationFrame(() => {
    const body = document.body;
    const html = document.documentElement;

    body.style.overflow = s.bodyOverflow;
    body.style.overscrollBehavior = s.bodyOverscroll;
    body.style.touchAction = s.bodyTouchAction;
    html.style.overscrollBehavior = s.htmlOverscroll;
  });
}

    };
  }, [open]);



// visualViewport keyboard insets БЕЗ setState — чтобы не было "подгрузки/мигания"
useEffect(() => {
  const root = document.documentElement;

  if (!open) {
    root.style.setProperty("--kb-bottom", "0px");
    return;
  }

  const vv = window.visualViewport;
  let raf = 0;
  let last = -1;

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const update = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      let bottom = 0;

      if (vv) {
        bottom = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      }

      bottom = clamp(Math.round(bottom), 0, 420);

      // не трогаем DOM если почти не изменилось
      if (Math.abs(bottom - last) < 2) return;
      last = bottom;

      root.style.setProperty("--kb-bottom", `${bottom}px`);
    });
  };

  update();

  if (vv) vv.addEventListener("resize", update);
  else window.addEventListener("resize", update);

  return () => {
    cancelAnimationFrame(raf);
    if (vv) vv.removeEventListener("resize", update);
    else window.removeEventListener("resize", update);
    root.style.setProperty("--kb-bottom", "0px");
  };
}, [open]);




  

  if (!open) return null;



  return (
    <Portal>
      <div
  ref={shellRef}
  className="fixed inset-0 isolate"
  style={{ zIndex }}
>

{/* backdrop */}
<div
  className="absolute inset-0"
  style={{
    background: APP_BG,
    opacity: 0.92,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  }}
  onPointerDown={(e) => {
    e.stopPropagation();
    softClose();
  }}
/>


    {/* content */}
{/* content */}
<div
  className="absolute inset-0 z-10 flex"
  style={{
    top:
      !plain && !hasTgAuth()
        ? "env(safe-area-inset-top, 0px)"
        : 0,
    height:
      !plain && !hasTgAuth()
        ? "calc(var(--app-height, 100vh) - env(safe-area-inset-top, 0px))"
        : "var(--app-height, 100vh)",
  }}
  onPointerDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
>






<div
  ref={scrollRef}
  className={cn(
    "w-full bg-transparent text-slate-50",
    plain ? "overflow-hidden" : "overflow-y-auto"
  )}
  data-modal-scroll-root="1"
  onPointerDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
  style={{
    height: "100%",
    paddingBottom: plain
      ? 0
      : `calc(env(safe-area-inset-bottom) + 16px + var(--kb-bottom, 0px))`,
    WebkitOverflowScrolling: plain ? "auto" : "touch",
    overscrollBehavior: "none",
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
  }}
>
  <div className={plain ? "h-full w-full" : "p-4"}>{children}</div>
</div>



</div>


      </div>
    </Portal>
  );
}






function FoodDayBaseModal({ open, onClose, title, subtitle, children }) {

  if (!open) return null;

    return (
    <Portal>
      {/*
        Важно:
        1) Не используем экстремальные z-index (2147483647) — в Telegram WebView это иногда
           приводит к багу "чёрный экран поверх всего".
        2) Ловим закрытие на backdrop через onPointerDown (а не onClick), чтобы не было
           ситуации когда клик, который открыл модалку, тут же "прилетает" в backdrop.
      */}
      <div className="fixed inset-0 z-[60000] isolate">

        {/* затемнение (backdrop) */}
        <div
          className="absolute inset-0"
          style={{ background: APP_BG, opacity: 0.92 }}
          onPointerDown={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />

        {/* окно */}
        <div
          className="absolute inset-0 z-10 overflow-y-auto"
          style={{
            top: hasTgAuth()
              ? 0
              : "env(safe-area-inset-top, 0px)",
          }}
        >

          <div className="min-h-full flex items-start justify-center px-4 pt-6 pb-[calc(112px+env(safe-area-inset-bottom,0px))]">
            <div
              className="relative w-full max-w-[360px] rounded-3xl border border-white/20 bg-slate-900/95 shadow-[0_18px_45px_rgba(0,0,0,0.8)] text-slate-50"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-2 px-4 pt-4">
                <div>
  <div className="text-[15px] font-semibold">
    {title || i18n.t("food.day.baseTitleFallback")}
  </div>

  {subtitle ? (
    <div className="mt-1 text-[12px] opacity-75 tabular-nums whitespace-nowrap">
      {subtitle}
    </div>
  ) : null}
</div>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-slate-900/70 text-slate-200 text-[13px] font-semibold hover:bg-slate-800 active:scale-[0.96]"
                  aria-label={i18n.t("actions.close")}
                >
                  ✕
                </button>
              </div>

             <div
  className="px-4 pt-3 pb-4"
  style={{
    minHeight: "calc(var(--app-height, 100vh) - 178px)",
  }}
>
  {children}
</div>

              
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );

}


// Редактирование названия упражнения
function ExerciseNameModal({
  open,
  onClose,
  onSave,
  initialName,
  savedWorkouts = [],
}) {
  const { t, i18n } = useTranslation();

  const allExercisesFilter = "__all__";

  const [value, setValue] = useState(initialName ?? "");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [selectedLibraryFilter, setSelectedLibraryFilter] =
    useState(allExercisesFilter);
  const inputRef = useRef(null);

  const normalizeExerciseLibraryName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/\s+/g, " ");

  const isPlaceholderExerciseLibraryName = (name) => {
    const n = normalizeExerciseLibraryName(name);
    return !n || /^\d+\s+упражнение$/.test(n) || n === "упражнение";
  };

const exerciseLibrary = useMemo(() => {
  const allLabel = allExercisesFilter;
  const byName = new Map();
  const byFilter = new Map();

  const exerciseLocale =
    i18n.resolvedLanguage === "ru"
      ? "ru"
      : "en";

  const getLocalizedWorkoutTitle = (workout) => {
    const customTitle = getWorkoutCustomTitle(workout);

    if (customTitle) {
      return customTitle;
    }

    if (workout?.mode === "cardio") {
      return t("workout.picker.cardio");
    }

    const groups = getWorkoutMuscleBadges(workout).filter(
      (group, index, arr) =>
        group &&
        arr.findIndex((item) => item?.key === group.key) === index
    );

    const names = groups
      .map((group) => t(`workout.muscles.${group.key}`))
      .filter(Boolean);

    return names.length
      ? names.join(" • ")
      : t("nav.workout");
  };

  (Array.isArray(savedWorkouts) ? savedWorkouts : []).forEach((workout) => {
    const workoutTitle = getLocalizedWorkoutTitle(workout);

    if (!byFilter.has(workoutTitle)) {
      byFilter.set(workoutTitle, new Set());
    }

    (Array.isArray(workout?.exercises) ? workout.exercises : []).forEach((exercise) => {
      const name = String(exercise?.name || "").trim();
      const key = normalizeExerciseLibraryName(name);

      if (!name || isPlaceholderExerciseLibraryName(name)) return;

      byFilter.get(workoutTitle).add(key);

      if (!byName.has(key)) {
        byName.set(key, {
          key,
          name,
          workoutTitles: new Set([workoutTitle]),
        });
      } else {
        byName.get(key).workoutTitles.add(workoutTitle);
      }
    });
  });

  const items = Array.from(byName.values()).map((item) => ({
    ...item,
    workoutTitles: Array.from(item.workoutTitles),
  }));

  const filters = [
    {
      name: allLabel,
      count: items.length,
    },
    ...Array.from(byFilter.entries())
      .map(([name, keys]) => ({
        name,
        count: keys.size,
      }))
      .filter((item) => item.count > 0)
      .sort(
        (a, b) =>
          b.count - a.count ||
          a.name.localeCompare(b.name, exerciseLocale)
      ),
  ];

  return {
    allLabel,
    items,
    filters,
  };
}, [savedWorkouts, i18n.resolvedLanguage, t]);

  const activeLibraryFilter = exerciseLibrary.filters.some(
    (item) => item.name === selectedLibraryFilter
  )
    ? selectedLibraryFilter
    : exerciseLibrary.allLabel;

  const visibleExerciseItems =
    activeLibraryFilter === exerciseLibrary.allLabel
      ? exerciseLibrary.items
      : exerciseLibrary.items.filter((item) =>
          item.workoutTitles.includes(activeLibraryFilter)
        );
  const resizeExerciseNameInput = () => {
    try {
      const el = inputRef.current;
      if (!el) return;

      const minHeight = 54;
      const maxHeight = 118;

      el.style.height = `${minHeight}px`;

      const nextHeight = Math.min(
        maxHeight,
        Math.max(minHeight, el.scrollHeight)
      );

      el.style.height = `${nextHeight}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    } catch {}
  };

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    const raf = requestAnimationFrame(resizeExerciseNameInput);
    const t1 = setTimeout(resizeExerciseNameInput, 80);
    const t2 = setTimeout(resizeExerciseNameInput, 220);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open, value, libraryOpen]);

  useEffect(() => {
    if (!open) return undefined;

    setValue(initialName ?? "");
    setLibraryOpen(false);
   setSelectedLibraryFilter(allExercisesFilter);

    // Автоматическое открытие клавиатуры остаётся.
    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, initialName]);

  const handleSave = () => {
    const name = value.trim();
    if (!name) return;

    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}

    onSave(name);
    onClose();
  };

  const selectExerciseName = (name) => {
    const cleanName = String(name || "").trim();
    if (!cleanName) return;

    setValue(cleanName);

    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}
  };

return (
    <BaseModal open={open} onClose={onClose} plain>
      <style>{`
        [data-exercise-name-modal="1"] * {
          outline: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }

[data-exercise-name-modal="1"] input::placeholder {
  color: rgba(100, 116, 139, 0.62) !important;
  -webkit-text-fill-color: rgba(100, 116, 139, 0.62) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
  opacity: 1 !important;
}

        /* Убираем белые обводки, которые появляются из-за tailwind-классов с нестандартной прозрачностью */
        [data-exercise-name-modal="1"] .border,
        [data-exercise-name-modal="1"] [class*="border-"] {
          border-color: rgba(53, 243, 223, 0.16) !important;
        }

        [data-exercise-name-modal="1"] input:focus {
          border-color: rgba(53, 243, 223, 0.58) !important;
          box-shadow: 0 0 0 2px rgba(32, 234, 212, 0.13), inset 0 1px 0 rgba(255,255,255,0.07) !important;
        }

[data-exercise-name-modal="1"] button:focus,
        [data-exercise-name-modal="1"] input:focus,
        [data-exercise-name-modal="1"] textarea:focus {
          outline: none !important;
        }
      `}</style>

      <div
        data-exercise-name-modal="1"
        className="relative flex h-full w-full items-start justify-center overflow-hidden"
        style={{
          paddingTop: libraryOpen
            ? "max(8px, env(safe-area-inset-top, 0px))"
            : "max(14px, calc(env(safe-area-inset-top, 0px) + 10px))",
          paddingLeft: libraryOpen ? 8 : 12,
          paddingRight: libraryOpen ? 8 : 12,
          paddingBottom: libraryOpen
            ? "max(8px, env(safe-area-inset-bottom, 0px))"
            : 18,
          touchAction: libraryOpen ? "pan-y" : "none",
          overscrollBehavior: "none",
        }}
      >
        <div
          className="relative flex w-full min-w-0 flex-col overflow-hidden rounded-[32px] border border-[#20ead4]/14 bg-[#06131d]/94 text-slate-50 shadow-[0_26px_90px_rgba(0,0,0,0.58),0_0_34px_rgba(32,234,212,0.10),inset_0_1px_0_rgba(255,255,255,0.055)] backdrop-blur-2xl"
          style={{
            maxWidth: libraryOpen ? "min(408px, calc(100vw - 16px))" : "min(372px, calc(100vw - 24px))",
            height: libraryOpen
              ? "calc(var(--vvh, var(--app-height, 100vh)) - max(16px, env(safe-area-inset-top, 0px)) - max(8px, env(safe-area-inset-bottom, 0px)))"
              : "auto",
            maxHeight: "calc(var(--vvh, var(--app-height, 100vh)) - 18px)",
          }}
        >
          <div
            className="pointer-events-none absolute -inset-[260px]"
            style={{
              background:
                "radial-gradient(circle at 12% 10%, rgba(32,234,212,0.28), transparent 34%), radial-gradient(circle at 90% 76%, rgba(14,165,233,0.18), transparent 36%), linear-gradient(135deg, rgba(6,19,29,0.98), rgba(3,12,22,0.98))",
            }}
          />

          <div
            className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-3 pt-4"
            style={{
              overscrollBehavior: "none",
              touchAction: "pan-y",
            }}
          >
            <div className="flex shrink-0 items-start justify-between gap-3">
              <div className="min-w-0">
<div className="text-[20px] font-black leading-tight tracking-[-0.035em] text-slate-50">
  {t("workout.session.exerciseNameTitle")}
</div>

<div className="mt-1.5 text-[12px] font-semibold leading-snug text-slate-300/70">
  {t("workout.session.exerciseNameHint")}
</div>
              </div>

              <AppCloseButton onClick={onClose} />
            </div>

            <div className="mt-4 shrink-0">
              <label className="grid gap-1.5 text-[12px] font-bold text-slate-200/78">
                <span>{t("workout.session.exerciseLabel")}</span>

                <div className="relative">
<textarea
  ref={inputRef}
  autoFocus
  rows={1}
  inputMode="text"
  enterKeyHint="done"
  spellCheck={false}
  onPointerDown={() => {
    if (libraryOpen) setLibraryOpen(false);
  }}
  onFocus={() => {
    if (libraryOpen) setLibraryOpen(false);
    resizeExerciseNameInput();
  }}
  value={value}
  onInput={resizeExerciseNameInput}
  onChange={(e) =>
    setValue(
      e.target.value
        .replace(/\s*\n+\s*/g, " ")
        .replace(/\s{2,}/g, " ")
    )
  }
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  }}
  className="block min-h-[54px] w-full resize-none rounded-[22px] border border-[#20ead4]/20 px-4 py-[14px] pr-11 text-[17px] font-extrabold leading-[20px] tracking-[-0.02em] text-slate-50 outline-none placeholder:text-slate-400/45 focus:border-[#35f3df]/65 focus:ring-2 focus:ring-[#20ead4]/14"
  placeholder={t("workout.session.exerciseNamePlaceholder")}
  style={{
    height: 54,
    caretColor: "#35F3DF",
    background: "rgba(3,18,28,0.86)",
    color: "#f8fafc",
    WebkitAppearance: "none",
    appearance: "none",
    boxSizing: "border-box",
    overflowX: "hidden",
    overflowY: "hidden",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(255,255,255,0.018)",
  }}
/>

                  {value.trim() ? (
                    <button
                      type="button"
                      onClick={() => setValue("")}
                      className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-[#18d7bd]/18 bg-[#18d7bd]/[0.06] text-[15px] font-black text-cyan-100/75 outline-none focus:outline-none focus:ring-0 active:scale-[0.96]"
                      aria-label={t("workout.session.clearAria")}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              </label>
            </div>

            <div className="relative mt-3 flex min-h-0 flex-1 flex-col">
              <button
                type="button"
                onClick={() => {
                  try {
                    const el = document.activeElement;
                    if (el && typeof el.blur === "function") el.blur();
                  } catch {}

                  setLibraryOpen((prev) => !prev);
                }}
                className="flex h-10 w-full shrink-0 items-center justify-between rounded-[18px] border border-[#18d7bd]/20 bg-[#18d7bd]/[0.065] px-3.5 text-left text-[13px] font-black text-cyan-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.035),0_0_16px_rgba(24,215,189,0.065)] outline-none focus:outline-none focus:ring-0 active:scale-[0.98]"
              >
<span>
  {t("workout.session.chooseExercise")}
  <span className="ml-2 text-[12px] font-bold text-slate-300/65">
    {exerciseLibrary.items.length}
  </span>
</span>

                <span
                  className={cn(
                    "grid h-6 w-6 shrink-0 place-items-center rounded-full border border-[#18d7bd]/30 bg-[#18d7bd]/[0.075] text-[#35F3DF] shadow-[0_0_12px_rgba(24,215,189,0.10)] transition-transform duration-200",
                    libraryOpen ? "rotate-180" : "rotate-0"
                  )}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {libraryOpen ? (
                <div className="mt-2 flex min-h-0 flex-1 flex-col rounded-[22px] border border-[rgba(53,243,223,0.16)] bg-[rgba(6,31,42,0.62)] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.045)]">
                                    {exerciseLibrary.items.length === 0 ? (
                    <div className="grid min-h-[180px] place-items-center rounded-[18px] border border-[#18d7bd]/18 bg-[#18d7bd]/[0.055] px-3 py-4 text-center">
                      <div>
<div className="text-[13px] font-extrabold text-slate-50">
  {t("workout.session.noSavedExercises")}
</div>

<div className="mx-auto mt-1.5 max-w-[260px] text-[12px] font-semibold leading-snug text-slate-300/72">
  {t("workout.session.noSavedExercisesHint")}
</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {exerciseLibrary.filters.length > 1 ? (
                        <div
                          className="-mx-1 mb-2 shrink-0 overflow-x-auto overflow-y-hidden px-1 pt-2 pb-1"
                          style={{
                            WebkitOverflowScrolling: "touch",
                            overscrollBehaviorX: "contain",
                            scrollbarWidth: "none",
                            touchAction: "pan-x",
                          }}
                        >
                          <div className="flex w-max items-center gap-1.5">
{exerciseLibrary.filters.map((filter) => {
  const isActive = activeLibraryFilter === filter.name;

  const filterDisplayName =
    filter.name === exerciseLibrary.allLabel
      ? t("workout.myWorkouts.all")
      : filter.name;

  return (
<button
  key={filter.name}
  type="button"
  onClick={() => setSelectedLibraryFilter(filter.name)}
  className={cn(
    "relative h-9 max-w-[156px] shrink-0 rounded-full border px-3.5 pr-5 text-[12.5px] font-semibold outline-none transition focus:outline-none focus:ring-0 active:scale-[0.97]",
    isActive
      ? "border-[#35F3DF]/62 bg-[#06131d]/56 text-[#35F3DF] shadow-[0_0_13px_rgba(53,243,223,0.13),inset_0_0_0_1px_rgba(53,243,223,0.10)]"
      : "border-white/10 bg-[#06131d]/52 text-slate-300/82 shadow-none"
  )}
title={`${filterDisplayName} · ${filter.count} ${t(
  "workout.myWorkouts.exerciseNoun",
  {
    count: filter.count,
  }
)}`}
  aria-pressed={isActive}
>
  <span
    className={cn(
      "block max-w-[112px] truncate",
      isActive
        ? "text-[#35F3DF] drop-shadow-[0_0_8px_rgba(53,243,223,0.46)]"
        : "text-slate-300/82"
    )}
  >
{filter.name === exerciseLibrary.allLabel
  ? t("workout.myWorkouts.all")
  : getCompactWorkoutFilterLabel(
      filterDisplayName,
      t("workout.myWorkouts.all")
    )}
  </span>

  <span
    className={cn(
      "absolute -right-1 -top-2 grid h-[17px] min-w-[17px] place-items-center rounded-full border px-[4px] text-[9px] font-extrabold leading-none shadow-[0_5px_12px_rgba(0,0,0,0.30)]",
      isActive
        ? "border-[#8fffee]/75 bg-[#35F3DF] text-[#03211f] shadow-[0_0_13px_rgba(53,243,223,0.42)]"
        : "border-[#18d7bd]/35 bg-[#08212b] text-cyan-50"
    )}
  >
    {filter.count}
  </span>
</button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

{visibleExerciseItems.length ? (
  <div
    data-exercise-library-scroll="1"
    className="grid min-h-0 flex-1 content-start gap-2 overflow-x-hidden overflow-y-scroll pr-1 pb-2"
    style={{
      maxHeight:
        "calc(var(--vvh, var(--app-height, 100vh)) - 315px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
      WebkitOverflowScrolling: "touch",
      overscrollBehaviorY: "contain",
      touchAction: "pan-y",
      scrollbarWidth: "none",
    }}
    onWheel={(e) => {
      e.stopPropagation();
    }}
    onTouchMove={(e) => {
      e.stopPropagation();
    }}
  >
                          {visibleExerciseItems.map((item) => {
                            const isSelected =
                              normalizeExerciseLibraryName(value) === item.key;

                            return (
                              <button
                                key={item.key}
                                type="button"
                                onClick={() => selectExerciseName(item.name)}
                                className={cn(
                                  "flex w-full items-center justify-between gap-3 rounded-[18px] border px-3 py-2.5 text-left outline-none transition focus:outline-none focus:ring-0 active:scale-[0.99]",
                                  isSelected
                                    ? "border-[#18d7bd]/52 bg-[#18d7bd]/[0.13] shadow-[0_0_16px_rgba(24,215,189,0.11)]"
                                    : "border-[#18d7bd]/10 bg-white/[0.035]"
                                )}
                              >
                                <div className="min-w-0">
                                  <div className="truncate text-[14px] font-extrabold tracking-[-0.02em] text-slate-50">
                                    {item.name}
                                  </div>

                                  {item.workoutTitles.length ? (
                                    <div className="mt-1 truncate text-[11px] font-semibold text-slate-300/62">
                                      {item.workoutTitles.slice(0, 2).join(" • ")}
                                      {item.workoutTitles.length > 2
                                        ? ` +${item.workoutTitles.length - 2}`
                                        : ""}
                                    </div>
                                  ) : null}
                                </div>

                                <span
                                  className={cn(
                                    "grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[13px] font-black",
                                    isSelected
                                      ? "border-[#18d7bd]/50 bg-[#18d7bd]/18 text-[#35F3DF]"
                                      : "border-[#18d7bd]/12 bg-white/[0.035] text-slate-400/70"
                                  )}
                                >
                                  {isSelected ? "✓" : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
<div className="rounded-[18px] border border-[#18d7bd]/12 bg-white/[0.035] px-3 py-3 text-center text-[12px] font-semibold text-slate-300/70">
  {t("workout.session.noExercisesInFilter")}
</div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>

<div className="relative shrink-0 bg-[#07131e]/72 px-3 pb-3 pt-2 backdrop-blur-xl">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 flex-1 rounded-full border border-[#18d7bd]/14 bg-[#18d7bd]/[0.045] text-[14px] font-bold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] outline-none focus:outline-none focus:ring-0 active:scale-[0.97]"
>
  {t("actions.cancel")}
</button>

              <button
                type="button"
                onClick={handleSave}
                disabled={!value.trim()}
                className={cn(
                  "h-11 flex-1 rounded-full text-[14px] font-black outline-none transition focus:outline-none focus:ring-0 active:scale-[0.97]",
                  value.trim()
                    ? "bg-gradient-to-r from-emerald-300 to-cyan-300 text-[#03131a] shadow-[0_0_28px_rgba(32,234,212,0.24),0_12px_26px_rgba(0,0,0,0.22)]"
                    : "border border-[#18d7bd]/10 bg-white/[0.04] text-slate-500"
                )}
>
  {t("workout.session.continue")}
</button>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

// Редактирование названия всей тренировки
function WorkoutTitleModal({
  open,
  onClose,
  onSave,
  initialTitle,
  defaultTitle,
  displayDefaultTitle,
}) {
  const { t } = useTranslation();

  const [value, setValue] = useState(initialTitle ?? "");
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, initialTitle]);

  useEffect(() => {
    if (!open) return undefined;

    setValue(initialTitle ?? "");
    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, initialTitle]);

  const handleSave = () => {
    const title =
      value.trim() ||
      String(defaultTitle || "Тренировка").trim() ||
      "Тренировка";

    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}

    onSave(title);
    onClose();
  };

  return (
    <BaseModal open={open} onClose={onClose}>
      <div className="flex min-h-[calc(var(--app-height,100vh)-32px)] items-start justify-center pt-[44px]">
        <div className="w-full max-w-[360px] overflow-hidden rounded-[30px] border border-[#20ead4]/30 bg-white/[0.09] text-slate-50 shadow-[0_24px_80px_rgba(0,0,0,0.48),0_0_34px_rgba(32,234,212,0.14)] backdrop-blur-2xl">
          <div className="relative overflow-hidden p-4">
            <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-[#20ead4]/22 blur-3xl" />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-48 w-48 rounded-full bg-[#0ea5e9]/16 blur-3xl" />

            <div className="relative flex items-start justify-between gap-3">
              
<div>
  <div className="text-[18px] font-bold tracking-[-0.02em] text-slate-50">
    {t("workout.myWorkouts.workoutNameTitle")}
  </div>
<div className="mt-1 text-[12px] text-slate-300/72">
  {t("workout.session.workoutTitleHint")}
</div>
</div>

              <AppCloseButton onClick={onClose} />
            </div>

            <div className="relative mt-4">
<label className="grid gap-1.5 text-[12px] font-medium text-slate-200/85">
  <span>{t("nav.workout")}</span>
  <input
                  ref={inputRef}
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  className="h-12 rounded-[20px] border border-[#20ead4]/25 bg-white/[0.08] px-4 text-[17px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] outline-none placeholder:text-slate-400/55 focus:border-[#20ead4]/70 focus:ring-2 focus:ring-[#20ead4]/20"
placeholder={
  displayDefaultTitle ||
  defaultTitle ||
  t("workout.session.workoutTitleExample")
}
                />
              </label>
            </div>

            <div className="relative mt-5 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 flex-1 rounded-full border border-white/15 bg-white/[0.07] text-[14px] font-semibold text-slate-100 active:scale-[0.97]"
>
  {t("actions.cancel")}
</button>

              <button
                type="button"
                onClick={handleSave}
                className="h-11 flex-1 rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-[14px] font-bold text-[#03131a] shadow-[0_0_30px_rgba(32,234,212,0.28)] transition active:scale-[0.97]"
>
  {t("actions.save")}
</button>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
// Редактирование подхода (вес + повторы)
function SetModal({ open, onClose, onSave, initialWeight, initialReps, label }) {
  const { t } = useTranslation();

  const [weight, setWeight] = useState(initialWeight ?? "");
  const [reps, setReps] = useState(initialReps ?? "");
  const weightRef = useRef(null);

  const parseNumber = (s) => {
    const n = Number(String(s || "").replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(weightRef);
  }, [open, initialWeight, initialReps]);

  useEffect(() => {
    if (!open) return undefined;

    setWeight(initialWeight ?? "");
    setReps(initialReps ?? "");

    return focusModalInputAndOpenKeyboard(weightRef);
  }, [open, initialWeight, initialReps]);

  const canSave = parseNumber(weight) !== null && parseNumber(reps) !== null;

  const handleSave = () => {
    const w = parseNumber(weight);
    const r = parseNumber(reps);
    if (w === null || r === null) return;

    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}

    onSave({ weight: w, reps: r });
    onClose();
  };

  return (
    <BaseModal open={open} onClose={onClose} plain>
      <div
        className="flex h-full items-start justify-center overflow-hidden px-4"
        style={{
          paddingTop: "calc(44px + env(safe-area-inset-top, 0px))",
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
          overscrollBehavior: "none",
          touchAction: "manipulation",
        }}
      >
        <div className="w-full max-w-[360px] overflow-hidden rounded-[30px] border border-[#20ead4]/30 bg-white/[0.09] text-slate-50 shadow-[0_24px_80px_rgba(0,0,0,0.48),0_0_34px_rgba(32,234,212,0.14)] backdrop-blur-2xl">
          <div className="relative overflow-hidden p-4">
            <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-[#20ead4]/18 blur-3xl" />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-48 w-48 rounded-full bg-[#0ea5e9]/12 blur-3xl" />

            <div className="relative flex items-start justify-between gap-3">
              <div>
<div className="text-[18px] font-bold tracking-[-0.02em] text-slate-50">
  {label || t("workout.session.setTitle")}
</div>
<div className="mt-1 text-[12px] text-slate-300/72">
  {t("workout.session.setHint")}
</div>
              </div>

              <AppCloseButton onClick={onClose} />
            </div>

            <div className="relative mt-4 grid gap-3">
              <label className="grid gap-1.5 text-[12px] font-medium text-slate-200/85">
                <span>{t("workout.session.weightLabel")}</span>
                <input
                  ref={weightRef}
                  inputMode="decimal"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  className="h-12 rounded-[20px] border border-[#20ead4]/25 bg-white/[0.08] px-4 text-[18px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] outline-none placeholder:text-slate-400/55 focus:border-[#20ead4]/70 focus:ring-2 focus:ring-[#20ead4]/20"
                  placeholder={t("workout.session.weightPlaceholder")}
                />
              </label>

              <label className="grid gap-1.5 text-[12px] font-medium text-slate-200/85">
                <span>{t("workout.session.repsLabel")}</span>
                <input
                  inputMode="numeric"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  className="h-12 rounded-[20px] border border-[#20ead4]/25 bg-white/[0.08] px-4 text-[18px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] outline-none placeholder:text-slate-400/55 focus:border-[#20ead4]/70 focus:ring-2 focus:ring-[#20ead4]/20"
                  placeholder={t("workout.session.repsPlaceholder")}
                />
              </label>
            </div>

            <div className="relative mt-5 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 flex-1 rounded-full border border-white/15 bg-white/[0.06] text-[14px] font-semibold text-slate-100 active:scale-[0.97]"
>
  {t("actions.cancel")}
</button>

<button
  type="button"
  onClick={handleSave}
                disabled={!canSave}
                className={cn(
                  "h-11 flex-1 rounded-full text-[14px] font-bold transition active:scale-[0.97]",
                  canSave
                    ? "bg-gradient-to-r from-emerald-300 to-cyan-300 text-[#03131a] shadow-[0_0_30px_rgba(32,234,212,0.28)]"
                    : "border border-white/10 bg-white/[0.05] text-slate-500"
                )}
>
  {t("actions.save")}
</button>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
function MinutesPicker({ value, onChange, step = 5, max = 240 }) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    const arr = [];
    for (let m = step; m <= max; m += step) arr.push(m);
    return arr;
  }, [step, max]);

  return (
    <div className="rounded-2xl border border-white/20 bg-white/10 p-2">
      <div className="max-h-[220px] overflow-y-auto overscroll-contain">
        {items.map((m) => {
          const active = Number(value) === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => onChange(m)}
              className={
                "w-full text-left px-3 py-2 rounded-xl text-[16px] " +
                (active ? "bg-white/20 border border-white/25" : "hover:bg-white/10")
              }
            >
              {t("workout.picker.minutesShort", {
  value: m,
})}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CardioModal({ open, onClose, onAdd, initial }) {
  const { t } = useTranslation();

  const [title, setTitle] = useState(initial?.title ?? "");
  const [minutes, setMinutes] = useState(Number(initial?.minutes ?? 20));
  const [comment, setComment] = useState(initial?.comment ?? "");
  const [minutesOpen, setMinutesOpen] = useState(false);

  const titleRef = useRef(null);

  const canSave = Number.isFinite(Number(minutes)) && Number(minutes) > 0;

  const submitCardio = () => {
    const m = Number(minutes);

    if (!Number.isFinite(m) || m <= 0) {
      safeAlert("Выбери время кардио, например 20 мин.", "Кардио");
      return;
    }

    onAdd({
      title: String(title).trim(),
      minutes: m,
      comment: String(comment || "").trim(),
    });

    onClose();
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(titleRef);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;

    setTitle(initial?.title ?? "");
    setMinutes(Number(initial?.minutes ?? 20));
    setComment(initial?.comment ?? "");
    setMinutesOpen(false);

    return focusModalInputAndOpenKeyboard(titleRef);
  }, [open, initial]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
className="fixed inset-0 z-[60000] flex items-start justify-center overflow-y-auto px-4 pt-[calc(env(safe-area-inset-top)+4px)] pb-4 bg-transparent backdrop-blur-[10px]"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[390px]"
        onClick={(e) => e.stopPropagation()}
      >
        <GlassCard
          dense
          className="!rounded-[26px] !border-cyan-300/35 !bg-slate-900/95 p-4 shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
        >
          <div className="mb-1 flex items-start justify-between gap-3">
<div>
  <div className="text-[16px] font-bold text-slate-50">
    {t("workout.picker.cardio")}
  </div>
  <div className="mt-0.5 text-[11px] font-semibold text-white/45">
    {t("workout.session.cardioSubtitle")}
  </div>
</div>

            <AppCloseButton onClick={onClose} />
          </div>

          <div className="mt-3 grid gap-2.5">
            <label className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("workout.session.nameLabel")}</span>
              <input
                ref={titleRef}
                enterKeyHint="next"
                placeholder={t("workout.session.cardioNamePlaceholder")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onFocus={(e) => focusToEndAndScroll(e, null)}
                className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
              />
            </label>

            <div className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("workout.session.timeLabel")}</span>

              <button
                type="button"
                onClick={() => setMinutesOpen((v) => !v)}
                className="flex h-11 w-full items-center justify-between rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] active:scale-[0.98]"
              >
                <span>
  {t("workout.picker.minutesShort", {
    value: minutes,
  })}
</span>

                <span
                  className={cn(
                    "grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition",
                    minutesOpen ? "rotate-180" : "rotate-0"
                  )}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {minutesOpen && (
                <div className="mt-1 rounded-[20px] border border-cyan-200/15 bg-slate-950/35 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                  <MinutesPicker
                    value={minutes}
                    onChange={(m) => {
                      setMinutes(m);
                      setMinutesOpen(false);
                    }}
                    step={5}
                    max={240}
                  />
                </div>
              )}
            </div>

            <label className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("workout.session.commentLabel")}</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onFocus={(e) => focusToEndAndScroll(e, null)}
                placeholder={t("workout.session.commentPlaceholder")}
                rows={3}
                enterKeyHint="done"
                className="min-h-[86px] rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 py-2.5 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] resize-none focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
              />
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-2xl border border-white/15 bg-white/[0.07] text-[13px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.97]"
>
  {t("actions.cancel")}
</button>

            <button
              type="button"
              onClick={submitCardio}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                (canSave
                  ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                  : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
              }
>
  {t("workout.session.add")}
</button>
          </div>
        </GlassCard>
      </div>
    </div>,
    document.body
  );
}

function NamesListModal({ open, onClose, title, names, customTitle }) {
  const { t } = useTranslation();

  return (
    <BaseModal open={open} onClose={onClose}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="text-[15px] font-semibold">
  {title || t("workout.session.listFallback")}
</div>
        <button
          type="button"
          onClick={onClose}
          className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-slate-900/70 text-slate-200 text-[13px] font-semibold hover:bg-slate-800 active:scale-[0.96]"
          aria-label={t("actions.close")}
        >
          ✕
        </button>
      </div>
      {customTitle ? (
        <div className="mb-3 rounded-2xl border border-[#18d7bd]/20 bg-[#06131d]/72 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-cyan-100/60">
            {t("workout.myWorkouts.workoutNameTitle")}
          </div>

          <div className="mt-1 break-words text-[14px] font-extrabold leading-snug text-slate-50">
            {customTitle}
          </div>
        </div>
      ) : null}
      <div className="max-h-[55vh] overflow-auto rounded-2xl border border-white/10 bg-slate-950/40">
        <ul className="p-3 space-y-2">
{(names || []).map((item, i) => {
  const main =
    typeof item === "string"
      ? item
      : item?.name || item?.title || item?.text || "";
  const sub =
    typeof item === "string"
      ? ""
      : item?.sub || item?.details || item?.meta || "";

  return (
    <li key={`${main}-${i}`} className="text-[13px] text-slate-50/95">
      <div className="flex items-baseline gap-2">
        <span className="opacity-60">{i + 1}.</span>
        <span className="break-words font-medium">{main}</span>
      </div>

      {sub ? (
        <div className="mt-0.5 pl-5 text-[12px] text-slate-200/70 break-words">
          {sub}
        </div>
      ) : null}
    </li>
  );
})}

          {(!names || names.length === 0) && (
            <li className="text-[12px] text-slate-200/70">
  {t("workout.session.none")}
</li>
          )}
        </ul>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={onClose}
          className="w-full h-10 rounded-2xl border border-white/20 bg-slate-900/60 text-[13px] font-medium text-slate-100 hover:bg-slate-800 active:scale-[0.97]"
>
  {t("actions.close")}
</button>
      </div>
    </BaseModal>
  );
}

// --- Узкие полоски-индикаторы для Б/Ж/У ---
// --- Узкие полоски-индикаторы для Б/Ж/У ---
// --- Узкие полоски-индикаторы для Б/Ж/У (как раньше, «жирные») ---
function TickBar({ ratio = 0, color = "#999" }) {
  const STRIPES = 10;
  const filled = Math.max(0, Math.min(STRIPES, Math.round(ratio * STRIPES)));

  return (
    <div
      className="grid w-[6px]"
      style={{ gridTemplateRows: `repeat(${STRIPES}, 2px)`, rowGap: "1px" }}
    >
      {Array.from({ length: STRIPES }).map((_, i) => {
        const fromBottom = STRIPES - 1 - i;
        const isFilled = fromBottom < filled;
        return (
          <div
            key={i}
            className="rounded-[999px]"
            style={{
              background: isFilled ? color : "rgba(0,0,0,0.35)",
            }}
          />
        );
      })}
    </div>
  );
}





function MiniRings({ carbsRatio, proteinsRatio, fatsRatio }) {
    const SIZE = 28;       // фактический размер контейнера h-7 w-7 (28px)
  const STROKE = 2;
  const center = SIZE / 2;
  const GAP = 1.0;


  const radii = [
    center - STROKE / 2,
    center - STROKE / 2 - (STROKE + GAP),
    center - STROKE / 2 - 2 * (STROKE + GAP),
  ];

  const mkRing = (r, ratio, color) => {
    const C = 2 * Math.PI * r;

    // ratio = value/target (может быть 0..100 и больше)
    const raw = Math.max(0, Number(ratio) || 0);

    // Кольцо НЕ сбрасываем: после 100% оно всегда полностью заполнено
    const progress = Math.min(1, raw);

    // Для "видимого начала" считаем текущий оборот (дробная часть)
    const laps = Math.floor(raw);
    const frac = raw - laps; // 0..1

    const len = progress * C; // 0..C, при progress=1 будет полный круг

    // Насечка-«начало» появляется только когда пошли во 2-й круг (raw > 1)
    const notchLen = Math.max(6, STROKE * 1.2);
    const notchOffset = -(frac * C);

    return {
      r,
      color,
      raw,
      dash: `${len} ${C}`,
    };

  };




  const rings = [
    mkRing(radii[0], carbsRatio, "#facc15"),
    mkRing(radii[1], proteinsRatio, "#22d3ee"),
    mkRing(radii[2], fatsRatio, "#f97373"),
  ];

  return (
  <svg
    viewBox={`0 0 ${SIZE} ${SIZE}`}
    width={SIZE}
    height={SIZE}
    className="absolute inset-0 block"
    preserveAspectRatio="xMidYMid meet"
    shapeRendering="geometricPrecision"
    style={{ pointerEvents: "none" }}
  >

      <g transform={`translate(${center},${center}) rotate(-90)`}>
        {rings.map((ring, idx) => (
          <g key={idx}>
            <circle
  r={ring.r}
  cx={0}
  cy={0}
  fill="none"
  stroke="rgba(0,0,0,0.18)"
  strokeWidth={STROKE}
/>

<circle
  r={ring.r}
  cx={0}
  cy={0}
  fill="none"
  stroke={ring.color}
  strokeLinecap="round"
  strokeWidth={STROKE}
  strokeDasharray={ring.dash}
  strokeDashoffset={0}
/>







          </g>
        ))}
      </g>
    </svg>
  );
}





// --- Виджет колец калорийности (Б/Ж/У + ккал) ---
function MacroIntakeCard({
  introMode = false,
  consumed = { carbs: 0, proteins: 0, fats: 0 },
  targets = { kcal: 0, carbs: 0, proteins: 0, fats: 0 },
  day = 1,
  onEdit = () => logEvent("macro:edit", { source: "macroCard" }),
  onOpenDetails = () => {},
  onOpenReport = () => {}, 
}) {
  const { t } = useTranslation();

  const SIZE = 132;
  const STROKE = 8;
  const RING_GROW = STROKE / 2; // +4px: увеличиваем только сами кольца
  // интро-анимация
const [introT, setIntroT] = useState(0);


// маленькая хлопушка-конфетти
const [showConfetti, setShowConfetti] = useState(false);
const prevAllClosedRef = useRef(false);

useEffect(() => {
  if (!introMode) {
    setIntroT(0);
    return;
  }


  let raf = 0;

  const upDur = 900;     // 0 -> 1
  const holdAtTop = 350; // пауза на 100%
  const downDur = 700;   // 1 -> 0

  const start = performance.now();

  const easeInOutCubic = (x) =>
    x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

  const tick = (now) => {
    const elapsed = now - start;

    let v = 0;

    if (elapsed <= upDur) {
      const t = elapsed / upDur;
      v = easeInOutCubic(t);
    } else if (elapsed <= upDur + holdAtTop) {
      v = 1;
    } else if (elapsed <= upDur + holdAtTop + downDur) {
      const t = (elapsed - upDur - holdAtTop) / downDur;
      v = 1 - easeInOutCubic(t);
    } else {
      setIntroT(0);
      return; // остановились
    }

    setIntroT(v);
    raf = requestAnimationFrame(tick);
  };

  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}, [introMode]);





  const parts = [
    // углеводы — жёлтые
    { key: "carbs", label: t("food.macro.carbs"), color: "#facc15", value: consumed.carbs, target: targets.carbs },
    // белки — холодный голубой
    { key: "proteins", label: t("food.macro.proteins"), color: "#22d3ee", value: consumed.proteins, target: targets.proteins },
    // жиры — красные
    { key: "fats", label: t("food.macro.fats"), color: "#f97373", value: consumed.fats, target: targets.fats },
  ];

    const center = SIZE / 2;
  const GAP = 2;
  const radii = [
    center - STROKE / 2 + RING_GROW,
    center - STROKE / 2 - (STROKE + GAP) + RING_GROW,
    center - STROKE / 2 - 2 * (STROKE + GAP) + RING_GROW,
  ];


  const rings = parts.map((p, index) => {
    const r = radii[index] ?? radii[radii.length - 1];
    const C = 2 * Math.PI * r;

    const raw = Math.max(0, (p.value || 0) / Math.max(1, p.target || 0));

    // Кольцо НЕ сбрасываем: после 100% оно всегда полностью заполнено
    const progress = introMode ? introT : Math.min(1, raw);

    // Для "видимого начала" используем дробную часть текущего оборота
    const laps = Math.floor(raw);
    const frac = raw - laps; // 0..1

    const len = progress * C; // 0..C

    const notchLen = Math.max(10, STROKE * 1.35);
    const notchOffset = -(frac * C);

return {
  ...p,
  r,
  raw,
  ratio: Math.min(1, raw), // ✅ добавили для полосок слева
  dash: `${len} ${C}`,
};


  });



const allClosed = useMemo(() => {
  return rings.every((p) => {
    const r = Math.max(
      0,
      Math.min(1, (p.value || 0) / Math.max(1, p.target || 0))
    );
    return r >= 0.999;
  });
}, [rings]);

useEffect(() => {
  // во время интро не дублируем конфетти
  if (introMode) {
    prevAllClosedRef.current = false;
    return;
  }

  const wasClosed = prevAllClosedRef.current;

  if (!wasClosed && allClosed) {
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 800);
    prevAllClosedRef.current = allClosed;
    return () => clearTimeout(t);
  }

  prevAllClosedRef.current = allClosed;
}, [allClosed, introMode]);

  const kcal =
  typeof consumed.kcal === "number"
    ? Math.round(consumed.kcal)
    : Math.round(
        (consumed.carbs || 0) * 4 +
          (consumed.proteins || 0) * 4 +
          (consumed.fats || 0) * 9
      );


  return (
<GlassCard
  solid
  data-onboarding="macro-rings"
  className={FOOD_HERO_CARD}
  onClick={onOpenDetails}
>
      {/* кнопка редактирования в углу */}
<button
  type="button"
  data-onboarding="macro-edit"
  onClick={(e) => {
    e.stopPropagation();
    onEdit();
  }}
  className="absolute top-1.5 right-1.5 grid h-12 w-12 touch-manipulation place-items-center rounded-full border-0 bg-transparent p-0 active:scale-[0.96]"
  aria-label={t("food.macro.editAria")}
>
  <span className="grid h-9 w-9 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl">
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </svg>
  </span>
</button>

      <div className="grid grid-cols-[auto_1fr] gap-6 items-center pl-2 pr-4">
        {/* SVG-кольца */}
        <div
  className={`relative ml-2 transition-transform duration-300 ${
    showConfetti ? "scale-105" : "scale-100"
  }`}
    style={{ width: SIZE, height: SIZE, overflow: "visible" }}
>



<svg
  width={SIZE}
  height={SIZE}
  viewBox={`0 0 ${SIZE} ${SIZE}`}
  className="block"
  style={{ overflow: "visible" }}
>
  <g transform={`translate(${center},${center}) rotate(-90)`}>
    {rings.map((ring) => (
      <g key={ring.key}>
        <circle
          r={ring.r}
          cx={0}
          cy={0}
          fill="none"
          stroke="rgba(0,0,0,0.12)"
          strokeWidth={STROKE}
        />


        <circle
          r={ring.r}
          cx={0}
          cy={0}
          fill="none"
          stroke={ring.color}
          strokeLinecap="round"
          strokeWidth={STROKE}
          strokeDasharray={ring.dash}
          strokeDashoffset={0}
        />






      </g>
    ))}
  </g>
</svg>



          {/* центр круга: день + ккал */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-[1px] pointer-events-none">
            <div className="px-2 py-0.5 rounded-xl bg-black/40 text-[9px] font-semibold text-white/80 inline-block">
              {t("food.macro.day", { day })}
            </div>
            <div className="text-xl font-bold leading-tight text-white tabular-nums">
              {kcal}
            </div>
            <div className="text-[10px] opacity-75 tabular-nums text-slate-200">
              {Math.round(Number(targets.kcal || 0))} {t("units.kcal")}

            </div>
          </div>
        </div>

        {/* Правый столбец: Б/Ж/У с полосками */}
        <div className="grid gap-0">
          {rings.map((p) => (
            <div
              key={p.key}
              className="grid grid-cols-[8px_1fr] gap-2 items-center"
            >
              <TickBar ratio={p.ratio} color={p.color} />
              <div className="flex flex-col">
                <div className="text-[13px] font-semibold tabular-nums">
                  <span className="text-white">{Math.round(Number(p.value || 0))}</span>
<span className="opacity-70 text-slate-200">/{Math.round(Number(p.target || 0))}{t("units.gramsShort")}</span>

                </div>
                <div className="text-[13px] font-medium opacity-75 text-slate-200">
                  {p.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

{/* Кнопка "Отчёт" */}
<button
  type="button"
  data-onboarding="report-button"
  onClick={(e) => {
    e.stopPropagation();
    logEvent("report:open", { source: "macroCard" });
    onOpenReport();
  }}
className="absolute bottom-4 right-4 h-8 rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] px-3.5 text-[12px] font-extrabold tracking-[0.04em] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl active:scale-[0.97]"
>
  {t("food.macro.report")}
</button>


    </GlassCard>
  );
}
// ===== Вспомогательные функции для еды =====
function calcMealTotals(meal) {
  return meal.items.reduce(
    (acc, item) => {
      acc.proteins += item.proteins || 0;
      acc.fats += item.fats || 0;
      acc.carbs += item.carbs || 0;
      acc.kcal += item.kcal || 0;
      return acc;
    },
    { proteins: 0, fats: 0, carbs: 0, kcal: 0 }
  );
}

function calcAllTotals(meals) {
  return meals.reduce(
    (acc, meal) => {
      const t = calcMealTotals(meal);
      acc.proteins += t.proteins;
      acc.fats += t.fats;
      acc.carbs += t.carbs;
      acc.kcal += t.kcal;
      return acc;
    },
    { proteins: 0, fats: 0, carbs: 0, kcal: 0 }
  );
}
function GramsModal({ open, onClose, onSubmit, initial }) {
  const { t } = useTranslation();

  const [value, setValue] = useState(initial ?? "");
  const inputRef = useRef(null);

  // Когда открыта граммовка, прячем нижнее меню.
  // Иначе оно имеет z-index выше этой маленькой модалки и вылезает поверх неё.
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const root = document.documentElement;
    const count = Number(root.dataset.fitmeupHideBottomNavCount || 0);

    root.dataset.fitmeupHideBottomNavCount = String(count + 1);
    root.dataset.fitmeupHideBottomNav = "1";

    return () => {
      const next = Math.max(
        0,
        Number(root.dataset.fitmeupHideBottomNavCount || 1) - 1
      );

      if (next > 0) {
        root.dataset.fitmeupHideBottomNavCount = String(next);
        root.dataset.fitmeupHideBottomNav = "1";
      } else {
        delete root.dataset.fitmeupHideBottomNavCount;
        delete root.dataset.fitmeupHideBottomNav;
      }
    };
  }, [open]);

const parse = (s) => {
  const cleaned = String(s ?? "")
    .replace(/\s+/g, "")
    .replace(",", ".");

  if (cleaned === "") return null;

  // Разрешаем промежуточный ввод: "12," / "12." — кнопка не должна гаснуть.
  const normalized = cleaned.endsWith(".") ? cleaned.slice(0, -1) : cleaned;

  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;

  const n = Number(normalized);
  return Number.isFinite(n) ? Math.max(0, +n.toFixed(1)) : null;
};

  const canSave = parse(value) !== null && parse(value) > 0;

  const trySubmit = () => {
    const grams = parse(value);
    if (grams === null) return;
    onSubmit(grams);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    setValue(String(initial ?? ""));

    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, initial]);

  if (!open) return null;

  return (
    <Portal>
      
<div
className="fixed inset-0 z-[50050] flex items-start justify-center bg-black/45 px-4 pb-4 pt-6 backdrop-blur-sm"
        style={{
          paddingTop: hasTgAuth()
            ? "24px"
            : "calc(24px + env(safe-area-inset-top, 0px))",
        }}        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          stopAndProtect(e, 1200);
          onClose?.();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div
          className="relative w-full max-w-[330px] rounded-[26px] border border-cyan-100/20 bg-[#1f2a3b] px-4 pb-4 pt-3 shadow-[0_24px_80px_rgba(0,0,0,0.72)]"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onPointerDown={(e) => {
              stopAndProtect(e);
              onClose?.();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.06] text-[14px] text-white/75 active:scale-[0.96]"
            aria-label={t("actions.close")}
          >
            ✕
          </button>

          <div className="pr-10">
            <div className="break-words text-[16px] font-bold leading-snug text-white">
              {t("gramsModal.title")}
            </div>
          </div>

          <label className="mt-3 grid gap-1.5 text-[12px] font-medium text-white/60">
            {t("gramsModal.question")}
            <input
              ref={inputRef}
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (!canSave) return;
                  trySubmit();
                }
              }}
              inputMode="decimal"
              enterKeyHint="done"
              className="h-12 w-full rounded-2xl border border-cyan-100/15 bg-slate-950/40 px-4 text-center text-[20px] font-bold text-white outline-none placeholder:text-white/35 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/20"
              placeholder="100"
            />
          </label>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onPointerDown={(e) => {
                stopAndProtect(e, 1200);
                onClose?.();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="h-11 rounded-2xl border border-white/10 bg-white/[0.055] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
            >
              {t("actions.cancel")}
            </button>

             <button
              type="button"
              onPointerDown={(e) => {
                if (!canSave) return;
                stopAndProtect(e, 1200);
                trySubmit();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                "h-11 rounded-2xl text-[13px] font-bold transition " +
                (canSave
                  ? "bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-950 shadow-[0_14px_34px_rgba(45,212,191,0.25)] active:scale-[0.98]"
                  : "bg-white/10 text-white/35 cursor-not-allowed")
              }
            >
              {t("feedback.done")}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ===== Добавить блюдо: выбор способа =====
function AddFoodModal({ open, onClose, mealTitle, onPickMode, onOpenMy }) {
  if (!open) return null;

  return (
  <Portal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <BrandOverlay onClick={onClose} />

      <div
        className="relative w-full max-w-[420px] px-4 pb-4"
        onClick={(e) => e.stopPropagation()}
      >
        <GlassCard className="bg-slate-900/95 border-white/15 rounded-[28px] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="grid">
              <div className="text-[13px] font-semibold text-slate-50">Добавить блюдо</div>
              <div className="text-[10px] text-slate-300 uppercase tracking-wide">
                {mealTitle || ""}
              </div>
            </div>
<AppCloseButton onClick={onClose} />
          </div>

          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => onPickMode("photo")}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-left hover:bg-white/15 active:scale-[0.99]"
            >
              <div className="text-[13px] font-semibold text-slate-50">📸 Сфотографировать блюдо</div>
              <div className="text-[11px] text-slate-200/80 mt-0.5">Быстро добавить по фото</div>
            </button>

            <button
              type="button"
              onClick={() => onPickMode("manual")}
              className="w-full rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-left hover:bg-white/15 active:scale-[0.99]"
            >
              <div className="text-[13px] font-semibold text-slate-50">✍️ Добавить руками</div>
              <div className="text-[11px] text-slate-200/80 mt-0.5">Создать вручную</div>
            </button>

            <button
              type="button"
              onClick={onOpenMy}
              className="w-full rounded-2xl border border-cyan-200/40 bg-cyan-500/10 px-3 py-2 text-left hover:bg-cyan-500/15 active:scale-[0.99]"
            >
              <div className="text-[13px] font-semibold text-cyan-50">⭐ Мои блюда</div>
              <div className="text-[11px] text-cyan-50/80 mt-0.5">
                Выбрать из ваших сохранённых блюд и ингредиентов
              </div>
            </button>
          </div>
        </GlassCard>
      </div>
    </div>
   </Portal>
);
}
function WorkoutConflictModal({
  open,
  onClose,
  title,
  canSave,
  saving,
  onSave,
  onCancelWorkout,
  onResume,
}) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-[22000] flex items-start justify-center px-3 pb-3 pt-[calc(env(safe-area-inset-top)+86px)]">
        <div
          className="absolute inset-0 bg-black/56 backdrop-blur-sm"
          onClick={onClose}
        />

        <div
          className="relative w-full max-w-[420px] overflow-hidden rounded-[30px] bg-[#07111f]/96 p-4 text-white shadow-[0_26px_88px_rgba(0,0,0,0.76),0_0_30px_rgba(45,212,191,0.10)] backdrop-blur-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(20,184,166,0.18),transparent_42%),radial-gradient(circle_at_100%_100%,rgba(14,165,233,0.10),transparent_48%)]" />

          <div className="relative mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
<div className="inline-flex h-9 items-center gap-2 rounded-full bg-cyan-300/[0.10] px-3 text-[12px] font-bold text-cyan-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
  ⚡ {t("workout.session.conflictBadge")}
</div>

<div className="mt-3 text-[20px] font-extrabold leading-tight text-slate-50">
  {t("workout.session.conflictTitle")}
</div>

<div className="mt-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-cyan-100/65">
  {title || t("nav.workout")}
</div>
            </div>

            <AppCloseButton onClick={onClose} />
          </div>

<div className="relative mb-3 rounded-2xl bg-white/[0.055] px-3.5 py-3 text-[13px] font-medium leading-snug text-slate-100/86 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
  {t("workout.session.conflictHint")}
</div>

          <div className="relative grid gap-2">
            <button
              type="button"
              onClick={onResume}
              className="w-full rounded-2xl bg-cyan-500/12 px-3.5 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:bg-cyan-500/16 active:scale-[0.99]"
            >
<div className="text-[14px] font-extrabold text-cyan-50">
  ↩️ {t("workout.session.resumeWorkout")}
</div>
<div className="mt-0.5 text-[11px] font-semibold text-cyan-50/72">
  {t("workout.session.resumeHint")}
</div>
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={!canSave || saving}
              className={cn(
                "w-full rounded-2xl px-3.5 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] active:scale-[0.99]",
                canSave && !saving
                  ? "bg-emerald-400/14 hover:bg-emerald-400/18"
                  : "bg-white/5 opacity-55"
              )}
            >
<div className="text-[14px] font-extrabold text-emerald-50">
  ✅{" "}
  {saving
    ? t("workout.session.saving")
    : t("workout.session.finishAndSave")}
</div>
<div className="mt-0.5 text-[11px] font-semibold text-emerald-50/72">
  {t("workout.session.saveToHistoryHint")}
</div>
            </button>

            <button
              type="button"
              onClick={onCancelWorkout}
              className="w-full rounded-2xl bg-rose-500/12 px-3.5 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:bg-rose-500/16 active:scale-[0.99]"
            >
<div className="text-[14px] font-extrabold text-rose-50">
  ✖ {t("workout.session.cancelWorkout")}
</div>
<div className="mt-0.5 text-[11px] font-semibold text-rose-50/72">
  {t("workout.session.discardProgress")}
</div>
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function WorkoutFinishConfirmModal({
  open,
  onClose,
  title,
  durationText,
  canSave,
  saving,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-[22000] flex items-end justify-center p-3 sm:items-center">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        <div
          className="relative w-full max-w-[420px] overflow-hidden rounded-[30px] border border-emerald-100/22 bg-[#07111f]/96 p-4 text-white shadow-[0_26px_88px_rgba(0,0,0,0.76),0_0_30px_rgba(16,185,129,0.12)] backdrop-blur-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(16,185,129,0.18),transparent_42%),radial-gradient(circle_at_100%_100%,rgba(14,165,233,0.10),transparent_48%)]" />

          <div className="relative mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex h-9 items-center gap-2 rounded-full border border-emerald-200/25 bg-emerald-300/[0.12] px-3 text-[12px] font-bold text-emerald-50">
                ✅ Завершение
              </div>

              <div className="mt-3 text-[20px] font-extrabold leading-tight text-slate-50">
                Сохранить тренировку?
              </div>

              <div className="mt-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-emerald-100/65">
                {title || "Тренировка"}
              </div>
            </div>

            <AppCloseButton onClick={onClose} />
          </div>

          <div className="relative mb-3 grid grid-cols-2 gap-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.055] px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
                Длительность
              </div>
              <div className="mt-1 font-mono text-[16px] font-extrabold text-cyan-50">
                {durationText || "00:00"}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.055] px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
                Статус
              </div>
              <div className="mt-1 text-[16px] font-extrabold text-emerald-50">
                В историю
              </div>
            </div>
          </div>

          <div className="relative grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-2xl border border-white/12 bg-white/[0.06] text-[13px] font-bold text-slate-100/82 active:scale-[0.98]"
            >
              Отмена
            </button>

            <button
              type="button"
              disabled={!canSave || saving}
              onClick={onConfirm}
              className={cn(
                "h-11 rounded-2xl text-[13px] font-extrabold active:scale-[0.98]",
                canSave && !saving
                  ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-950 shadow-[0_12px_32px_rgba(16,185,129,0.34)]"
                  : "bg-slate-700/70 text-slate-300 opacity-60"
              )}
            >
              {saving ? "Сохраняю…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
// ===== Конструктор блюда (сборка из ингредиентов) =====
function DishBuilderModal({
  open,
  onClose,
  foods,
  mealTitle,
  onCreateDish,
  onUpdateFood,
  initialDish = null,
}) {
  const { t: tr } = useTranslation();

  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [components, setComponents] = useState([]);
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [selectedCollapsed, setSelectedCollapsed] = useState(false);
  const [isSavingDish, setIsSavingDish] = useState(false);
  const [editingGrams, setEditingGrams] = useState(null);
  const [editingFood, setEditingFood] = useState(null);
  const [createIngredientOpen, setCreateIngredientOpen] = useState(false);
  const [createdFoods, setCreatedFoods] = useState([]);
  const [localFoodEdits, setLocalFoodEdits] = useState({});
  const [sortOpen, setSortOpen] = useState(false);
  const [ingredientSortMode, setIngredientSortMode] = useState("recent");

  const usageStatsVersion = useMyFoodUsageStatsVersion(open);

const getDishBuilderSortLabel = (value) => {
  if (value === "meal") {
    return tr("ration.myFoods.sortMeal");
  }

  if (value === "frequent") {
    return tr("ration.myFoods.sortFrequent");
  }

  if (value === "recent") {
    return tr("ration.myFoods.sortRecent");
  }

  return tr("ration.myFoods.sortMeal");
};

const getDishBuilderSectionTitle = (title) => {
  if (title === "Завтрак") {
    return tr("ration.breakfast");
  }

  if (title === "Обед") {
    return tr("ration.lunch");
  }

  if (title === "Ужин") {
    return tr("ration.dinner");
  }

  if (title === "Перекус") {
    return tr("ration.snack");
  }

  if (title === "Другое") {
    return tr("ration.myFoods.other");
  }

  return title;
};

const chooseIngredientSortMode = (nextMode) => {
  setIngredientSortMode(nextMode);

  if (nextMode === "frequent") {
    ensureMyFoodUsageStatsReady().catch((e) => {
      debugWarn("[myFoodUsage] rebuild failed:", e);
    });
  }
};

const dishScrollRef = useRef(null);
const searchWrapRef = useRef(null);

const onboardingPrefillAppliedRef =
  useRef(false);

const fmt = (value, digits = 1) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";

    const rounded = Math.round(n * 10 ** digits) / 10 ** digits;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(digits);
  };

  const makeLocalIngredientId = () =>
    `ingredient_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const resetBuilderState = () => {
    setName("");
    setQ("");
    setComponents([]);
    setShowAllIngredients(false);
    setSelectedCollapsed(false);
    setIsSavingDish(false);
    setEditingGrams(null);
    setEditingFood(null);
    setCreateIngredientOpen(false);
    setCreatedFoods([]);
    setLocalFoodEdits({});
    setSortOpen(false);
  };

  const normalizeMealForSort = (value) => {
    const mealOrder = ["Завтрак", "Обед", "Перекус", "Ужин"];
    const s = String(value || "").trim();

    if (mealOrder.includes(s)) return s;

    const low = s.toLowerCase();

    if (low.includes("завтрак") || low === "breakfast") return "Завтрак";
    if (low.includes("обед") || low === "lunch") return "Обед";
    if (low.includes("перекус") || low === "snack") return "Перекус";
    if (low.includes("ужин") || low === "dinner") return "Ужин";

    return "Другое";
  };

  const normalizedFoods = useMemo(() => {
    const merged = [...(createdFoods || []), ...(foods || [])];
    const seenIds = new Set();
    const seenNames = new Set();

    return merged
      .map((sourceFood) => {
        const id = String(sourceFood?.id ?? "");
        const edited = id ? localFoodEdits[id] : null;

        const nextFood = edited
          ? {
              ...sourceFood,
              ...edited,
              per100: {
                ...(sourceFood?.per100 || {}),
                ...(edited?.per100 || {}),
              },
            }
          : sourceFood;

        return normalizeMyFoodForList(nextFood);
      })
      .filter((food) => {
        const idKey = String(food?.id ?? "").trim();
        const typeKey = isMyFoodsDish(food) ? "dish" : "ingredient";
        const nameKey = `${typeKey}:${String(food?.name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ")}`;

        if (idKey) {
          if (seenIds.has(idKey)) return false;
          seenIds.add(idKey);
        }

        if (nameKey !== `${typeKey}:`) {
          if (seenNames.has(nameKey)) return false;
          seenNames.add(nameKey);
        }

        return true;
      });
  }, [foods, createdFoods, localFoodEdits]);

  useEffect(() => {
    if (!open) {
      onboardingPrefillAppliedRef.current =
        false;

      return undefined;
    }

    return subscribeAppEvents(
      ({ type, payload }) => {
        if (
          type !==
            "dish-builder:prefill" ||
          onboardingPrefillAppliedRef.current
        ) {
          return;
        }

        const firstIngredient =
          normalizedFoods.find((food) =>
            isMyFoodsSingleProduct(food)
          );

        if (
          !firstIngredient ||
          firstIngredient.id == null
        ) {
          return;
        }

        onboardingPrefillAppliedRef.current =
          true;

setName(
  String(
    payload?.name ||
      "Моё новое блюдо"
  )
);

setQ("");

setComponents([]);

setSelectedCollapsed(false);
setShowAllIngredients(true);
      }
    );
  }, [
    open,
    normalizedFoods,
  ]);

  const foodById = useMemo(() => {
    const map = new Map();

    normalizedFoods.forEach((food) => {
      if (food?.id == null) return;
      map.set(String(food.id), food);
    });

    return map;
  }, [normalizedFoods]);

  const selectedIds = useMemo(
    () => new Set(components.map((c) => String(c.foodId))),
    [components]
  );

  const baseIngredients = useMemo(() => {
    const query = q.trim().toLowerCase();

    return normalizedFoods
      .filter((x) => isMyFoodsSingleProduct(x))
      .filter((x) =>
        !query ? true : (x.name || "").toLowerCase().includes(query)
      )
      .filter((x) => !selectedIds.has(String(x.id)));
  }, [normalizedFoods, q, selectedIds]);

  const ingredientSections = useMemo(() => {
    const list = [...baseIngredients];

    if (ingredientSortMode !== "meal") {
      return [
        {
          title: null,
          items: sortMyFoodsFlat(list, ingredientSortMode),
        },
      ];
    }

    const mealOrder = ["Завтрак", "Обед", "Перекус", "Ужин"];
    const byMeal = new Map();

    for (const meal of mealOrder) byMeal.set(meal, []);
    byMeal.set("Другое", []);

    for (const item of list) {
      const key = normalizeMealForSort(item?.lastMeal);
      if (!byMeal.has(key)) byMeal.set(key, []);
      byMeal.get(key).push(item);
    }

    for (const arr of byMeal.values()) {
      arr.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    }

    const currentMeal = normalizeMealForSort(mealTitle);
    const orderedMeals = [
      currentMeal,
      ...mealOrder.filter((meal) => meal !== currentMeal),
      "Другое",
    ];

    return orderedMeals
      .map((title) => ({ title, items: byMeal.get(title) || [] }))
      .filter((section) => section.items.length);
  }, [baseIngredients, ingredientSortMode, mealTitle, usageStatsVersion]);

  const totalIngredientCount = useMemo(
    () =>
      ingredientSections.reduce(
        (sum, section) => sum + section.items.length,
        0
      ),
    [ingredientSections]
  );

  const visibleIngredientSections = useMemo(() => {
    if (showAllIngredients) return ingredientSections;

    let left = 4;
    const result = [];

    for (const section of ingredientSections) {
      if (left <= 0) break;

      const items = section.items.slice(0, left);
      if (!items.length) continue;

      result.push({ ...section, items });
      left -= items.length;
    }

    return result;
  }, [ingredientSections, showAllIngredients]);

  const visibleIngredientCount = useMemo(
    () =>
      visibleIngredientSections.reduce(
        (sum, section) => sum + section.items.length,
        0
      ),
    [visibleIngredientSections]
  );

  const hiddenIngredientsCount = Math.max(
    0,
    totalIngredientCount - visibleIngredientCount
  );

  const currentSortOption =
    MY_FOODS_SORT_OPTIONS.find((option) => option.value === ingredientSortMode) ||
    MY_FOODS_SORT_OPTIONS[0];

  const macroForPortion = (food, gramsValue) => {
    const grams = Math.max(
      0,
      Number(String(gramsValue ?? food?.defaultGrams ?? 100).replace(",", ".")) ||
        0
    );

    const k = grams / 100;

    const proteins = +((food?.per100?.proteins || 0) * k).toFixed(1);
    const fats = +((food?.per100?.fats || 0) * k).toFixed(1);
    const carbs = +((food?.per100?.carbs || 0) * k).toFixed(1);

    const kcalPer100 =
      food?.per100?.kcal ??
      (food?.per100?.proteins || 0) * 4 +
        (food?.per100?.fats || 0) * 9 +
        (food?.per100?.carbs || 0) * 4;

    return {
      grams,
      proteins,
      fats,
      carbs,
      kcal: Math.round(kcalPer100 * k),
    };
  };

  const computed = useMemo(() => {
    let totalGrams = 0;
    let proteins = 0;
    let fats = 0;
    let carbs = 0;
    let kcal = 0;

    for (const c of components) {
      const ing = foodById.get(String(c.foodId));
      if (!ing) continue;

      const m = macroForPortion(ing, c.grams);
      if (m.grams <= 0) continue;

      totalGrams += m.grams;
      proteins += m.proteins;
      fats += m.fats;
      carbs += m.carbs;
      kcal += m.kcal;
    }

    if (totalGrams <= 0) {
      return {
        totalGrams: 0,
        total: { proteins: 0, fats: 0, carbs: 0, kcal: 0 },
        per100: { proteins: 0, fats: 0, carbs: 0, kcal: 0 },
      };
    }

    return {
      totalGrams,
      total: {
        proteins: +proteins.toFixed(1),
        fats: +fats.toFixed(1),
        carbs: +carbs.toFixed(1),
        kcal: Math.round(kcal),
      },
      per100: {
        proteins: +((proteins / totalGrams) * 100).toFixed(1),
        fats: +((fats / totalGrams) * 100).toFixed(1),
        carbs: +((carbs / totalGrams) * 100).toFixed(1),
        kcal: Math.round((kcal / totalGrams) * 100),
      },
    };
  }, [components, foodById]);

  const canSave = name.trim().length > 0 && computed.totalGrams > 0;
  const canPressSave = canSave && !isSavingDish;

  useEffect(() => {
    if (!open) return;

    resetBuilderState();

    if (!initialDish) return;

    setName(String(initialDish.name || ""));

    const initialIngredients = buildDishIngredientsList(initialDish, foods);

    const missingInitialFoods = initialIngredients
      .map((ing) => {
        const id = ing.foodId ?? ing.id;
        if (id == null || String(id).trim() === "") return null;

        const exists = (foods || []).some(
          (food) => String(food?.id) === String(id)
        );
        if (exists) return null;

        const grams = Math.max(1, Number(ing.grams || ing.weight || 100));

        return normalizeMyFoodForList({
          id,
          type: "ingredient",
          name: ing.name || "Ингредиент",
          defaultGrams: grams,
          per100: {
            proteins: +((Number(ing.proteins || 0) / grams) * 100).toFixed(1),
            fats: +((Number(ing.fats || 0) / grams) * 100).toFixed(1),
            carbs: +((Number(ing.carbs || 0) / grams) * 100).toFixed(1),
            kcal: Math.round((Number(ing.kcal || 0) / grams) * 100),
          },
        });
      })
      .filter(Boolean);

    setCreatedFoods(missingInitialFoods);

    const initialComponents = initialIngredients
      .map((ing) => {
        const id = ing.foodId ?? ing.id;
        if (id == null || String(id).trim() === "") return null;

        return {
          foodId: id,
          grams: Math.max(1, Number(ing.grams || ing.weight || 100)),
        };
      })
      .filter(Boolean);

    setComponents(initialComponents);
    setSelectedCollapsed(initialComponents.length > 0);
  }, [open, initialDish]);

  useEffect(() => {
    setShowAllIngredients(false);
  }, [q, ingredientSortMode]);

  const getComponentGrams = (foodId, fallback = 100) => {
    const found = components.find((c) => String(c.foodId) === String(foodId));
    return Math.max(1, Number(found?.grams ?? fallback ?? 100));
  };

  const addIngredient = (id) => {
    if (components.some((c) => String(c.foodId) === String(id))) return;

    const found = foodById.get(String(id));

    setSelectedCollapsed(false);
    setComponents((prev) => [
      ...prev,
      { foodId: id, grams: Math.max(1, Number(found?.defaultGrams ?? 50)) },
    ]);
  };

  const updateGrams = (id, grams) => {
    const g = Math.max(1, Number(String(grams).replace(",", ".")) || 1);

    setComponents((prev) =>
      prev.map((c) =>
        String(c.foodId) === String(id) ? { ...c, grams: g } : c
      )
    );
  };

  const removeIngredient = (id) => {
    setComponents((prev) =>
      prev.filter((c) => String(c.foodId) !== String(id))
    );
  };

const closeBuilder = () => {
  resetBuilderState();
  protectNextTap(500);
  onClose?.();

  window.setTimeout(() => {
    emitAppEvent(
      "dish-builder:cancelled"
    );
  }, 0);
};

  const dismissKeyboard = () => {
    const el = document.activeElement;
    if (!el) return false;

    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") {
      el.blur();
      return true;
    }

    return false;
  };

const scrollElementToTop = (element) => {
  if (!element) return;

  const root = dishScrollRef.current;

  if (!root) {
    element.scrollIntoView({
      behavior: "auto",
      block: "start",
      inline: "nearest",
    });
    return;
  }

  const rootRect = root.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const nextTop = root.scrollTop + elementRect.top - rootRect.top - 12;

  root.scrollTo({
    top: Math.max(0, nextTop),
    behavior: "auto",
  });
};

const liftSearchAboveKeyboard = () => {
  const viewport = window.visualViewport;

  let settleTimer = 0;
  let fallbackTimer = 0;
  let finished = false;

  function cleanup() {
    window.clearTimeout(settleTimer);
    window.clearTimeout(fallbackTimer);

    viewport?.removeEventListener("resize", scheduleMove);
    viewport?.removeEventListener("scroll", scheduleMove);
  }

  function moveSearch() {
    if (finished) return;

    finished = true;
    cleanup();

    window.requestAnimationFrame(() => {
      scrollElementToTop(searchWrapRef.current);
    });
  }

  function scheduleMove() {
    window.clearTimeout(settleTimer);

    // Ждём, пока клавиатура и viewport закончат движение.
    settleTimer = window.setTimeout(moveSearch, 120);
  }

  viewport?.addEventListener("resize", scheduleMove);
  viewport?.addEventListener("scroll", scheduleMove);

  // Запасной вариант для WebView, где visualViewport не прислал событие.
  fallbackTimer = window.setTimeout(moveSearch, 450);
};

  const buildDishPayload = () => {
    const ingredientSnapshots = components
      .map((component) => {
        const food = foodById.get(String(component.foodId));
        if (!food) return null;

        const portion = macroForPortion(food, component.grams);

        return {
          id: food.id,
          foodId: food.id,
          name: food.name,
          grams: portion.grams,
          weight: portion.grams,
          kcal: portion.kcal,
          proteins: portion.proteins,
          fats: portion.fats,
          carbs: portion.carbs,
          per100: food.per100,
          type: "ingredient",
        };
      })
      .filter(Boolean);

    return {
      id: `dish_${Date.now()}`,
      type: "dish",
      name: name.trim(),
      per100: computed.per100,
      defaultGrams: Math.max(1, Math.round(computed.totalGrams || 1)),
      lastMeal: mealTitle,
      updatedAt: Date.now(),
      ingredients: ingredientSnapshots,
      recipe: {
        components: components.map((c) => ({ ...c })),
      },
    };
  };
const normalizeDishBuilderName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ");
const saveDish = async () => {
  if (!canPressSave) return;

  setIsSavingDish(true);

  try {
const isSaveAsNew =
  Boolean(initialDish?.__saveAsNew) ||
  Boolean(initialDish?.__sourceDishId) ||
  Boolean(initialDish?.__sourceItemId);

const originalDishName = String(initialDish?.name || "").trim();
const nextDishName = String(name || "").trim();

const dishNameWasEdited =
  isSaveAsNew &&
  originalDishName &&
  normalizeDishBuilderName(nextDishName) !==
    normalizeDishBuilderName(originalDishName);

await Promise.resolve(
  onCreateDish?.({
    ...buildDishPayload(),
    __saveAsNew: isSaveAsNew,
    __dishNameWasEdited: dishNameWasEdited,
    __sourceDishId: initialDish?.__sourceDishId,
    __sourceItemId: initialDish?.__sourceItemId,
    __sourceMealId: initialDish?.__sourceMealId,
  })
);

    safeAlert(
      isSaveAsNew ? "Блюдо сохранено как новое" : "Блюдо создано",
      "Готово"
    );

    resetBuilderState();
    onClose?.();

    window.setTimeout(() => {
      emitAppEvent(
        "dish:created",
        {
          name: nextDishName,
        }
      );
    }, 120);
  } catch (err) {
    console.error("create dish failed:", err);
    setIsSavingDish(false);
    safeAlert("Не удалось сохранить блюдо. Попробуй ещё раз.", "Ошибка");
  }
};

  const addCreatedFoodToDish = (food) => {
    const nextFood = normalizeMyFoodForList(food);

    setCreatedFoods((prev) => {
      const next = [nextFood, ...(prev || [])];
      const seen = new Set();

      return next.filter((item) => {
        const key = String(item?.id ?? "");
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });

    setLocalFoodEdits((prev) => ({
      ...prev,
      [String(nextFood.id)]: nextFood,
    }));

    setSelectedCollapsed(false);

    setComponents((prev) => {
      if (prev.some((c) => String(c.foodId) === String(nextFood.id))) {
        return prev;
      }

      return [
        ...prev,
        {
          foodId: nextFood.id,
          grams: Math.max(1, Number(nextFood.defaultGrams ?? 100)),
        },
      ];
    });
  };

  const handleCreatedIngredientSave = async (draft) => {
    if (!draft?.name?.trim()) return;

    const localDraft = {
      ...draft,
      id: draft.id || makeLocalIngredientId(),
      type: "ingredient",
      name: draft.name.trim(),
      defaultGrams: Math.max(1, Number(draft.defaultGrams ?? 100)),
      lastMeal: draft.lastMeal || mealTitle,
      updatedAt: Date.now(),
    };

    setCreateIngredientOpen(false);

    try {
      const savedResp = await apiUpsertUserFood({
        type: "ingredient",
        name: localDraft.name,
        per100: localDraft.per100,
        defaultGrams: localDraft.defaultGrams,
        lastMeal: localDraft.lastMeal,
      });

      const savedFood = savedResp?.data ? savedResp.data : savedResp;

      const savedAppFood = normalizeMyFoodForList({
        ...localDraft,
        ...savedFood,
        id: savedFood?.id ?? localDraft.id,
        type: "ingredient",
        name: savedFood?.name ?? localDraft.name,
        per100: {
          proteins: Number(
            savedFood?.per100?.proteins ?? localDraft.per100?.proteins ?? 0
          ),
          fats: Number(savedFood?.per100?.fats ?? localDraft.per100?.fats ?? 0),
          carbs: Number(
            savedFood?.per100?.carbs ?? localDraft.per100?.carbs ?? 0
          ),
          kcal: Number(savedFood?.per100?.kcal ?? localDraft.per100?.kcal ?? 0),
        },
        defaultGrams: Number(
          savedFood?.defaultGrams ??
            savedFood?.default_grams ??
            localDraft.defaultGrams ??
            100
        ),
        lastMeal: savedFood?.lastMeal ?? localDraft.lastMeal,
        updatedAt: Date.now(),
      });

      addCreatedFoodToDish(savedAppFood);

      Promise.resolve(onUpdateFood?.(savedAppFood)).catch((err) => {
        console.error("sync created ingredient to my foods failed:", err);
      });
      safeAlert("Продукт создан", "Готово");
    } catch (err) {
      console.error("create ingredient from dish failed:", err);

      const fallbackFood = normalizeMyFoodForList(localDraft);
      addCreatedFoodToDish(fallbackFood);

      safeAlert(
        "Продукт добавлен в блюдо, но не сохранился в список продуктов. Проверь интернет.",
        "Ошибка"
      );
    }
  };

  if (!open) return null;

  const inputTextStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    backgroundColor: "rgba(2,6,23,0.44)",
    color: "#f8fafc",
    WebkitTextFillColor: "#f8fafc",
    caretColor: "#a5f3fc",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.035)",
  };

const TotalCell = ({ label, value, unit }) => (
  <div className="min-w-0 text-center">
    <div className="text-[11px] font-bold leading-none tracking-wide text-slate-200/78">
      {label}
    </div>

    <div className="mt-2 whitespace-nowrap text-[17px] font-extrabold leading-none tracking-[-0.01em] text-white tabular-nums">
      {value}
      {unit ? (
        <span className="ml-0.5 text-[11px] font-bold tracking-normal text-white/72">
          {unit}
        </span>
      ) : null}
    </div>
  </div>
);

  const MacroChip = ({ label, value }) => (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[8px] border border-white/10 bg-white/[0.075] px-2 py-1 text-[10px] font-semibold leading-none text-slate-100 shadow-[0_0_10px_rgba(15,23,42,0.10)]">
      {label ? (
        <span className="font-bold text-slate-200/88">{label}</span>
      ) : null}
      <span className="text-slate-50/92">{value}</span>
    </span>
  );

  const ChevronIcon = ({ expanded }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={"shrink-0 transition-transform " + (expanded ? "rotate-180" : "")}
    >
      <path
        d="M7 10l5 5 5-5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const IngredientCard = ({ food, selected }) => {
    if (!food) return null;

    const grams = selected
      ? getComponentGrams(food.id, food.defaultGrams ?? 100)
      : Math.max(1, Number(food.defaultGrams ?? 100));

    const m = macroForPortion(food, grams);

    return (
<div className="relative overflow-hidden rounded-[18px] border border-white/10 bg-slate-950/23 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2.5">
          <div
            className="mt-1 h-[58px] w-1.5 rounded-full"
            style={{ backgroundColor: getMyFoodAccentColor(food) }}
          />

          <div className="min-w-0">
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
              <div className="min-w-0 whitespace-normal break-words text-[15px] font-bold leading-snug tracking-[-0.01em] text-slate-50">
                {food.name}
              </div>

              <div className="flex shrink-0 items-start gap-1.5">
{selected ? (
  <button
    type="button"
    data-onboarding="dish-builder-grams"
    onPointerDown={rememberTapStart}
                    onPointerUp={(e) => {
                      runMenuTap(e, () => {
                        setEditingGrams({ foodId: food.id, initial: grams });
                      });
                    }}
                    onPointerCancel={clearTapStart}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="h-8 min-w-[58px] rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2 text-center text-[13px] font-bold text-cyan-100 tabular-nums shadow-[0_0_12px_rgba(45,230,230,0.04)] active:scale-[0.97]"
                    aria-label={tr(
                      "ration.myFoods.dishBuilder.changeGramsAria",
                      {
                        name: food.name,
                      }
                    )}
                  >
                    {Math.round(Number(grams || 0))} {tr("units.gramsShort")}
                  </button>
                ) : (
                  <div className="shrink-0 rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 tabular-nums shadow-[0_0_12px_rgba(45,230,230,0.04)]">
                    {Math.round(Number(grams || 0))} {tr("units.gramsShort")}
                  </div>
                )}

<button
  type="button"
  data-onboarding={
    selected
      ? "dish-builder-edit"
      : undefined
  }
  onPointerDown={rememberTapStart}
  onPointerUp={(e) => {
    runMenuTap(e, () => setEditingFood(food));
  }}
                  onPointerCancel={clearTapStart}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.07] text-[14px] text-white/76 transition active:scale-[0.96]"
                  aria-label={tr("ration.myFoods.dishBuilder.editMacrosAria")}
                >
                  ✎
                </button>

<button
  type="button"

  data-onboarding={
    !selected
      ? "dish-builder-add-ingredient"
      : undefined
  }

  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selected) {
      removeIngredient(food.id);
      return;
    }

    addIngredient(food.id);

    emitAppEvent(
      "dish-builder:ingredient-added",
      {
        foodId: food.id,
        name: food.name,
      }
    );
  }}
  style={{ touchAction: "manipulation" }}
  className={
    "grid h-8 w-8 shrink-0 place-items-center rounded-full border text-[19px] leading-none transition active:scale-[0.96] " +
    (selected
      ? "border-rose-300/15 bg-rose-400/10 text-rose-300"
      : "border-white/10 bg-white/[0.075] text-white/82 hover:bg-white/[0.11]")
  }
  aria-label={
    selected
      ? tr("ration.myFoods.dishBuilder.removeIngredientAria")
      : tr("ration.myFoods.dishBuilder.addIngredientAria")
  }
>
  {selected ? "×" : "+"}
</button>
              </div>
            </div>

            <div className="mt-2 flex min-w-0 flex-wrap gap-1 overflow-visible">
              <MacroChip
  value={`${Math.round(Number(m.kcal || 0))} ${tr("units.kcal")}`}
/>
              <MacroChip
  label={tr("food.report.proteinShort")}
  value={`${fmt(m.proteins)} ${tr("units.gramsShort")}`}
/>
              <MacroChip
  label={tr("food.report.fatShort")}
  value={`${fmt(m.fats)} ${tr("units.gramsShort")}`}
/>
              <MacroChip
  label={tr("food.report.carbsShort")}
  value={`${fmt(m.carbs)} ${tr("units.gramsShort")}`}
/>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CreateIngredientModal = ({ open, onClose, onSave }) => {
    const [draftName, setDraftName] = useState("");
    const [draftProteins, setDraftProteins] = useState("");
    const [draftFats, setDraftFats] = useState("");
    const [draftCarbs, setDraftCarbs] = useState("");
    const nameInputRef = useRef(null);

    useEffect(() => {
      if (!open) return undefined;

      setDraftName("");
      setDraftProteins("");
      setDraftFats("");
      setDraftCarbs("");

      return focusModalInputAndOpenKeyboard(nameInputRef);
    }, [open]);

    if (!open) return null;

    const proteins = parseMacro(draftProteins);
    const fats = parseMacro(draftFats);
    const carbs = parseMacro(draftCarbs);
    const kcal = Math.round(proteins * 4 + fats * 9 + carbs * 4);

    const submit = () => {
      if (!draftName.trim()) {
        safeAlert("Введите название продукта.", "Добавление продукта");
        return;
      }

      if (isTooHighMacrosPer100({ proteins, fats, carbs })) {
        safeAlert(
          "БЖУ указывается на 100 г продукта, значения слишком большие",
          "Ошибка"
        );
        return;
      }

      onSave?.({
        id: makeLocalIngredientId(),
        type: "ingredient",
        name: draftName.trim(),
        per100: { proteins, fats, carbs, kcal },
        defaultGrams: 100,
        lastMeal: mealTitle,
        updatedAt: Date.now(),
      });
    };

const modalInputStyle = {
  WebkitAppearance: "none",
  appearance: "none",
  backgroundColor: "#07111f",
  color: "#f8fafc",
  WebkitTextFillColor: "#f8fafc",
  caretColor: "#f8fafc",
  lineHeight: "normal",
};

    return (
      <Portal>
        <div
className="fixed inset-0 z-[41000] flex items-start justify-center overflow-y-auto bg-black/60 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+18px)] pt-[calc(env(safe-area-inset-top,0px)+72px)] backdrop-blur-sm"
style={{
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
}}
          onPointerDown={(e) => {
            if (e.target !== e.currentTarget) return;
            stopAndProtect(e, 700);
            onClose?.();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
className="relative mx-auto w-full max-w-[390px] rounded-[26px] border border-cyan-300/35 bg-slate-900/95 p-4 text-left shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08)] backdrop-blur-xl"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-[15px] font-semibold text-slate-50">
                {tr("ration.myFoods.productForm.title")}
              </div>

<AppCloseButton
  onPointerDown={rememberTapStart}
  onPointerUp={(e) => runMenuTap(e, onClose)}
  onPointerCancel={clearTapStart}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
/>
            </div>

            <input
              ref={nameInputRef}
              type="text"
              enterKeyHint="next"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={tr("ration.myFoods.productForm.namePlaceholder")}
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              style={modalInputStyle}
className="block h-11 w-full rounded-2xl border border-cyan-300/35 bg-[#07111f] px-3 py-0 text-[16px] text-slate-50 placeholder:text-slate-400 outline-none shadow-[0_0_12px_rgba(45,230,230,0.04)] focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
            />

            <div className="mt-3 grid grid-cols-3 gap-2">
              <input
                value={draftProteins}
                onChange={(e) => setDraftProteins(e.target.value)}
                type="text"
                inputMode="decimal"
                placeholder={tr("food.report.proteinShort")}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                style={modalInputStyle}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
              />

              <input
                value={draftFats}
                onChange={(e) => setDraftFats(e.target.value)}
                type="text"
                inputMode="decimal"
                placeholder={tr("food.report.fatShort")}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                style={modalInputStyle}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
              />

              <input
                value={draftCarbs}
                onChange={(e) => setDraftCarbs(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  submit();
                }}
                type="text"
                inputMode="decimal"
                placeholder={tr("food.report.carbsShort")}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                style={modalInputStyle}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
              />
            </div>

<div className="mt-3 rounded-2xl border border-transparent bg-cyan-400/[0.07] px-3 py-2 text-[12px] font-semibold text-cyan-50">
  {tr("ration.myFoods.productForm.per100")}{" "}
  {tr("food.report.proteinShort")} {formatMacroPreview(proteins)} ·{" "}
  {tr("food.report.fatShort")} {formatMacroPreview(fats)} ·{" "}
  {tr("food.report.carbsShort")} {formatMacroPreview(carbs)} ·{" "}
  {Math.round(kcal)} {tr("units.kcal")}
</div>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onPointerDown={rememberTapStart}
                onPointerUp={(e) => runMenuTap(e, onClose)}
                onPointerCancel={clearTapStart}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
className="h-11 flex-1 rounded-2xl border border-cyan-300/25 bg-[#07111f] text-[13px] font-semibold text-slate-50 shadow-[0_0_10px_rgba(45,230,230,0.04)] active:scale-[0.97]"
              >
                {tr("actions.cancel")}
              </button>

              <button
                type="button"
                onPointerDown={rememberTapStart}
                onPointerUp={(e) => runMenuTap(e, submit)}
                onPointerCancel={clearTapStart}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
className="h-11 flex-1 rounded-2xl border border-cyan-200/60 bg-cyan-500/20 text-[13px] font-bold text-cyan-50 shadow-[0_0_18px_rgba(45,230,230,0.14)] active:scale-[0.97]"
              >
                {tr("actions.save")}
              </button>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  return (
    <Portal>
      <div
        ref={dishScrollRef}
        data-modal-scroll-root="1"
        className="fixed inset-0 z-[40000] overflow-y-auto overscroll-contain"
        style={{
          background: APP_BG,
          boxSizing: "border-box",
          paddingTop: hasTgAuth()
            ? 0
            : "env(safe-area-inset-top, 0px)",
        }}
        onClick={(e) => {
          if (e.target !== e.currentTarget) return;
          if (dismissKeyboard()) return;
          closeBuilder();
        }}
      >
<div className="min-h-full px-4 pb-[calc(env(safe-area-inset-bottom)+80px)] pt-7">
  <div
    data-onboarding="dish-builder"
className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-[30px] border border-cyan-300/20 bg-slate-950/42 px-4 pb-5 pt-5 text-slate-50 shadow-[0_28px_90px_rgba(0,0,0,0.58),0_0_34px_rgba(45,212,191,0.10),inset_0_1px_0_rgba(255,255,255,0.045)] backdrop-blur-xl"
            onClick={(e) => {
              e.stopPropagation();
              if (sortOpen) setSortOpen(false);
            }}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(20,184,166,0.06),transparent_38%),radial-gradient(circle_at_100%_100%,rgba(14,165,233,0.06),transparent_42%)]" />

            <div className="relative">
  <div className="mb-5 flex items-start justify-between gap-3">
    <div className="min-w-0 pt-1 text-[21px] font-extrabold leading-none tracking-[0.025em] text-white drop-shadow-[0_8px_24px_rgba(255,255,255,0.08)]">
      {tr("ration.myFoods.dishBuilder.title")}
    </div>

    <AppCloseButton
      className="-mr-1 -mt-1 shrink-0"
      onPointerDown={(e) => {
        stopAndProtect(e, 700);
        closeBuilder();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  </div>

<input
  data-onboarding="dish-builder-name"
  value={name}
  onFocus={(e) => focusToEndAndScroll(e, dishScrollRef)}
  onChange={(e) => setName(e.target.value)}
  placeholder={tr("ration.myFoods.dishBuilder.namePlaceholder")}
                className="mt-0 h-[52px] w-full rounded-[17px]
                 border border-cyan-300/50 bg-slate-950/24 px-4 text-[16px] outline-none placeholder:text-slate-200/70 shadow-[0_0_12px_rgba(45,230,230,0.04)] focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/14"
                style={inputTextStyle}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />

<div
  data-onboarding="dish-builder-total"
  className="mt-4 rounded-[20px] border border-cyan-300/30 bg-cyan-500/[0.032] px-3.5 pb-3.5 pt-3.5 shadow-[0_0_18px_rgba(34,211,238,0.04),inset_0_1px_0_rgba(255,255,255,0.045)]"
>
  <div className="mb-3 text-[14px] font-semibold text-cyan-300">
    {tr("ration.myFoods.dishBuilder.total")}
  </div>

                <div className="grid grid-cols-5 divide-x divide-white/10">
                  <TotalCell
                    label={tr("ration.myFoods.dishBuilder.weight")}
                    value={Math.round(computed.totalGrams)}
                    unit={tr("units.gramsShort")}
                  />
                  <TotalCell
                    label={tr("ration.myFoods.dishBuilder.caloriesLabel")}
                    value={Math.round(computed.total.kcal)}
                  />
                  <TotalCell
                    label={tr("food.report.proteinShort")}
                    value={fmt(computed.total.proteins)}
                    unit={tr("units.gramsShort")}
                  />
                  <TotalCell
                    label={tr("food.report.fatShort")}
                    value={fmt(computed.total.fats)}
                    unit={tr("units.gramsShort")}
                  />
                  <TotalCell
                    label={tr("food.report.carbsShort")}
                    value={fmt(computed.total.carbs)}
                    unit={tr("units.gramsShort")}
                  />
                </div>
              </div>

{components.length > 0 && (
  <div
    data-onboarding="dish-builder-selected"
    className="mt-5 rounded-[20px] border border-transparent bg-slate-950/26 px-3 py-3 shadow-[0_12px_26px_rgba(0,0,0,0.16)]"
  >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onPointerDown={rememberTapStart}
                      onPointerUp={(e) =>
                        runMenuTap(e, () => setSelectedCollapsed((v) => !v))
                      }
                      onPointerCancel={clearTapStart}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span className="truncate text-[17px] font-semibold text-cyan-300">
                        {tr("ration.myFoods.dishBuilder.ingredients")}
                      </span>

                      <span className="grid h-7 min-w-7 place-items-center rounded-full bg-cyan-400/15 px-2.5 text-[14px] font-extrabold text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.10)]">
                        {components.length}
                      </span>

                      <span className="grid h-6 w-6 place-items-center text-cyan-100/75">
                        <ChevronIcon expanded={!selectedCollapsed} />
                      </span>
                    </button>

<button
  type="button"
  data-onboarding="dish-builder-save"
  disabled={!canPressSave}
  onPointerDown={rememberTapStart}
  onPointerUp={(e) => runMenuTap(e, saveDish)}
  onPointerCancel={clearTapStart}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
  className={
    "h-8 shrink-0 rounded-full px-3.5 text-[12px] font-bold tracking-[0.01em] transition active:scale-[0.97] " +
    (canPressSave
      ? "border border-emerald-200/75 bg-white/[0.075] text-emerald-50 shadow-[0_0_0_1px_rgba(110,231,183,0.22),inset_0_1px_0_rgba(255,255,255,0.14)]"
      : "border border-white/14 bg-white/[0.05] text-white/32")
  }
>
  {tr("actions.save")}
</button>
                  </div>

                  {!selectedCollapsed && (
                    <div className="mt-3 grid gap-2">
                      {components.map((c) => {
                        const food = foodById.get(String(c.foodId));

                        return (
                          <IngredientCard
                            key={String(c.foodId)}
                            food={food}
                            selected
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div ref={searchWrapRef} className="relative mt-5 scroll-mt-3">
                <div className="grid grid-cols-[minmax(0,1fr)_58px] gap-2.5">
                  <label className="relative block">
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/48">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle
                          cx="11"
                          cy="11"
                          r="6.5"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M16.2 16.2L21 21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>

<input
  data-onboarding="dish-builder-search"
  value={q}
  onChange={(e) => setQ(e.target.value)}
onFocus={() => {
  liftSearchAboveKeyboard();
}}

                      placeholder={tr("ration.myFoods.dishBuilder.searchIngredients")}
className="h-12 w-full rounded-[19px] border border-cyan-300/35 bg-[#07111f] pl-11 pr-4 text-[15px] text-slate-50 outline-none placeholder:text-slate-200/60 shadow-[0_0_12px_rgba(45,230,230,0.045)] ring-0 focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
                      style={inputTextStyle}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </label>

<button
  type="button"
  onPointerDown={rememberTapStart}
  onPointerUp={(e) =>
    runMenuTap(e, () => setSortOpen((v) => !v))
  }
  onPointerCancel={clearTapStart}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
  className="grid h-12 w-[58px] appearance-none grid-cols-[1fr_auto] items-center justify-center gap-0.5 rounded-[18px] px-2 text-cyan-50 transition active:scale-[0.96]"
  style={{
    WebkitAppearance: "none",
    appearance: "none",
    background: sortOpen
      ? "rgba(24, 215, 189, 0.16)"
      : "rgba(6, 23, 35, 0.94)",
    border: sortOpen
      ? "1px solid rgba(53, 243, 223, 0.58)"
      : "1px solid rgba(24, 215, 189, 0.38)",
    boxShadow:
      "0 0 18px rgba(24, 215, 189, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.055)",
    WebkitBackdropFilter: "blur(18px)",
    backdropFilter: "blur(18px)",
  }}
  aria-label={tr("ration.myFoods.dishBuilder.sorting")}
    title={getDishBuilderSortLabel(
    currentSortOption?.value
  )}
>
  <span className="text-[17px] leading-none">
    {currentSortOption?.icon || "⇅"}
  </span>

  <span className="grid h-4 w-4 place-items-center text-cyan-100/75">
    <ChevronIcon expanded={sortOpen} />
  </span>
</button>
                </div>

                {sortOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
className="absolute right-0 top-[54px] z-[80] w-[245px] overflow-hidden rounded-[18px] p-1.5"
style={{
  backgroundColor: "#07111f",
  border: "1px solid rgba(24, 215, 189, 0.38)",
  boxShadow:
    "0 18px 44px rgba(0, 0, 0, 0.58), 0 0 18px rgba(24, 215, 189, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.055)",
}}
                >
                    {MY_FOODS_SORT_OPTIONS.map((option, index) => {
                      const active = ingredientSortMode === option.value;
                      const isLast = index === MY_FOODS_SORT_OPTIONS.length - 1;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onPointerDown={rememberTapStart}
                          onPointerUp={(e) =>
                            runMenuTap(e, () => {
                              chooseIngredientSortMode(option.value);
                              setSortOpen(false);
                            })
                          }
                          onPointerCancel={clearTapStart}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          style={{ color: "#ffffff" }}
                          className={
                            "flex h-[44px] w-full items-center justify-between rounded-[14px] px-3 text-left text-[14px] font-semibold transition active:scale-[0.99] " +
                            (!isLast ? "mb-1 " : "") +
                            (active
                              ? "border border-cyan-200/55 bg-cyan-300/[0.11] shadow-[0_0_18px_rgba(45,230,230,0.10),inset_0_1px_0_rgba(255,255,255,0.06)]"
                              : "border border-transparent bg-transparent hover:bg-white/[0.055]")
                          }
                        >
                          <span className="flex min-w-0 items-center gap-3">
                            <span
                              className={
                                "grid h-7 w-7 shrink-0 place-items-center rounded-full text-white " +
                                (active ? "bg-cyan-300/16" : "bg-white/8")
                              }
                            >
                              <span className="text-[18px] leading-none">
                                {option.icon}
                              </span>
                            </span>

                            <span className="truncate" style={{ color: "#ffffff" }}>
                              {getDishBuilderSortLabel(option.value)}
                            </span>
                          </span>

                          <span
                            className={
                              "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition " +
                              (active
                                ? "border-cyan-100 bg-cyan-300/16 shadow-[0_0_10px_rgba(45,230,230,0.22)]"
                                : "border-white/45 bg-transparent")
                            }
                          >
                            {active ? (
                              <span className="h-[8px] w-[8px] rounded-full bg-cyan-100 shadow-[0_0_6px_rgba(165,243,252,0.55)]" />
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-[17px] font-semibold text-cyan-300">
                    {tr("ration.myFoods.dishBuilder.addIngredients")}
                  </div>

<button
  type="button"
  data-onboarding="dish-builder-create-product"
  onPointerDown={rememberTapStart}
  onPointerUp={(e) =>
    runMenuTap(e, () => setCreateIngredientOpen(true))
  }
                    onPointerCancel={clearTapStart}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="h-8 shrink-0 rounded-full border border-cyan-200/30 bg-cyan-300/[0.09] px-3 text-[12px] font-bold text-cyan-50 active:scale-[0.97]"
                  >
                    + {tr("ration.myFoods.dishBuilder.create")}
                  </button>
                </div>

<div
  data-onboarding="dish-builder-ingredient-list"
  className="grid gap-2 overflow-hidden rounded-[18px]"
>
  {visibleIngredientSections.map((section) => (
                    <div key={section.title || "all"} className="grid gap-2">
                      {section.title ? (
                        <div className="px-1 pt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-cyan-100/55">
                          {getDishBuilderSectionTitle(section.title)}
                        </div>
                      ) : null}

                      {section.items.map((ing) => (
                        <IngredientCard key={String(ing.id)} food={ing} />
                      ))}
                    </div>
                  ))}
                </div>

{hiddenIngredientsCount > 0 && (
  <div className="mt-3 pt-[2px]">
    <button
      type="button"
      onPointerDown={rememberTapStart}
      onPointerUp={(e) =>
        runMenuTap(e, () => setShowAllIngredients(true))
      }
      onPointerCancel={clearTapStart}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex h-12 w-full items-center justify-center gap-1 rounded-[18px] border border-[#18d7bd]/35 bg-[#061723]/80 text-[14px] font-semibold text-[#bffcf4] shadow-[0_10px_24px_rgba(0,0,0,0.20),0_0_14px_rgba(24,215,189,0.08)] transition active:scale-[0.99]"
    >
      {tr("ration.myFoods.dishBuilder.showMore")} <ChevronIcon expanded={false} />
    </button>
  </div>
)}
              </div>

<div className="mt-5 grid grid-cols-2 gap-3">
  <button
    type="button"
    onPointerDown={rememberTapStart}
    onPointerUp={(e) => runMenuTap(e, closeBuilder)}
    onPointerCancel={clearTapStart}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className="h-12 appearance-none rounded-[18px] text-[15px] font-semibold text-cyan-50/85 shadow-[0_10px_26px_rgba(0,0,0,0.24),0_0_12px_rgba(24,215,189,0.07)] transition active:scale-[0.98]"
    style={{
      WebkitAppearance: "none",
      appearance: "none",
      background: "rgba(6, 23, 35, 0.88)",
      border: "1px solid rgba(24, 215, 189, 0.38)",
    }}
  >
    {tr("actions.cancel")}
  </button>

<button
  type="button"
  data-onboarding="dish-builder-save"
  disabled={!canPressSave}
    onPointerDown={rememberTapStart}
    onPointerUp={(e) => runMenuTap(e, saveDish)}
    onPointerCancel={clearTapStart}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className={
      "h-12 appearance-none rounded-[18px] text-[15px] font-bold transition active:scale-[0.98] " +
      (canPressSave
        ? "text-cyan-50 shadow-[0_12px_30px_rgba(13,148,136,0.22),0_0_14px_rgba(24,215,189,0.10)]"
        : "cursor-not-allowed text-white/35 shadow-none")
    }
    style={{
      WebkitAppearance: "none",
      appearance: "none",
      background: canPressSave
        ? "linear-gradient(90deg, #0b6f68 0%, #0e8f84 52%, #0f7f8f 100%)"
        : "rgba(255, 255, 255, 0.055)",
      border: canPressSave
        ? "1px solid rgba(53, 243, 223, 0.58)"
        : "1px solid rgba(24, 215, 189, 0.18)",
    }}
  >
    {isSavingDish
      ? tr("ration.myFoods.dishBuilder.saving")
      : tr("actions.save")}
  </button>
</div>
            </div>
          </div>
        </div>

        <GramsModal
          open={!!editingGrams}
          initial={editingGrams?.initial ?? ""}
          onClose={() => setEditingGrams(null)}
          onSubmit={(grams) => {
            if (!editingGrams) return;
            updateGrams(editingGrams.foodId, grams);
            setEditingGrams(null);
          }}
        />

        <EditMyFoodModal
          open={!!editingFood}
          food={editingFood}
          onClose={() => setEditingFood(null)}
          onSave={(updatedFood) => {
            if (updatedFood?.id != null) {
              setLocalFoodEdits((prev) => ({
                ...prev,
                [String(updatedFood.id)]: updatedFood,
              }));
            }

            Promise.resolve(onUpdateFood?.(updatedFood)).catch((err) => {
              console.error("update ingredient from dish failed:", err);
              safeAlert(
                "Ингредиент обновился на экране, но не сохранился на сервере.",
                "Ошибка"
              );
            });

            setEditingFood(null);
          }}
        />

        <CreateIngredientModal
          open={createIngredientOpen}
          onClose={() => setCreateIngredientOpen(false)}
          onSave={handleCreatedIngredientSave}
        />
      </div>
    </Portal>
  );
}
function buildDishIngredientsList(dish, allFoods = []) {
  const directIngredients = Array.isArray(dish?.ingredients)
    ? dish.ingredients
    : [];

  if (directIngredients.length > 0) {
    return directIngredients;
  }

  const components = Array.isArray(dish?.recipe?.components)
    ? dish.recipe.components
    : [];

  return components
    .map((component, index) => {
      const source = (allFoods || []).find(
        (food) => String(food.id) === String(component.foodId)
      );

      const grams = Math.max(0, Number(component.grams || 0));
      const k = grams / 100;

      const proteins = Math.round((source?.per100?.proteins || 0) * k);
      const fats = Math.round((source?.per100?.fats || 0) * k);
      const carbs = Math.round((source?.per100?.carbs || 0) * k);

      const kcalPer100 =
        source?.per100?.kcal ??
        (source?.per100?.proteins || 0) * 4 +
          (source?.per100?.fats || 0) * 9 +
          (source?.per100?.carbs || 0) * 4;

      const kcal = Math.round(kcalPer100 * k);

      return {
        id: component.foodId || `${dish?.id || "dish"}_${index}`,
        name: source?.name || component.name || "Ингредиент",
        grams,
        proteins,
        fats,
        carbs,
        kcal,
      };
    })
    .filter(Boolean);
}
function rebuildDishFromIngredients(dish, nextIngredients) {
  const totals = (nextIngredients || []).reduce(
    (acc, ing) => {
      acc.grams += Number(ing.grams || 0);
      acc.proteins += Number(ing.proteins || 0);
      acc.fats += Number(ing.fats || 0);
      acc.carbs += Number(ing.carbs || 0);
      acc.kcal += Number(ing.kcal || 0);
      return acc;
    },
    { grams: 0, proteins: 0, fats: 0, carbs: 0, kcal: 0 }
  );

  const totalGrams = Math.max(0, Number(totals.grams || 0));
  const nextPer100 =
    totalGrams > 0
      ? {
          proteins: +((totals.proteins / totalGrams) * 100).toFixed(1),
          fats: +((totals.fats / totalGrams) * 100).toFixed(1),
          carbs: +((totals.carbs / totalGrams) * 100).toFixed(1),
          kcal: Math.round((totals.kcal / totalGrams) * 100),
        }
      : { proteins: 0, fats: 0, carbs: 0, kcal: 0 };

  return {
    ...dish,
    type: "dish",
    ingredients: nextIngredients,
    recipe: {
      ...(dish?.recipe || {}),
      components: (nextIngredients || []).map((ing) => ({
        foodId: ing.id,
        name: ing.name,
        grams: Number(ing.grams || 0),
      })),
    },
per100: nextPer100,
defaultGrams: +totalGrams.toFixed(1),
weight: +totalGrams.toFixed(1),
proteins: +totals.proteins.toFixed(1),
    fats: +totals.fats.toFixed(1),
    carbs: +totals.carbs.toFixed(1),
    kcal: Math.round(totals.kcal),
  };
}

function updateDishIngredientByIndex(dish, foods, ingredientIndex, newGrams) {
  const ingredients = buildDishIngredientsList(dish, foods);

  if (!ingredients[ingredientIndex]) return null;

  const nextGrams = Math.max(1, Number(newGrams || 1));
  const nextIngredients = ingredients.map((ing, index) => {
    if (index !== ingredientIndex) return ing;

    const oldGrams = Math.max(1, Number(ing.grams || 1));
    const k = nextGrams / oldGrams;

    return {
      ...ing,
      grams: nextGrams,
      proteins: +((Number(ing.proteins || 0)) * k).toFixed(1),
      fats: +((Number(ing.fats || 0)) * k).toFixed(1),
      carbs: +((Number(ing.carbs || 0)) * k).toFixed(1),
      kcal: Math.round(Number(ing.kcal || 0) * k),
    };
  });

  return rebuildDishFromIngredients(dish, nextIngredients);
}
function addDishIngredientToDish(dish, foods, ingredientFood, gramsValue) {
  if (!dish || !ingredientFood) return null;

  const food = normalizeMyFoodForList(ingredientFood);
  const grams = Math.max(
    1,
    Number(
      String(
        gramsValue ?? food?.defaultGrams ?? food?.weight ?? 100
      ).replace(",", ".")
    ) || 1
  );

  const per100 = food?.per100 || {};
  const kcalPer100 =
    per100.kcal ??
    calcKcalPer100(
      Number(per100.proteins || 0),
      Number(per100.fats || 0),
      Number(per100.carbs || 0)
    );

  const k = grams / 100;
  const ingredientId = food.id ?? `ingredient_${Date.now()}`;

  const nextIngredient = {
    id: ingredientId,
    foodId: ingredientId,
    name: food.name || "Ингредиент",
    grams,
    weight: grams,
    proteins: +(Number(per100.proteins || 0) * k).toFixed(1),
    fats: +(Number(per100.fats || 0) * k).toFixed(1),
    carbs: +(Number(per100.carbs || 0) * k).toFixed(1),
    kcal: Math.round(Number(kcalPer100 || 0) * k),
    per100,
    type: "ingredient",
  };

  const ingredients = buildDishIngredientsList(dish, foods);

  return rebuildDishFromIngredients(dish, [...ingredients, nextIngredient]);
}
function addDishIngredientsToDish(dish, foods, ingredientFoods = []) {
  const list = Array.isArray(ingredientFoods)
    ? ingredientFoods.filter(Boolean)
    : [ingredientFoods].filter(Boolean);

  let nextDish = dish;

  for (const ingredientFood of list) {
    const updatedDish = addDishIngredientToDish(nextDish, foods, ingredientFood);

    if (updatedDish) {
      nextDish = updatedDish;
    }
  }

  return nextDish;
}
function DishIngredientPickerModal({
  open,
  onClose,
  foods = [],
  excludeIngredientIds = [],
  onPick,
}) {
  const { t: tr } = useTranslation();

  const [q, setQ] = useState("");
  const [selectedFoods, setSelectedFoods] = useState({});
  const [sortOpen, setSortOpen] = useState(false);
  const [sortMode, setSortModeState] = useState(() => {
    try {
      return window.localStorage.getItem(myFoodsSortStorageKey) || "meal";
    } catch {
      return "meal";
    }
  });
  const [modalHeight, setModalHeight] = useState(null);
const usageStatsVersion = useMyFoodUsageStatsVersion(open);

const getIngredientPickerSortLabel = (value) => {
  if (value === "meal") {
    return tr("ration.myFoods.sortMeal");
  }

  if (value === "frequent") {
    return tr("ration.myFoods.sortFrequent");
  }

  if (value === "recent") {
    return tr("ration.myFoods.sortRecent");
  }

  return tr("ration.myFoods.sortMeal");
};

const setSortMode = (nextMode) => {
  setSortModeState(nextMode);

  try {
    window.localStorage.setItem(myFoodsSortStorageKey, nextMode);
  } catch {}

  if (nextMode === "frequent") {
    ensureMyFoodUsageStatsReady().catch((e) => {
      debugWarn("[myFoodUsage] rebuild failed:", e);
    });
  }
};

  useEffect(() => {
    if (!open) return;

    setQ("");
    setSelectedFoods({});
    setSortOpen(false);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

const height = Math.min(Math.round((window.innerHeight || 720) * 0.88), 760);
setModalHeight(Math.max(520, height));
  }, [open]);

  const excludeSet = useMemo(
    () => new Set((excludeIngredientIds || []).map((id) => String(id))),
    [excludeIngredientIds]
  );

  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase();

    return (foods || [])
      .map((food) => normalizeMyFoodForList(food))
      .filter((food) => isMyFoodsSingleProduct(food))
      .filter((food) => !excludeSet.has(String(food?.id)))
      .filter((food) =>
        query ? String(food?.name || "").toLowerCase().includes(query) : true
      );
  }, [foods, q, excludeSet]);

  const sections = useMemo(
    () =>
      buildMyFoodsSections(filteredItems, sortMode, "Продукты").map((section) => ({
        ...section,
        items: section.items.slice(0, 80),
      })),
    [filteredItems, sortMode, usageStatsVersion]
  );

  const totalItemsCount = filteredItems.length;
  const currentSortOption =
    MY_FOODS_SORT_OPTIONS.find((option) => option.value === sortMode) ||
    MY_FOODS_SORT_OPTIONS[0];

  const getIngredientPickerSectionTitle = (title) => {
    if (title === "Продукты") {
      return tr("ration.myFoods.products");
    }

    if (title === "Завтрак") {
      return tr("ration.breakfast");
    }

    if (title === "Обед") {
      return tr("ration.lunch");
    }

    if (title === "Ужин") {
      return tr("ration.dinner");
    }

    if (title === "Перекус") {
      return tr("ration.snack");
    }

    if (title === "Другое") {
      return tr("ration.myFoods.other");
    }

    return title;
  };

  const selectedFoodsList = useMemo(
    () => Object.values(selectedFoods).filter(Boolean),
    [selectedFoods]
  );

  const selectedCount = selectedFoodsList.length;

  const getFoodPickKey = (food) =>
    String(food?.id ?? food?.name ?? "").trim();

  const togglePickedFood = (food) => {
    const key = getFoodPickKey(food);
    if (!key) return;

    setSelectedFoods((prev) => {
      const next = { ...(prev || {}) };

      if (next[key]) {
        delete next[key];
      } else {
        next[key] = food;
      }

      return next;
    });
  };

  const confirmPickedFoods = () => {
    if (!selectedFoodsList.length) {
      safeAlert("Выбери хотя бы один ингредиент.", "Добавление ингредиента");
      return;
    }

    const pickedFoods = selectedFoodsList.map((food) => {
      const usageStat = bumpMyFoodUsageStat(food);

      return {
        ...food,
        useCount: usageStat.count,
        uses: usageStat.count,
        lastUsedAt: usageStat.lastUsedAt,
      };
    });

    onPick?.(pickedFoods);
  };

  if (!open) return null;

  const PickerIngredientCard = ({ food }) => {
    const grams = Math.max(1, Number(food?.defaultGrams ?? 100));
    const k = grams / 100;
    const p = +((Number(food?.per100?.proteins || 0)) * k).toFixed(1);
    const f = +((Number(food?.per100?.fats || 0)) * k).toFixed(1);
    const c = +((Number(food?.per100?.carbs || 0)) * k).toFixed(1);
    const kcalPer100 =
      food?.per100?.kcal ??
      calcKcalPer100(
        Number(food?.per100?.proteins || 0),
        Number(food?.per100?.fats || 0),
        Number(food?.per100?.carbs || 0)
      );
    const kcal = Math.round(Number(kcalPer100 || 0) * k);

    const pickKey = getFoodPickKey(food);
    const picked = Boolean(selectedFoods[pickKey]);

    return (
      <button
        key={food.id || food.name}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePickedFood(food);
        }}
        className={
          "relative w-full min-w-0 overflow-hidden rounded-[18px] px-3 py-3 pr-12 text-left transition active:scale-[0.99] " +
          (picked
            ? "border border-cyan-200/65 bg-cyan-300/[0.13] shadow-[0_0_20px_rgba(45,212,191,0.14),inset_0_1px_0_rgba(255,255,255,0.07)]"
            : "border border-white/10 bg-slate-950/28 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]")
        }
      >
        <div
          className="absolute bottom-3 left-3 top-3 w-1.5 rounded-full"
          style={{ backgroundColor: getMyFoodAccentColor(food) }}
        />

        <div
          className={
            "absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full border text-[15px] font-black transition " +
            (picked
              ? "border-cyan-100 bg-cyan-300 text-slate-950 shadow-[0_0_14px_rgba(45,212,191,0.28)]"
              : "border-white/15 bg-white/[0.06] text-white/55")
          }
        >
          {picked ? "✓" : "+"}
        </div>

        <div className="min-w-0 pl-3">
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
            <div className="min-w-0 whitespace-normal break-words text-[15px] font-bold leading-snug tracking-[-0.01em] text-slate-50">
              {food.name ||
                tr("ration.myFoods.ingredientPicker.ingredientFallback")}
            </div>

<div className="shrink-0 rounded-[13px] border border-cyan-300/35 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 tabular-nums shadow-[0_0_12px_rgba(45,230,230,0.04)]">
  {Math.round(grams)} {tr("units.gramsShort")}
</div>
          </div>

          <div className="mt-2 -mr-7 flex min-w-0 flex-wrap gap-1 overflow-visible">
            <MyFoodMacroChip
              label=""
              value={`${kcal} ${tr("units.kcal")}`}
            />
                        <MyFoodMacroChip
              label={tr("food.report.proteinShort")}
              value={`${+p.toFixed(1)} ${tr("units.gramsShort")}`}
            />
                        <MyFoodMacroChip
              label={tr("food.report.fatShort")}
              value={`${+f.toFixed(1)} ${tr("units.gramsShort")}`}
            />
                        <MyFoodMacroChip
              label={tr("food.report.carbsShort")}
              value={`${+c.toFixed(1)} ${tr("units.gramsShort")}`}
            />
          </div>
        </div>
      </button>
    );
  };

  return (
    <Portal>
<div
className="fixed inset-0 z-[50060] flex items-start justify-center overflow-hidden overscroll-none px-3 pb-3"
  style={{
    WebkitOverflowScrolling: "auto",
    background: SHEET_PREMIUM_BG,
    height: "100dvh",
    maxHeight: "100dvh",
    boxSizing: "border-box",
    paddingTop: hasTgAuth()
      ? "18px"
      : "calc(18px + env(safe-area-inset-top, 0px))",
  }}
>
  <div
    className="absolute inset-0"
    style={{ background: SHEET_PREMIUM_BG }}
    onClick={onClose}
  />

        <div
          className="relative w-full max-w-[420px] min-w-0"
          onClick={(e) => e.stopPropagation()}
        >
<div
  className="relative flex min-h-0 flex-col overflow-hidden rounded-[28px] border border-cyan-300/20 bg-[#07111f]/96 p-4 text-left shadow-[0_30px_90px_rgba(0,0,0,0.78),0_0_0_1px_rgba(45,212,191,0.08),0_0_34px_rgba(45,212,191,0.10),inset_0_1px_0_rgba(255,255,255,0.035)]"
  style={{
    height: "min(760px, calc(100dvh - 32px))",
    maxHeight: "calc(100dvh - 32px)",
  }}
>
            <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[15px] font-bold text-slate-50">
                  {tr("ration.myFoods.ingredientPicker.title")}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-300">
                  {tr("ration.myFoods.ingredientPicker.subtitle")}
                </div>
              </div>

<AppCloseButton
  className="-mr-1 -mt-1 shrink-0"
  onClick={onClose}
/>
            </div>

            <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_58px] gap-2.5">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tr(
                  "ration.myFoods.ingredientPicker.searchPlaceholder"
                )}
className="h-12 w-full min-w-0 rounded-[18px] border border-transparent bg-slate-950/45 px-3.5 text-[14px] font-medium text-white outline-none placeholder:text-white/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] ring-0 focus:border-transparent focus:ring-0"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSortOpen((v) => !v);
                }}
className={
  "grid h-12 w-[58px] grid-cols-[1fr_auto] items-center justify-center gap-0.5 rounded-[18px] border px-2 transition shadow-[0_0_18px_rgba(45,212,191,0.10),inset_0_1px_0_rgba(255,255,255,0.055)] active:scale-[0.96] " +
  (sortOpen
    ? "border-cyan-200/45 bg-cyan-300/[0.14] text-cyan-50"
    : "border-cyan-300/[0.28] bg-cyan-300/[0.065] text-cyan-50/85")
}
                aria-label={tr("ration.myFoods.ingredientPicker.sorting")}
                title={getIngredientPickerSortLabel(
                  currentSortOption?.value
                )}
              >
                <span className="text-[17px] leading-none">
                  {currentSortOption?.icon || "⇅"}
                </span>
<span className="ml-1 grid h-5 w-5 shrink-0 place-items-center text-cyan-100">
  <span
    className={
      "inline-block origin-center text-[16px] font-semibold leading-none transition-transform duration-200 " +
      (sortOpen ? "rotate-0" : "rotate-180")
    }
  >
    ⌃
  </span>
</span>
              </button>
            </div>

            <div className="relative shrink-0">
              {sortOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-[54px] z-[80] w-[245px] overflow-hidden rounded-[18px] border border-cyan-300/35 bg-[#1f2a3b]/98 p-1.5 shadow-[0_18px_44px_rgba(0,0,0,0.42),0_0_18px_rgba(45,230,230,0.10),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
                >
                  {MY_FOODS_SORT_OPTIONS.map((option, index) => {
                    const active = sortMode === option.value;
                    const isLast = index === MY_FOODS_SORT_OPTIONS.length - 1;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSortMode(option.value);
                          setSortOpen(false);
                        }}
                        style={{ color: "#ffffff" }}
                        className={
                          "flex h-[44px] w-full items-center justify-between rounded-[14px] px-3 text-left text-[14px] font-semibold transition active:scale-[0.99] " +
                          (!isLast ? "mb-1 " : "") +
                          (active
                            ? "border border-cyan-200/55 bg-cyan-300/[0.11] shadow-[0_0_18px_rgba(45,230,230,0.10),inset_0_1px_0_rgba(255,255,255,0.06)]"
                            : "border border-transparent bg-transparent hover:bg-white/[0.055]")
                        }
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span
                            className={
                              "grid h-7 w-7 shrink-0 place-items-center rounded-full text-white " +
                              (active ? "bg-cyan-300/16" : "bg-white/8")
                            }
                          >
                            <span className="text-[18px] leading-none">
                              {option.icon}
                            </span>
                          </span>

                          <span className="truncate">
                            {getIngredientPickerSortLabel(option.value)}
                          </span>
                        </span>

                        <span
                          className={
                            "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition " +
                            (active
                              ? "border-cyan-100 bg-cyan-300/16 shadow-[0_0_10px_rgba(45,230,230,0.22)]"
                              : "border-white/45 bg-transparent")
                          }
                        >
                          {active ? (
                            <span className="h-[8px] w-[8px] rounded-full bg-cyan-100 shadow-[0_0_6px_rgba(165,243,252,0.55)]" />
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

<div
  className="mt-3 min-h-0 flex-1 overflow-y-scroll pr-1 overscroll-contain"
  style={{
    WebkitOverflowScrolling: "touch",
    touchAction: "auto",
    scrollbarWidth: "thin",
    overscrollBehavior: "contain",
  }}
>
              {totalItemsCount ? (
                <div className="grid gap-2 pb-2">
                  {sections.map((section) => (
                    <div key={section.title || "all"} className="grid gap-2">
                      {section.title ? (
                        <div className="px-1 pt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-cyan-100/55">
                          {getIngredientPickerSectionTitle(section.title)}
                        </div>
                      ) : null}

                      {section.items.map((food) => (
                        <PickerIngredientCard key={String(food.id || food.name)} food={food} />
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-[18px] border border-white/10 bg-slate-950/35 px-3 py-4 text-center text-[12px] text-slate-400">
                  {tr("ration.myFoods.ingredientPicker.empty")}
                </div>
              )}
            </div>
                        <div className="mt-3 grid shrink-0 grid-cols-[0.85fr_1.15fr] gap-2">
<button
  type="button"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose?.();
  }}
  style={{ color: "#ecfeff" }}
  className="h-12 appearance-none rounded-[18px] border border-[#18d7bd]/35 bg-[#061723]/88 text-[14px] font-extrabold text-cyan-50 shadow-[0_14px_34px_rgba(0,0,0,0.30),0_0_14px_rgba(45,212,191,0.10),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl active:scale-[0.98]"
>
  {tr("actions.cancel")}
</button>

              <button
                type="button"
                disabled={!selectedCount}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmPickedFoods();
                }}
                className={
                  "h-12 rounded-[18px] text-[14px] font-extrabold transition active:scale-[0.98] " +
                  (selectedCount
                    ? "bg-gradient-to-r from-emerald-400 via-teal-300 to-sky-400 text-slate-950 shadow-[0_16px_34px_rgba(45,212,191,0.22)]"
                    : "bg-white/[0.075] text-white/35 cursor-not-allowed shadow-none")
                }
              >
                {selectedCount > 0
                  ? tr("ration.myFoods.ingredientPicker.nextCount", {
                      count: selectedCount,
                    })
                  : tr("ration.myFoods.ingredientPicker.next")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
function removeDishIngredientByIndex(dish, foods, ingredientIndex) {
  const ingredients = buildDishIngredientsList(dish, foods);

  if (!ingredients[ingredientIndex]) return null;

  const nextIngredients = ingredients.filter(
    (_, index) => index !== ingredientIndex
  );

  return rebuildDishFromIngredients(dish, nextIngredients);
}

function updateDishIngredientDetailsByIndex(
  dish,
  foods,
  ingredientIndex,
  updatedIngredient
) {
  const ingredients = buildDishIngredientsList(dish, foods);

  if (!ingredients[ingredientIndex]) return null;

  const nextIngredients = ingredients.map((ing, index) => {
    if (index !== ingredientIndex) return ing;

    const grams = Math.max(
      0,
      Number(ing.grams || updatedIngredient?.defaultGrams || 0)
    );
    const k = grams / 100;
    const per100 = updatedIngredient?.per100 || {};

    return {
      ...ing,
      name: updatedIngredient?.name || ing.name,
      grams,
      proteins: +((Number(per100.proteins || 0)) * k).toFixed(1),
      fats: +((Number(per100.fats || 0)) * k).toFixed(1),
      carbs: +((Number(per100.carbs || 0)) * k).toFixed(1),
      kcal: Math.round(Number(per100.kcal || 0) * k),
    };
  });

  return rebuildDishFromIngredients(dish, nextIngredients);
}
function DishIngredientsList({
  dish,
  foods = [],
  onEditIngredient,
  onEditIngredientDetails,
  onDeleteIngredient,
  compactMeal = false,
  onAddIngredient,
  onUpdateCurrentDish,
  onSaveAsNewDish,
  hasDishDraftChanges = false,
}) {
  const { t: tr } = useTranslation();

  const [openedIngredientMenuIndex, setOpenedIngredientMenuIndex] =
    useState(null);
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false);

  const ingredients = buildDishIngredientsList(dish, foods);
  const canAddIngredient = typeof onAddIngredient === "function";
  const showDraftActions = Boolean(hasDishDraftChanges);
  const excludeIngredientIds = ingredients.map((ing) => ing.foodId ?? ing.id);

  const actionsBlock = canAddIngredient ? (
    <>
      <div className="mt-1">
{!showDraftActions ? (
  <button
    type="button"
    data-onboarding="my-foods-dish-add-ingredient"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      setIngredientPickerOpen(true);
    }}
            className="h-11 w-full rounded-[16px] border border-cyan-300/40 bg-cyan-400/10 px-3 text-[13px] font-bold text-cyan-50 active:scale-[0.99]"
          >
            + {tr("ration.addIngredient")}
          </button>
        ) : (
          <div
            className={
              "grid gap-2 " +
              (onUpdateCurrentDish
                ? "grid-cols-[0.8fr_1fr_1fr]"
                : "grid-cols-[0.8fr_1.2fr]")
            }
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIngredientPickerOpen(true);
              }}
              className="h-10 rounded-[15px] border border-cyan-300/40 bg-cyan-400/10 px-2 text-[12px] font-bold text-cyan-50 active:scale-[0.99]"
            >
              {tr("ration.add")}
            </button>

            {onUpdateCurrentDish && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUpdateCurrentDish?.();
                }}
                className="h-10 rounded-[15px] border border-emerald-300/45 bg-emerald-400/12 px-2 text-[11px] font-bold text-emerald-50 active:scale-[0.99]"
              >
                {tr("ration.updateCurrent")}
              </button>
            )}

            {onSaveAsNewDish && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSaveAsNewDish?.();
                }}
                className="h-10 rounded-[15px] border border-cyan-100/75 bg-gradient-to-r from-cyan-300 via-sky-300 to-emerald-300 px-2 text-[11px] font-extrabold text-slate-950 shadow-[0_0_18px_rgba(45,212,191,0.28),inset_0_1px_0_rgba(255,255,255,0.35)] active:scale-[0.99]"
              >
                {tr("ration.saveAsNew")}
              </button>
            )}
          </div>
        )}
      </div>

      <DishIngredientPickerModal
        open={ingredientPickerOpen}
        onClose={() => setIngredientPickerOpen(false)}
        foods={foods}
        excludeIngredientIds={excludeIngredientIds}
        onPick={(pickedFoods) => {
          const foodsToAdd = Array.isArray(pickedFoods)
            ? pickedFoods.filter(Boolean)
            : [pickedFoods].filter(Boolean);

          setIngredientPickerOpen(false);
          onAddIngredient?.(foodsToAdd);
        }}
      />
    </>
  ) : null;

  if (!ingredients.length) {
    return (
      <div className="grid w-full gap-2">
        <div className="w-full rounded-[18px] border border-cyan-200/15 bg-slate-950/35 px-3 py-3 text-[12px] text-slate-400">
          {tr("ration.noIngredients")}
        </div>
        {actionsBlock}
      </div>
    );
  }

  return (
    <div
      className="grid w-full gap-2"
      onClick={() => setOpenedIngredientMenuIndex(null)}
    >
      {ingredients.map((ing, index) => {
        const ingredientAccentColor = getMyFoodAccentColor({
          per100: {
            proteins: Number(ing.proteins || 0),
            fats: Number(ing.fats || 0),
            carbs: Number(ing.carbs || 0),
          },
        });

        return (
          <div
            key={ing.id || `${ing.name}-${index}`}
            onPointerDownCapture={(e) => {
              if (openedIngredientMenuIndex == null) return;

              const target = e.target;

              const insideMenu =
                target?.closest?.('[data-ingredient-menu="1"]') ||
                target?.closest?.('[data-ingredient-menu-button="1"]');

              if (insideMenu) return;

              // Если меню ингредиента открыто, первый тап по ингредиенту
              // только закрывает меню. Не открываем граммовку и редактирование.
              stopAndProtect(e, 450);
              setOpenedIngredientMenuIndex(null);
            }}
            className={
              compactMeal
                ? "relative -mr-12 w-[calc(100%+0.5rem)] min-w-0 overflow-visible rounded-[18px] border border-white/10 bg-slate-950/23 py-3 pl-3 pr-10 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
                : "relative -mr-12 w-[calc(100%+0.5rem)] min-w-0 overflow-visible rounded-[18px] border border-white/10 bg-slate-950/23 px-3 py-3 pr-10 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
            }
          >
            <div
              className={
                compactMeal
                  ? "absolute bottom-3 left-2 top-3 w-1.5 rounded-full"
                  : "absolute bottom-3 left-3 top-3 w-1.5 rounded-full"
              }
              style={{ backgroundColor: ingredientAccentColor }}
            />

{(onEditIngredientDetails || onDeleteIngredient) && (
  <div className={DOT_MENU_WRAP_CLASS} data-ingredient-menu-button="1">
    <button
      type="button"
      data-onboarding="dish-ingredient-menu"
      onPointerDown={rememberTapStart}
      onPointerUp={(e) => {
        runMenuTap(e, () => {
          setOpenedIngredientMenuIndex((current) =>
            current === index ? null : index
          );
        });
      }}
      onPointerCancel={clearTapStart}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={DOT_MENU_BTN_CLASS}
      aria-label={tr("ration.ingredientMenu")}
      title={tr("ration.ingredientMenu")}
    >
      ⋯
    </button>
  </div>
)}

            {openedIngredientMenuIndex === index && (
              <div
                data-ingredient-menu="1"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className={
"absolute right-1.5 z-[120] w-[184px] overflow-hidden rounded-[18px] border border-slate-500/35 bg-[#151d2b] shadow-[0_18px_44px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.06)] " +
(index >= ingredients.length - 1 && ingredients.length > 1
  ? "bottom-10"
  : "top-10")
                }
              >
<button
  type="button"
  data-onboarding="dish-ingredient-edit"
  onPointerDown={(e) => {
    stopAndProtect(e);
    setOpenedIngredientMenuIndex(null);

    if (onEditIngredientDetails) {
                      onEditIngredientDetails(index, ing);
                      return;
                    }

                    onEditIngredient?.(index, ing);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-slate-700/55"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center text-[15px] text-cyan-100">
                    ✎
                  </span>

                  <span className="min-w-0 text-[13px] font-semibold leading-none text-white">
                    {tr("ration.edit")}
                  </span>
                </button>

                {onDeleteIngredient && (
                  <>
                    <div className="mx-3 h-px bg-slate-600/70" />

                    <button
                      type="button"
                      onPointerDown={(e) => {
                        stopAndProtect(e, 1200);
                        setOpenedIngredientMenuIndex(null);
                        onDeleteIngredient(index, ing);
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-rose-950/45"
                    >
                      <span className="grid h-6 w-6 shrink-0 place-items-center text-[16px] leading-none text-rose-300">
                        ⌫
                      </span>

                      <span className="min-w-0 text-[13px] font-semibold leading-none text-rose-300">
                        {tr("ration.delete")}
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
              <div className="w-1.5" />

              <div className="min-w-0">
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0 whitespace-normal break-words text-[15px] font-bold leading-snug tracking-[-0.01em] text-slate-50">
                    {ing.name}
                  </div>

                  <button
                    type="button"
                    onPointerDown={rememberTapStart}
                    onPointerUp={(e) => {
                      runIfRealTap(e, () => {
                        onEditIngredient?.(index, ing);
                      });
                    }}
                    onPointerCancel={clearTapStart}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className={
                      "shrink-0 rounded-[13px] border px-2.5 py-1.5 text-[12px] font-bold tabular-nums shadow-none [text-shadow:none] " +
                      (onEditIngredient
                        ? "border-cyan-300/50 bg-slate-950/24 text-cyan-100 active:scale-[0.97]"
                        : "border-cyan-300/50 bg-slate-950/24 text-cyan-100")
                    }
                    title={tr("ration.ingredientWeightTitle")}
                  >
                    {Math.round(Number(ing.grams || 0))} {tr("units.gramsShort")}
                  </button>
                </div>

                <div className="mt-2 -mr-10 flex min-w-0 flex-wrap gap-1 overflow-visible">
                  <MyFoodMacroChip
                    label=""
                    value={`${Math.round(Number(ing.kcal || 0))} ${tr("units.kcal")}`}
                  />
                  <MyFoodMacroChip
                    label={tr("food.report.proteinShort")}
                    value={`${+Number(ing.proteins || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                  />
                  <MyFoodMacroChip
                    label={tr("food.report.fatShort")}
                    value={`${+Number(ing.fats || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                  />
                  <MyFoodMacroChip
                    label={tr("food.report.carbsShort")}
                    value={`${+Number(ing.carbs || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {actionsBlock}
    </div>
  );
}
const MY_FOODS_SORT_OPTIONS = [
  { value: "meal", label: "По приёмам пищи", icon: "🍽️" },
  { value: "frequent", label: "Чаще всего", icon: "🔥" },
  { value: "recent", label: "Недавно", icon: "🕒" },
];

const MY_FOODS_MACRO_ACCENT = {
  protein: "#55c8ff",
  fat: "#ff6b6b",
  carbs: "#f5c84c",
};

const myFoodsSortStorageKey = "fitmeup:myFoodsSort";

const hasMyFoodId = (id) =>
  id !== null && id !== undefined && String(id).trim() !== "";

const getMyFoodType = (food) =>
  String(food?.type || "").trim().toLowerCase();

const isMyFoodsDish = (food) => {
  const type = getMyFoodType(food);

  return (
    type === "dish" ||
    Boolean(food?.recipe) ||
    (Array.isArray(food?.ingredients) && food.ingredients.length > 0)
  );
};

const isMyFoodsSingleProduct = (food) => {
  const type = getMyFoodType(food) || "ingredient";

  return (
    !isMyFoodsDish(food) &&
    (type === "ingredient" || type === "product")
  );
};

const normalizeMyFoodForList = (food) => {
  if (!food) return food;

  const type = isMyFoodsDish(food)
    ? "dish"
    : getMyFoodType(food) || "ingredient";

  const per100 = food.per100 || {};
  const proteins = Number(per100.proteins || 0);
  const fats = Number(per100.fats || 0);
  const carbs = Number(per100.carbs || 0);
  const kcalFromMacros = Math.round(proteins * 4 + fats * 9 + carbs * 4);

  const rawKcal = Number(per100.kcal);
  const kcal = Number.isFinite(rawKcal) && rawKcal > 0 ? rawKcal : kcalFromMacros;

  return {
    ...food,
    type,
    defaultGrams: Number(food.defaultGrams ?? food.weight ?? 100),
    per100: {
      ...per100,
      proteins,
      fats,
      carbs,
      kcal,
    },
  };
};
const MY_FOOD_USAGE_HISTORY_MONTHS = 36;

const myFoodUsageStatsStorageKey = () => k("myFoodUsageStats:v2");
const myFoodUsageStatsBuiltKey = () => k("myFoodUsageStatsBuilt:v2");

const normalizeMyFoodUsageName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ");

const getMyFoodUsageKeys = (food) => {
  const keys = [];

  const add = (value) => {
    const key = String(value || "").trim();
    if (key && !keys.includes(key)) keys.push(key);
  };

  [
    food?.id,
    food?.userFoodId,
    food?.user_food_id,
    food?.sourceUserFoodId,
    food?.source_user_food_id,
    food?.sourceMyFoodId,
    food?.myFoodId,
  ].forEach((id) => {
    if (hasMyFoodId(id)) add(`id:${String(id).trim()}`);
  });

  const name = normalizeMyFoodUsageName(food?.name);

  if (name) {
    const p = Number(food?.per100?.proteins || 0);
    const f = Number(food?.per100?.fats || 0);
    const c = Number(food?.per100?.carbs || 0);
    const kcal = Number(
      food?.per100?.kcal ?? Math.round(p * 4 + f * 9 + c * 4)
    );

    add(
      `fp:${name}|p:${p.toFixed(1)}|f:${f.toFixed(1)}|c:${c.toFixed(
        1
      )}|k:${Math.round(kcal || 0)}`
    );
  }

  return keys;
};

const readMyFoodUsageStats = () => {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(myFoodUsageStatsStorageKey());
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const notifyMyFoodUsageStatsChanged = () => {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(new Event("fitmeup:myFoodUsageStatsChanged"));
  } catch {}
};

const writeMyFoodUsageStats = (stats) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      myFoodUsageStatsStorageKey(),
      JSON.stringify(stats || {})
    );

    notifyMyFoodUsageStatsChanged();
  } catch {}
};

function useMyFoodUsageStatsVersion(enabled = true) {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const onChange = () => setVersion((x) => x + 1);

    window.addEventListener("fitmeup:myFoodUsageStatsChanged", onChange);

    return () => {
      window.removeEventListener("fitmeup:myFoodUsageStatsChanged", onChange);
    };
  }, [enabled]);

  return version;
}

const parseMyFoodTime = (value) => {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getMyFoodUsageStat = (food, stats = null) => {
  const usageStats = stats || readMyFoodUsageStats();
  const keys = getMyFoodUsageKeys(food);

  let localCount = 0;
  let localLastUsedAt = 0;

  keys.forEach((key) => {
    const stat = usageStats[key] || {};

    localCount = Math.max(localCount, Number(stat.count || 0));
    localLastUsedAt = Math.max(
      localLastUsedAt,
      Number(stat.lastUsedAt || 0)
    );
  });

  return {
    count: localCount,
    lastUsedAt: localLastUsedAt,
  };
};

const bumpMyFoodUsageStat = (food) => {
  const stats = readMyFoodUsageStats();
  const keys = getMyFoodUsageKeys(food);
  const now = Date.now();

  const current = getMyFoodUsageStat(food, stats);
  const nextCount = Math.max(0, Number(current.count || 0)) + 1;

  const nextStat = {
    count: nextCount,
    lastUsedAt: now,
    name: String(food?.name || "").trim(),
  };

  keys.forEach((key) => {
    stats[key] = nextStat;
  });

  writeMyFoodUsageStats(stats);

  return {
    count: nextCount,
    lastUsedAt: now,
  };
};
const getMyFoodSortTitle = (sortMode) => {
  const option = MY_FOODS_SORT_OPTIONS.find((x) => x.value === sortMode);
  return option ? option.label : "По приёмам пищи";
};

const getMyFoodDominantMacroType = (food) => {
  const p = Number(food?.per100?.proteins || 0);
  const f = Number(food?.per100?.fats || 0);
  const c = Number(food?.per100?.carbs || 0);

  if (p >= f && p >= c) return "protein";
  if (f >= p && f >= c) return "fat";
  return "carbs";
};

const getMyFoodAccentColor = (food) => {
  return MY_FOODS_MACRO_ACCENT[getMyFoodDominantMacroType(food)];
};

const getMyFoodUsageCount = (food, stats = null) => {
  return getMyFoodUsageStat(food, stats).count;
};

const getMyFoodUsedAt = (food, stats = null) => {
  return getMyFoodUsageStat(food, stats).lastUsedAt;
};

const sortMyFoodsFlat = (items, sortMode) => {
  const arr = [...(items || [])];
  const usageStats = readMyFoodUsageStats();

  const sortLocale =
    i18n.resolvedLanguage === "ru"
      ? "ru"
      : "en";

  const getName = (food) =>
    String(food?.name || "").trim().toLowerCase();

  if (sortMode === "frequent") {
    return arr.sort((a, b) => {
      const aCount = getMyFoodUsageCount(a, usageStats);
      const bCount = getMyFoodUsageCount(b, usageStats);

      const aUsed = aCount > 0;
      const bUsed = bCount > 0;

      // Использованные продукты всегда выше неиспользованных.
      if (aUsed !== bUsed) return aUsed ? -1 : 1;

      // Чем чаще использовался — тем выше.
      if (bCount !== aCount) return bCount - aCount;

      // При одинаковой частоте выше тот, который использовался свежее.
      if (aUsed && bUsed) {
        const bUsedAt = getMyFoodUsedAt(b, usageStats);
        const aUsedAt = getMyFoodUsedAt(a, usageStats);

        if (bUsedAt !== aUsedAt) return bUsedAt - aUsedAt;
      }

      // Неиспользованные продукты внизу, спокойно по названию.
      return getName(a).localeCompare(getName(b), sortLocale);
    });
  }

  if (sortMode === "recent") {
    return arr.sort(
      (a, b) =>
        getMyFoodUsedAt(b, usageStats) - getMyFoodUsedAt(a, usageStats) ||
        getName(a).localeCompare(getName(b), sortLocale)
    );
  }

  return arr;
};
const formatMyFoodUsageDayKey = (date) =>
  date.toLocaleDateString("en-CA");

const hasFoodMonthData = (value) => {
  if (value == null) return false;

  if (typeof value === "number") return value > 0;

  if (typeof value === "object") {
    return Object.values(value).some((v) => Number(v || 0) > 0);
  }

  return true;
};

const makeFoodLikeFromFoodEntry = (row) => {
  const grams = Math.max(1, Number(row?.grams ?? row?.weight ?? 100) || 100);

  const proteins = Number(row?.proteins ?? 0);
  const fats = Number(row?.fats ?? 0);
  const carbs = Number(row?.carbs ?? 0);
  const kcal = Number(row?.kcal ?? Math.round(proteins * 4 + fats * 9 + carbs * 4));

  const k100 = 100 / grams;

  const userFoodId =
    row?.user_food_id ??
    row?.userFoodId ??
    null;

  const sourceUserFoodId =
    row?.source_user_food_id ??
    row?.sourceUserFoodId ??
    userFoodId ??
    null;

  return {
    id: userFoodId ?? sourceUserFoodId ?? null,
    userFoodId,
    sourceUserFoodId,
    name: row?.name || "",
    type: String(row?.entry_type || row?.entryType || "ingredient"),
    per100: {
      kcal: +(kcal * k100).toFixed(1),
      proteins: +(proteins * k100).toFixed(1),
      fats: +(fats * k100).toFixed(1),
      carbs: +(carbs * k100).toFixed(1),
    },
  };
};

const addFoodUsageToStats = (stats, foodLike, usedAt) => {
  const keys = getMyFoodUsageKeys(foodLike);
  if (!keys.length) return;

  const currentCount = Math.max(
    0,
    ...keys.map((key) => Number(stats[key]?.count || 0))
  );

  const currentLastUsedAt = Math.max(
    0,
    ...keys.map((key) => Number(stats[key]?.lastUsedAt || 0))
  );

  const nextStat = {
    count: currentCount + 1,
    lastUsedAt: Math.max(Number(usedAt || 0), currentLastUsedAt),
    name: String(foodLike?.name || "").trim(),
  };

  keys.forEach((key) => {
    stats[key] = nextStat;
  });
};

let myFoodUsageStatsRebuildPromise = null;

const rebuildMyFoodUsageStatsFromHistory = async ({
  monthsBack = MY_FOOD_USAGE_HISTORY_MONTHS,
} = {}) => {
  const stats = {};
  const usedDays = new Set();
  const today = new Date();

  for (let offset = monthsBack - 1; offset >= 0; offset -= 1) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - offset, 1);

    const fromDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const toDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

    const from = formatMyFoodUsageDayKey(fromDate);
    const to = formatMyFoodUsageDayKey(toDate);

    try {
      const params = new URLSearchParams({ from, to });
const res = await tgFetch(`/api/food-month?${params.toString()}`);
const json = await res.json();

if (!json.ok) {
  throw new Error(json.error || "Ошибка загрузки календаря еды");
}

const monthMap = json.data || {};

      Object.entries(monthMap || {}).forEach(([dayKey, value]) => {
        if (hasFoodMonthData(value)) {
          usedDays.add(dayKey);
        }
      });
    } catch (e) {
    debugWarn("[myFoodUsage] month scan failed:", from, to, e);
    }
  }

  for (const dayKey of Array.from(usedDays).sort()) {
    try {
      const params = new URLSearchParams({ day: dayKey });
const res = await tgFetch(`/api/food-entries?${params.toString()}`);
const json = await res.json();

if (!json.ok) {
  throw new Error(json.error || "Ошибка загрузки еды");
}

const entries = json.data || [];

      for (const row of entries || []) {
        const usedAt = parseMyFoodTime(
          row?.created_at ??
            row?.createdAt ??
            row?.updated_at ??
            row?.updatedAt ??
            dayKey
        );

        addFoodUsageToStats(
          stats,
          makeFoodLikeFromFoodEntry(row),
          usedAt || Date.parse(dayKey) || 0
        );
      }
    } catch (e) {
      debugWarn("[myFoodUsage] day scan failed:", dayKey, e);
    }
  }

  writeMyFoodUsageStats(stats);

  try {
    window.localStorage.setItem(myFoodUsageStatsBuiltKey(), String(Date.now()));
  } catch {}

  return stats;
};

const ensureMyFoodUsageStatsReady = () => {
  if (typeof window === "undefined") return Promise.resolve({});

  try {
    const builtAt = Number(window.localStorage.getItem(myFoodUsageStatsBuiltKey()) || 0);

    if (builtAt > 0) {
      return Promise.resolve(readMyFoodUsageStats());
    }
  } catch {}

  if (!myFoodUsageStatsRebuildPromise) {
    myFoodUsageStatsRebuildPromise = rebuildMyFoodUsageStatsFromHistory().finally(() => {
      myFoodUsageStatsRebuildPromise = null;
    });
  }

  return myFoodUsageStatsRebuildPromise;
};
const normalizeMyFoodMealTitle = (value) => {
  const mealOrder = ["Завтрак", "Обед", "Перекус", "Ужин"];
  const s = String(value || "").trim();

  if (mealOrder.includes(s)) return s;

  const low = s.toLowerCase();
  if (low.includes("завтрак") || low === "breakfast") return "Завтрак";
  if (low.includes("обед") || low === "lunch") return "Обед";
  if (low.includes("перекус") || low === "snack") return "Перекус";
  if (low.includes("ужин") || low === "dinner") return "Ужин";

  return "Другое";
};

const buildMyFoodsSections = (items, sortMode, flatTitle = "Продукты") => {
  const list = [...(items || [])];

  if (sortMode !== "meal") {
    return [
      {
        title: flatTitle,
        items: sortMyFoodsFlat(list, sortMode),
      },
    ];
  }

  const mealOrder = ["Завтрак", "Обед", "Перекус", "Ужин"];
  const byMeal = new Map();

  for (const meal of mealOrder) byMeal.set(meal, []);
  byMeal.set("Другое", []);

  for (const item of list) {
    const key = normalizeMyFoodMealTitle(item?.lastMeal);
    if (!byMeal.has(key)) byMeal.set(key, []);
    byMeal.get(key).push(item);
  }

  for (const arr of byMeal.values()) {
    arr.sort((a, b) => getMyFoodUsedAt(b) - getMyFoodUsedAt(a));
  }

  return [...mealOrder, "Другое"]
    .map((title) => ({ title, items: byMeal.get(title) || [] }))
    .filter((section) => section.items.length);
};

const formatDishSavedNoticeText = (oldDish, newDish) => {
  const title = newDish?.name
    ? i18n.t("ration.myFoods.dishSavedNotice.namedTitle", {
        name: newDish.name,
      })
    : i18n.t("ration.myFoods.dishSavedNotice.genericTitle");

  const oldIngredients = buildDishIngredientsList(oldDish, []);
  const newIngredients = buildDishIngredientsList(newDish, []);

  const makeKey = (ing) =>
    String(ing?.foodId ?? ing?.id ?? ing?.name ?? "")
      .trim()
      .toLowerCase();

  const oldKeys = new Set(oldIngredients.map(makeKey).filter(Boolean));

  const addedNames = newIngredients
    .filter((ing) => {
      const key = makeKey(ing);
      return key && !oldKeys.has(key);
    })
    .map((ing) =>
      String(
        ing?.name ||
          i18n.t(
            "ration.myFoods.dishSavedNotice.ingredientFallback"
          )
      ).trim()
    )
    .filter(Boolean);

  if (!addedNames.length) {
    return `${title}

${i18n.t("ration.myFoods.dishSavedNotice.updated")}`;
  }

  const visibleNames = addedNames.slice(0, 5).join(", ");

  const tail =
    addedNames.length > 5
      ? i18n.t("ration.myFoods.dishSavedNotice.more", {
          count: addedNames.length - 5,
        })
      : "";

  return `${title}

${i18n.t("ration.myFoods.dishSavedNotice.added", {
  names: visibleNames,
  tail,
})}`;
};
const TinyDotsIcon = () => (
  <span
    aria-hidden="true"
    className="flex items-center justify-center gap-[2px]"
  >
    <span className="h-[3px] w-[3px] rounded-full bg-current" />
    <span className="h-[3px] w-[3px] rounded-full bg-current" />
    <span className="h-[3px] w-[3px] rounded-full bg-current" />
  </span>
);

const calcKcalPer100 = (proteins, fats, carbs) =>
  Math.round(
    Number(proteins || 0) * 4 +
      Number(fats || 0) * 9 +
      Number(carbs || 0) * 4
  );
const formatRuIngredientCount = (count) => {
  const n = Math.max(0, Number(count || 0));
  const lastTwo = n % 100;
  const lastOne = n % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return `${n} ингредиентов`;
  if (lastOne === 1) return `${n} ингредиент`;
  if (lastOne >= 2 && lastOne <= 4) return `${n} ингредиента`;
  return `${n} ингредиентов`;
};
function EditMyFoodModal({ open, food, onClose, onSave }) {
  const { t: tr } = useTranslation();

  const [name, setName] = useState("");
  const [proteins, setProteins] = useState("");
  const [fats, setFats] = useState("");
  const [carbs, setCarbs] = useState("");
  const nameInputRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !food) return undefined;

    return focusModalInputAndOpenKeyboard(nameInputRef);
  }, [open, food?.id]);

  useEffect(() => {
    if (!open || !food) return undefined;

    setName(food.name || "");
    setProteins(String(food.per100?.proteins ?? ""));
    setFats(String(food.per100?.fats ?? ""));
    setCarbs(String(food.per100?.carbs ?? ""));

    return focusModalInputAndOpenKeyboard(nameInputRef);
  }, [open, food]);

  if (!open || !food) return null;

  const parseNum = (value, fallback = 0) => {
    const n = Number(String(value || "").replace(",", "."));
    return Number.isFinite(n) ? n : fallback;
  };

  const p = Math.max(0, parseNum(proteins, food.per100?.proteins ?? 0));
  const f = Math.max(0, parseNum(fats, food.per100?.fats ?? 0));
  const c = Math.max(0, parseNum(carbs, food.per100?.carbs ?? 0));
  const autoKcal = calcKcalPer100(p, f, c);

  const save = () => {
    const nextName = name.trim();

    if (!nextName) {
      safeAlert("Введите название.", "Редактирование");
      return;
    }

    onSave?.({
      ...food,
      name: nextName,
      per100: {
        ...(food.per100 || {}),
        proteins: +p.toFixed(1),
        fats: +f.toFixed(1),
        carbs: +c.toFixed(1),
        kcal: autoKcal,
      },
      updatedAt: Date.now(),
    });

    onClose?.();
  };

  const nameInputClass =
    "h-11 w-full rounded-2xl border border-white/10 bg-slate-950/40 px-3 text-[16px] leading-none text-white caret-cyan-100 outline-none placeholder:text-white/35 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/20";

  const macroInputClass =
    "h-11 w-full rounded-2xl border border-white/10 bg-slate-950/40 px-3 py-0 text-[16px] leading-none text-white caret-cyan-100 outline-none placeholder:text-white/35 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/20";

  const inputStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    fontSize: "16px",
    lineHeight: "16px",
    minHeight: "44px",
  };

  return (
    <Portal>
      
<div
className="fixed inset-0 z-[50060] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/45 px-4 pb-[calc(env(safe-area-inset-bottom)+18px)] pt-4 backdrop-blur-sm"
        style={{
          paddingTop: hasTgAuth()
            ? "16px"
            : "calc(16px + env(safe-area-inset-top, 0px))",
        }}
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          stopAndProtect(e, 1200);
          onClose?.();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
<div
  data-onboarding="dish-edit-modal"

  className="relative w-full max-w-[420px] rounded-[28px] border border-cyan-100/20 bg-[#1f2a3b] px-4 pb-4 pt-3 shadow-[0_24px_80px_rgba(0,0,0,0.72)]"

  onPointerDown={(e) =>
    e.stopPropagation()
  }

  onClick={(e) =>
    e.stopPropagation()
  }
>
<AppCloseButton
  data-onboarding="food-edit-close"
  className="absolute right-3 top-1"
  onPointerDown={(e) => {
    stopAndProtect(e, 1200);
    onClose?.();
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
/>

<div className="mt-3 grid gap-2.5">
  <label className="grid gap-1.5 text-[14px] font-semibold text-white/85">
    {tr("ration.myFoods.productEdit.nameLabel")}
              <input
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameInputClass}
                style={inputStyle}
                placeholder={tr("ration.myFoods.productEdit.namePlaceholder")}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </label>

            <div className="rounded-[22px] border border-white/10 bg-white/[0.035] px-3 py-2.5">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="text-[13px] font-semibold text-white">
                  {tr("ration.myFoods.productEdit.macrosPer100")}
                </div>

                <div className="shrink-0 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-2.5 py-1 text-[13px] font-bold text-cyan-50">
                  {autoKcal} {tr("units.kcal")}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <label className="grid gap-1.5 text-[12px] font-medium text-white/60">
                  {tr("ration.protein")}
                  <input
                    value={proteins}
                    onChange={(e) => setProteins(e.target.value)}
                    inputMode="decimal"
                    type="text"
                    className={macroInputClass}
                    style={inputStyle}
                    placeholder="0"
                    autoComplete="off"
                  />
                </label>

                <label className="grid gap-1.5 text-[12px] font-medium text-white/60">
                  {tr("ration.fat")}
                  <input
                    value={fats}
                    onChange={(e) => setFats(e.target.value)}
                    inputMode="decimal"
                    type="text"
                    className={macroInputClass}
                    style={inputStyle}
                    placeholder="0"
                    autoComplete="off"
                  />
                </label>

                <label className="grid gap-1.5 text-[12px] font-medium text-white/60">
                  {tr("ration.carbs")}
                  <input
                    value={carbs}
                    onChange={(e) => setCarbs(e.target.value)}
                    inputMode="decimal"
                    type="text"
                    className={macroInputClass}
                    style={inputStyle}
                    placeholder="0"
                    autoComplete="off"
                  />
                </label>
              </div>
            </div>

            <div className="rounded-[18px] border border-cyan-200/15 bg-cyan-300/[0.06] px-3 py-2.5 text-[12px] leading-snug text-cyan-50/85">
              {tr("ration.myFoods.productEdit.hint")}
            </div>

            <div className="mt-0.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onPointerDown={(e) => {
                  stopAndProtect(e, 1800);
                  softCloseModal(onClose);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="h-11 rounded-2xl border border-white/10 bg-white/[0.055] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
              >
                {tr("actions.cancel")}
              </button>
              <button
                type="button"
                onPointerDown={(e) => {
                  stopAndProtect(e, 1200);
                  save();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="h-11 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 text-[13px] font-bold text-slate-950 shadow-[0_14px_34px_rgba(45,212,191,0.25)] active:scale-[0.98]"
              >
                {tr("actions.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
function EditDishNameModal({ open, dish, onClose, onSave }) {
  const { t: tr } = useTranslation();

  const [name, setName] = useState("");
  const nameInputRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !dish) return undefined;

    return focusModalInputAndOpenKeyboard(nameInputRef);
  }, [open, dish?.id]);

  useEffect(() => {
    if (!open || !dish) return undefined;

    setName(dish.name || "");

    return focusModalInputAndOpenKeyboard(nameInputRef);
  }, [open, dish]);

  if (!open || !dish) return null;

  const save = () => {
    const nextName = name.trim();

    if (!nextName) {
      safeAlert("Введите название блюда.", "Редактирование");
      return;
    }

    onSave?.({
      ...dish,
      name: nextName,
      updatedAt: Date.now(),
    });

    onClose?.();
  };

  const inputStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    fontSize: "16px",
    lineHeight: "16px",
    minHeight: "44px",
  };

  return (
    <Portal>
      
<div
className="fixed inset-0 z-[50060] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/45 px-4 pb-[calc(env(safe-area-inset-bottom)+18px)] pt-4 backdrop-blur-sm"
        style={{
          paddingTop: hasTgAuth()
            ? "16px"
            : "calc(16px + env(safe-area-inset-top, 0px))",
        }}
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          stopAndProtect(e, 1200);
          onClose?.();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div
          className="relative w-full max-w-[420px] rounded-[28px] border border-cyan-100/20 bg-[#1f2a3b] px-4 pb-4 pt-3 shadow-[0_24px_80px_rgba(0,0,0,0.72)]"
                    onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
<AppCloseButton
  data-onboarding="dish-edit-close"
  className="absolute right-3 top-1"
  onPointerDown={(e) => {
    stopAndProtect(e, 1200);
    onClose?.();
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
/>
<div className="mt-1 grid gap-2.5">
  <label className="grid gap-1.5 text-[14px] font-semibold text-white/85">
    {tr("ration.myFoods.dishEdit.nameLabel")}
<input
  ref={nameInputRef}
  data-onboarding="dish-edit-name"
  value={name}
  onChange={(e) => setName(e.target.value)}
                className="h-11 w-full rounded-2xl border border-white/10 bg-slate-950/40 px-3 text-[16px] leading-none text-white caret-cyan-100 outline-none placeholder:text-white/35 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/20"
                style={inputStyle}
                placeholder={tr("ration.myFoods.dishEdit.namePlaceholder")}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </label>

            <div className="rounded-[18px] border border-cyan-200/15 bg-cyan-300/[0.06] px-3 py-2.5 text-[12px] leading-snug text-cyan-50/85">
              {tr("ration.myFoods.dishEdit.hint")}
            </div>

            <div className="mt-0.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onPointerDown={rememberTapStart}
                onPointerUp={(e) => {
                  runIfRealTap(e, () => {
                    onClose?.();
                  }, 1800);
                }}
                onPointerCancel={clearTapStart}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="h-11 rounded-2xl border border-white/10 bg-white/[0.055] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
              >
                {tr("actions.cancel")}
              </button>
<button
  type="button"
  data-onboarding="dish-edit-save"
  onPointerDown={(e) => {
    stopAndProtect(e, 1200);
    save();
  }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="h-11 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 text-[13px] font-bold text-slate-950 shadow-[0_14px_34px_rgba(45,212,191,0.25)] active:scale-[0.98]"
              >
                {tr("actions.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function EditGramsModal({ open, food, onClose, onSave }) {
  const { t: tr } = useTranslation();

  const [grams, setGrams] = useState("");
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !food) return undefined;

    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, food?.id]);

  useEffect(() => {
    if (!open || !food) return undefined;

    setGrams(String(food.defaultGrams ?? 100));

    return focusModalInputAndOpenKeyboard(inputRef);
  }, [open, food]);

  if (!open || !food) return null;

  const parseNum = (value, fallback = 100) => {
    const n = Number(String(value || "").replace(",", "."));
    return Number.isFinite(n) ? n : fallback;
  };

const save = () => {
  const nextGrams = Math.max(
    1,
    +parseNum(grams, food.defaultGrams ?? 100).toFixed(1)
  );

    onSave?.({
      ...food,
      defaultGrams: nextGrams,
      updatedAt: Date.now(),
    });

    onClose?.();
  };

  return (
    <Portal>

<div
  className="fixed inset-0 z-[50050] flex items-start justify-center bg-black/45 px-4 pb-4 pt-6 backdrop-blur-sm"
        style={{
          paddingTop: hasTgAuth()
            ? "24px"
            : "calc(24px + env(safe-area-inset-top, 0px))",
        }}
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) return;
          stopAndProtect(e, 1200);
          onClose?.();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div
          className="relative w-full max-w-[330px] rounded-[26px] border border-cyan-100/20 bg-[#1f2a3b] px-4 pb-4 pt-3 shadow-[0_24px_80px_rgba(0,0,0,0.72)]"
                    onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onPointerDown={(e) => {
              stopAndProtect(e, 1200);
              onClose?.();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.06] text-[14px] text-white/75 active:scale-[0.96]"
            aria-label={tr("actions.close")}
          >
            ✕
          </button>

          <div className="pr-10">
            <div className="break-words text-[16px] font-bold leading-snug text-white">
              {food.name}
            </div>
          </div>

          <label className="mt-3 grid gap-1.5 text-[12px] font-medium text-white/60">
            {tr("ration.myFoods.gramsEdit.label")}
            <input
              ref={inputRef}
              autoFocus
              value={grams}
              onChange={(e) => setGrams(e.target.value)}
              inputMode="decimal"
              enterKeyHint="done"
              className="h-12 w-full rounded-2xl border border-cyan-100/15 bg-slate-950/40 px-4 text-center text-[20px] font-bold text-white outline-none placeholder:text-white/35 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/20"
              placeholder="100"
            />
          </label>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onPointerDown={(e) => {
                stopAndProtect(e);
                onClose?.();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="h-11 rounded-2xl border border-white/10 bg-white/[0.055] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
            >
              {tr("actions.cancel")}
            </button>

            <button
              type="button"
              onPointerDown={(e) => {
                stopAndProtect(e);
                save();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="h-11 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 text-[13px] font-bold text-slate-950 shadow-[0_14px_34px_rgba(45,212,191,0.25)] active:scale-[0.98]"
            >
              {tr("feedback.done")}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
function FoodActionMenuSheet({ food, onClose, onEdit, onDelete }) {
  const { t: tr } = useTranslation();

  if (!food) return null;

  const isDish = food.type === "dish";

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[50040]"
        onClick={onClose}
      >
        <BrandOverlay onClick={onClose} />

        <div className="absolute inset-x-0 bottom-0 p-3 pb-4">
          <div
            className="mx-auto w-full max-w-[420px] rounded-[28px] border border-white/15 bg-[#202b3d]/98 p-3 shadow-[0_-18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-start justify-between gap-3 px-1">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-white/40">
                  {isDish
                    ? tr("ration.myFoods.actionMenu.dish")
                    : tr("ration.myFoods.actionMenu.product")}
                </div>
                <div className="mt-0.5 break-words text-[15px] font-bold leading-snug text-white">
                  {food.name}
                </div>
              </div>

<AppCloseButton onClick={onClose} />
            </div>

            <div className="grid gap-1.5">
              <button
                type="button"
                onClick={() => onEdit?.(food)}
                className="flex w-full items-center gap-3 rounded-[18px] border border-cyan-200/20 bg-cyan-300/10 px-3 py-3 text-left active:scale-[0.99]"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-cyan-200/25 bg-cyan-300/12 text-[15px] text-cyan-50">
                  ✎
                </span>

                <span className="min-w-0">
                  <span className="block text-[13px] font-bold text-white">
                    {tr("ration.myFoods.actionMenu.edit")}
                  </span>
                  <span className="block text-[11px] leading-snug text-white/45">
                    {tr("ration.myFoods.actionMenu.editHint")}
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => onDelete?.(food)}
                className="flex w-full items-center gap-3 rounded-[18px] border border-rose-200/15 bg-rose-400/10 px-3 py-3 text-left active:scale-[0.99]"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-rose-200/20 bg-rose-400/10 text-[15px] text-rose-100">
                  ✕
                </span>

                <span className="min-w-0">
                  <span className="block text-[13px] font-bold text-rose-100">
                    {tr("ration.myFoods.actionMenu.delete")}
                  </span>
                  <span className="block text-[11px] leading-snug text-white/45">
                    {isDish
                      ? tr("ration.myFoods.actionMenu.removeDish")
                      : tr("ration.myFoods.actionMenu.removeProduct")}
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="mt-1 h-11 rounded-2xl border border-white/10 bg-white/[0.055] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
              >
                {tr("actions.cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
function MyFoodsSheet({
  open,
  onClose,
  mealTitle,
  foods,
  onSelect,
  onConfirm,
  onCreateDish,
  onCreateProduct,
  onUpdateFood,
  onDelete,
  initialTab = "dishes",
}) {
  const { t: tr } = useTranslation();

  const [q, setQ] = useState("");
  const [tab, setTab] = useState(initialTab);
  const [builderOpen, setBuilderOpen] = useState(false);
    const [builderSeedDish, setBuilderSeedDish] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedFoodsByKey, setSelectedFoodsByKey] = useState(() => ({}));

const getSelectableFoodKey = (food) => {
  const normalized = normalizeMyFoodForList(food);
  const type = isMyFoodsDish(normalized) ? "dish" : "ingredient";
  const id = normalized?.id ?? normalized?.name ?? "";

  return `${type}:${String(id)}`;
};

const getSelectedFoodsForConfirm = () =>
  Object.values(selectedFoodsByKey)
    .map((food) => {
      const id = food?.id;

      return normalizeMyFoodForList(
        id != null
          ? editedDishes[String(id)] || food
          : food
      );
    })
    .filter(Boolean);

const confirmSelectedFoods = () => {
  const picked = getSelectedFoodsForConfirm();

  if (!picked.length) return;

  if (typeof onConfirm === "function") {
    onConfirm(picked);
    return;
  }

  if (picked[0]) onSelect?.(picked[0]);
};

const getMyFoodsSectionTitle = (title) => {
  if (title === "Блюда") {
    return tr("ration.myFoods.dishes");
  }

  if (title === "Продукты") {
    return tr("ration.myFoods.products");
  }

  if (title === "Завтрак") {
    return tr("ration.breakfast");
  }

  if (title === "Обед") {
    return tr("ration.lunch");
  }

  if (title === "Ужин") {
    return tr("ration.dinner");
  }

  if (title === "Перекус") {
    return tr("ration.snack");
  }

  if (title === "Другое") {
    return tr("ration.myFoods.other");
  }

  return title;
};

const clearSelectedFoods = () => {
  setSelectedIds(new Set());
  setSelectedFoodsByKey({});
};
  const [openedDishIds, setOpenedDishIds] = useState(() => new Set());

const [
  onboardingCreatedDishId,
  setOnboardingCreatedDishId,
] = useState(null);
  const [sortMode, setSortModeState] = useState(() => {
  try {
    return window.localStorage.getItem(myFoodsSortStorageKey) || "meal";
  } catch {
    return "meal";
  }
});

const [sortOpen, setSortOpen] = useState(false);
const usageStatsVersion = useMyFoodUsageStatsVersion(open);
const [openedMenuId, setOpenedMenuId] = useState(null);
const [actionFood, setActionFood] = useState(null);
const isMyFoodsInputFocused = useInputFocusFlag();
const getMyFoodsSortLabel = (value) => {
  if (value === "meal") {
    return tr("ration.myFoods.sortMeal");
  }

  if (value === "frequent") {
    return tr("ration.myFoods.sortFrequent");
  }

  if (value === "recent") {
    return tr("ration.myFoods.sortRecent");
  }

  return tr("ration.myFoods.sortMeal");
};

const setSortMode = (nextMode) => {
  setSortModeState(nextMode);

  try {
    window.localStorage.setItem(myFoodsSortStorageKey, nextMode);
  } catch {}

  if (nextMode === "frequent") {
    ensureMyFoodUsageStatsReady().catch((e) => {
      debugWarn("[myFoodUsage] rebuild failed:", e);
    });
  }
};
const [editedDishes, setEditedDishes] = useState(() => ({}));
const [editingGramsFood, setEditingGramsFood] = useState(null);
const [ingredientWeightModal, setIngredientWeightModal] = useState(null);
const [editingFood, setEditingFood] = useState(null);
const [editingDishName, setEditingDishName] = useState(null);
const [editingIngredientFood, setEditingIngredientFood] = useState(null);
const [deleteConfirmFood, setDeleteConfirmFood] = useState(null);
const [isDeletingMyFood, setIsDeletingMyFood] = useState(false);
const [dishSavedNotice, setDishSavedNotice] = useState(null);
const [fullNamePopup, setFullNamePopup] = useState(null);
const hasMyFoodsTopOverlay = Boolean(
  builderOpen ||
  actionFood ||
  editingGramsFood ||
  ingredientWeightModal ||
  editingFood ||
  editingDishName ||
  editingIngredientFood ||
  deleteConfirmFood
);

useFitmeupBottomNavHidden(open && hasMyFoodsTopOverlay);
const normalizeDishNameForCopy = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const stripDishCopyNumber = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+\(\d+\)$/u, "")
    .trim();

const makeUniqueDishCopyName = (baseName) => {
  const cleanBase = String(baseName || "Новое блюдо").trim() || "Новое блюдо";
  const baseWithoutCopyNumber = stripDishCopyNumber(cleanBase) || cleanBase;

  const existingNames = new Set(
    (foods || [])
      .map(normalizeMyFoodForList)
      .filter((food) => isMyFoodsDish(food))
      .map((food) => normalizeDishNameForCopy(food.name))
      .filter(Boolean)
  );

  let index = 1;
  let candidate = `${baseWithoutCopyNumber} (${index})`;

  while (existingNames.has(normalizeDishNameForCopy(candidate))) {
    index += 1;
    candidate = `${baseWithoutCopyNumber} (${index})`;
  }

  return candidate;
};
const togglePick = (it) => {
  const food = normalizeMyFoodForList(editedDishes[String(it.id)] || it);
  const key = getSelectableFoodKey(food);

  setSelectedIds((prev) => {
    const next = new Set(prev);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    return next;
  });

  setSelectedFoodsByKey((prev) => {
    const next = { ...(prev || {}) };

    if (next[key]) {
      delete next[key];
    } else {
      next[key] = food;
    }

    return next;
  });
};

  const toggleDishDetails = (dishId) => {
    setOpenedDishIds((prev) => {
      const next = new Set(prev);
      if (next.has(dishId)) next.delete(dishId);
      else next.add(dishId);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  const openDeleteConfirm = async (it, displayFood, isDish) => {
    if (isDeletingMyFood) return;

    protectNextTap(1200);

    setOpenedMenuId(null);
    setSortOpen(false);
    setDeleteConfirmFood(null);

    const foodToDelete = {
      ...displayFood,
      id: it.id ?? displayFood.id,
      __deleteId: String(it.id ?? displayFood.id ?? ""),
      __deleteName: displayFood.name || "",
      __deleteType: isDish ? "dish" : "ingredient",
    };

    const ok = await safeConfirm(
      isDish
        ? tr("ration.myFoods.deleteConfirm.dish", {
            name:
              foodToDelete.__deleteName ||
              foodToDelete.name,
          })
        : tr("ration.myFoods.deleteConfirm.product", {
            name:
              foodToDelete.__deleteName ||
              foodToDelete.name,
          }),
      tr("ration.myFoods.deleteConfirm.title")
    );

    protectNextTap(1200);

    if (!ok) return;

    const idA = foodToDelete.id;
    const idB = foodToDelete.__deleteId;

    setSelectedIds((prev) => {
      const next = new Set(prev);

      [idA, idB].forEach((id) => {
        if (id == null) return;
        next.delete(id);
        next.delete(String(id));
        next.delete(Number(id));
      });

      return next;
    });

    setOpenedDishIds((prev) => {
      const next = new Set(prev);

      [idA, idB].forEach((id) => {
        if (id == null) return;
        next.delete(id);
        next.delete(String(id));
        next.delete(Number(id));
      });

      return next;
    });

    setEditedDishes((prev) => {
      const next = { ...prev };

      [idA, idB].forEach((id) => {
        if (id == null) return;
        delete next[String(id)];
      });

      return next;
    });

    setIsDeletingMyFood(true);

    try {
      await onDelete?.(foodToDelete);
    } finally {
      setIsDeletingMyFood(false);
      protectNextTap(1200);
    }
  };

  const confirmDeleteMyFoodNow = async () => {
    const food = deleteConfirmFood;

    if (!food || isDeletingMyFood) return;

    setIsDeletingMyFood(true);

    const idA = food.id;
    const idB = food.__deleteId;

    setDeleteConfirmFood(null);

    setSelectedIds((prev) => {
      const next = new Set(prev);

      [idA, idB].forEach((id) => {
        if (id == null) return;
        next.delete(id);
        next.delete(String(id));
        next.delete(Number(id));
      });

      return next;
    });

    setOpenedDishIds((prev) => {
      const next = new Set(prev);

      [idA, idB].forEach((id) => {
        if (id == null) return;
        next.delete(id);
        next.delete(String(id));
        next.delete(Number(id));
      });

      return next;
    });

    setEditedDishes((prev) => {
      const next = { ...prev };

      [idA, idB].forEach((id) => {
        if (id == null) return;
        delete next[String(id)];
      });

      return next;
    });

    try {
      await onDelete?.(food);
    } finally {
      setIsDeletingMyFood(false);
    }
  };

  const mealOrder = ["Завтрак", "Обед", "Перекус", "Ужин"];

  const macroForPortion = (it) => {
    const grams = Math.max(1, Number(it.defaultGrams ?? 100));
    const k = grams / 100;

    const p = +((it.per100?.proteins || 0) * k).toFixed(1);
    const f = +((it.per100?.fats || 0) * k).toFixed(1);
    const c = +((it.per100?.carbs || 0) * k).toFixed(1);

    const kcalPer100 =
      it.per100?.kcal ||
      (it.per100?.proteins || 0) * 4 +
        (it.per100?.fats || 0) * 9 +
        (it.per100?.carbs || 0) * 4;

    const kcal = Math.round(kcalPer100 * k);
    return { grams, p, f, c, kcal };
  };

  const sections = useMemo(() => {
    const query = q.trim().toLowerCase();

const filtered = (foods || [])
  .map(normalizeMyFoodForList)
  .filter((it) => {
    if (tab === "dishes") return isMyFoodsDish(it);
    return isMyFoodsSingleProduct(it);
  })
  .filter((it) =>
    !query ? true : (it.name || "").toLowerCase().includes(query)
  )
  .slice();

if (sortMode !== "meal") {
  return [
    {
      title: tab === "dishes" ? "Блюда" : "Продукты",
      items: sortMyFoodsFlat(filtered, sortMode),
    },
  ];
}

const byMeal = new Map();
    for (const m of mealOrder) byMeal.set(m, []);
    byMeal.set("Другое", []);

   const normalizeLastMeal = (v) => {
  const s = String(v || "").trim();

  // если уже норм русские заголовки
  if (mealOrder.includes(s)) return s;

  // если вдруг сохранилось англ (старые данные)
  const map = {
    breakfast: "Завтрак",
    lunch: "Обед",
    dinner: "Ужин",
    snack: "Перекус",
  };
  if (map[s]) return map[s];

  // если там было "Ужин · ..." или "УЖИН" — тоже нормализуем
  const low = s.toLowerCase();
  if (low.includes("завтрак")) return "Завтрак";
  if (low.includes("обед")) return "Обед";
  if (low.includes("ужин")) return "Ужин";
  if (low.includes("перекус")) return "Перекус";

  return "Другое";
};

for (const it of filtered) {
  const key = normalizeLastMeal(it.lastMeal);
  byMeal.get(key).push(it);
}


    for (const arr of byMeal.values()) arr.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    const ordered = [];
    const pushIfNonEmpty = (title, arr) => {
      if (arr && arr.length) ordered.push({ title, items: arr });
    };

    if (mealTitle) pushIfNonEmpty(mealTitle, byMeal.get(mealTitle));
    for (const m of mealOrder) if (m !== mealTitle) pushIfNonEmpty(m, byMeal.get(m));
    pushIfNonEmpty("Другое", byMeal.get("Другое"));

    return ordered;
 }, [foods, q, tab, mealTitle, sortMode, usageStatsVersion]);

  if (!open) return null;

  return (
    <div
      className="w-full"
      onClick={() => {
        setOpenedMenuId(null);
        setSortOpen(false);
      }}
    >
      {deleteConfirmFood && (
        <div
          className="fixed inset-0 z-[50050] flex items-end justify-center p-3"
          onPointerDown={(e) => {
            if (e.target !== e.currentTarget) return;
            stopAndProtect(e, 1200);
            setDeleteConfirmFood(null);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />

          <div
            className="relative w-full max-w-[420px] rounded-[28px] border border-white/15 bg-[#202b3d] p-4 shadow-[0_-18px_60px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[17px] font-bold text-white">
              {tr("ration.myFoods.deleteConfirm.title")}
            </div>

            <div className="mt-2 text-[13px] leading-snug text-white/70">
              {deleteConfirmFood.__deleteType === "dish"
                ? tr("ration.myFoods.deleteConfirm.dish", {
                    name:
                      deleteConfirmFood.__deleteName ||
                      deleteConfirmFood.name,
                  })
                : tr("ration.myFoods.deleteConfirm.product", {
                    name:
                      deleteConfirmFood.__deleteName ||
                      deleteConfirmFood.name,
                  })}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onPointerDown={(e) => {
                  stopAndProtect(e, 1200);
                  setDeleteConfirmFood(null);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="h-11 rounded-2xl border border-white/10 bg-white/[0.06] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
              >
                {tr("actions.cancel")}
              </button>

              <button
                type="button"
                disabled={isDeletingMyFood}
                onPointerDown={async (e) => {
                  stopAndProtect(e, 1200);
                  await confirmDeleteMyFoodNow();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={
                  "h-11 rounded-2xl border border-rose-200/20 bg-rose-500/20 text-[13px] font-bold text-rose-100 active:scale-[0.98] " +
                  (isDeletingMyFood ? "opacity-60 pointer-events-none" : "")
                }
              >
                {isDeletingMyFood
                  ? tr("ration.myFoods.deleteConfirm.deleting")
                  : tr("ration.myFoods.actionMenu.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
{dishSavedNotice && (
  <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+16px)] z-[21000] px-4">
    <div
      className="pointer-events-auto relative mx-auto w-full max-w-[420px] overflow-hidden rounded-[24px] border border-cyan-100/22 bg-[#07111f]/96 p-3 text-white shadow-[0_18px_52px_rgba(0,0,0,0.58),0_0_26px_rgba(45,212,191,0.10)] backdrop-blur-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(20,184,166,0.14),transparent_42%),radial-gradient(circle_at_100%_100%,rgba(14,165,233,0.12),transparent_46%)]" />

      <div className="relative flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-emerald-100/45 bg-emerald-300/95 text-slate-950 shadow-[0_0_18px_rgba(52,211,153,0.35)]">
          <svg viewBox="0 0 20 20" fill="none" className="h-6 w-6" aria-hidden="true">
            <path
              d="M4.5 10.4L8.2 14L15.7 6"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="min-w-0 flex-1 pr-1">
          <div className="text-[16px] font-extrabold leading-snug text-white">
            {dishSavedNotice.title ||
              tr("ration.myFoods.dishSavedNotice.fallbackTitle")}
          </div>

          <div className="mt-1.5 whitespace-pre-line text-[12px] font-medium leading-snug text-slate-100/82">
            {dishSavedNotice.message}
          </div>
        </div>

<AppCloseButton
  className="h-9 w-9 text-[18px]"
  onClick={() => setDishSavedNotice(null)}
/>
      </div>
    </div>
  </div>
)}
<div
  className="relative overflow-hidden rounded-[28px] border border-cyan-300/20 bg-[#07111f]/96 px-3 py-3 text-left shadow-[0_30px_90px_rgba(0,0,0,0.58),0_0_34px_rgba(45,212,191,0.10),inset_0_1px_0_rgba(255,255,255,0.045)] backdrop-blur-xl"
  style={{
    minHeight: "calc(var(--app-height, 100vh) - 220px)",
  }}
>
<div className="pointer-events-none absolute inset-0 rounded-[28px] bg-[radial-gradient(circle_at_0%_0%,rgba(20,184,166,0.08),transparent_45%),radial-gradient(circle_at_100%_100%,rgba(14,165,233,0.045),transparent_50%)]" />
  <div className="relative">
<div
  data-onboarding="my-foods-header"
  className="mb-3 flex items-center justify-between gap-3"
>
  <div className="text-[15px] font-semibold text-slate-50">
    {tr("ration.myFoods.title")}
  </div>
<AppCloseButton onClick={onClose} />
</div>

<div
  data-onboarding="my-foods-tabs"
  className="mt-3 grid h-11 grid-cols-2 gap-1 rounded-[18px] border border-cyan-300/50 bg-slate-950/24 p-1 shadow-[0_0_12px_rgba(45,230,230,0.04)]">
<button
    type="button"
    data-onboarding="my-foods-products-tab"
onClick={() => {
  setTab("ingredients");
  setOpenedDishIds(new Set());
  setSortOpen(false);
  setOpenedMenuId(null);
}}
    className={
      "h-full rounded-[14px] px-4 text-[13px] font-semibold transition active:scale-[0.99] " +
      (tab === "ingredients"
? "border border-cyan-200/70 bg-cyan-400/[0.16] text-white shadow-[0_0_18px_rgba(45,230,230,0.18),inset_0_1px_0_rgba(255,255,255,0.12)]"
: "border border-transparent bg-transparent text-white/70")
    }
  >
    {tr("ration.myFoods.products")}
  </button>

<button
    type="button"
    data-onboarding="my-foods-dishes-tab"
onClick={() => {
  setTab("dishes");
  setOpenedDishIds(new Set());
  setSortOpen(false);
  setOpenedMenuId(null);
}}
    className={
      "h-full rounded-[14px] px-4 text-[13px] font-semibold transition active:scale-[0.99] " +
      (tab === "dishes"
? "border border-cyan-200/70 bg-cyan-400/[0.16] text-white shadow-[0_0_18px_rgba(45,230,230,0.18),inset_0_1px_0_rgba(255,255,255,0.12)]"
: "border border-transparent bg-transparent text-white/70")
    }
  >
    {tr("ration.myFoods.dishes")}
  </button>
</div>
        <div className="mt-3 grid grid-cols-[minmax(0,1fr)_146px] gap-2">
<input
            data-onboarding="my-foods-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={(e) => keepMyFoodsSearchVisible(e.currentTarget)}
            onClick={(e) => keepMyFoodsSearchVisible(e.currentTarget)}
            placeholder={
              tab === "dishes"
                ? tr("ration.myFoods.searchDish")
                : tr("ration.myFoods.searchProduct")
            }
            className="h-[44px] min-w-0 rounded-[16px] border border-white/15 bg-slate-950/35 px-3.5 text-[13px] font-medium text-white placeholder:text-white/42 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] focus:border-cyan-300/45 focus:bg-slate-950/45 focus:ring-2 focus:ring-cyan-300/25"
          />

<button
  type="button"
  data-onboarding="my-foods-add-button"
  onClick={() => {
    if (tab === "dishes") {
      setBuilderSeedDish(null);
      setBuilderOpen(true);
                return;
              }

              onCreateProduct?.();
            }}
            className="flex h-[44px] min-w-0 items-center justify-center whitespace-nowrap rounded-[16px] border border-cyan-300/45 bg-cyan-400/10 px-2.5 text-[12px] font-bold text-cyan-50 shadow-[0_0_14px_rgba(45,230,230,0.06)] active:scale-[0.99]"
          >
            {tab === "dishes"
              ? tr("ration.myFoods.addDish")
              : tr("ration.myFoods.addProduct")}
          </button>
        </div>

        <div
          className="relative mt-3"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenedMenuId(null);
              setSortOpen((v) => !v);
            }}
className={
  "flex h-[44px] w-full items-center justify-between rounded-[16px] border px-4 transition active:scale-[0.99] " +
  (sortOpen
    ? "border-cyan-300/60 bg-cyan-400/[0.10] shadow-[0_0_18px_rgba(45,230,230,0.14),inset_0_1px_0_rgba(255,255,255,0.06)]"
    : "border-cyan-300/45 bg-slate-950/30 shadow-[0_0_12px_rgba(45,230,230,0.05),inset_0_1px_0_rgba(255,255,255,0.04)]")
}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-cyan-300/12 text-[18px] text-cyan-100">
                ⇅
              </span>

              <span className="truncate text-[13px] font-medium text-white/70">
                {tr("ration.myFoods.sorting")}{" "}
                <span className="font-bold text-white">
                  {getMyFoodsSortLabel(sortMode)}
                </span>
              </span>
            </div>

<span className="ml-2 grid h-5 w-5 shrink-0 place-items-center text-cyan-100">
  <span
    className={
      "inline-block origin-center text-[16px] font-semibold leading-none transition-transform duration-200 " +
      (sortOpen ? "rotate-0" : "rotate-180")
    }
  >
    ⌃
  </span>
</span>
          </button>

          {sortOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
             className="mt-2 overflow-hidden rounded-[18px] border border-cyan-300/35 bg-[#1f2a3b]/98 p-1.5 shadow-[0_18px_44px_rgba(0,0,0,0.42),0_0_18px_rgba(45,230,230,0.10),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
            >
              {MY_FOODS_SORT_OPTIONS.map((option, index) => {
                const active = sortMode === option.value;
                const isLast = index === MY_FOODS_SORT_OPTIONS.length - 1;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortMode(option.value);
                      setSortOpen(false);
                    }}
                    style={{ color: "#ffffff" }}
                    className={
                      "flex h-[44px] w-full items-center justify-between rounded-[14px] px-3 text-left text-[14px] font-semibold transition active:scale-[0.99] " +
                      (!isLast ? "mb-1 " : "") +
                      (active
                        ? "border border-cyan-200/55 bg-cyan-300/[0.11] shadow-[0_0_18px_rgba(45,230,230,0.10),inset_0_1px_0_rgba(255,255,255,0.06)]"
                        : "border border-transparent bg-transparent hover:bg-white/[0.055]")
                    }
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span
                        className={
                          "grid h-7 w-7 shrink-0 place-items-center rounded-full leading-none " +
                          (active
                            ? "bg-cyan-300/16 text-white"
                            : "bg-white/8 text-white")
                        }
                      >
                        <span className="text-[18px] leading-none">
                          {option.icon}
                        </span>
                      </span>

                      <span
                        style={{ color: "#ffffff" }}
                        className="truncate"
                      >
                        {getMyFoodsSortLabel(option.value)}
                      </span>
                    </span>

                    <span
                      className={
                        "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition " +
                        (active
                          ? "border-cyan-100 bg-cyan-300/16 shadow-[0_0_10px_rgba(45,230,230,0.22)]"
                          : "border-white/45 bg-transparent")
                      }
                    >
                      {active ? (
                        <span className="h-[8px] w-[8px] rounded-full bg-cyan-100 shadow-[0_0_6px_rgba(165,243,252,0.55)]" />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>


<button
  type="button"
  disabled={!selectedCount}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedCount) return;

    confirmSelectedFoods();
  }}
  className={
    "mt-2 w-full h-10 rounded-2xl text-[13px] font-semibold transition " +
    (selectedCount
      ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110 active:scale-[0.99]"
      : "cursor-not-allowed border border-white/10 bg-white/[0.07] text-white/35 shadow-none")
  }
>
  {selectedCount > 0
    ? tr("ration.myFoods.nextCount", {
        count: selectedCount,
      })
    : tr("ration.myFoods.next")}
</button>

        <div className="mt-3 grid gap-2 pb-16">
          {sections.map((sec) => (
            <div key={sec.title}>
              <div className="mb-1 flex items-center justify-between px-1">
                <div className="text-[10px] uppercase tracking-wide text-slate-300/75">
                  {getMyFoodsSectionTitle(sec.title)}
                </div>

                <div className="text-[10px] text-slate-500">
                  {sec.items.length}
                </div>
              </div>

              <div className="grid gap-2">
{sec.items.map((it) => {
const displayFood = normalizeMyFoodForList(
  editedDishes[String(it.id)] || it
);

const m = macroForPortion(displayFood);
const active = selectedIds.has(getSelectableFoodKey(displayFood));
const isDish =
  isMyFoodsDish(displayFood);

const isOnboardingCreatedDish =
  isDish &&
  onboardingCreatedDishId != null &&
  String(it.id) ===
    String(onboardingCreatedDishId);

const detailsOpen =
  openedDishIds.has(it.id);

const ingredientCount = isDish
  ? buildDishIngredientsList(displayFood, foods).length
  : 0;

return (
  <div
    key={it.id}
    data-onboarding-created-dish={
      isOnboardingCreatedDish
        ? "1"
        : undefined
    }
    className="relative"
  >
                <div
          data-onboarding={
            isDish
              ? "my-foods-dish-card"
              : "my-foods-product-card"
          }
          onPointerDownCapture={(e) => {
            if (openedMenuId == null) return;

            const target = e.target;

            const insideMenu =
              target?.closest?.('[data-my-food-menu="1"]') ||
              target?.closest?.('[data-my-food-menu-button="1"]');

            if (insideMenu) return;

            // Если меню "..." открыто, первый тап по карточке только закрывает меню.
            // Не выбираем блюдо/продукт, не открываем граммовку и состав.
            stopAndProtect(e, 450);
            setOpenedMenuId(null);
          }}
          onClick={(e) => {
            if (isTapGuardActive()) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            togglePick(it);
          }}
          className={
"relative rounded-[18px] border px-4 py-3.5 pr-6 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] cursor-pointer active:scale-[0.99] "
 +      (active
              ? "border-emerald-300/60 bg-emerald-500/10"
              : "border-white/10 bg-slate-950/23")
          }
        >
<div className={DOT_MENU_WRAP_CLASS} data-my-food-menu-button="1">
  <button
    type="button"
    data-onboarding={
      isDish
        ? "my-foods-dish-menu"
        : "my-foods-product-menu"
    }
    onPointerDown={rememberTapStart}
    onPointerUp={(e) => {
      runMenuTap(e, () => {
        setSortOpen(false);
        setOpenedMenuId((current) =>
          current === it.id ? null : it.id
        );
      });
    }}
    onPointerCancel={clearTapStart}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className={DOT_MENU_BTN_CLASS}
    aria-label={tr("ration.menu")}
    title={tr("ration.menu")}
  >
    ⋯
  </button>
</div>

{openedMenuId === it.id && (
  <div
    data-my-food-menu="1"
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
className="absolute right-1.5 top-10 z-[300] w-[176px] overflow-hidden rounded-[18px] border border-slate-500/35 bg-[#151d2b] shadow-[0_18px_44px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.06)]"
  >
<button
  type="button"
  data-onboarding={
    isDish
      ? "my-foods-dish-edit"
      : "my-foods-product-edit"
  }
  onPointerDown={(e) => {
    stopAndProtect(e);
    setOpenedMenuId(null);

    if (isDish) {
          setEditingDishName({
            ...displayFood,
            type: "dish",
          });
          return;
        }

        setEditingFood({
          ...displayFood,
          __editTitle: displayFood.name || "Редактировать продукт",
        });
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-slate-700/55"
    >
      <span className="grid h-6 w-6 shrink-0 place-items-center text-[15px] text-cyan-100">
        ✎
      </span>

      <span className="min-w-0 text-[13px] font-semibold leading-none text-white">
        {tr("ration.edit")}
      </span>
    </button>

    <div className="mx-3 h-px bg-slate-600/70" />

    <button
      type="button"
      onPointerDown={(e) => {
        stopAndProtect(e, 1200);
        openDeleteConfirm(it, displayFood, isDish);
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-rose-950/45"
    >
      <span className="grid h-6 w-6 shrink-0 place-items-center text-[16px] leading-none text-rose-300">
        ⌫
      </span>

      <span className="min-w-0 text-[13px] font-semibold leading-none text-rose-300">
        {tr("ration.delete")}
      </span>
    </button>
  </div>
)}

                    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
            <div
              className="h-[58px] w-1.5 rounded-full"
              style={{ backgroundColor: getMyFoodAccentColor(displayFood) }}
            />

            <div className="min-w-0">
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
<FoodNameButton
  name={displayFood.name}
  kind={isDish ? "Блюдо" : "Продукт"}
  onOpen={setFullNamePopup}
/>

                <div className="flex shrink-0 items-start gap-1.5">
{isDish && (
<button
  type="button"
  data-onboarding="my-foods-dish-composition-button"
  onClick={(e) => {
    e.stopPropagation();

    const willOpen =
      !detailsOpen;

    toggleDishDetails(
      it.id
    );

    if (willOpen) {
      emitAppEvent(
        "dish-composition:opened",
        {
          dishId: it.id,
        }
      );
    }
  }}
  className={
    "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium active:scale-[0.97] " +
    (detailsOpen
      ? "border-cyan-200/35 bg-cyan-300/15 text-cyan-50 shadow-[0_0_14px_rgba(34,211,238,0.10)]"
      : "border-cyan-200/20 bg-cyan-400/10 text-cyan-100")
  }
>
  {detailsOpen
    ? `▴ ${tr("ration.composition")}`
    : `☰ ${tr("ration.composition")}`}
</button>
)}

                  {isDish ? (
                    <div
                      className="shrink-0 rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)]"
                    >
                      {m.grams} {tr("units.gramsShort")}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onPointerDown={rememberTapStart}
                      onPointerUp={(e) => {
                        runIfRealTap(e, () => {
                          setOpenedMenuId(null);
                          setSortOpen(false);
                          setEditingGramsFood(displayFood);
                        });
                      }}
                      onPointerCancel={clearTapStart}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className="shrink-0 rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)] active:scale-[0.97]"
                      title={tr("ration.productWeightTitle")}
                    >
                      {m.grams} {tr("units.gramsShort")}
                    </button>
                  )}
                </div>
              </div>

                            <div className="mt-2 -mr-8 flex min-w-0 flex-wrap gap-1 overflow-visible">
                <MyFoodMacroChip
                  label=""
                  value={`${Math.round(Number(m.kcal || 0))} ${tr("units.kcal")}`}
                />
                <MyFoodMacroChip
                  label={tr("food.report.proteinShort")}
                  value={`${+Number(m.p || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                />
                <MyFoodMacroChip
                  label={tr("food.report.fatShort")}
                  value={`${+Number(m.f || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                />
                <MyFoodMacroChip
                  label={tr("food.report.carbsShort")}
                  value={`${+Number(m.c || 0).toFixed(1)} ${tr("units.gramsShort")}`}
                />
              </div>
            </div>
          </div>
{isDish && detailsOpen && (
  <div
    data-onboarding="my-foods-dish-composition"
    className="mt-3 min-w-0 overflow-visible"
    onClick={(e) => e.stopPropagation()}
  >
    <div className="mb-2 flex items-center justify-start">
      <div className="shrink-0 rounded-[13px] border border-cyan-300/25 bg-cyan-400/10 px-3 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)]">
        {tr("ration.ingredientCount", {
          count: ingredientCount,
        })}
      </div>
    </div>

    <div className="pb-1">
      <DishIngredientsList
        dish={displayFood}
        foods={foods}
                hasDishDraftChanges={Boolean(editedDishes[String(it.id)])}
        onAddIngredient={(ingredientFood) => {
          const pickedIngredients = Array.isArray(ingredientFood)
            ? ingredientFood.filter(Boolean)
            : [ingredientFood].filter(Boolean);

          if (!pickedIngredients.length) return;

          const sourceDish =
            editedDishes[String(it.id)] ||
            (foods || []).find((x) => String(x.id) === String(it.id)) ||
            displayFood;

          const updatedDish = addDishIngredientsToDish(
            sourceDish,
            foods,
            pickedIngredients
          );

          if (!updatedDish) return;

          setEditedDishes((prev) => ({
            ...prev,
            [String(it.id)]: {
              ...updatedDish,
              id: it.id,
              updatedAt: Date.now(),
            },
          }));

          safeAlert(
            pickedIngredients.length > 1
              ? "Ингредиенты добавлены"
              : "Ингредиент добавлен",
            "Готово"
          );
        }}
onUpdateCurrentDish={async () => {
  const draftDish = editedDishes[String(it.id)];
  if (!draftDish) return;

  const sourceDish =
    (foods || []).find((x) => String(x.id) === String(it.id)) ||
    displayFood;

  const nextDish = {
    ...draftDish,
    id: it.id,
    updatedAt: Date.now(),
  };

  try {
    await Promise.resolve(onUpdateFood?.(nextDish));

    setEditedDishes((prev) => {
      const next = { ...prev };
      delete next[String(it.id)];
      return next;
    });

safeAlert("Блюдо сохранено", "Готово");
  } catch (err) {
    console.error("update current dish failed:", err);
    safeAlert("Не удалось сохранить текущее блюдо.", "Ошибка");
  }
}}
onSaveAsNewDish={() => {
  const draftDish = editedDishes[String(it.id)] || displayFood;
  const baseName = draftDish.name || displayFood.name || "Новое блюдо";

  setBuilderSeedDish({
    ...draftDish,
    id: `seed_${Date.now()}`,
    name: makeUniqueDishCopyName(baseName),
    __sourceDishId: it.id,
    __saveAsNew: true,
  });

  setQ("");
  setTab("dishes");
  setBuilderOpen(true);
}}
        onEditIngredient={(ingredientIndex, ing) => {
          setIngredientWeightModal({
            dishId: it.id,
            ingredientIndex,
            initial: String(Math.round(Number(ing.grams || 0))),
            name: ing.name || "Ингредиент",
          });
        }}
        onEditIngredientDetails={(ingredientIndex, ing) => {
          const sourceIngredient = (foods || []).find(
            (food) => String(food.id) === String(ing.id)
          );

          const grams = Math.max(1, Number(ing.grams || 100));
          const per100FromDish = {
            proteins: +((Number(ing.proteins || 0) / grams) * 100).toFixed(1),
            fats: +((Number(ing.fats || 0) / grams) * 100).toFixed(1),
            carbs: +((Number(ing.carbs || 0) / grams) * 100).toFixed(1),
            kcal: Math.round((Number(ing.kcal || 0) / grams) * 100),
          };

          setEditingIngredientFood({
            dishId: it.id,
            ingredientIndex,
            food: {
              ...(sourceIngredient || {}),
              id: ing.id,
              type: "ingredient",
              name: ing.name || "Ингредиент",
              defaultGrams: grams,
              per100: sourceIngredient?.per100 || per100FromDish,
              __editTitle: ing.name || "Редактировать ингредиент",
            },
          });
        }}
        onDeleteIngredient={async (ingredientIndex, ing) => {
const ok = await safeConfirm(
  tr("ration.myFoods.deleteConfirm.ingredientFromDish", {
    ingredient: ing.name,
    dish: displayFood.name,
  }),
  tr("ration.myFoods.deleteConfirm.title")
);

          if (!ok) return;

          const sourceDish =
            editedDishes[String(it.id)] ||
            (foods || []).find((x) => String(x.id) === String(it.id));

          const updatedDish = removeDishIngredientByIndex(
            sourceDish,
            foods,
            ingredientIndex
          );

          if (updatedDish) {
            const nextDish = {
              ...updatedDish,
              updatedAt: Date.now(),
            };

            setEditedDishes((prev) => ({
              ...prev,
              [String(it.id)]: nextDish,
            }));
          }
        }}
      />
    </div>
  </div>
)}
        </div>
      </div>
    );
  })}
</div>
              </div>
            ))}
             </div>
          </div>
        </div>
{selectedCount > 0 && (
  <div
    className="fixed inset-x-0 z-[18000] px-4 pointer-events-none"
    style={{
      bottom: isMyFoodsInputFocused
        ? "calc(env(safe-area-inset-bottom, 0px) + 12px)"
        : "calc(env(safe-area-inset-bottom, 0px) + 86px)",
    }}
  >
    <div className="mx-auto flex w-full max-w-[420px] justify-end gap-2">
      <button
        type="button"
        onClick={clearSelectedFoods}
        className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-full border border-cyan-100/25 bg-[#061723]/88 px-4 text-[13px] font-extrabold text-cyan-50 shadow-[0_14px_34px_rgba(0,0,0,0.32),0_0_14px_rgba(45,212,191,0.10)] backdrop-blur-xl active:scale-[0.97]"
      >
        {tr("ration.myFoods.cancelCount", {
          count: selectedCount,
        })}
      </button>

      <button
        type="button"
        onClick={confirmSelectedFoods}
        className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-full border border-cyan-100/35 bg-gradient-to-r from-emerald-400 to-cyan-300 px-5 text-[13px] font-extrabold text-slate-950 shadow-[0_14px_34px_rgba(16,185,129,0.35),0_0_18px_rgba(45,212,191,0.18)] active:scale-[0.97]"
      >
        {tr("ration.myFoods.nextCount", {
          count: selectedCount,
        })}
      </button>
    </div>
  </div>
)}
        {/* Создание блюда — оставляем модалкой (одной), но она уже НЕ “попап на попап” */}
<DishBuilderModal
  open={builderOpen}
  onClose={() => {
    setBuilderOpen(false);
    setBuilderSeedDish(null);
  }}
  foods={foods}
  mealTitle={mealTitle}
  initialDish={builderSeedDish}
  onUpdateFood={onUpdateFood}
onCreateDish={async (dish) => {
  const sourceDishId =
    builderSeedDish?.__sourceDishId;

  const dishForCreate = {
    ...dish,

    __saveAsNew: Boolean(
      dish.__saveAsNew ||
        builderSeedDish?.__saveAsNew ||
        sourceDishId != null
    ),

    __sourceDishId:
      sourceDishId ??
      dish.__sourceDishId,

    __sourceItemId:
      dish.__sourceItemId,

    __sourceMealId:
      dish.__sourceMealId,
  };

  const savedDish =
    await Promise.resolve(
      onCreateDish?.(
        dishForCreate
      )
    );

  const finalDish =
    savedDish ||
    dishForCreate;

  if (sourceDishId != null) {
    setEditedDishes((prev) => {
      const next = {
        ...prev,
      };

      delete next[
        String(sourceDishId)
      ];

      return next;
    });
  }

  const createdDishId =
    finalDish?.id;

  if (
    createdDishId != null &&
    String(createdDishId).trim() !== ""
  ) {
    // Запоминаем именно то блюдо,
    // которое пользователь создал.
    setOnboardingCreatedDishId(
      String(createdDishId)
    );

    setQ("");
    setTab("dishes");
    setSortOpen(false);
    setOpenedMenuId(null);

    // Состав НЕ открываем.
    // Пользователь нажмёт
    // кнопку «Состав» сам.
    setOpenedDishIds(
      new Set()
    );
  }

  setBuilderOpen(false);
  setBuilderSeedDish(null);

  return finalDish;
}}
/>
<EditDishNameModal
  open={!!editingDishName}
  dish={editingDishName}
  onClose={() => setEditingDishName(null)}
  onSave={(updatedDish) => {
    if (updatedDish?.id != null) {
      setEditedDishes((prev) => ({
        ...prev,
        [String(updatedDish.id)]: updatedDish,
      }));
    }

    onUpdateFood?.(updatedDish);
    setEditingDishName(null);
  }}
/>
<EditMyFoodModal
  open={!!editingFood}
  food={editingFood}
  onClose={() => setEditingFood(null)}
  onSave={(updatedFood) => {
    if (updatedFood?.id != null) {
      setEditedDishes((prev) => ({
        ...prev,
        [String(updatedFood.id)]: updatedFood,
      }));
    }

    onUpdateFood?.(updatedFood);
    setEditingFood(null);
  }}
/>

<EditMyFoodModal
  open={!!editingIngredientFood}
  food={editingIngredientFood?.food}
  onClose={() => setEditingIngredientFood(null)}
  onSave={(updatedIngredient) => {
    if (!editingIngredientFood) return;

    const sourceDish =
      editedDishes[String(editingIngredientFood.dishId)] ||
      (foods || []).find(
        (x) => String(x.id) === String(editingIngredientFood.dishId)
      );

    const updatedDish = updateDishIngredientDetailsByIndex(
      sourceDish,
      foods,
      editingIngredientFood.ingredientIndex,
      updatedIngredient
    );

    if (updatedDish) {
      const nextDish = {
        ...updatedDish,
        updatedAt: Date.now(),
      };

      setEditedDishes((prev) => ({
        ...prev,
        [String(editingIngredientFood.dishId)]: nextDish,
      }));
    }

    setEditingIngredientFood(null);
  }}
/>

<EditGramsModal
  open={!!editingGramsFood}
  food={editingGramsFood}
  onClose={() => setEditingGramsFood(null)}
  onSave={(updatedFood) => {
    if (updatedFood?.id != null) {
      setEditedDishes((prev) => ({
        ...prev,
        [String(updatedFood.id)]: updatedFood,
      }));
    }

    onUpdateFood?.(updatedFood);
    setEditingGramsFood(null);
  }}
/>
  
{ingredientWeightModal && (
  <EditGramsModal
    open={!!ingredientWeightModal}
    food={{
      id: `ingredient_${ingredientWeightModal.dishId}_${ingredientWeightModal.ingredientIndex}`,
      name: ingredientWeightModal.name || "Ингредиент",
      defaultGrams: Number(ingredientWeightModal.initial || 0),
    }}
    onClose={() => setIngredientWeightModal(null)}
    onSave={(updatedIngredient) => {
      const grams = Math.max(1, Number(updatedIngredient?.defaultGrams || 1));
      const sourceDish =
        editedDishes[String(ingredientWeightModal.dishId)] ||
        (foods || []).find(
          (x) => String(x.id) === String(ingredientWeightModal.dishId)
        );

      const updatedDish = updateDishIngredientByIndex(
        sourceDish,
        foods,
        ingredientWeightModal.ingredientIndex,
        grams
      );

      if (updatedDish) {
        const nextDish = {
          ...updatedDish,
          updatedAt: Date.now(),
        };

        setEditedDishes((prev) => ({
          ...prev,
          [String(ingredientWeightModal.dishId)]: nextDish,
        }));
      }

      setIngredientWeightModal(null);
    }}
  />
)}
<FullNamePopup
  open={!!fullNamePopup}
  title={fullNamePopup?.title}
  name={fullNamePopup?.name}
  onClose={() => setFullNamePopup(null)}
/>
          </div>
  );
}
// ===== FIX: helpers for saving food entries (used in MealsDetails) =====
function getLocalDayKey(d = new Date()) {
  return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
}

function mealSlotFromTitle(title) {
  const t = (title || "").toLowerCase();
  if (t.includes("завтрак")) return "breakfast";
  if (t.includes("обед")) return "lunch";
  if (t.includes("ужин")) return "dinner";
  if (t.includes("перекус")) return "snack";
  return "other";
}

function getPreviousDayKey(dayKey) {
  const parts = String(
    dayKey || ""
  )
    .split("-")
    .map(Number);

  if (
    parts.length !== 3 ||
    parts.some(
      (value) =>
        !Number.isFinite(value)
    )
  ) {
    return "";
  }

  const [year, month, day] =
    parts;

  const previousDate =
    new Date(
      year,
      month - 1,
      day - 1
    );

  return getLocalDayKey(
    previousDate
  );
}

function getRepeatYesterdayLabel(
  title
) {
  if (title === "Завтрак") {
    return "Повторить вчерашний завтрак";
  }

  if (title === "Обед") {
    return "Повторить вчерашний обед";
  }

  if (title === "Ужин") {
    return "Повторить вчерашний ужин";
  }

  if (title === "Перекус") {
    return "Повторить вчерашний перекус";
  }

  return "Повторить вчера";
}

function dbFoodEntryToItem(row) {
  const rawSnapshot = row?.dish_snapshot ?? row?.dishSnapshot ?? null;
  const dishSnapshot =
    rawSnapshot && typeof rawSnapshot === "object" ? rawSnapshot : null;

  const entryType =
    String(row?.entry_type || row?.entryType || (dishSnapshot ? "dish" : "ingredient"))
      .trim()
      .toLowerCase() || "ingredient";

  const isDish = entryType === "dish";
  const snapshotTotals = dishSnapshot?.totals || {};

  const grams = Number(row?.grams ?? dishSnapshot?.grams ?? 0);
  const proteins = Number(row?.proteins ?? snapshotTotals.proteins ?? 0);
  const fats = Number(row?.fats ?? snapshotTotals.fats ?? 0);
  const carbs = Number(row?.carbs ?? snapshotTotals.carbs ?? 0);
  const kcal = Number(row?.kcal ?? snapshotTotals.kcal ?? 0);

  const userFoodId = row?.user_food_id != null ? Number(row.user_food_id) : null;
  const sourceUserFoodId =
    row?.source_user_food_id != null ? Number(row.source_user_food_id) : userFoodId;

  return {
    id: Number(row.id),
    name: row.name || dishSnapshot?.name || "Блюдо",
    type: isDish ? "dish" : "ingredient",
    entryType,
    proteins,
    fats,
    carbs,
    weight: grams,
    grams,
    kcal,
    userFoodId,
    sourceUserFoodId,
    sourceMyFoodId: sourceUserFoodId != null ? String(sourceUserFoodId) : "",
    dishSnapshot,
    dish_snapshot: dishSnapshot,
    recipe: isDish ? dishSnapshot?.recipe || row?.recipe || null : row?.recipe,
    ingredients: isDish
      ? dishSnapshot?.ingredients || row?.ingredients || []
      : row?.ingredients,
    changedFromSource: Boolean(dishSnapshot?.changed_from_source),
  };
}

async function apiAddFoodEntry(payload) {
  const body = {
    ...payload,
    meal_slot: payload.meal_slot ?? payload.mealSlot ?? "snack",
    entry_type: payload.entry_type ?? payload.entryType,
    user_food_id: payload.user_food_id ?? payload.userFoodId ?? null,
    source_user_food_id:
      payload.source_user_food_id ?? payload.sourceUserFoodId ?? null,
    dish_snapshot: payload.dish_snapshot ?? payload.dishSnapshot ?? null,
  };

  delete body.mealSlot;
  delete body.entryType;
  delete body.userFoodId;
  delete body.sourceUserFoodId;
  delete body.dishSnapshot;

  const res = await tgFetch("/api/food-entries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiAddFoodEntry failed");
  return json.data;
}

async function apiUpdateFoodEntry(id, payload) {
  const body = {
    id,
    ...payload,
    meal_slot: payload.meal_slot ?? payload.mealSlot,
    entry_type: payload.entry_type ?? payload.entryType,
    user_food_id: payload.user_food_id ?? payload.userFoodId,
    source_user_food_id:
      payload.source_user_food_id ?? payload.sourceUserFoodId,
    dish_snapshot: payload.dish_snapshot ?? payload.dishSnapshot,
  };

  delete body.mealSlot;
  delete body.entryType;
  delete body.userFoodId;
  delete body.sourceUserFoodId;
  delete body.dishSnapshot;

  const res = await tgFetch("/api/food-entries", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiUpdateFoodEntry failed");
  return json.data;
}

async function apiListFoodEntriesForRepeat(day) {
  const params =
    new URLSearchParams({
      day,
    });

  const url =
    `/api/food-entries?${params.toString()}`;

  const res =
    await tgFetch(url);

  const json =
    await res.json();

  if (!json.ok) {
    throw new Error(
      json.error ||
        "Ошибка загрузки еды"
    );
  }

  return json.data || [];
}

async function repeatEntryToday(entry) {
  try {
    const todayKey = getLocalDayKey();

    await apiAddFoodEntry({
      day: todayKey,
      meal_slot: entry.meal_slot,
      entry_type: entry.entry_type ?? entry.entryType,
      user_food_id: entry.user_food_id ?? entry.userFoodId ?? null,
      source_user_food_id:
        entry.source_user_food_id ?? entry.sourceUserFoodId ?? null,
      name: entry.name,
      grams: entry.grams,
      kcal: entry.kcal,
      proteins: entry.proteins,
      fats: entry.fats,
      carbs: entry.carbs,
      dish_snapshot: entry.dish_snapshot ?? entry.dishSnapshot ?? null,
    });
  } catch (e) {
    console.error("repeatEntryToday failed:", e);
  }
}
// ===== Экран деталей рациона (страница после клика по кольцам) =====
function MealsDetails({
  dbUserId,
  dayKey,
  onChangeDay = () => {},
  meals,
  setMeals,
  onBack,
  onEditMacros = () => {},
  setTrainScreen,
  bumpFoodMonth,
  macroTargets,
  premiumAccessLoading = false,
  canUsePremiumFeatures = true,
  onPremiumRequired,
}) {
  const { t: tr, i18n } = useTranslation();

  const todayKey = getLocalDayKey();

  // YYYY-MM-DD можно безопасно сравнивать как строки.
  const canGoNextRationDay = Boolean(
    dayKey &&
    dayKey < todayKey
  );

  const formatRationDayLabel = (key) => {
    if (!key) return "";

    const parts = String(key).split("-").map(Number);

    if (
      parts.length !== 3 ||
      parts.some((value) => !Number.isFinite(value))
    ) {
      return key;
    }

    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString(
      i18n.resolvedLanguage === "ru"
        ? "ru-RU"
        : "en-GB",
      {
        day: "numeric",
        month: "long",
      }
    );
  };

  const shiftRationDay = (delta) => {
    if (!dayKey) return;

    const parts = String(dayKey).split("-").map(Number);

    if (
      parts.length !== 3 ||
      parts.some((value) => !Number.isFinite(value))
    ) {
      return;
    }

    const [year, month, day] = parts;
    const nextDate = new Date(year, month - 1, day + delta);
    const nextDayKey = getLocalDayKey(nextDate);

    // В будущие дни не переходим.
    if (nextDayKey > todayKey) return;

    onChangeDay(nextDayKey);
  };

  const isInputFocused = useInputFocusFlag();

  const [expandedIds, setExpandedIds] = useState([]); // какие приёмы пищи раскрыты
  const [openedMealDishIds, setOpenedMealDishIds] = useState(() => new Set());
const [openedMealItemMenuId, setOpenedMealItemMenuId] = useState(null);
const [editingMealDish, setEditingMealDish] = useState(null);
const [editingMealFood, setEditingMealFood] = useState(null);
const [editingMealDishIngredientFood, setEditingMealDishIngredientFood] = useState(null);
const [mealFullNamePopup, setMealFullNamePopup] = useState(null);
const [changedMealDishKeys, setChangedMealDishKeys] = useState(() => new Set());
const [mealDishBuilderSeed, setMealDishBuilderSeed] = useState(null);
const [
  previousDayFood,
  setPreviousDayFood,
] = useState({
  dayKey: "",
  entries: [],
});

const [
  repeatMealBusySlot,
  setRepeatMealBusySlot,
] = useState("");

const previousDayKey =
  getPreviousDayKey(dayKey);

useEffect(() => {
  if (
    !dbUserId ||
    !previousDayKey
  ) {
    setPreviousDayFood({
      dayKey: "",
      entries: [],
    });

    return undefined;
  }

  let cancelled = false;

  setPreviousDayFood({
    dayKey: "",
    entries: [],
  });

  (async () => {
    try {
      const rows =
        await apiListFoodEntriesForRepeat(
          previousDayKey
        );

      if (cancelled) return;

      setPreviousDayFood({
        dayKey: previousDayKey,
        entries:
          Array.isArray(rows)
            ? rows
            : [],
      });
    } catch (error) {
      if (cancelled) return;

      console.error(
        "load previous day food failed:",
        error
      );

      setPreviousDayFood({
        dayKey: previousDayKey,
        entries: [],
      });
    }
  })();

  return () => {
    cancelled = true;
  };
}, [
  dbUserId,
  previousDayKey,
]);

const getPreviousMealEntries = (
  mealTitle
) => {
  if (
    previousDayFood.dayKey !==
    previousDayKey
  ) {
    return [];
  }

  const mealSlot =
    mealSlotFromTitle(
      mealTitle
    );

  return (
    previousDayFood.entries || []
  ).filter(
    (entry) =>
      String(
        entry?.meal_slot ||
          "other"
      ) === mealSlot
  );
};

const repeatPreviousMeal =
  async (meal) => {
    const mealSlot =
      mealSlotFromTitle(
        meal?.title
      );

    if (
      !dayKey ||
      !meal?.id ||
      mealSlot === "other" ||
      repeatMealBusySlot
    ) {
      return;
    }

    const entries =
      getPreviousMealEntries(
        meal.title
      );

    if (!entries.length) {
      return;
    }

    try {
      setRepeatMealBusySlot(
        mealSlot
      );

      for (
        const entry of entries
      ) {
        const row =
          await apiAddFoodEntry({
            day: dayKey,

            meal_slot:
              mealSlot,

            entry_type:
              entry.entry_type ??
              entry.entryType,

            user_food_id:
              entry.user_food_id ??
              entry.userFoodId ??
              null,

            source_user_food_id:
              entry.source_user_food_id ??
              entry.sourceUserFoodId ??
              null,

            name:
              entry.name,

            grams:
              entry.grams,

            kcal:
              entry.kcal,

            proteins:
              entry.proteins,

            fats:
              entry.fats,

            carbs:
              entry.carbs,

            dish_snapshot:
              entry.dish_snapshot ??
              entry.dishSnapshot ??
              null,
          });

        const newItem =
          dbFoodEntryToItem(
            row
          );

        setMeals((prev) =>
          prev.map((item) =>
            String(item.id) ===
            String(meal.id)
              ? {
                  ...item,
                  items: [
                    ...(item.items ||
                      []),
                    newItem,
                  ],
                }
              : item
          )
        );
      }

      bumpFoodMonth?.();
    } catch (error) {
      console.error(
        "repeatPreviousMeal failed:",
        error
      );

      safeAlert(
        "Не удалось повторить приём пищи.",
        "Ошибка"
      );
    } finally {
      setRepeatMealBusySlot(
        ""
      );
    }
  };

const toggleMealDishDetails = (itemId) => {
  setOpenedMealDishIds((prev) => {
    const next = new Set(prev);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    return next;
  });
};
const getMealDishDraftKey = (mealId, itemId) => `${mealId}:${itemId}`;

const markMealDishChanged = (mealId, itemId) => {
  const key = getMealDishDraftKey(mealId, itemId);

  setChangedMealDishKeys((prev) => {
    const next = new Set(prev);
    next.add(key);
    return next;
  });
};
const getEntrySnapshotFromItem = (item) => {
  const snapshot = item?.dishSnapshot ?? item?.dish_snapshot ?? null;
  return snapshot && typeof snapshot === "object" ? snapshot : null;
};

const getEntrySourceUserFoodId = (item, fallbackDish) => {
  const raw =
    item?.sourceUserFoodId ??
    item?.source_user_food_id ??
    fallbackDish?.sourceUserFoodId ??
    fallbackDish?.source_user_food_id ??
    item?.userFoodId ??
    item?.user_food_id ??
    fallbackDish?.id ??
    null;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const makeMealDishSnapshot = (dish, sourceItem = null, changedFromSource = false) => {
  const ingredients = buildDishIngredientsList(dish, myFoods);

  const grams = Math.max(
    1,
    Number(
      dish?.weight ??
        dish?.grams ??
        dish?.defaultGrams ??
        sourceItem?.weight ??
        sourceItem?.grams ??
        100
    )
  );

  const proteins = Number(dish?.proteins ?? sourceItem?.proteins ?? 0);
  const fats = Number(dish?.fats ?? sourceItem?.fats ?? 0);
  const carbs = Number(dish?.carbs ?? sourceItem?.carbs ?? 0);
  const kcal = Number(dish?.kcal ?? sourceItem?.kcal ?? 0);

  return {
    name: dish?.name || sourceItem?.name || "Блюдо",
    grams,
    ingredients,
    recipe:
      dish?.recipe ||
      {
        components: ingredients.map((ing) => ({
          foodId: ing.foodId ?? ing.id,
          name: ing.name,
          grams: Number(ing.grams || 0),
        })),
      },
    totals: {
      kcal: Math.round(kcal),
      proteins,
      fats,
      carbs,
    },
    source_user_food_id: getEntrySourceUserFoodId(sourceItem, dish),
    changed_from_source: Boolean(changedFromSource),
    saved_at: new Date().toISOString(),
  };
};

const makeMealDishEntryPatch = (item, updatedDish, changedFromSource = true) => {
  const snapshot = makeMealDishSnapshot(updatedDish, item, changedFromSource);

  return {
    entry_type: "dish",
    source_user_food_id: snapshot.source_user_food_id,
    user_food_id: null,
    name: snapshot.name,
    grams: snapshot.grams,
    kcal: snapshot.totals.kcal,
    proteins: snapshot.totals.proteins,
    fats: snapshot.totals.fats,
    carbs: snapshot.totals.carbs,
    dish_snapshot: snapshot,
  };
};
  // ✅ итоги за день (КБЖУ + ккал) — нужно и для overview, и для details
const totals = useMemo(() => {
  const t = calcAllTotals(meals);
  return {
    proteins: +t.proteins.toFixed(1),
    fats: +t.fats.toFixed(1),
    carbs: +t.carbs.toFixed(1),
    kcal: Math.round(t.kcal),
  };
}, [meals]);

// ✅ интро-режим: если ещё ничего не внесли за день
const macroIntro =
  (totals.carbs || 0) === 0 &&
  (totals.proteins || 0) === 0 &&
  (totals.fats || 0) === 0 &&
  (totals.kcal || 0) === 0;
const safeTarget = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const DAY_TARGETS = {
  kcal: safeTarget(macroTargets?.kcal, 2200),
  proteins: safeTarget(macroTargets?.proteins, 180),
  fats: safeTarget(macroTargets?.fats, 60),
  carbs: safeTarget(macroTargets?.carbs, 240),
};

const round = (value) => Math.round(Number(value || 0));

const fmtMacro = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";

  return new Intl.NumberFormat(
    i18n.resolvedLanguage === "ru"
      ? "ru-RU"
      : "en-GB",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }
  ).format(n);
};

const pct = (value, target) => {
  const v = Number(value || 0);
  const t = Number(target || 1);
  return Math.max(0, Math.round((v / t) * 100));
};

const clampPct = (value, target) => Math.min(100, pct(value, target));

const DayMacroCard = ({ label, value, target }) => {
  const percent = pct(value, target);

  return (
    <div className="min-w-0">
      <div className="text-[11px] leading-none text-slate-300">
        {label}
      </div>

      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-[18px] font-bold leading-none tracking-[-0.03em] text-slate-50 tabular-nums">
          {fmtMacro(value)}
        </span>
        <span className="text-[11px] font-medium leading-none text-slate-300">
          {tr("units.gramsShort")}
        </span>
      </div>

      <div className="mt-1 text-[10px] leading-none text-slate-400">
        {tr("ration.ofTarget", {
          value: fmtMacro(target),
          unit: tr("units.gramsShort"),
        })}
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.10]">
        <div
          className="h-full rounded-full bg-emerald-300"
          style={{ width: `${clampPct(value, target)}%` }}
        />
      </div>

      <div className="mt-1 text-[10px] font-semibold leading-none text-emerald-200 tabular-nums">
        {percent}%
      </div>
    </div>
  );
};
const getRationMealTitle = (title) => {
  if (title === "Завтрак") return tr("ration.breakfast");
  if (title === "Обед") return tr("ration.lunch");
  if (title === "Ужин") return tr("ration.dinner");
  if (title === "Перекус") return tr("ration.snack");

  return title;
};

const getRationRepeatLabel = (title) => {
  if (title === "Завтрак") return tr("ration.repeatBreakfast");
  if (title === "Обед") return tr("ration.repeatLunch");
  if (title === "Ужин") return tr("ration.repeatDinner");
  if (title === "Перекус") return tr("ration.repeatSnack");

  return tr("ration.repeatGeneric");
};

const MealMacroStrip = ({ totals, targets }) => {
  return (
    <div className="grid min-w-0 grid-cols-3 overflow-hidden rounded-[14px] border border-white/10 bg-slate-950/20">
      <div className="min-w-0 border-r border-white/10 px-3 py-2">
        <div className="text-[11px] font-bold leading-none text-slate-50">
  {tr("food.report.proteinShort")}
</div>
        <div className="mt-1 flex items-baseline gap-1 whitespace-nowrap tabular-nums">
          <span className="text-[13px] font-semibold leading-none text-slate-50">
            {fmtMacro(totals.proteins)} {tr("units.gramsShort")}
          </span>
          <span className="text-[10px] font-medium leading-none text-slate-400">
            · {pct(totals.proteins, targets.proteins)}%
          </span>
        </div>
      </div>

      <div className="min-w-0 border-r border-white/10 px-3 py-2">
        <div className="text-[11px] font-bold leading-none text-slate-50">
  {tr("food.report.fatShort")}
</div>
        <div className="mt-1 flex items-baseline gap-1 whitespace-nowrap tabular-nums">
          <span className="text-[13px] font-semibold leading-none text-slate-50">
            {fmtMacro(totals.fats)} {tr("units.gramsShort")}
          </span>
          <span className="text-[10px] font-medium leading-none text-slate-400">
            · {pct(totals.fats, targets.fats)}%
          </span>
        </div>
      </div>

      <div className="min-w-0 px-3 py-2">
        <div className="text-[11px] font-bold leading-none text-slate-50">
  {tr("food.report.carbsShort")}
</div>
        <div className="mt-1 flex items-baseline gap-1 whitespace-nowrap tabular-nums">
          <span className="text-[13px] font-semibold leading-none text-slate-50">
            {fmtMacro(totals.carbs)} {tr("units.gramsShort")}
          </span>
          <span className="text-[10px] font-medium leading-none text-slate-400">
            · {pct(totals.carbs, targets.carbs)}%
          </span>
        </div>
      </div>
    </div>
  );
};
    // модалка выбора способа добавления блюда (ТОЛЬКО выбор)
  const [addModal, setAddModal] = useState(null);
  // { mealId, step: "mode" }

// “страницы” после выбора (чтобы не было попап-на-попап)
const [addFlow, setAddFlow] = useState(null);
// { mealId, screen: "my" | "manual", chooseMealAfter?: boolean }

// ✅ Верхняя мини-полоска КБЖУ: появляется, когда большая карточка "Итого за день" ушла вверх
const daySummaryRef = useRef(null);
const [showDailySticky, setShowDailySticky] = useState(false);

useEffect(() => {
  if (typeof window === "undefined") return undefined;

  const updateDailySticky = () => {
    const el = daySummaryRef.current;

    if (!el || addFlow || addModal || isInputFocused) {
      setShowDailySticky(false);
      return;
    }

    const rect = el.getBoundingClientRect();
const stickyTopLine = 4;
const shouldShow = rect.bottom <= stickyTopLine;

    setShowDailySticky((prev) =>
      prev === shouldShow ? prev : shouldShow
    );
  };

  const root =
    daySummaryRef.current?.closest?.("[data-app-scroll-root='1']") || window;

  updateDailySticky();

  root.addEventListener?.("scroll", updateDailySticky, { passive: true });
  window.addEventListener("resize", updateDailySticky);
  window.visualViewport?.addEventListener("resize", updateDailySticky);

  return () => {
    root.removeEventListener?.("scroll", updateDailySticky);
    window.removeEventListener("resize", updateDailySticky);
    window.visualViewport?.removeEventListener("resize", updateDailySticky);
  };
}, [addFlow, addModal, isInputFocused]);

// Когда "Мои блюда" открыты из дневного рациона,
// выбранную еду сначала держим тут,
// а потом спрашиваем, в какой приём пищи её добавить.
const [pendingMyFoodsAdd, setPendingMyFoodsAdd] = useState(null);
// { foods: [] }

const [pendingMyFoodsMealId, setPendingMyFoodsMealId] = useState("");

  // ===== photo add flow =====
const [photoPick, setPhotoPick] = useState(null);
// { previewUrl, base64 }

const [photoLoading, setPhotoLoading] = useState(false);
const [photoError, setPhotoError] = useState(null);
const [photoLimitNotice, setPhotoLimitNotice] = useState(null);
const [photoResult, setPhotoResult] = useState(null);
const [photoTargetMealId, setPhotoTargetMealId] = useState(null); // выбранный приём пищи для добавления результата фото
const [isAddingPhotoDish, setIsAddingPhotoDish] = useState(false);
const [isSavingManualFood, setIsSavingManualFood] = useState(false);
// Тексты "живого" анализа (чтобы не было ощущения дешёвой подгрузки)
const photoLoadingSteps = [
  tr("ration.myFoods.photoFlow.loadingAnalyze"),
  tr("ration.myFoods.photoFlow.loadingDish"),
  tr("ration.myFoods.photoFlow.loadingIngredients"),
  tr("ration.myFoods.photoFlow.loadingMacros"),
  tr("ration.myFoods.photoFlow.loadingResult"),
];

const [photoLoadingText, setPhotoLoadingText] = useState(photoLoadingSteps[0]);

useEffect(() => {
  if (!photoLoading) {
    setPhotoLoadingText(photoLoadingSteps[0]);
    return;
  }

  let i = 0;
  const t = setInterval(() => {
    i = (i + 1) % photoLoadingSteps.length;
    setPhotoLoadingText(photoLoadingSteps[i]);
  }, 2400);

  return () => clearInterval(t);
}, [photoLoading]);

// ===== one photo picker for both buttons (iOS: Media / Camera / File) =====
const photoInputRef = useRef(null);
const pendingPhotoMealIdRef = useRef(null);



const compressPhotoForAnalysis = async (file) => {
  const TARGET_MAX_CHARS = 2_900_000;
  const MAX_SIDE_STEPS = [1600, 1400, 1200, 1000, 850, 700];
  const QUALITY_STEPS = [0.82, 0.74, 0.66, 0.58, 0.5, 0.42];

  const readAsDataUrl = (sourceFile) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(sourceFile);
    });

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const canvasToBlob = (canvas, quality) =>
    new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    });

  const originalDataUrl = await readAsDataUrl(file);

  let img;

  try {
    img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = originalDataUrl;
    });
  } catch {
    throw new Error("Не удалось подготовить фото");
  }

  const originalWidth = Number(img.width || 0);
  const originalHeight = Number(img.height || 0);

  if (!originalWidth || !originalHeight) {
    throw new Error("Не удалось подготовить фото");
  }

  let bestDataUrl = "";

  for (const maxSide of MAX_SIDE_STEPS) {
    const scale = Math.min(1, maxSide / Math.max(originalWidth, originalHeight));
    const width = Math.max(1, Math.round(originalWidth * scale));
    const height = Math.max(1, Math.round(originalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) continue;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, quality);
      if (!blob) continue;

      const dataUrl = await blobToDataUrl(blob);

      if (!bestDataUrl || dataUrl.length < bestDataUrl.length) {
        bestDataUrl = dataUrl;
      }

      if (dataUrl.length <= TARGET_MAX_CHARS) {
        return dataUrl;
      }
    }
  }

  if (bestDataUrl) {
    return bestDataUrl;
  }

  throw new Error("Не удалось подготовить фото");
};

const analyzePhotoBase64 = async (base64) => {
  if (!base64 || photoLoading) return;

  const normalizePhotoResultForUi = (data) => {
    const t = data?.total || {};
    const grams = Number(t.portion_grams);

    return {
      ...data,
      total: {
        ...t,
        base_grams: Number.isFinite(grams) && grams > 0 ? grams : null,
        base_kcal: t.kcal ?? null,
        base_p: t.proteins ?? null,
        base_f: t.fats ?? null,
        base_c: t.carbs ?? null,
      },
    };
  };

  const sleep = (ms) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

setPhotoLoading(true);
setPhotoError(null);
setPhotoLimitNotice(null);
setPhotoResult(null);

  let jobId = null;

  const pumpWorker = async () => {
    try {
      await tgFetch("/api/photo-analysis-pump", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId }),
      });
    } catch (e) {
      debugWarn("[photo queue] pump failed:", e);
    }
  };

  try {
    // 1) Быстро создаём задачу в очереди
const createRes = await tgFetch("/api/photo-analysis-create", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    imageBase64: base64,
    language:
      i18n.resolvedLanguage === "ru"
        ? "ru"
        : "en",
  }),
});

    const createJson = await createRes.json().catch(() => ({}));

    if (!createRes.ok || !createJson?.ok || !createJson?.jobId) {
      if (createRes.status === 402 || createJson?.code === "ACCESS_REQUIRED") {
        onPremiumRequired?.("photo_analysis_api");

throw new Error(
  tr("ration.myFoods.photoFlow.accessRequired")
);
      }

if (createRes.status === 429 || createJson?.code === "PHOTO_DAILY_LIMIT") {
  const limit = Number(createJson?.limit || 0);
  const premiumLimit = Number(createJson?.premiumLimit || 20);
  const limitType = String(createJson?.limitType || "trial");
  const upgradeAvailable = Boolean(createJson?.upgradeAvailable);

  setPhotoError(null);
  setPhotoLimitNotice({
    message: "Вы достигли лимита распознавания фото на сегодня.",
    limit: Number.isFinite(limit) && limit > 0 ? limit : 10,
    premiumLimit:
      Number.isFinite(premiumLimit) && premiumLimit > 0 ? premiumLimit : 20,
    limitType,
    upgradeAvailable,
  });

  return;
}

throw new Error(
  tr("ration.myFoods.photoFlow.queueStartError")
);
    }

    jobId = Number(createJson.jobId);

    // 2) Запускаем обработчик.
    // Секрет worker остаётся на сервере, во фронт он не попадает.
    void pumpWorker();

// 3) Ждём результат через status polling
// Важно: Telegram WebView иногда может уронить 1-2 сетевых запроса.
// Поэтому НЕ показываем ошибку после первой неудачной проверки статуса.
const startedAt = Date.now();
const timeoutMs = 300000;
let lastStatus = "";
let statusErrorCount = 0;
const maxStatusErrors = 8;

while (Date.now() - startedAt < timeoutMs) {
  await sleep(lastStatus ? 2200 : 1200);

  let statusRes = null;
  let statusJson = null;

  try {
    statusRes = await tgFetch(
      `/api/photo-analysis-status?jobId=${encodeURIComponent(jobId)}`,
      {
        method: "GET",
      }
    );

    statusJson = await statusRes.json().catch(() => null);
  } catch (pollError) {
    statusErrorCount += 1;

    debugWarn("[photo queue] status fetch failed:", pollError);

if (statusErrorCount >= maxStatusErrors) {
  throw new Error(
    tr("ration.myFoods.photoFlow.statusNetworkError")
  );
}

    void pumpWorker();
    continue;
  }

  if (!statusRes.ok || !statusJson?.ok) {
    statusErrorCount += 1;

    debugWarn("[photo queue] bad status response:", {
      httpStatus: statusRes.status,
      body: statusJson,
    });

if (statusErrorCount >= maxStatusErrors) {
  throw new Error(
    tr("ration.myFoods.photoFlow.statusError")
  );
}

    void pumpWorker();
    continue;
  }

  statusErrorCount = 0;

  const job = statusJson.job || {};
  lastStatus = String(job.status || "");

  if (job.status === "done") {
    const result = job.result || {};

    setPhotoResult(normalizePhotoResultForUi(result));
    return;
  }

  if (job.status === "failed") {
    const jobError = String(job.error || "");

if (jobError.includes("ACCESS_REQUIRED")) {
  throw new Error(
    tr("ration.myFoods.photoFlow.accessRequired")
  );
}

    throw new Error(
      tr("ration.myFoods.photoFlow.recognitionError")
    );
  }

  // Если задача ещё стоит в очереди или ждёт retry — мягко пинаем worker ещё раз.
  if (job.status === "queued" || job.status === "retry") {
    void pumpWorker();
  }
}

throw new Error(
  tr("ration.myFoods.photoFlow.timeoutError")
);
} catch (e) {
  // Важно:
  // Если Telegram WebView один раз отвалился по сети,
  // worker мог всё равно успешно закончить задачу.
  // Перед тем как показать ошибку, делаем последнюю проверку результата.
  if (jobId) {
    try {
      const finalRes = await tgFetch(
        `/api/photo-analysis-status?jobId=${encodeURIComponent(jobId)}`,
        {
          method: "GET",
        }
      );

      const finalJson = await finalRes.json().catch(() => null);
      const finalJob = finalJson?.job || null;

      if (finalRes.ok && finalJson?.ok && finalJob?.status === "done") {
        setPhotoResult(normalizePhotoResultForUi(finalJob.result || {}));
        return;
      }
    } catch (finalError) {
      debugWarn("[photo queue] final status check failed:", finalError);
    }
  }

  setPhotoError(
    e?.message && !String(e.message).includes("HTTP")
      ? e.message
      : tr("ration.myFoods.photoFlow.recognitionError")
  );
} finally {
  setPhotoLoading(false);
}
};

const startPhotoPick = (
  mealId,
  { cameraOnly = false } = {}
) => {
  if (!mealId) return;

  if (premiumAccessLoading) {
    safeAlert(
      "Проверяем доступ. Попробуй ещё раз через пару секунд.",
      "Фото еды"
    );
    return;
  }

  if (!canUsePremiumFeatures) {
    setAddModal(null);
    onPremiumRequired?.(
      "photo_analysis"
    );
    return;
  }

  // Запоминаем приём пищи,
  // в который добавится результат.
  pendingPhotoMealIdRef.current =
    mealId;

  // Позволяет повторно выбрать
  // или снять то же самое фото.
  resetNativePhotoPickers();

  const input =
    photoInputRef.current;

  if (!input) {
    safeAlert(
      "Не удалось открыть выбор фото. Попробуй ещё раз.",
      "Фото еды"
    );
    return;
  }

  try {
    if (cameraOnly) {
      // Сразу открываем заднюю камеру.
      input.setAttribute(
        "capture",
        "environment"
      );
    } else {
      // Обычный системный выбор:
      // медиатека / камера / файл.
      input.removeAttribute(
        "capture"
      );
    }

    input.click();
  } catch (error) {
    debugWarn(
      "[photo picker] click failed:",
      error
    );

    safeAlert(
      "Не удалось открыть выбор фото. Попробуй ещё раз.",
      "Фото еды"
    );
  }
};

// refs/flags for better UX in photo flow
const cameraInputRef = useRef(null);
const galleryInputRef = useRef(null);
const resetNativePhotoPickers = () => {
  try {
    if (photoInputRef.current) photoInputRef.current.value = "";
  } catch {}

  try {
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  } catch {}

  try {
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  } catch {}
};

useEffect(() => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return undefined;
  }

  const resetAfterSystemReturn = () => {
    // Пока ждём файл из камеры/медиатеки — вообще ничего не чистим.
    // Время ожидания не ограничиваем: пользователь может фотографировать хоть 30 секунд.
    if (pendingPhotoMealIdRef.current) {
      return;
    }

    // Если активного выбора фото нет — можно мягко очистить input
    // после возврата в приложение.
    window.setTimeout(resetNativePhotoPickers, 1200);
  };

  window.addEventListener("focus", resetAfterSystemReturn);
  window.addEventListener("pageshow", resetAfterSystemReturn);
  document.addEventListener("visibilitychange", resetAfterSystemReturn);

  return () => {
    window.removeEventListener("focus", resetAfterSystemReturn);
    window.removeEventListener("pageshow", resetAfterSystemReturn);
    document.removeEventListener("visibilitychange", resetAfterSystemReturn);
  };
}, []);
const addFlowScrollRef = useRef(null);

const photoResultRef = useRef(null);

const [photoAutoOpenCamera, setPhotoAutoOpenCamera] = useState(false);
const photoFocusScrollRef = useRef({
  timers: [],
});

const clearPhotoFocusTimers = () => {
  const timers = photoFocusScrollRef.current?.timers || [];
  timers.forEach((timer) => window.clearTimeout(timer));
  photoFocusScrollRef.current.timers = [];
};

const scrollPhotoResultInputAboveKeyboard = (el) => {
  if (!el || typeof window === "undefined") return;

  clearPhotoFocusTimers();

  const run = () => {
    try {
      if (!el.isConnected) return;
      if (document.activeElement !== el) return;

      const root = el.closest?.('[data-photo-scroll-root="1"]');
      if (!root) return;

      const rootRect = root.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const vv = window.visualViewport;
      const visibleBottom = vv
        ? Math.min(rootRect.bottom, vv.offsetTop + vv.height)
        : rootRect.bottom;

      const safeBottom = visibleBottom - 18;
      const delta = elRect.bottom - safeBottom;

      // Не двигаем окно из-за мелких изменений цифр.
      // Скроллим только если поле реально оказалось под клавиатурой.
      if (delta > 32) {
        root.scrollTo({
          top: Math.max(0, root.scrollTop + delta),
          behavior: "auto",
        });
      }
    } catch {}
  };

  const timer = window.setTimeout(() => {
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, 180);

  photoFocusScrollRef.current.timers.push(timer);
};

const placePhotoCaretToEnd = (el) => {
  try {
    if (!el) return;

    const len = String(el.value ?? "").length;

    if (typeof el.setSelectionRange === "function") {
      el.setSelectionRange(len, len);
    }
  } catch {}
};

const focusPhotoResultInput = (e) => {
  const el = e?.currentTarget || e?.target;
  if (!el) return;

  placePhotoCaretToEnd(el);
  scrollPhotoResultInputAboveKeyboard(el);
};


const photoResultInputProps = {
  "data-photo-result-input": "1",

  onPointerDown: (e) => {
    const el = e.currentTarget;

    try {
      el.focus({ preventScroll: true });
    } catch {
      try {
        el.focus();
      } catch {}
    }
  },

  onFocus: focusPhotoResultInput,

  style: {
    lineHeight: "normal",
    paddingTop: 0,
    paddingBottom: 0,
    caretColor: "#f8fafc",
    WebkitAppearance: "none",
    appearance: "none",
    WebkitTapHighlightColor: "transparent",
  },
};
const getPhotoBaseGrams = (t = {}) => {
  const baseGrams = readPhotoNumber(t.base_grams);
  if (Number.isFinite(baseGrams) && baseGrams > 0) return baseGrams;

  const currentGrams = readPhotoNumber(t.portion_grams);
  if (Number.isFinite(currentGrams) && currentGrams > 0) return currentGrams;

  return 100;
};

const updatePhotoKcalInput = (rawValue) => {
  const nextRaw = sanitizePhotoNumberInput(rawValue, { decimal: false });
  const nextKcal = readPhotoNumber(nextRaw);

  setPhotoResult((prev) => {
    const t = prev?.total || {};

    return {
      ...prev,
      total: {
        ...t,
        kcal: nextRaw,
        base_grams: getPhotoBaseGrams(t),
        base_kcal: Number.isFinite(nextKcal) ? nextKcal : 0,
      },
    };
  });
};

const updatePhotoMacroInput = (macroKey, baseKey, rawValue) => {
  const nextRaw = sanitizePhotoNumberInput(rawValue, { decimal: true });
  const nextValue = readPhotoNumber(nextRaw);

  setPhotoResult((prev) => {
    const t = prev?.total || {};
    const nextTotal = {
      ...t,
      [macroKey]: nextRaw,
      base_grams: getPhotoBaseGrams(t),
      [baseKey]: Number.isFinite(nextValue) ? nextValue : 0,
    };

    const nextKcal = calcPhotoKcalFromMacros(nextTotal);

    return {
      ...prev,
      total: {
        ...nextTotal,
        // Если пользователь меняет БЖУ — ккал автоматически пересчитываются.
        // Поле ккал всё равно остаётся редактируемым вручную.
        kcal: nextKcal,
        base_kcal: nextKcal,
      },
    };
  });
};
useEffect(() => {
  return () => {
    clearPhotoFocusTimers();
  };
}, []);
  const [formName, setFormName] = useState("");
  const nameRef = useRef(null);
const pRef = useRef(null);
const fRef = useRef(null);
const cRef = useRef(null);
const manualFocusedRef =
  useRef(false);

const manualPrefillRef =
  useRef(false);

useLayoutEffect(() => {
  if (
    addFlow?.screen !== "manual"
  ) {
    manualFocusedRef.current =
      false;

    manualPrefillRef.current =
      false;

    return undefined;
  }

  // Когда пришёл готовый шаблон,
  // не открываем клавиатуру.
  if (
    manualPrefillRef.current
  ) {
    manualFocusedRef.current =
      true;

    manualPrefillRef.current =
      false;

    return undefined;
  }

  if (
    manualFocusedRef.current
  ) {
    return undefined;
  }

  manualFocusedRef.current =
    true;

  return focusModalInputAndOpenKeyboard(
    nameRef
  );
}, [
  addFlow?.screen,
  addFlow?.mealId,
  addFlow?.from,
]);

// Фото-экран: блокируем фон, но НЕ двигаем body и НЕ режем высоту страницы клавиатурой
useEffect(() => {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;

  // чистим след от старой CSS-правки, если она осталась в DOM
  document.getElementById("fitmeup-photo-flow-style")?.remove();
  document.body.classList.remove("fitmeup-photo-flow-open");

  if (addFlow?.screen !== "photo") return undefined;

  const html = document.documentElement;
  const body = document.body;

  const saved = {
    htmlOverflow: html.style.overflow,
    htmlOverscroll: html.style.overscrollBehavior,
    bodyOverflow: body.style.overflow,
    bodyOverscroll: body.style.overscrollBehavior,
    bodyTouchAction: body.style.touchAction,
  };

  html.style.overflow = "hidden";
  html.style.overscrollBehavior = "none";
  body.style.overflow = "hidden";
  body.style.overscrollBehavior = "none";
  body.style.touchAction = "manipulation";

  return () => {
    html.style.overflow = saved.htmlOverflow;
    html.style.overscrollBehavior = saved.htmlOverscroll;
    body.style.overflow = saved.bodyOverflow;
    body.style.overscrollBehavior = saved.bodyOverscroll;
    body.style.touchAction = saved.bodyTouchAction;
  };
}, [addFlow?.screen]);
// Фото-экран: не даём свайпу на краях скролла сворачивать Telegram Mini App
useEffect(() => {
  if (addFlow?.screen !== "photo") return undefined;

  try {
    const tg = getTg();
    tg?.expand?.();

    if (tg?.isVersionAtLeast?.("7.7")) {
      tg?.disableVerticalSwipes?.();
    }
  } catch {}

  const el = addFlowScrollRef.current;
  if (!el) return undefined;

  let startY = 0;

  const onTouchStart = (e) => {
    startY = Number(e.touches?.[0]?.clientY ?? 0);
  };

  const onTouchMove = (e) => {
    const currentY = Number(e.touches?.[0]?.clientY ?? 0);
    const dy = currentY - startY;

    const atTop = el.scrollTop <= 0;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    if ((atTop && dy > 0) || (atBottom && dy < 0)) {
      e.preventDefault();
    }
  };

  el.addEventListener("touchstart", onTouchStart, { passive: true });
  el.addEventListener("touchmove", onTouchMove, { passive: false });

  return () => {
    el.removeEventListener("touchstart", onTouchStart);
    el.removeEventListener("touchmove", onTouchMove);
  };
}, [addFlow?.screen]);
// когда пришёл результат — возвращаем внутренний скролл в начало.
// Теперь сверху нет превью/кнопок, поэтому форма сразу стоит нормально.
useEffect(() => {
  if (!photoResult) return;

  const root = addFlowScrollRef.current;
  if (!root) return;

  requestAnimationFrame(() => {
    root.scrollTo({
      top: 0,
      behavior: "auto",
    });
  });
}, [Boolean(photoResult)]);



const [formProteins, setFormProteins] = useState("");
const [formFats, setFormFats] = useState("");
const [formCarbs, setFormCarbs] = useState("");

useEffect(() => {
  return subscribeAppEvents(
    ({ type, payload }) => {
      if (
        type !==
        "manual-product:prefill"
      ) {
        return;
      }

      manualPrefillRef.current =
        true;

      setFormName(
        String(
          payload?.name ?? ""
        )
      );

      setFormProteins(
        String(
          payload?.proteins ?? ""
        )
      );

      setFormFats(
        String(
          payload?.fats ?? ""
        )
      );

      setFormCarbs(
        String(
          payload?.carbs ?? ""
        )
      );
    }
  );
}, []);

// модалка изменения граммовки конкретного блюда
  const [weightModal, setWeightModal] = useState(null); 
    // ===== Мои блюда (локальное хранилище) =====
  const [myFoodsQ, setMyFoodsQ] = useState("");

const getMyFoodsStorageKeys = () => {
  const keys = [
    k("myFoods"),
  ];

    try {
      const tgUserId = getTg()?.initDataUnsafe?.user?.id;

      if (tgUserId) {
        keys.push(`fitmeup:${tgUserId}:myFoods`);
      }
    } catch {}

    return Array.from(new Set(keys.filter(Boolean)));
  };

  const getDeletedMyFoodsStorageKeys = () =>
    getMyFoodsStorageKeys().map((key) => `${key}:deleted`);

  const normalizeMyFoodNameForStorage = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const normalizeMyFoodTypeForStorage = (foodOrType) => {
    const raw =
      typeof foodOrType === "string"
        ? foodOrType
        : getMyFoodType(foodOrType) ||
          (isMyFoodsDish(foodOrType) ? "dish" : "ingredient");

    const type = String(raw || "").trim().toLowerCase();

    if (type === "dish") return "dish";
    return "ingredient";
  };

  const getMyFoodStorageIdKey = (food) => {
    const candidates = [
      food?.id,
      food?.userFoodId,
      food?.user_food_id,
      food?.sourceMyFoodId,
      food?.myFoodId,
      food?.__deleteId,
      food?.__deleteKey,
    ];

    const found = candidates.find(
      (id) => id !== null && id !== undefined && String(id).trim() !== ""
    );

    return found == null ? "" : String(found).trim();
  };

  const buildDeletedMyFoodStorageKeys = (food) => {
    const keys = new Set();

    const idKey = getMyFoodStorageIdKey(food);
    if (idKey) keys.add(`id:${idKey}`);

    const name = normalizeMyFoodNameForStorage(
      food?.__deleteName ||
        food?.name ||
        food?.nameKey ||
        food?.name_key ||
        ""
    );

    const type = normalizeMyFoodTypeForStorage(
      food?.__deleteType || food || "ingredient"
    );

    if (name) {
      keys.add(`name:${type}:${name}`);
    }

    return keys;
  };

  const readDeletedMyFoodsSet = () => {
    const result = new Set();

    try {
      if (typeof window === "undefined") return result;

      for (const key of getDeletedMyFoodsStorageKeys()) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) {
          parsed.forEach((item) => {
            if (item) result.add(String(item));
          });
        }
      }
    } catch {}

    return result;
  };

  const writeDeletedMyFoodsSet = (marks) => {
    try {
      if (typeof window === "undefined") return;

      const raw = JSON.stringify(Array.from(marks || []));

      getDeletedMyFoodsStorageKeys().forEach((key) => {
        localStorage.setItem(key, raw);
      });
    } catch {}
  };

  const markMyFoodAsDeletedInStorage = (food) => {
    const marks = readDeletedMyFoodsSet();

    buildDeletedMyFoodStorageKeys(food).forEach((key) => {
      marks.add(key);
    });

    writeDeletedMyFoodsSet(marks);
  };

  const unmarkMyFoodFromDeletedStorage = (food) => {
    const marks = readDeletedMyFoodsSet();

    buildDeletedMyFoodStorageKeys(food).forEach((key) => {
      marks.delete(key);
    });

    writeDeletedMyFoodsSet(marks);
  };

  const isMyFoodDeletedInStorage = (food, marks = readDeletedMyFoodsSet()) => {
    for (const key of buildDeletedMyFoodStorageKeys(food)) {
      if (marks.has(key)) return true;
    }

    return false;
  };

  const readMyFoodsStorage = () => {
    try {
      if (typeof window === "undefined") return [];

      const deletedMarks = readDeletedMyFoodsSet();
      const result = [];
      const seen = new Set();

      for (const key of getMyFoodsStorageKeys()) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) continue;

        for (const rawItem of parsed) {
          if (!rawItem?.name) continue;

          if (isMyFoodDeletedInStorage(rawItem, deletedMarks)) {
            continue;
          }

          const item = normalizeMyFoodForList(rawItem);
          const idKey = getMyFoodStorageIdKey(item);
          const nameKey = normalizeMyFoodNameForStorage(item.name);
          const typeKey = normalizeMyFoodTypeForStorage(item);

          const uniqueKey = idKey
            ? `id:${idKey}`
            : `name:${typeKey}:${nameKey}`;

          if (seen.has(uniqueKey)) continue;

          seen.add(uniqueKey);
          result.push(item);
        }
      }

      return result;
    } catch {
      return [];
    }
  };

  const writeMyFoodsStorage = (items) => {
    try {
      if (typeof window === "undefined") return;

      const deletedMarks = readDeletedMyFoodsSet();

      const cleaned = (Array.isArray(items) ? items : []).filter(
        (item) => !isMyFoodDeletedInStorage(item, deletedMarks)
      );

      const raw = JSON.stringify(cleaned);

      getMyFoodsStorageKeys().forEach((key) => {
        localStorage.setItem(key, raw);
      });
    } catch {}
  };

const [myFoods, setMyFoods] = useState(() => readMyFoodsStorage());

useEffect(() => {
  writeMyFoodsStorage(myFoods);
}, [myFoods]);

useEffect(() => {
  if (!dbUserId) return;

  let cancelled = false;

  const getFoodNameTypeKey = (food) => {
    const normalized = normalizeMyFoodForList(food);

    const type = isMyFoodsDish(normalized)
      ? "dish"
      : "ingredient";

    return `${type}:${normalizeFoodName(normalized?.name)}`;
  };

  const hasDishComposition = (food) => {
    if (!isMyFoodsDish(food)) return false;

    const hasIngredients =
      Array.isArray(food?.ingredients) &&
      food.ingredients.length > 0;

    const hasRecipeComponents =
      Array.isArray(food?.recipe?.components) &&
      food.recipe.components.length > 0;

    return hasIngredients || hasRecipeComponents;
  };

  (async () => {
    try {
      let serverFoods = await apiListUserFoods();

      if (cancelled) return;

      const makeMigrationNameKey = (value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");

      // Переносим в БД локальные записи именно с этого устройства.
      const localFoodsForMigration = removeExactIdDuplicates(
        readMyFoodsStorage()
      );

      const serverByName = new Map(
        (serverFoods || []).map((food) => [
          makeMigrationNameKey(food?.name),
          normalizeMyFoodForList(food),
        ])
      );

      for (const localFood of localFoodsForMigration) {
        if (cancelled) return;
        if (!localFood?.name) continue;

        const nameKey = makeMigrationNameKey(localFood.name);
        if (!nameKey) continue;

        const serverFood = serverByName.get(nameKey) || null;

        const localIdNumber = Number(
          getMyFoodIdKey(localFood)
        );

        const hasRealServerId =
          Number.isFinite(localIdNumber) &&
          localIdNumber > 0;

        const shouldUpload =
          (!serverFood && !hasRealServerId) ||
          (
            serverFood &&
            hasDishComposition(localFood) &&
            !hasDishComposition(serverFood)
          );

        if (!shouldUpload) continue;

        try {
          const savedFood = await apiUpsertUserFood({
            ...localFood,
            id: null,
          });

          serverByName.set(nameKey, savedFood);
        } catch (migrationError) {
          console.error(
            "migrate local user food failed:",
            localFood.name,
            migrationError
          );
        }
      }

      // Забираем уже обновлённый общий список из БД.
      serverFoods = await apiListUserFoods();

      if (cancelled) return;

      setMyFoods((prev) => {
        const localList = removeExactIdDuplicates(prev || []);

        const serverList = removeExactIdDuplicates(
          Array.isArray(serverFoods) ? serverFoods : []
        );

        const localById = new Map();
        const localByNameType = new Map();

        localList.forEach((localFood) => {
          const idKey = getMyFoodIdKey(localFood);
          const nameTypeKey = getFoodNameTypeKey(localFood);

          if (idKey) {
            localById.set(String(idKey), localFood);
          }

          if (nameTypeKey) {
            localByNameType.set(nameTypeKey, localFood);
          }
        });

        const serverIdKeys = new Set();
        const serverNameTypeKeys = new Set();

        const mergedServerFoods = serverList.map((serverFood) => {
          const serverIdKey = getMyFoodIdKey(serverFood);
          const nameTypeKey = getFoodNameTypeKey(serverFood);

          if (serverIdKey) {
            serverIdKeys.add(String(serverIdKey));
          }

          if (nameTypeKey) {
            serverNameTypeKeys.add(nameTypeKey);
          }

          const localFood =
            (serverIdKey
              ? localById.get(String(serverIdKey))
              : null) ||
            localByNameType.get(nameTypeKey) ||
            null;

          if (!localFood) {
            return normalizeMyFoodForList(serverFood);
          }

          const keepLocalDishComposition =
            hasDishComposition(localFood) &&
            !hasDishComposition(serverFood);

          return normalizeMyFoodForList({
            ...localFood,
            ...serverFood,

            // Серверный numeric id становится основным.
            id: serverFood.id ?? localFood.id,

            // Старые блюда на сервере могли быть без состава.
            // До миграции сохраняем полный локальный состав телефона.
            ingredients: keepLocalDishComposition
              ? localFood.ingredients
              : serverFood.ingredients,

            recipe: keepLocalDishComposition
              ? localFood.recipe
              : serverFood.recipe,
          });
        });

        const localOnlyFoods = localList.filter((localFood) => {
          const localIdKey = getMyFoodIdKey(localFood);
          const nameTypeKey = getFoodNameTypeKey(localFood);

          const existsOnServerById =
            localIdKey &&
            serverIdKeys.has(String(localIdKey));

          const existsOnServerByName =
            nameTypeKey &&
            serverNameTypeKeys.has(nameTypeKey);

          const localIdNumber = Number(localIdKey);

          const isLegacyLocalFood =
            !Number.isFinite(localIdNumber) ||
            localIdNumber <= 0;

          return (
            isLegacyLocalFood &&
            !existsOnServerById &&
            !existsOnServerByName
          );
        });

        const deletedMarks = readDeletedMyFoodsSet();

        const merged = [
          ...mergedServerFoods,
          ...localOnlyFoods,
        ].filter(
          (food) =>
            !isMyFoodDeletedInStorage(food, deletedMarks)
        );

        return removeExactIdDuplicates(merged);
      });
    } catch (e) {
      console.error(
        "load user foods from server failed:",
        e
      );

      // При ошибке интернета оставляем локальные данные.
    }
  })();

  return () => {
    cancelled = true;
  };
}, [dbUserId]);

const normalizeFoodName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isRealFoodId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) && n > 0;
};
const isSingleProductFood = (food) =>
  isMyFoodsSingleProduct(normalizeMyFoodForList(food));
const sameMacroValue = (a, b) => {
  const left = Number(a ?? 0);
  const right = Number(b ?? 0);

  return Math.abs(left - right) < 0.01;
};

const samePer100 = (a, b) => {
  const left = a || {};
  const right = b || {};

  return (
    sameMacroValue(left.proteins, right.proteins) &&
    sameMacroValue(left.fats, right.fats) &&
    sameMacroValue(left.carbs, right.carbs) &&
    sameMacroValue(left.kcal, right.kcal)
  );
};

const makeLocalMyFoodId = (food) => {
  const prefix = isMyFoodsDish(food) ? "dish" : "food";
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const getMyFoodIdKey = (food) => {
  const candidates = [
    food?.id,
    food?.userFoodId,
    food?.user_food_id,
    food?.sourceMyFoodId,
    food?.myFoodId,
  ];

  const found = candidates.find((id) => hasMyFoodId(id));
  return found == null ? "" : String(found).trim();
};
const getMealItemDeletedLibraryMarker = (item, displayFood) => {
  const isDish =
    item?.type === "dish" ||
    displayFood?.type === "dish" ||
    item?.recipe ||
    displayFood?.recipe ||
    (Array.isArray(item?.ingredients) && item.ingredients.length > 0) ||
    (Array.isArray(displayFood?.ingredients) && displayFood.ingredients.length > 0);

  const idKey = String(
    item?.userFoodId ??
      item?.sourceMyFoodId ??
      item?.myFoodId ??
      displayFood?.userFoodId ??
      displayFood?.sourceMyFoodId ??
      displayFood?.myFoodId ??
      displayFood?.id ??
      ""
  ).trim();

  const name = String(displayFood?.name || item?.name || "").trim();
  const type = isDish ? "dish" : "ingredient";

  return {
    id: idKey,
    name,
    type,
    __deleteId: idKey,
    __deleteName: name,
    __deleteType: type,
  };
};

const isMealItemDeletedFromMyFoods = (item, displayFood) => {
  const marker = getMealItemDeletedLibraryMarker(item, displayFood);

  const oppositeMarker = {
    ...marker,
    type: marker.type === "dish" ? "ingredient" : "dish",
    __deleteType: marker.type === "dish" ? "ingredient" : "dish",
  };

  if (
    isMyFoodDeletedInStorage(marker) ||
    isMyFoodDeletedInStorage(oppositeMarker)
  ) {
    return true;
  }

  const linkedId = String(marker.id || "").trim();

  if (!linkedId) return false;

  return !(myFoods || []).some(
    (food) => String(getMyFoodIdKey(food)) === String(linkedId)
  );
};

const showDeletedMealItemEditAlert = (item, displayFood) => {
  const marker = getMealItemDeletedLibraryMarker(item, displayFood);
  const label = marker.type === "dish" ? "Блюдо" : "Продукт";
  const name = marker.name || item?.name || displayFood?.name || "Эта позиция";

  safeAlert(
    `${label} "${name}" удалено из общего списка «Мои продукты и блюда». Редактировать его из рациона нельзя. Удали эту позицию из рациона и добавь заново, если нужно изменить её данные.`,
    "Редактирование недоступно"
  );
};
// Чистим только реальные дубли с одинаковым id.
// Никаких сравнений по названию, КБЖУ, граммовке или составу.
const removeExactIdDuplicates = (items) => {
  const result = [];
  const seenIds = new Set();

  for (const rawItem of items || []) {
    const item = normalizeMyFoodForList(rawItem);
    if (!item?.name) continue;

    const idKey = getMyFoodIdKey(item);

    if (idKey) {
      if (seenIds.has(idKey)) continue;
      seenIds.add(idKey);
    }

    result.push(item);
  }

  return result;
};

// CREATE: создание новой записи.
// Не ищет похожие продукты/блюда.
const createMyFood = (food) => {
  if (!food?.name) return;

  const incoming = normalizeMyFoodForList({
    ...food,
    id: hasMyFoodId(food.id) ? food.id : makeLocalMyFoodId(food),
    updatedAt: Date.now(),
  });

  unmarkMyFoodAsDeleted(incoming);

  setMyFoods((prev) => {
    const list = removeExactIdDuplicates(prev || []);
    const incomingId = getMyFoodIdKey(incoming);

    // Если id уже есть, это физически та же запись.
    // Такое возможно, если сервер вернул уже существующий id.
    const existingIdx = incomingId
      ? list.findIndex((x) => getMyFoodIdKey(x) === incomingId)
      : -1;

    if (existingIdx !== -1) {
      const copy = list.slice();

      copy[existingIdx] = normalizeMyFoodForList({
        ...copy[existingIdx],
        ...incoming,
        id: copy[existingIdx].id,
        updatedAt: Date.now(),
      });

      return copy;
    }

    return [incoming, ...list];
  });
};

// UPDATE: редактирование конкретной записи.
// Обновляет только объект с таким же id.
const updateMyFood = (food) => {
  if (!food?.name) return;

  const incoming = normalizeMyFoodForList({
    ...food,
    id: hasMyFoodId(food.id) ? food.id : makeLocalMyFoodId(food),
    updatedAt: Date.now(),
  });

  const incomingId = getMyFoodIdKey(incoming);

  if (!incomingId) {
    createMyFood(incoming);
    return;
  }

  setMyFoods((prev) => {
    const list = removeExactIdDuplicates(prev || []);

    const idx = list.findIndex(
      (x) => getMyFoodIdKey(x) === incomingId
    );

    if (idx === -1) {
      return [incoming, ...list];
    }

    const copy = list.slice();

    copy[idx] = normalizeMyFoodForList({
      ...copy[idx],
      ...incoming,
      id: copy[idx].id,
      updatedAt: Date.now(),
    });

    return copy;
  });
};

// Если пользователь заново создал такой же продукт/блюдо,
// разрешаем ему снова появляться в "Моих продуктах".
const unmarkMyFoodAsDeleted = (food) => {
  unmarkMyFoodFromDeletedStorage(food);
};

const normalizeMyFoodDeleteType = (value) => {
  const raw =
    typeof value === "string"
      ? value
      : getMyFoodType(value) || (isMyFoodsDish(value) ? "dish" : "ingredient");

  const type = String(raw || "").trim().toLowerCase();

  if (type === "product") return "ingredient";
  if (type === "dish") return "dish";

  return "ingredient";
};

const getDeleteTargetFromMyFood = (foodOrId) => {
  const selectedFood =
    foodOrId && typeof foodOrId === "object"
      ? normalizeMyFoodForList(foodOrId)
      : null;

  const idKey = selectedFood
    ? String(
        selectedFood.__deleteId ??
          selectedFood.__deleteKey ??
          getMyFoodIdKey(selectedFood) ??
          ""
      ).trim()
    : String(foodOrId ?? "").trim();

  const name = normalizeFoodName(
    selectedFood?.__deleteName || selectedFood?.name || ""
  );

  const type = normalizeMyFoodDeleteType(
    selectedFood?.__deleteType || selectedFood || "ingredient"
  );

  return {
    selectedFood,
    idKey,
    name,
    type,
  };
};

const deleteMyFood = async (foodOrId) => {
  const target = getDeleteTargetFromMyFood(foodOrId);

  if (!target.idKey && !target.name) {
    safeAlert("Не получилось определить, что удалять.", "Удаление");
    return;
  }

  const oldFoods = removeExactIdDuplicates(myFoods || []);

  const isSameFoodForDelete = (rawItem) => {
    const item = normalizeMyFoodForList(rawItem);
    const itemIdKey = getMyFoodIdKey(item);

    const sameById =
      target.idKey &&
      itemIdKey &&
      String(itemIdKey) === String(target.idKey);

    const sameByNameAndType =
      target.name &&
      normalizeFoodName(item?.name) === target.name &&
      normalizeMyFoodDeleteType(item) === target.type;

    return Boolean(sameById || sameByNameAndType);
  };

  const removedItems = oldFoods.filter(isSameFoodForDelete);

  if (removedItems.length === 0) {
    safeAlert(
      "Не нашёл этот продукт или блюдо в списке. Обнови страницу и попробуй ещё раз.",
      "Удаление"
    );
    return;
  }

  const nextFoods = oldFoods.filter((item) => !isSameFoodForDelete(item));

  const realServerFood =
    removedItems.find((item) => isRealFoodId(item?.id)) || null;

  const firstRemoved = removedItems[0] || null;

  const deleteMarker = {
    ...(target.selectedFood || {}),
    ...(firstRemoved || {}),
    id:
      realServerFood?.id ??
      target.selectedFood?.id ??
      firstRemoved?.id ??
      target.idKey,
    name:
      target.selectedFood?.__deleteName ||
      target.selectedFood?.name ||
      firstRemoved?.name ||
      target.name,
    type: target.type,
    __deleteId: target.idKey,
    __deleteName:
      target.selectedFood?.__deleteName ||
      target.selectedFood?.name ||
      firstRemoved?.name ||
      target.name,
    __deleteType: target.type,
  };
const finishLocalDelete = () => {
  markMyFoodAsDeletedInStorage(deleteMarker);

  removedItems.forEach((item) => {
    markMyFoodAsDeletedInStorage(item);
  });

  setMyFoods(nextFoods);
  writeMyFoodsStorage(nextFoods);
};
  const serverTarget = {
    ...deleteMarker,
    id:
      realServerFood?.id ??
      target.selectedFood?.id ??
      firstRemoved?.id ??
      target.idKey,
    name:
      target.selectedFood?.__deleteName ||
      target.selectedFood?.name ||
      firstRemoved?.name ||
      target.name,
    nameKey: target.name,
    type: target.type,
  };

  try {
    const serverResult = await apiDeleteUserFood(serverTarget);

    const hadRealServerId =
      isRealFoodId(target.idKey) ||
      removedItems.some((item) => isRealFoodId(getMyFoodIdKey(item)));

    if (hadRealServerId && serverResult?.deleted === false) {
      throw new Error("Сервер не нашёл продукт в user_foods");
    }

    markMyFoodAsDeletedInStorage(deleteMarker);

    removedItems.forEach((item) => {
      markMyFoodAsDeletedInStorage(item);
    });

    setMyFoods(nextFoods);
    writeMyFoodsStorage(nextFoods);
  } catch (e) {
    console.error("deleteMyFood failed:", e);

    safeAlert(
      "Не удалось удалить продукт на сервере. Проверь интернет и попробуй ещё раз.",
      "Ошибка"
    );
  }
};
const buildMealItemFromUpdatedFood = (item, updatedFood) => {
  const grams = Math.max(
    1,
    Number(item.weight ?? updatedFood.defaultGrams ?? 100)
  );

  const per100 = updatedFood.per100 || {};
  const k = grams / 100;

  const proteins = +((Number(per100.proteins || 0)) * k).toFixed(1);
  const fats = +((Number(per100.fats || 0)) * k).toFixed(1);
  const carbs = +((Number(per100.carbs || 0)) * k).toFixed(1);

  const kcalPer100 =
    per100.kcal ??
    Number(per100.proteins || 0) * 4 +
      Number(per100.fats || 0) * 9 +
      Number(per100.carbs || 0) * 4;

  const kcal = Math.round(Number(kcalPer100 || 0) * k);
  const isDish = updatedFood.type === "dish";

return {
  ...item,
  type: updatedFood.type || item.type,
  name: updatedFood.name || item.name,
  weight: grams,
  proteins,
  fats,
  carbs,
  kcal,
  sourceMyFoodId: getMyFoodIdKey(updatedFood) || item.sourceMyFoodId,
  userFoodId: isRealFoodId(updatedFood.id)
    ? Number(updatedFood.id)
    : item.userFoodId,
    ...(isDish
      ? {
          recipe: updatedFood.recipe,
          ingredients: updatedFood.ingredients,
        }
      : {}),
  };
};

const syncTodayMealItemsFromMyFood = async (updatedFood, previousFood) => {
  if (!updatedFood) return;

const updatedIdKey = getMyFoodIdKey(updatedFood);
const previousIdKey = getMyFoodIdKey(previousFood);

const shouldUpdateMealItem = (item) => {
    if (
    isMyFoodsDish(updatedFood) ||
    item?.type === "dish" ||
    item?.entryType === "dish" ||
    getEntrySnapshotFromItem(item)
  ) {
    return false;
  }
  const itemUserFoodIdKey =
    item.userFoodId !== null &&
    item.userFoodId !== undefined &&
    String(item.userFoodId).trim() !== "" &&
    String(item.userFoodId) !== "NaN"
      ? String(item.userFoodId)
      : "";

  const itemSourceMyFoodIdKey = String(
    item.sourceMyFoodId || item.myFoodId || ""
  ).trim();

  return (
    Boolean(updatedIdKey) &&
    (itemUserFoodIdKey === updatedIdKey ||
      itemSourceMyFoodIdKey === updatedIdKey)
  ) || (
    Boolean(previousIdKey) &&
    (itemUserFoodIdKey === previousIdKey ||
      itemSourceMyFoodIdKey === previousIdKey)
  );
};

  const updates = [];

  const nextMeals = (meals || []).map((meal) => ({
    ...meal,
    items: (meal.items || []).map((item) => {
      if (!shouldUpdateMealItem(item)) return item;

      const nextItem = buildMealItemFromUpdatedFood(item, updatedFood);

      if (isRealFoodId(item.id)) {
        updates.push({
          itemId: item.id,
          payload: {
            grams: nextItem.weight,
            kcal: nextItem.kcal,
            proteins: nextItem.proteins,
            fats: nextItem.fats,
            carbs: nextItem.carbs,
            userFoodId: isRealFoodId(nextItem.userFoodId)
              ? Number(nextItem.userFoodId)
              : null,
          },
        });
      }

      return nextItem;
    }),
  }));

  if (!updates.length) {
    setMeals(nextMeals);
    return;
  }

  const oldMeals = meals;
  setMeals(nextMeals);

  try {
    await Promise.all(
      updates.map(({ itemId, payload }) => apiUpdateFoodEntry(itemId, payload))
    );
  } catch (e) {
    console.error("syncTodayMealItemsFromMyFood failed:", e);
    setMeals(oldMeals);
    safeAlert("Не удалось обновить продукт в сегодняшнем дне.", "Ошибка");
  }
};

const saveMyFoodAndSyncToday = async (updatedFood) => {
  if (!updatedFood) return;

  const updatedIdKey = getMyFoodIdKey(updatedFood);

  const previousFood = (myFoods || []).find((food) => {
    const foodIdKey = getMyFoodIdKey(food);

    return (
      (updatedIdKey && foodIdKey === updatedIdKey) ||
      (updatedFood.id != null && String(food.id) === String(updatedFood.id))
    );
  });

  const previousIdKey = getMyFoodIdKey(previousFood);

  const nextFood = normalizeMyFoodForList({
    ...(previousFood || {}),
    ...updatedFood,
    id: updatedFood.id ?? previousFood?.id,
    updatedAt: Date.now(),
  });

  updateMyFood(nextFood);

  if (isMyFoodsSingleProduct(nextFood)) {
    syncTodayMealItemsFromMyFood(
      nextFood,
      previousFood
    );
  }

  try {
    const savedFood = await apiUpsertUserFood(nextFood);

    const normalizedFood = normalizeMyFoodForList({
      ...nextFood,
      ...savedFood,
      id: savedFood?.id ?? nextFood.id,
      per100: savedFood?.per100 || nextFood.per100,
      defaultGrams: Number(savedFood?.defaultGrams ?? nextFood.defaultGrams ?? 100),
      updatedAt: Date.now(),
    });

    const savedIdKey = getMyFoodIdKey(normalizedFood);

    // Важно: если сервер вернул новый numeric id вместо старого local id,
    // заменяем старую запись, а не добавляем вторую такую же.
    setMyFoods((prev) => {
      const list = removeExactIdDuplicates(prev || []);
      const nextList = [];
      let inserted = false;

      for (const item of list) {
        const itemIdKey = getMyFoodIdKey(item);

        const sameRecord =
          (previousIdKey && itemIdKey === previousIdKey) ||
          (updatedIdKey && itemIdKey === updatedIdKey) ||
          (savedIdKey && itemIdKey === savedIdKey);

        if (sameRecord) {
          if (!inserted) {
            nextList.push(normalizedFood);
            inserted = true;
          }
          continue;
        }

        nextList.push(item);
      }

      if (!inserted) {
        nextList.unshift(normalizedFood);
      }

      return nextList;
    });

    if (isMyFoodsSingleProduct(normalizedFood)) {
      syncTodayMealItemsFromMyFood(
        normalizedFood,
        previousFood
      );
    }
  } catch (e) {
    console.error("saveMyFoodAndSyncToday failed:", e);
    safeAlert(
      "Изменения появились на экране, но не сохранились на сервере.",
      "Ошибка"
    );
  }
};
const createDishInMyFoodsAndServer = async (dish) => {
  if (!dish?.name) {
    throw new Error("empty dish name");
  }

const forceCreateNew = Boolean(
  dish.__saveAsNew ||
    dish.__sourceDishId ||
    dish.__sourceItemId
);

const dishNameWasEdited = Boolean(dish.__dishNameWasEdited);

const cleanDish = (() => {
  const {
    __saveAsNew,
    __dishNameWasEdited,
    __sourceDishId,
    __sourceItemId,
    __sourceMealId,
    __serverId,
    ...rest
  } = dish || {};

  return rest;
})();

  const getExistingDishNames = () =>
    new Set(
      (myFoods || [])
        .map(normalizeMyFoodForList)
        .filter((food) => isMyFoodsDish(food))
        .map((food) => normalizeFoodName(food.name))
        .filter(Boolean)
    );

  const stripCopyNumber = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+\(\d+\)$/u, "")
      .trim();

  const makeUniqueDishNameForCreate = (baseName, options = {}) => {
    const { alwaysAddNumber = false } = options;

    const cleanName =
      String(baseName || "Новое блюдо").trim() || "Новое блюдо";

    const existingNames = getExistingDishNames();
    const normalizedCleanName = normalizeFoodName(cleanName);

    // Обычное создание:
    // если имени ещё нет — оставляем как есть.
    if (!alwaysAddNumber && !existingNames.has(normalizedCleanName)) {
      return cleanName;
    }

    // Сохранить как новое:
    // всегда делаем "Название (1)", даже если исходного блюда нет в myFoods,
    // потому что оно может быть только в рационе/снимке блюда.
    const baseWithoutNumber = stripCopyNumber(cleanName) || cleanName;

    let index = 1;
    let candidate = `${baseWithoutNumber} (${index})`;

    while (existingNames.has(normalizeFoodName(candidate))) {
      index += 1;
      candidate = `${baseWithoutNumber} (${index})`;
    }

    return candidate;
  };

const finalName = makeUniqueDishNameForCreate(cleanDish.name, {
  alwaysAddNumber: forceCreateNew && !dishNameWasEdited,
});

  const nameWasChangedForUniqueness =
    normalizeFoodName(finalName) !== normalizeFoodName(cleanDish.name);

  const shouldProtectFromServerOverwrite =
    forceCreateNew || nameWasChangedForUniqueness;

  const cleanDishForSave = {
    ...cleanDish,
    name: finalName,
  };

  const localCopyId = makeLocalMyFoodId({
    ...cleanDishForSave,
    type: "dish",
  });

  const dishForSave = normalizeMyFoodForList({
    ...cleanDishForSave,

    // ВАЖНО:
    // при создании копии или при автопереименовании не отправляем старый id,
    // иначе backend может обновить существующее блюдо.
    id: shouldProtectFromServerOverwrite
      ? null
      : isRealFoodId(cleanDishForSave.id)
        ? Number(cleanDishForSave.id)
        : null,

    type: "dish",
    updatedAt: Date.now(),
  });

  const savedDish = await apiUpsertUserFood(dishForSave);

  const savedDishApp = normalizeMyFoodForList({
    ...dishForSave,
    ...savedDish,

    // Если backend вернул id существующего блюда, ниже мы защитимся локальным id.
    id: savedDish?.id ?? localCopyId,

    type: "dish",

    // ВАЖНО:
    // никогда не даём серверу вернуть старое имя поверх нашего "Название (1)".
    name: dishForSave.name,

    per100: savedDish?.per100 || dishForSave.per100,
    defaultGrams: Number(
      savedDish?.defaultGrams ??
        savedDish?.default_grams ??
        dishForSave.defaultGrams ??
        100
    ),

    // сервер часто возвращает только КБЖУ, состав держим локально
    ingredients: cleanDishForSave.ingredients || dishForSave.ingredients || [],
    recipe: cleanDishForSave.recipe || dishForSave.recipe || null,

    lastMeal: savedDish?.lastMeal ?? dishForSave.lastMeal ?? null,
    updatedAt: Date.now(),
  });

  if (shouldProtectFromServerOverwrite) {
    const savedIdKey = getMyFoodIdKey(savedDishApp);

    const serverIdAlreadyExists = Boolean(
      savedIdKey &&
        (myFoods || []).some(
          (item) => String(getMyFoodIdKey(item)) === String(savedIdKey)
        )
    );

    const finalDishApp = serverIdAlreadyExists
      ? normalizeMyFoodForList({
          ...savedDishApp,
          id: localCopyId,
          __serverId: savedIdKey,
          name: dishForSave.name,
          updatedAt: Date.now(),
        })
      : savedDishApp;

    const finalIdKey = getMyFoodIdKey(finalDishApp);

    setMyFoods((prev) => {
      const list = removeExactIdDuplicates(prev || []);

      return [
        finalDishApp,
        ...list.filter((item) => {
          const key = getMyFoodIdKey(item);
          if (!key) return true;

          // Убираем только физический дубль новой записи.
          // Старое блюдо с исходным названием НЕ трогаем.
          return String(key) !== String(finalIdKey);
        }),
      ];
    });

    return finalDishApp;
  }

  createMyFood(savedDishApp);

  return savedDishApp;
};

const addFoodToMealFromMyFoods = async (mealId, rawFood) => {
  const food = normalizeMyFoodForList(rawFood);

  const grams = Math.max(1, Number(food.defaultGrams ?? 100));
  const k = grams / 100;

  const proteins = +((food.per100?.proteins || 0) * k).toFixed(1);
  const fats = +((food.per100?.fats || 0) * k).toFixed(1);
  const carbs = +((food.per100?.carbs || 0) * k).toFixed(1);

  const kcalPer100 =
    food.per100?.kcal ??
    (food.per100?.proteins || 0) * 4 +
      (food.per100?.fats || 0) * 9 +
      (food.per100?.carbs || 0) * 4;

  const kcal = Math.round(kcalPer100 * k);

  const mealTitle =
    meals.find((m) => Number(m.id) === Number(mealId))?.title || "";

  const mealSlot = mealSlotFromTitle(mealTitle);

  const foodIdKey = getMyFoodIdKey(food);
  const foodIdNumber = isRealFoodId(food.id) ? Number(food.id) : null;
  const isDish = isMyFoodsDish(food);

  const dishForEntry = isDish
    ? {
        ...food,
        weight: grams,
        proteins,
        fats,
        carbs,
        kcal,
      }
    : null;

  const dishSnapshot = isDish
    ? makeMealDishSnapshot(dishForEntry, { userFoodId: foodIdNumber }, false)
    : null;

  try {
    const row = await apiAddFoodEntry({
      day: dayKey,
      mealSlot,
      entry_type: isDish ? "dish" : "ingredient",
      sourceUserFoodId: isDish ? foodIdNumber : null,
      name: food.name,
      grams,
      kcal,
      proteins,
      fats,
      carbs,
      userFoodId: isDish ? null : foodIdNumber,
      dishSnapshot,
    });

    const newItem = {
      ...dbFoodEntryToItem(row),
      type: isDish ? "dish" : food.type,
      recipe: dishSnapshot?.recipe || food.recipe,
      ingredients: dishSnapshot?.ingredients || food.ingredients,
      sourceMyFoodId: foodIdKey,
      sourceUserFoodId: isDish ? foodIdNumber : null,
      userFoodId: isDish ? null : foodIdNumber,
      dishSnapshot,
      dish_snapshot: dishSnapshot,
    };

    setMeals((prev) =>
      prev.map((m) =>
        Number(m.id) !== Number(mealId)
          ? m
          : { ...m, items: [...(m.items || []), newItem] }
      )
    );

    const usageStat = bumpMyFoodUsageStat(food);

    if (isMyFoodsSingleProduct(food)) {
      const savedFoodResp = await apiUpsertUserFood({
        ...food,
        id: foodIdNumber,
        type: food.type || "ingredient",
        lastMeal: mealTitle,
        defaultGrams: grams,
        useCount: usageStat.count,
        uses: usageStat.count,
        lastUsedAt: usageStat.lastUsedAt,
      });

      const savedFood = savedFoodResp?.data ? savedFoodResp.data : savedFoodResp;

      updateMyFood({
        ...food,
        ...savedFood,
        type: food.type || "ingredient",
        lastMeal: mealTitle,
        updatedAt: Date.now(),
        defaultGrams: Number(
          savedFood?.default_grams ??
            savedFood?.defaultGrams ??
            grams
        ),
        useCount: usageStat.count,
        uses: usageStat.count,
        lastUsedAt: usageStat.lastUsedAt,
      });
} else {
  try {
    const savedFoodResp =
      foodIdNumber
        ? await apiUpsertUserFood({
            ...food,
            id: foodIdNumber,
            type: "dish",
            lastMeal: mealTitle,
            defaultGrams:
              food.defaultGrams ?? grams,
          })
        : null;

    const savedFood =
      savedFoodResp?.data
        ? savedFoodResp.data
        : savedFoodResp;

    updateMyFood({
      ...food,
      ...(savedFood || {}),
      type: "dish",
      lastMeal: mealTitle,
      updatedAt: Date.now(),
      defaultGrams: Number(
        savedFood?.defaultGrams ??
          savedFood?.default_grams ??
          food.defaultGrams ??
          grams
      ),
      useCount: usageStat.count,
      uses: usageStat.count,
      lastUsedAt: usageStat.lastUsedAt,
    });
  } catch (e) {
    console.warn(
      "dish lastMeal save failed:",
      e
    );

    updateMyFood({
      ...food,
      lastMeal: mealTitle,
      updatedAt: Date.now(),
      defaultGrams:
        food.defaultGrams ?? grams,
      useCount: usageStat.count,
      uses: usageStat.count,
      lastUsedAt: usageStat.lastUsedAt,
    });
  }
}
  } catch (e) {
    console.error("save food entry error:", e);
    safeAlert("Не удалось сохранить еду. Проверь интернет/сервер.", "Ошибка");
  }
};

    const handleEditItemWeight = (mealId, itemId) => {
    const meal = meals.find((m) => m.id === mealId);
    if (!meal) return;
    const item = meal.items.find((it) => it.id === itemId);
    if (!item) return;

    const { isDish, displayFood } = getMealItemCardData(item);

if (isDish) {
  safeAlert(
    "Вес блюда считается из ингредиентов. Откройте состав блюда и измените граммовку нужного ингредиента.",
    "Блюдо"
  );
  return;
}



    const initial =
      item && typeof item.weight === "number" ? String(item.weight) : "";

    // просто открываем нашу модалку, без prompt
    setWeightModal({
      mealId,
      itemId,
      initial,
    });
    };

const getDishForMealItem = (item) => {
  if (!item) return null;

  const snapshot = getEntrySnapshotFromItem(item);

if (snapshot) {
  const currentIngredients = Array.isArray(item.ingredients)
    ? item.ingredients
    : Array.isArray(snapshot.ingredients)
    ? snapshot.ingredients
    : [];

  return normalizeMyFoodForList({
    ...snapshot,
    ...item,
    type: "dish",
    name: item.name || snapshot.name || "Блюдо",
    recipe: item.recipe || snapshot.recipe || null,
    ingredients: currentIngredients,
    defaultGrams: Number(
      item.weight ?? item.grams ?? snapshot.grams ?? item.defaultGrams ?? 100
    ),
    weight: Number(item.weight ?? item.grams ?? snapshot.grams ?? 100),
    proteins: Number(item.proteins ?? snapshot?.totals?.proteins ?? 0),
    fats: Number(item.fats ?? snapshot?.totals?.fats ?? 0),
    carbs: Number(item.carbs ?? snapshot?.totals?.carbs ?? 0),
    kcal: Number(item.kcal ?? snapshot?.totals?.kcal ?? 0),
  });
}

  const itemSourceId = String(
    item.sourceMyFoodId || item.myFoodId || item.sourceUserFoodId || ""
  ).trim();

  const linkedDishBySourceId = itemSourceId
    ? (myFoods || []).find(
        (food) =>
          getMyFoodIdKey(food) === itemSourceId &&
          isMyFoodsDish(food)
      )
    : null;

  const itemUserFoodIdKey =
    item.userFoodId !== null &&
    item.userFoodId !== undefined &&
    String(item.userFoodId).trim() !== "" &&
    String(item.userFoodId) !== "NaN"
      ? String(item.userFoodId)
      : "";

  const linkedDishByUserFoodId = itemUserFoodIdKey
    ? (myFoods || []).find(
        (food) =>
          getMyFoodIdKey(food) === itemUserFoodIdKey &&
          isMyFoodsDish(food)
      )
    : null;

  const itemLooksLikeDish =
    isMyFoodsDish(item) ||
    item.type === "dish" ||
    item.entryType === "dish" ||
    Boolean(item.recipe) ||
    (Array.isArray(item.ingredients) && item.ingredients.length > 0);

  const linkedDishByName =
    !linkedDishBySourceId && !linkedDishByUserFoodId
      ? (myFoods || []).find(
          (food) =>
            isMyFoodsDish(food) &&
            normalizeFoodName(food.name) === normalizeFoodName(item.name)
        )
      : null;

  const linkedDish =
    linkedDishBySourceId || linkedDishByUserFoodId || linkedDishByName || null;

  const looksLikeDish = itemLooksLikeDish || Boolean(linkedDish);

  if (!looksLikeDish) return null;

  return normalizeMyFoodForList({
    ...linkedDish,
    ...item,
    type: "dish",
    name: item.name || linkedDish?.name,
    recipe: item.recipe || linkedDish?.recipe,
    ingredients: item.ingredients || linkedDish?.ingredients,
    defaultGrams: Number(
      item.weight ??
        item.grams ??
        item.defaultGrams ??
        linkedDish?.defaultGrams ??
        100
    ),
  });
};
const getMealItemCardData = (item) => {
  const dishForDetails = getDishForMealItem(item);
  const isDish = Boolean(dishForDetails);

  const grams = Math.max(
    1,
    Number(item.weight ?? item.grams ?? dishForDetails?.defaultGrams ?? 100)
  );

  const proteins = Number(item.proteins ?? dishForDetails?.proteins ?? 0);
  const fats = Number(item.fats ?? dishForDetails?.fats ?? 0);
  const carbs = Number(item.carbs ?? dishForDetails?.carbs ?? 0);
  const kcal = Number(item.kcal ?? dishForDetails?.kcal ?? 0);

  const per100 = {
    proteins: +((proteins / grams) * 100).toFixed(1),
    fats: +((fats / grams) * 100).toFixed(1),
    carbs: +((carbs / grams) * 100).toFixed(1),
    kcal: Math.round((kcal / grams) * 100),
  };

const displayFood = normalizeMyFoodForList({
  ...(dishForDetails || {}),
  ...item,
  id: item.id,
  type: isDish ? "dish" : item.type || "ingredient",
  name: item.name || dishForDetails?.name || "Без названия",
  defaultGrams: grams,
  per100,
  recipe: item.recipe || dishForDetails?.recipe,
  ingredients: item.ingredients || dishForDetails?.ingredients,
});
  return {
    isDish,
    dishForDetails: isDish ? displayFood : null,
    displayFood,
    m: {
      grams,
      p: proteins,
      f: fats,
      c: carbs,
      kcal,
    },
    ingredientCount: isDish
      ? buildDishIngredientsList(displayFood, myFoods).length
      : 0,
  };
};
const saveMealDishName = async (updatedDish) => {
  if (!editingMealDish || !updatedDish) return;

  const mealId = editingMealDish.mealId;
  const itemId = editingMealDish.itemId;
  const nextName = String(updatedDish.name || "").trim();

  if (!nextName) {
    safeAlert("Введите название блюда.", "Редактирование");
    return;
  }

  const meal = meals.find((m) => String(m.id) === String(mealId));
  const oldItem = meal?.items?.find((it) => String(it.id) === String(itemId));

  if (!oldItem) {
    setEditingMealDish(null);
    return;
  }

  const oldMeals = meals;

  setMeals((prev) =>
    prev.map((m) =>
      String(m.id) !== String(mealId)
        ? m
        : {
            ...m,
            items: (m.items || []).map((it) =>
              String(it.id) !== String(itemId)
                ? it
                : {
                    ...it,
                    type: "dish",
                    name: nextName,
                  }
            ),
          }
    )
  );

  setEditingMealDish(null);

  try {
    if (isRealFoodId(itemId)) {
      await apiUpdateFoodEntry(itemId, {
        name: nextName,
        grams: oldItem.weight,
        kcal: oldItem.kcal,
        proteins: oldItem.proteins,
        fats: oldItem.fats,
        carbs: oldItem.carbs,
        userFoodId:
          oldItem.userFoodId != null ? Number(oldItem.userFoodId) : null,
      });
    }
  } catch (e) {
    console.error("saveMealDishName failed:", e);
    setMeals(oldMeals);
    safeAlert("Не удалось сохранить название блюда.", "Ошибка");
  }
};
const saveMealFoodDetails = async (updatedFood) => {
  if (!editingMealFood || !updatedFood) return;

  const mealId = editingMealFood.mealId;
  const itemId = editingMealFood.itemId;

  const nextName = String(updatedFood.name || "").trim();

  if (!nextName) {
    safeAlert("Введите название продукта.", "Редактирование");
    return;
  }

  const meal = meals.find((m) => String(m.id) === String(mealId));
  const oldItem = meal?.items?.find((it) => String(it.id) === String(itemId));

  if (!oldItem) {
    setEditingMealFood(null);
    return;
  }

  const grams = Math.max(
    1,
    Number(oldItem.weight ?? oldItem.grams ?? updatedFood.defaultGrams ?? 100)
  );

  const per100 = updatedFood.per100 || {};

  const proteins = +((Number(per100.proteins || 0) * grams) / 100).toFixed(1);
  const fats = +((Number(per100.fats || 0) * grams) / 100).toFixed(1);
  const carbs = +((Number(per100.carbs || 0) * grams) / 100).toFixed(1);

  const kcalPer100 =
    per100.kcal ??
    calcKcalPer100(
      Number(per100.proteins || 0),
      Number(per100.fats || 0),
      Number(per100.carbs || 0)
    );

  const kcal = Math.round((Number(kcalPer100 || 0) * grams) / 100);

  const updatedFoodIdKey = getMyFoodIdKey(updatedFood);
  const linkedFoodIdNumber = isRealFoodId(updatedFood.id)
    ? Number(updatedFood.id)
    : oldItem.userFoodId != null
      ? Number(oldItem.userFoodId)
      : null;

  const oldMeals = meals;

  setMeals((prev) =>
    prev.map((m) =>
      String(m.id) !== String(mealId)
        ? m
        : {
            ...m,
            items: (m.items || []).map((it) =>
              String(it.id) !== String(itemId)
                ? it
                : {
                    ...it,
                    type: "ingredient",
                    name: nextName,
                    weight: grams,
                    proteins,
                    fats,
                    carbs,
                    kcal,
                    userFoodId: linkedFoodIdNumber ?? it.userFoodId,
                    sourceMyFoodId: updatedFoodIdKey || it.sourceMyFoodId,
                  }
            ),
          }
    )
  );

  setEditingMealFood(null);

  try {
    await apiUpdateFoodEntry(itemId, {
      name: nextName,
      grams,
      kcal,
      proteins,
      fats,
      carbs,
      userFoodId: linkedFoodIdNumber,
    });
  } catch (e) {
    console.error("saveMealFoodDetails failed:", e);
    setMeals(oldMeals);
    safeAlert("Не удалось сохранить изменения продукта.", "Ошибка");
    return;
  }

  const foodForMyFoods = normalizeMyFoodForList({
    ...updatedFood,
    type: "ingredient",
    name: nextName,
    per100: {
      ...per100,
      proteins: +Number(per100.proteins || 0).toFixed(1),
      fats: +Number(per100.fats || 0).toFixed(1),
      carbs: +Number(per100.carbs || 0).toFixed(1),
      kcal: Number(kcalPer100 || 0),
    },
    defaultGrams: Number(updatedFood.defaultGrams ?? grams),
    updatedAt: Date.now(),
  });

  updateMyFood(foodForMyFoods);

  if (isRealFoodId(foodForMyFoods.id)) {
    try {
      const savedFoodResp = await apiUpsertUserFood(foodForMyFoods);
      const savedFood = savedFoodResp?.data ? savedFoodResp.data : savedFoodResp;

      updateMyFood({
        ...foodForMyFoods,
        ...savedFood,
        per100: savedFood?.per100 || foodForMyFoods.per100,
        defaultGrams: Number(
          savedFood?.defaultGrams ??
            savedFood?.default_grams ??
            foodForMyFoods.defaultGrams
        ),
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error("apiUpsertUserFood after meal edit failed:", e);
      safeAlert("В рационе продукт изменился, но в 'Моих продуктах' мог не сохраниться.", "Ошибка");
    }
  }
};

const saveMealDishIngredientDetails = async (updatedIngredient) => {
  if (!editingMealDishIngredientFood || !updatedIngredient) return;

  const mealId = editingMealDishIngredientFood.mealId;
  const itemId = editingMealDishIngredientFood.itemId;
  const ingredientIndex = editingMealDishIngredientFood.ingredientIndex;

  const meal = meals.find((m) => String(m.id) === String(mealId));
  const item = meal?.items?.find((it) => String(it.id) === String(itemId));

  if (!item) {
    setEditingMealDishIngredientFood(null);
      markMealDishChanged(mealId, itemId);
    return;
  }

  const dish = getDishForMealItem(item);
  if (!dish) {
    setEditingMealDishIngredientFood(null);
    return;
  }

  const updatedDish = updateDishIngredientDetailsByIndex(
    dish,
    myFoods,
    ingredientIndex,
    updatedIngredient
  );

  if (!updatedDish) {
    setEditingMealDishIngredientFood(null);
    return;
  }

  const oldMeals = meals;
const patch = makeMealDishEntryPatch(item, updatedDish, true);
  setMeals((prev) =>
    prev.map((m) =>
      String(m.id) !== String(mealId)
        ? m
        : {
            ...m,
            items: (m.items || []).map((it) =>
              String(it.id) !== String(itemId)
                ? it
                :
 {
  ...it,
  type: "dish",
  recipe: updatedDish.recipe,
  ingredients: updatedDish.ingredients,
  weight: updatedDish.weight,
  proteins: updatedDish.proteins,
  fats: updatedDish.fats,
  carbs: updatedDish.carbs,
  kcal: updatedDish.kcal,
  userFoodId: null,
  sourceUserFoodId: patch.source_user_food_id ?? it.sourceUserFoodId,
  dishSnapshot: patch.dish_snapshot,
  dish_snapshot: patch.dish_snapshot,
}
            ),
          }
    )
  );

  setEditingMealDishIngredientFood(null);
  markMealDishChanged(mealId, itemId);
  try {
await apiUpdateFoodEntry(itemId, patch);
  } catch (e) {
    console.error("saveMealDishIngredientDetails failed:", e);
    setMeals(oldMeals);
    safeAlert("Не удалось сохранить изменения ингредиента.", "Ошибка");
  }
};
const handleEditDishIngredientWeight = (mealId, itemId, ingredientIndex, ing) => {
  setWeightModal({
    kind: "dishIngredient",
    mealId,
    itemId,
    ingredientIndex,
    initial: String(Math.round(Number(ing.grams || 0))),
  });
};

const applyDishIngredientWeight = async (
  mealId,
  itemId,
  ingredientIndex,
  newGrams
) => {
  if (!Number.isFinite(newGrams) || newGrams <= 0) return;

  const meal = meals.find((m) => m.id === mealId);
  if (!meal) return;

  const item = meal.items.find((it) => it.id === itemId);
  if (!item) return;

  const dish = getDishForMealItem(item);
  if (!dish) return;

  const updatedDish = updateDishIngredientByIndex(
    dish,
    myFoods,
    ingredientIndex,
    newGrams
  );

  if (!updatedDish) return;

  const oldMeals = meals;
const patch = makeMealDishEntryPatch(item, updatedDish, true);
  setMeals((prev) =>
    prev.map((m) =>
      m.id !== mealId
        ? m
        : {
            ...m,
            items: m.items.map((it) =>
              it.id !== itemId
                ? it
                : {
  ...it,
  type: "dish",
  recipe: updatedDish.recipe,
  ingredients: updatedDish.ingredients,
  weight: updatedDish.weight,
  proteins: updatedDish.proteins,
  fats: updatedDish.fats,
  carbs: updatedDish.carbs,
  kcal: updatedDish.kcal,
  userFoodId: null,
  sourceUserFoodId: patch.source_user_food_id ?? it.sourceUserFoodId,
  dishSnapshot: patch.dish_snapshot,
  dish_snapshot: patch.dish_snapshot,
}
            ),
          }
    )
  );
  markMealDishChanged(mealId, itemId);
  try {
await apiUpdateFoodEntry(itemId, patch);
  } catch (e) {
    console.error("applyDishIngredientWeight failed:", e);
    safeAlert("Не удалось сохранить новый вес ингредиента.", "Ошибка");
    setMeals(oldMeals);
  }
};
const deleteDishIngredientFromMeal = async (
  mealId,
  itemId,
  ingredientIndex,
  ing
) => {
const ok = await safeConfirm(
  tr("ration.myFoods.deleteConfirm.ingredientFromThisDish", {
    name: ing.name,
  }),
  tr("ration.myFoods.deleteConfirm.title")
);

  if (!ok) return;

  const meal = meals.find((m) => m.id === mealId);
  if (!meal) return;

  const item = meal.items.find((it) => it.id === itemId);
  if (!item) return;

  const dish = getDishForMealItem(item);
  if (!dish) return;

  const updatedDish = removeDishIngredientByIndex(
    dish,
    myFoods,
    ingredientIndex
  );

  if (!updatedDish) return;

  const oldMeals = meals;
const patch = makeMealDishEntryPatch(item, updatedDish, true);
  setMeals((prev) =>
    prev.map((m) =>
      m.id !== mealId
        ? m
        : {
            ...m,
            items: m.items.map((it) =>
              it.id !== itemId
                ? it
                : {
  ...it,
  type: "dish",
  recipe: updatedDish.recipe,
  ingredients: updatedDish.ingredients,
  weight: updatedDish.weight,
  proteins: updatedDish.proteins,
  fats: updatedDish.fats,
  carbs: updatedDish.carbs,
  kcal: updatedDish.kcal,
  userFoodId: null,
  sourceUserFoodId: patch.source_user_food_id ?? it.sourceUserFoodId,
  dishSnapshot: patch.dish_snapshot,
  dish_snapshot: patch.dish_snapshot,
}
            ),
          }
    )
  );
  markMealDishChanged(mealId, itemId);
  try {
await apiUpdateFoodEntry(itemId, patch);
  } catch (e) {
    console.error("deleteDishIngredientFromMeal failed:", e);
    safeAlert("Не удалось удалить ингредиент.", "Ошибка");
    setMeals(oldMeals);
  }
};
const addIngredientToMealDish = async (mealId, itemId, ingredientFood) => {
  const pickedIngredients = Array.isArray(ingredientFood)
    ? ingredientFood.filter(Boolean)
    : [ingredientFood].filter(Boolean);

  if (!pickedIngredients.length) return;

  const meal = meals.find((m) => String(m.id) === String(mealId));
  if (!meal) return;

  const item = (meal.items || []).find((it) => String(it.id) === String(itemId));
  if (!item) return;

  const dish = getDishForMealItem(item);
  if (!dish) return;

  const updatedDish = addDishIngredientsToDish(dish, myFoods, pickedIngredients);
  if (!updatedDish) return;

  const oldMeals = meals;
  const nextSnapshot = makeMealDishSnapshot(updatedDish, item, true);

  setMeals((prev) =>
    prev.map((m) =>
      String(m.id) !== String(mealId)
        ? m
        : {
            ...m,
            items: (m.items || []).map((it) =>
              String(it.id) !== String(itemId)
                ? it
                : {
                    ...it,
                    type: "dish",
                    recipe: updatedDish.recipe,
                    ingredients: updatedDish.ingredients,
                    weight: updatedDish.weight,
                    proteins: updatedDish.proteins,
                    fats: updatedDish.fats,
                    carbs: updatedDish.carbs,
                    kcal: updatedDish.kcal,
                    dishSnapshot: nextSnapshot,
                    dish_snapshot: nextSnapshot,
                  }
            ),
          }
    )
  );

  markMealDishChanged(mealId, itemId);

  try {
    await apiUpdateFoodEntry(
      itemId,
      makeMealDishEntryPatch(item, updatedDish, true)
    );

    safeAlert(
      pickedIngredients.length > 1
        ? "Ингредиенты добавлены"
        : "Ингредиент добавлен",
      "Готово"
    );
  } catch (e) {
    console.error("addIngredientToMealDish failed:", e);
    setMeals(oldMeals);
    safeAlert("Не удалось добавить ингредиент в блюдо.", "Ошибка");
  }
};
const openMealDishAsNewBuilder = (mealId, itemId, displayFood) => {
  const meal = meals.find((m) => String(m.id) === String(mealId));
  const item = (meal?.items || []).find((it) => String(it.id) === String(itemId));
  const dish = item ? getDishForMealItem(item) : displayFood;

  if (!dish) return;

  const baseName =
    String(dish.name || displayFood?.name || "Новое блюдо").trim() ||
    "Новое блюдо";

  const existingDishNames = new Set(
    (myFoods || [])
      .map(normalizeMyFoodForList)
      .filter((food) => isMyFoodsDish(food))
      .map((food) => normalizeFoodName(food.name))
      .filter(Boolean)
  );

  const baseWithoutNumber =
    baseName.replace(/\s+\(\d+\)$/u, "").trim() || baseName;

  let copyIndex = 1;
  let copyName = `${baseWithoutNumber} (${copyIndex})`;

  while (existingDishNames.has(normalizeFoodName(copyName))) {
    copyIndex += 1;
    copyName = `${baseWithoutNumber} (${copyIndex})`;
  }

  setMealDishBuilderSeed({
    ...dish,
    id: `seed_${Date.now()}`,
    name: copyName,
    __saveAsNew: true,
    __sourceMealId: mealId,
    __sourceItemId: itemId,
  });
};
const applyNewWeight = async (mealId, itemId, newWeightNumber) => {
  if (!Number.isFinite(newWeightNumber) || newWeightNumber <= 0) return;

  const meal = meals.find((m) => m.id === mealId);
  if (!meal) return;

  const item = meal.items.find((it) => it.id === itemId);
  if (!item) return;

  const prevWeight =
    typeof item.weight === "number" && item.weight > 0
      ? item.weight
      : newWeightNumber;

  const k = newWeightNumber / prevWeight;

const proteins = +((item.proteins || 0) * k).toFixed(1);
const fats = +((item.fats || 0) * k).toFixed(1);
const carbs = +((item.carbs || 0) * k).toFixed(1);

// Ккал пересчитываем от старых ккал, а не заново из округлённых БЖУ.
// Иначе у продуктов/блюд с готовыми ккал ккал могли пропадать или "плыть".
const oldKcal = Number(item.kcal);
const kcal = Number.isFinite(oldKcal)
  ? Math.round(oldKcal * k)
  : Math.round(proteins * 4 + fats * 9 + carbs * 4);


  const oldMeals = meals;
  const oldMyFoods = myFoods;

  // БЖУ на 100 г, восстановленные из текущей записи в рационе.
  // Это нужно для старых записей, где userFoodId почему-то null.
  const itemPer100 = {
    proteins:
      prevWeight > 0
        ? +(((item.proteins || 0) / prevWeight) * 100).toFixed(1)
        : 0,
    fats:
      prevWeight > 0
        ? +(((item.fats || 0) / prevWeight) * 100).toFixed(1)
        : 0,
    carbs:
      prevWeight > 0
        ? +(((item.carbs || 0) / prevWeight) * 100).toFixed(1)
        : 0,
    kcal:
      prevWeight > 0
        ? Math.round(((item.kcal || 0) / prevWeight) * 100)
        : 0,
  };

  const looseSamePer100 = (a, b) => {
    const left = a || {};
    const right = b || {};

    const macroClose = (x, y) => Math.abs(Number(x ?? 0) - Number(y ?? 0)) <= 2;
    const kcalClose = (x, y) => Math.abs(Number(x ?? 0) - Number(y ?? 0)) <= 15;

    return (
      macroClose(left.proteins, right.proteins) &&
      macroClose(left.fats, right.fats) &&
      macroClose(left.carbs, right.carbs) &&
      kcalClose(left.kcal, right.kcal)
    );
  };

  const linkedFoodById =
    item.userFoodId != null
      ? (myFoods || []).find(
          (food) => Number(food.id) === Number(item.userFoodId)
        )
      : null;

  const linkedFoodByNameAndMacros = (myFoods || []).find((food) => {
    const foodType = food.type || "ingredient";

    return (
      (foodType === "ingredient" || foodType === "product") &&
      normalizeFoodName(food.name) === normalizeFoodName(item.name) &&
      looseSamePer100(food.per100, itemPer100)
    );
  });

   const linkedFoodCandidate = linkedFoodById || linkedFoodByNameAndMacros;

  // ✅ Запоминаем последнюю граммовку ТОЛЬКО для отдельных продуктов.
  // Сохранённые блюда/рецепты не трогаем, чтобы состав рецепта не менялся.
const linkedFood = isMyFoodsSingleProduct(linkedFoodCandidate)
  ? normalizeMyFoodForList(linkedFoodCandidate)
  : null;

const linkedFoodIdNumber = isRealFoodId(linkedFood?.id)
  ? Number(linkedFood.id)
  : null;
  // 1. Сразу обновляем рацион на экране
  setMeals((prev) =>
    prev.map((m) => {
      if (m.id !== mealId) return m;

      return {
        ...m,
        items: m.items.map((it) =>
          it.id !== itemId
            ? it
            : {
                ...it,
                weight: newWeightNumber,
                proteins,
                fats,
                carbs,
                kcal,
userFoodId: linkedFoodIdNumber ?? (linkedFoodById ? it.userFoodId : null),
              }
        ),
      };
    })
  );

  // 2. Сразу обновляем "Мои продукты" локально
  if (linkedFood) {
updateMyFood({
  ...linkedFood,
  defaultGrams: newWeightNumber,
  updatedAt: Date.now(),
});
  }

try {
  // 3. Главное: сохраняем вес именно в рационе.
  // userFoodId НЕ отправляем, иначе можно заново привязать запись
  // к продукту, который уже удалён из "Мои продукты".
  await apiUpdateFoodEntry(itemId, {
    grams: newWeightNumber,
    kcal,
    proteins,
    fats,
    carbs,
  });
} catch (e) {
  console.error("applyNewWeight: food entry save failed:", e);
  safeAlert("Не удалось сохранить новый вес продукта.", "Ошибка");

  setMeals(oldMeals);
  setMyFoods(oldMyFoods);
  return;
}

// 4. Второстепенное: пробуем запомнить последнюю граммовку в "Мои продукты".
// Если это не получилось — НЕ откатываем вес в рационе.
if (linkedFood) {
  try {
    const savedFoodResp = await apiUpsertUserFood({
      ...linkedFood,
      defaultGrams: newWeightNumber,
    });

    const savedFood = savedFoodResp?.data ? savedFoodResp.data : savedFoodResp;

    updateMyFood({
      ...linkedFood,
      ...savedFood,
      defaultGrams: Number(
        savedFood?.defaultGrams ??
          savedFood?.default_grams ??
          newWeightNumber
      ),
      updatedAt: Date.now(),
    });
  } catch (e) {
    console.warn("applyNewWeight: my food default grams save failed:", e);
  }
}
};
const removeMealItem = async (mealId, itemId) => {
  const meal = meals.find((m) => m.id === mealId);
  const item = meal?.items?.find((it) => String(it.id) === String(itemId));

  if (!item) return;

  const isDish =
    item.type === "dish" ||
    item.recipe ||
    (Array.isArray(item.ingredients) && item.ingredients.length > 0);

const ok = await safeConfirm(
  isDish
    ? tr("ration.myFoods.deleteConfirm.mealDish", {
        name: item.name,
      })
    : tr("ration.myFoods.deleteConfirm.mealProduct", {
        name: item.name,
      }),
  tr("ration.myFoods.deleteConfirm.title")
);
if (!ok) return;

  const oldMeals = meals;

  setMeals((prev) =>
    prev.map((m) =>
      String(m.id) === String(mealId)
        ? {
            ...m,
            items: (m.items || []).filter(
              (it) => String(it.id) !== String(itemId)
            ),
          }
        : m
    )
  );

  try {
    const params = new URLSearchParams({
      id: String(itemId),
    });

    const numericId = Number(itemId);

    const res = await tgFetch(`/api/food-entries?${params.toString()}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: Number.isFinite(numericId) && numericId > 0 ? numericId : itemId,
        entry_id: Number.isFinite(numericId) && numericId > 0 ? numericId : itemId,
      }),
    });

    let json = null;

    try {
      json = await res.json();
    } catch {
      json = { ok: res.ok };
    }

    if (!res.ok || json?.ok === false) {
      throw new Error(json?.error || "delete food entry failed");
    }

    bumpFoodMonth?.();
  } catch (e) {
    console.error("delete food entry error:", e);

    setMeals(oldMeals);

    safeAlert(
      "Не удалось удалить продукт из рациона на сервере. Проверь интернет и попробуй ещё раз.",
      "Ошибка"
    );
  }
};


  const resetForm = () => {
    setFormName("");
    setFormProteins("");
    setFormFats("");
    setFormCarbs("");
  };

    const openAddModal = (mealId) => {
    setAddFlow(null);
    setAddModal({ mealId, step: "mode" });
    resetForm();
  };

  const closeAddModal = () => {
    setAddFlow(null);
    setAddModal(null);
    resetForm();
  };

  // кнопка “назад” с отдельных экранов возвращает к попапу выбора
const backToAddModePopup = (
  mealId
) => {
  setAddFlow(null);

  setAddModal({
    mealId,
    step: "mode",
  });

  emitAppEvent(
    "manual-product:cancelled",
    {
      mealId,
    }
  );
};
  const openGlobalMyFoodsPicker = () => {
  setAddModal(null);
  setPendingMyFoodsAdd(null);
  setPendingMyFoodsMealId("");

  setAddFlow({
    mealId: null,
    screen: "my",
    initialTab: "ingredients",
    chooseMealAfter: true,
  });
};

const closeGlobalMyFoodsPicker = () => {
  resetKeyboardLayoutVars();
  setPendingMyFoodsAdd(null);
  setPendingMyFoodsMealId("");
  setAddFlow(null);
};
useEffect(() => {
  return subscribeAppEvents(
    ({ type, payload }) => {
      if (
        type ===
        "my-foods:open"
      ) {
        setAddModal(null);

        setPendingMyFoodsAdd(
          null
        );

        setPendingMyFoodsMealId(
          ""
        );

        setAddFlow(
          (current) => {
            if (
              current?.screen ===
              "my"
            ) {
              return current;
            }

            return {
              mealId: null,
              screen: "my",

              initialTab:
                payload?.tab ===
                "dishes"
                  ? "dishes"
                  : "ingredients",

              chooseMealAfter:
                true,
            };
          }
        );

        return;
      }

      if (
        type ===
        "my-foods:close"
      ) {
        setPendingMyFoodsAdd(
          null
        );

        setPendingMyFoodsMealId(
          ""
        );

        setAddModal(null);
        setAddFlow(null);
      }
    }
  );
}, []);
const openPhotoFlow = (mealId, { autoOpenGallery = false } = {}) => {
  setAddModal(null);
  setAddFlow({ mealId, screen: "photo", from: "quick" });

  setPhotoPick(null);
setPhotoResult(null);
setPhotoError(null);
setPhotoLoading(false);
setIsAddingPhotoDish(false);

  setPhotoAutoOpenCamera(!!autoOpenGallery);
};


const resetKeyboardLayoutVars = () => {
  if (typeof document === "undefined") return;

  try {
    const el = document.activeElement;
    if (el && typeof el.blur === "function") el.blur();
  } catch {}

  try {
    const root = document.documentElement;
    root.style.setProperty("--kb", "0px");
    root.style.setProperty("--kb-bottom", "0px");
  } catch {}
};

const dismissKeyboard = () => {
  if (typeof document === "undefined") return false;

  const el = document.activeElement;
  if (!el) return false;

  const tag = el.tagName;
  const didBlur = tag === "INPUT" || tag === "TEXTAREA";

  if (didBlur) {
    resetKeyboardLayoutVars();
  }

  return didBlur;
};

const returnToMyFoodsAfterManualClose = () => {
  if (addFlow?.from !== "my-foods") return false;

  resetKeyboardLayoutVars();
  resetForm();
  protectNextTap(700);

  setAddFlow({
    mealId: addFlow.mealId,
    screen: "my",
    initialTab: addFlow.returnTab || "ingredients",
    chooseMealAfter: !!addFlow?.chooseMealAfter,
  });

  return true;
};


const handleAddDishManual = async () => {
  if (isSavingManualFood) return;

const mealId = addFlow?.mealId ?? addModal?.mealId;
const saveOnlyToMyFoods =
  !mealId && addFlow?.from === "my-foods" && addFlow?.chooseMealAfter;

if (!mealId && !saveOnlyToMyFoods) return;
  const ingredientName = formName.trim();
  if (!ingredientName) return;

  const proteins = parseMacro(formProteins);
  const fats = parseMacro(formFats);
  const carbs = parseMacro(formCarbs);

  const weight = 100;
  const kcal = Math.round(proteins * 4 + fats * 9 + carbs * 4);

  if (isTooHighMacrosPer100({ proteins, fats, carbs })) {
    safeAlert(
      "БЖУ указывается на 100 г продукта, значения слишком большие",
      "Ошибка"
    );
    return;
  }

 const mealTitle = mealId
  ? meals.find((m) => String(m.id) === String(mealId))?.title || ""
  : "";

  const mealSlot = mealSlotFromTitle(mealTitle);

  const currentPer100 = {
    proteins,
    fats,
    carbs,
    kcal,
  };

  setIsSavingManualFood(true);

  try {
    const savedFoodResp = await apiUpsertUserFood({
      type: "ingredient",
      name: ingredientName,
      per100: currentPer100,
      defaultGrams: weight,
      lastMeal: mealTitle,
    });

    const savedFood = savedFoodResp?.data ? savedFoodResp.data : savedFoodResp;

    const savedFoodApp = {
      ...savedFood,
      id: savedFood?.id,
      type: "ingredient",
      name: savedFood?.name ?? ingredientName,
      per100: {
        proteins: Number(savedFood?.per100?.proteins ?? proteins),
        fats: Number(savedFood?.per100?.fats ?? fats),
        carbs: Number(savedFood?.per100?.carbs ?? carbs),
        kcal: Number(savedFood?.per100?.kcal ?? kcal),
      },
      defaultGrams: Number(
        savedFood?.defaultGrams ??
          savedFood?.default_grams ??
          weight
      ),
      lastMeal: savedFood?.lastMeal ?? mealTitle,
      updatedAt: Date.now(),
    };

    createMyFood(savedFoodApp);
if (saveOnlyToMyFoods) {
  setAddFlow({
    mealId: null,
    screen: "my",
    initialTab: "ingredients",
    chooseMealAfter: true,
  });

  resetForm();
  safeAlert("Продукт создан", "Готово");
  return;
}
    const row = await apiAddFoodEntry({
      day: dayKey,
      mealSlot,
      name: ingredientName,
      grams: weight,
      kcal,
      proteins,
      fats,
      carbs,
      userFoodId: isRealFoodId(savedFoodApp.id)
        ? Number(savedFoodApp.id)
        : null,
    });

    const newItem = {
      ...dbFoodEntryToItem(row),
      type: "ingredient",
      sourceMyFoodId: getMyFoodIdKey(savedFoodApp),
      userFoodId: isRealFoodId(savedFoodApp.id)
        ? Number(savedFoodApp.id)
        : null,
    };

    setMeals((prev) =>
      prev.map((m) =>
        String(m.id) === String(mealId)
          ? { ...m, items: [...m.items, newItem] }
          : m
      )
    );

    setExpandedIds((prev) =>
      prev.includes(mealId) ? prev : [...prev, mealId]
    );

setAddFlow(null);
setAddModal(null);
resetForm();

emitAppEvent(
  "product-created-and-added",
  {
    mealId,
    name: ingredientName,
    foodId: savedFoodApp.id,
  }
);

safeAlert(
  "Продукт создан",
  "Готово"
);
  } catch (e) {
    console.error("manual add save error:", e);

    safeAlert(
      `Не удалось сохранить ингредиент.\n\nОшибка: ${
        e?.message || String(e)
      }`,
      "Ошибка"
    );
  } finally {
    setIsSavingManualFood(false);
  }
};


return (
<div className="mx-auto w-full max-w-[448px] px-0 pt-1 pb-1 flex flex-col gap-2 relative">
  {showDailySticky && (
    <Portal>
      <div
        className="fixed left-1/2 -translate-x-1/2 rounded-[24px] border border-emerald-200/30 px-3 py-2 text-slate-50 backdrop-blur-xl"
        style={{
          top: "calc(7px + env(safe-area-inset-top, 0px))",
          width: "min(420px, calc(100vw - 24px))",
          zIndex: 18000,
          background:
            "radial-gradient(circle at 9% 0%, rgba(45,212,191,0.34), transparent 46%), linear-gradient(180deg, rgba(7,31,43,0.98) 0%, rgba(5,21,33,0.97) 100%)",
          boxShadow:
            "0 16px 38px rgba(0,0,0,0.46), 0 0 0 1px rgba(45,212,191,0.10), 0 0 24px rgba(45,212,191,0.12), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
            <span className="text-[19px] font-black leading-none tracking-[-0.05em] tabular-nums text-slate-50">
              {round(totals.kcal)}
            </span>
            <span className="text-[11px] font-semibold leading-none text-slate-300">
              {tr("units.kcal")}
            </span>
            <span className="text-[10px] font-bold leading-none text-emerald-200 tabular-nums">
              · {pct(totals.kcal, DAY_TARGETS.kcal)}%
            </span>
          </div>

          <div className="grid min-w-[174px] grid-cols-3 overflow-hidden rounded-[13px] border border-white/10 bg-white/[0.045]">
            <div className="border-r border-white/10 px-2 py-1.5 text-center">
              <div className="text-[10px] font-black leading-none text-cyan-100">
  {tr("food.report.proteinShort")}
</div>
              <div className="mt-1 text-[11px] font-bold leading-none tabular-nums text-slate-50">
                {fmtMacro(totals.proteins)}
              </div>
            </div>

            <div className="border-r border-white/10 px-2 py-1.5 text-center">
              <div className="text-[10px] font-black leading-none text-cyan-100">
  {tr("food.report.fatShort")}
</div>
              <div className="mt-1 text-[11px] font-bold leading-none tabular-nums text-slate-50">
                {fmtMacro(totals.fats)}
              </div>
            </div>

            <div className="px-2 py-1.5 text-center">
              <div className="text-[10px] font-black leading-none text-cyan-100">
  {tr("food.report.carbsShort")}
</div>
              <div className="mt-1 text-[11px] font-bold leading-none tabular-nums text-slate-50">
                {fmtMacro(totals.carbs)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.10]">
          <div
            className="h-full rounded-full bg-emerald-300"
            style={{ width: `${clampPct(totals.kcal, DAY_TARGETS.kcal)}%` }}
          />
        </div>
      </div>
    </Portal>
  )}

  <div
  data-onboarding="ration-day-switch"
  className="grid grid-cols-[44px_minmax(0,1fr)_44px] items-center"
>
    <button
      type="button"
      onClick={() => shiftRationDay(-1)}
      className="grid h-11 w-11 place-items-center text-[32px] font-light leading-none text-slate-300 transition active:scale-[0.88] active:text-[#35F3DF]"
            aria-label={tr("ration.previousDayAria")}
    >
      ‹
    </button>

    <div className="min-w-0 text-center">
      <div className="truncate text-[24px] font-bold leading-tight tracking-[-0.04em] text-slate-50">
        {dayKey === todayKey
          ? tr("ration.todayTitle")
          : tr("ration.dateTitle", {
              date: formatRationDayLabel(dayKey),
            })}
      </div>
    </div>

    <button
      type="button"
      onClick={() => shiftRationDay(1)}
      disabled={!canGoNextRationDay}
      className="grid h-11 w-11 place-items-center text-[32px] font-light leading-none text-slate-300 transition active:scale-[0.88] active:text-[#35F3DF] disabled:pointer-events-none disabled:opacity-0"
      aria-label={tr("ration.nextDayAria")}
    >
      ›
    </button>
  </div>

 <div className="-mx-1 grid grid-cols-[1fr_1.08fr_0.84fr] gap-2 px-0">
    <button
      type="button"
      onClick={onEditMacros}
      className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[18px] border border-emerald-300/30 bg-slate-950/22 px-2 text-[12px] font-semibold text-emerald-100 active:scale-[0.98]"
      aria-label={tr("ration.myMacrosAria")}
    >
      <svg
  viewBox="0 0 24 24"
  fill="none"
  className="h-[16px] w-[16px] shrink-0 text-[#67e8d4]"
  aria-hidden="true"
>
  <path
    d="M4 7h5M15 7h5"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  />
  <circle
    cx="12"
    cy="7"
    r="2.2"
    stroke="currentColor"
    strokeWidth="1.8"
  />

  <path
    d="M4 12h10M20 12h0"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  />
  <circle
    cx="17"
    cy="12"
    r="2.2"
    stroke="currentColor"
    strokeWidth="1.8"
  />

  <path
    d="M4 17h2M12 17h8"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  />
  <circle
    cx="9"
    cy="17"
    r="2.2"
    stroke="currentColor"
    strokeWidth="1.8"
  />
</svg>
      <span className="whitespace-nowrap">
        {tr("ration.myMacros")}
      </span>
    </button>

    <button

  type="button"
  data-onboarding="my-foods-entry"

  onClick={() => {
    openGlobalMyFoodsPicker();

    emitAppEvent(
      "my-foods:entered"
    );
  }}

  className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[18px] border border-emerald-300/30 bg-emerald-300/[0.045] px-2 text-[12px] font-semibold text-emerald-100 active:scale-[0.98]"
>
<svg
  viewBox="0 0 24 24"
  fill="none"
  className="h-[16px] w-[16px] shrink-0 text-[#67e8d4]"
  aria-hidden="true"
>
  <path
    d="M12 3.8l2.45 4.96 5.47.8-3.96 3.86.94 5.45L12 16.3l-4.9 2.57.94-5.45-3.96-3.86 5.47-.8L12 3.8Z"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
</svg>

  <span className="whitespace-nowrap">
    {tr("ration.myDishes")}
  </span>
</button>

<button
  type="button"
  data-onboarding="photo-add"
  onClick={() => {
    const fallbackMealId = expandedIds?.[0] ?? meals?.[0]?.id;
        if (!fallbackMealId) return;
        startPhotoPick(
  fallbackMealId,
  {
    cameraOnly: true,
  }
);
      }}
className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[18px] border border-emerald-300/30 bg-emerald-300/[0.045] px-2 text-[12px] font-semibold text-emerald-100 active:scale-[0.98]"
    >
      <svg
  viewBox="0 0 24 24"
  fill="none"
  className="h-[17px] w-[17px] shrink-0 text-[#67e8d4]"
  aria-hidden="true"
>
  <path
    d="M7.5 6.5 9 4.8h6l1.5 1.7H19a2 2 0 0 1 2 2v8.7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2h2.5Z"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />

  <circle
    cx="12"
    cy="12.8"
    r="3.25"
    stroke="currentColor"
    strokeWidth="1.8"
  />

  <path
    d="M18.2 9.1h.01"
    stroke="currentColor"
    strokeWidth="2.3"
    strokeLinecap="round"
  />
</svg>
      <span className="whitespace-nowrap">
        {tr("ration.photo")}
      </span>
    </button>
  </div>
<div
  ref={daySummaryRef}
  data-onboarding="day-summary"
  className="-mx-1 rounded-[24px] bg-[linear-gradient(135deg,rgba(16,185,129,0.42),rgba(34,211,238,0.22),rgba(16,185,129,0.14))] p-px shadow-[0_16px_38px_rgba(0,0,0,0.22)]"
>
    <div className="rounded-[23px] bg-[radial-gradient(circle_at_0%_0%,rgba(16,185,129,0.18),rgba(15,23,42,0.86)_44%,rgba(2,6,23,0.96)_100%)] p-3.5 text-slate-50">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-300">
          {tr("ration.dayTotal")}
        </div>

        <div className="mt-1.5 flex items-baseline gap-2 whitespace-nowrap">
<span className="text-[38px] font-bold leading-none tracking-[-0.06em] text-slate-50 tabular-nums">
  {round(totals.kcal)}
</span>
          <span className="text-[15px] font-semibold leading-none text-slate-100">
            {tr("units.kcal")}
          </span>
        </div>
      </div>

      <div className="shrink-0 rounded-[15px] border border-emerald-300/30 bg-emerald-300/[0.06] px-3 py-1.5 text-[12px] font-semibold leading-none text-emerald-100 whitespace-nowrap">
        {tr("ration.goalKcal", {
          value: DAY_TARGETS.kcal,
        })}
      </div>
    </div>

    <div className="mt-3">
      <div className="h-2 overflow-hidden rounded-full bg-white/[0.11]">
        <div
          className="h-full rounded-full bg-emerald-300"
          style={{ width: `${clampPct(totals.kcal, DAY_TARGETS.kcal)}%` }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 text-[13px] text-slate-300">
        <span>
          {tr("ration.remaining")}{" "}
          <span className="font-bold text-emerald-200">
            {Math.max(0, DAY_TARGETS.kcal - round(totals.kcal))} {tr("units.kcal")}
          </span>
        </span>

        <span className="font-bold text-slate-50 tabular-nums">
          {pct(totals.kcal, DAY_TARGETS.kcal)}%
        </span>
      </div>
    </div>

    <div className="mt-3 grid grid-cols-3 gap-3">
      <DayMacroCard
  label={tr("ration.protein")}
  value={totals.proteins}
  target={DAY_TARGETS.proteins}
/>
      <DayMacroCard
  label={tr("ration.fat")}
  value={totals.fats}
  target={DAY_TARGETS.fats}
/>
      <DayMacroCard
  label={tr("ration.carbs")}
  value={totals.carbs}
  target={DAY_TARGETS.carbs}
/>
    </div>
  </div>
    </div>
      {/* приёмы пищи */}
      <div className="flex flex-col gap-2.5 px-0 pb-2">
{meals.map((meal) => {
  const t =
    calcMealTotals(meal);

  const isExpanded =
    expandedIds.includes(
      meal.id
    );

  const previousMealEntries =
    getPreviousMealEntries(
      meal.title
    );

  const canRepeatPreviousMeal =
    (meal.items?.length || 0) ===
      0 &&
    previousMealEntries.length >
      0;

  const currentMealSlot =
    mealSlotFromTitle(
      meal.title
    );

  const repeatMealBusy =
    repeatMealBusySlot ===
    currentMealSlot;

return (
  <div
    key={meal.id}
    className={cn(
      "-mx-1 rounded-[22px] bg-white/[0.04] p-2.5 text-slate-50 shadow-[0_12px_28px_rgba(0,0,0,0.18)]",
      isExpanded && "bg-emerald-300/[0.035]"
    )}
  >
    <div className="grid grid-cols-[1fr_auto_auto] items-start gap-2">
      <div className="min-w-0">
        <div className="text-[18px] font-bold leading-tight tracking-[-0.03em] text-slate-50">
          {getRationMealTitle(meal.title)}
        </div>

<div className="mt-1 text-[13px] leading-none text-slate-300">
  {tr("ration.dishCount", {
    count: meal.items?.length || 0,
  })}
</div>
      </div>

<div className="pt-0.5 text-right">
  <div className="flex items-baseline justify-end gap-1 whitespace-nowrap">
    <span className="text-[18px] font-bold leading-none tracking-[-0.04em] text-slate-50 tabular-nums">
      {round(t.kcal)}
    </span>

    <span className="text-[11px] font-medium leading-none text-slate-300">
      {tr("units.kcal")}
    </span>

    <span className="text-[10px] font-semibold leading-none text-emerald-200 tabular-nums">
      · {pct(t.kcal, DAY_TARGETS.kcal)}%
    </span>
  </div>
</div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpenedMealItemMenuId(null);

          if (isExpanded) {
            setOpenedMealDishIds(new Set());
          }

          setExpandedIds((prev) =>
            isExpanded
              ? prev.filter((id) => id !== meal.id)
              : [...prev, meal.id]
          );
        }}
        className="flex h-7 w-7 items-center justify-center rounded-[13px] bg-white/[0.045] text-slate-100 active:scale-[0.96]"
        aria-label={
          isExpanded
            ? tr("ration.collapseAria")
            : tr("ration.expandAria")
        }
      >
        {isExpanded ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 15L12 9L18 15"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>

<div
  className="mt-3"

  data-onboarding={
    meal.title === "Завтрак"
      ? "breakfast-macros"
      : undefined
  }
>
  <MealMacroStrip
    totals={t}
    targets={DAY_TARGETS}
  />
</div>

<div className="mt-3 grid gap-2">
  {canRepeatPreviousMeal && (
    <button
      type="button"

      disabled={
        Boolean(
          repeatMealBusySlot
        )
      }

      onClick={(e) => {
        e.stopPropagation();

        void repeatPreviousMeal(
          meal
        );
      }}

      className="inline-flex h-8 w-full items-center justify-center gap-2 rounded-[15px] border border-emerald-300/20 bg-emerald-300/[0.025] text-[12px] font-medium text-emerald-100/80 active:scale-[0.98] disabled:opacity-50"

      aria-label={
        getRationRepeatLabel(
          meal.title
        )
      }
    >
      <span className="text-[16px] leading-none">
        ↻
      </span>

      <span>
        {repeatMealBusy
          ? tr("ration.repeating")
          : getRationRepeatLabel(
              meal.title
            )}
      </span>
    </button>
  )}

<div
  data-onboarding={
    meal.title === "Завтрак"
      ? "breakfast-add"
      : undefined
  }
  className="w-full"
>
  <div className="mb-1.5 px-0.5 text-[11px] font-semibold tracking-[-0.01em] text-slate-300/75">
    {tr("ration.quickAddTitle")}
  </div>

  <div className="grid grid-cols-3 gap-2">

    {/* МОИ */}
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();

        setAddModal(null);
        setPendingMyFoodsAdd(null);
        setPendingMyFoodsMealId("");

        setAddFlow({
          mealId: meal.id,
          screen: "my",
          initialTab: "ingredients",
        });

        if (meal.title === "Завтрак") {
          emitAppEvent("breakfast-add-opened", {
            mealId: meal.id,
          });
        }
      }}
      aria-label={`Добавить в ${meal.title} из моих`}
      className="inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-[13px] border border-[rgba(45,226,204,0.32)] bg-[rgba(5,38,44,0.26)] px-1.5 text-[11px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition active:scale-[0.97] active:border-[rgba(45,226,204,0.50)] active:bg-[rgba(5,46,52,0.38)]"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="h-[16px] w-[16px] shrink-0 text-[#67e8d4]"
        aria-hidden="true"
      >
        <path
          d="M12 3.8l2.45 4.96 5.47.8-3.96 3.86.94 5.45L12 16.3l-4.9 2.57.94-5.45-3.96-3.86 5.47-.8L12 3.8Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <span>{tr("ration.quickAddMy")}</span>
    </button>


    {/* ФОТО */}
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();

        logEvent("meal:add:photo", {
          mealId: meal.id,
        });

        if (meal.title === "Завтрак") {
          emitAppEvent("breakfast-add-opened", {
            mealId: meal.id,
          });
        }

        startPhotoPick(meal.id);
      }}
      aria-label={`Добавить в ${meal.title} по фото`}
      className="inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-[13px] border border-[rgba(45,226,204,0.32)] bg-[rgba(5,38,44,0.26)] px-1.5 text-[11px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition active:scale-[0.97] active:border-[rgba(45,226,204,0.50)] active:bg-[rgba(5,46,52,0.38)]"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="h-[17px] w-[17px] shrink-0 text-[#67e8d4]"
        aria-hidden="true"
      >
        <path
          d="M7.5 6.5 9 4.8h6l1.5 1.7H19a2 2 0 0 1 2 2v8.7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2h2.5Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <circle
          cx="12"
          cy="12.8"
          r="3.25"
          stroke="currentColor"
          strokeWidth="1.8"
        />

        <path
          d="M18.2 9.1h.01"
          stroke="currentColor"
          strokeWidth="2.3"
          strokeLinecap="round"
        />
      </svg>

      <span>{tr("ration.quickAddPhoto")}</span>
    </button>


    {/* ВРУЧНУЮ */}
    <button
      type="button"
      data-onboarding="manual-add"
      onClick={(e) => {
        e.stopPropagation();

        setAddModal(null);

        setAddFlow({
          mealId: meal.id,
          screen: "manual",
          from: "quick",
        });

        resetForm();

        emitAppEvent("manual-form-opened", {
          mealId: meal.id,
        });

        if (meal.title === "Завтрак") {
          emitAppEvent("breakfast-add-opened", {
            mealId: meal.id,
          });
        }
      }}
      aria-label={`Добавить в ${meal.title} вручную`}
      className="inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-[13px] border border-[rgba(45,226,204,0.32)] bg-[rgba(5,38,44,0.26)] px-1.5 text-[11px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition active:scale-[0.97] active:border-[rgba(45,226,204,0.50)] active:bg-[rgba(5,46,52,0.38)]"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="h-[16px] w-[16px] shrink-0 text-[#67e8d4]"
        aria-hidden="true"
      >
        <path
          d="M4.8 19.2 5.7 15.4 15.9 5.2a1.8 1.8 0 0 1 2.55 0l.35.35a1.8 1.8 0 0 1 0 2.55L8.6 18.3l-3.8.9Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <path
          d="m14.7 6.4 2.9 2.9"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>

      <span>{tr("ration.quickAddManual")}</span>
    </button>

  </div>
</div>
</div>

{isExpanded && meal.items.length > 0 && (
  <div className="mt-2 grid gap-2">
    {meal.items.map((item, itemIndex) => {
      const {
        isDish,
        dishForDetails,
        displayFood,
        m,
        ingredientCount,
      } = getMealItemCardData(item);

      const detailsOpen = openedMealDishIds.has(item.id);
      const mealDishDraftKey = getMealDishDraftKey(meal.id, item.id);
      const hasMealDishDraftChanges =
        changedMealDishKeys.has(mealDishDraftKey) ||
        Boolean(getEntrySnapshotFromItem(item)?.changed_from_source);
      const mealMenuOpensUp =
        itemIndex >= meal.items.length - 1 && meal.items.length > 1;

      return (
        <div key={item.id} className="relative">
          <div
            onPointerDownCapture={(e) => {
              if (openedMealItemMenuId == null) return;

              const target = e.target;

              const insideMenu =
                target?.closest?.('[data-meal-item-menu="1"]') ||
                target?.closest?.('[data-meal-item-menu-button="1"]');

              if (insideMenu) return;

              // Если меню открыто, первый тап по карточке только закрывает меню.
              // Не даём этому же тапу открыть alert по названию, граммовку или состав.
              stopAndProtect(e, 450);
              setOpenedMealItemMenuId(null);
            }}
            onClick={(e) => {
              if (isTapGuardActive()) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              e.stopPropagation();
              setOpenedMealItemMenuId(null);
            }}
className="relative -mx-1.5 overflow-visible rounded-[18px] border border-white/[0.14] bg-white/[0.06] px-3 py-3 pr-10 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] active:scale-[0.99]"
>
<div className={MEAL_ITEM_DOT_MENU_WRAP_CLASS} data-meal-item-menu-button="1">
  <button
    type="button"
    onPointerDown={rememberTapStart}
    onPointerUp={(e) => {
      runMenuTap(e, () => {
        setOpenedMealItemMenuId((current) =>
          current === item.id ? null : item.id
        );
      });
    }}
    onPointerCancel={clearTapStart}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className={DOT_MENU_BTN_CLASS}
aria-label={tr("ration.menu")}
title={tr("ration.menu")}
  >
    ⋯
  </button>
</div>
            {openedMealItemMenuId === item.id && (
              <div
                data-meal-item-menu="1"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
 className={
  "absolute right-1.5 z-[120] w-[184px] overflow-hidden rounded-[18px] border border-slate-500/35 bg-[#151d2b] shadow-[0_18px_44px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.06)] " +
  (mealMenuOpensUp ? "bottom-10" : "top-10")
}
              >
                <button
  type="button"
  onPointerDown={(e) => {
    stopAndProtect(e, 1200);
    setOpenedMealItemMenuId(null);



    if (isDish) {
      setEditingMealDish({
        mealId: meal.id,
        itemId: item.id,
        dish: {
          ...dishForDetails,
          ...displayFood,
          type: "dish",
        },
      });
      return;
    }

    const editFoodId =
      item.userFoodId ??
      item.sourceMyFoodId ??
      displayFood.sourceMyFoodId ??
      "";

    setEditingMealFood({
      mealId: meal.id,
      itemId: item.id,
      food: {
        ...displayFood,
        id: editFoodId,
        type: "ingredient",
        name: displayFood.name || "Продукт",
        defaultGrams: Number(m.grams || 100),
        __editTitle: displayFood.name || "Редактировать продукт",
      },
    });
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
  className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-slate-700/55"
>
  <span className="grid h-6 w-6 shrink-0 place-items-center text-[15px] text-cyan-100">
    ✎
  </span>

  <span className="min-w-0 text-[13px] font-semibold leading-none text-white">
    {tr("ration.edit")}
  </span>
</button>
                <div className="mx-3 h-px bg-slate-600/70" />

                <button
                  type="button"
                  onPointerDown={(e) => {
                    stopAndProtect(e, 1500);
                    setOpenedMealItemMenuId(null);
                    removeMealItem(meal.id, item.id);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="flex h-11 w-full items-center gap-2.5 px-3 text-left active:bg-rose-950/45"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center text-[16px] leading-none text-rose-300">
                    ⌫
                  </span>

                  <span className="min-w-0 text-[13px] font-semibold leading-none text-rose-300">
                    {tr("ration.delete")}
                  </span>
                </button>
              </div>
            )}

<div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
  <div
    className="h-[58px] w-1.5 rounded-full"
    style={{ backgroundColor: getMyFoodAccentColor(displayFood) }}
  />

  <div className="min-w-0">
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
<FoodNameButton
  name={
    item.name ||
    dishForDetails?.name ||
    tr("ration.myFoods.unnamed")
  }
  kind={isDish ? "Блюдо" : "Продукт"}
  onOpen={setMealFullNamePopup}
/>

      <div className="flex shrink-0 items-start gap-1.5">
        {isDish && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenedMealItemMenuId(null);
              toggleMealDishDetails(item.id);
            }}
            className={
              "shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium active:scale-[0.97] " +
              (detailsOpen
                ? "border-cyan-200/35 bg-cyan-300/15 text-cyan-50 shadow-[0_0_14px_rgba(34,211,238,0.10)]"
                : "border-cyan-200/20 bg-cyan-400/10 text-cyan-100")
            }
          >
            {detailsOpen
              ? `▴ ${tr("ration.composition")}`
              : `☰ ${tr("ration.composition")}`}
          </button>
        )}

{isDish ? (
  <div
    className="shrink-0 rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)]"
    title={tr("ration.dishWeightTitle")}
  >
    {Math.round(Number(m.grams || 0))} {tr("units.gramsShort")}
  </div>
) : (
  <button
    type="button"
    onPointerDown={rememberTapStart}
    onPointerUp={(e) => {
      runIfRealTap(e, () => {
        setOpenedMealItemMenuId(null);
        handleEditItemWeight(meal.id, item.id);
      }, 1200);
    }}
    onPointerCancel={clearTapStart}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className="shrink-0 rounded-[13px] border border-cyan-300/50 bg-slate-950/24 px-2.5 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)] active:scale-[0.97]"
    title={tr("ration.productWeightTitle")}
  >
    {Math.round(Number(m.grams || 0))} {tr("units.gramsShort")}
  </button>
)}
      </div>
    </div>

        <div className="mt-2 -mr-8 flex min-w-0 flex-wrap gap-1 overflow-visible">
      <MyFoodMacroChip
        label=""
        value={`${Math.round(Number(m.kcal || 0))} ${tr("units.kcal")}`}
      />
      <MyFoodMacroChip
        label={tr("food.report.proteinShort")}
        value={`${+Number(m.p || 0).toFixed(1)} ${tr("units.gramsShort")}`}
      />
      <MyFoodMacroChip
        label={tr("food.report.fatShort")}
        value={`${+Number(m.f || 0).toFixed(1)} ${tr("units.gramsShort")}`}
      />
      <MyFoodMacroChip
        label={tr("food.report.carbsShort")}
        value={`${+Number(m.c || 0).toFixed(1)} ${tr("units.gramsShort")}`}
      />
    </div>
  </div>
</div>

            {isDish && detailsOpen && (
              <div
                className="mt-3 min-w-0 overflow-visible"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-2 flex items-center justify-start">
                  <div className="inline-flex shrink-0 rounded-[13px] border border-cyan-300/25 bg-cyan-400/10 px-3 py-1.5 text-[12px] font-semibold text-cyan-100 shadow-[0_0_12px_rgba(45,230,230,0.04)]">
                    {tr("ration.ingredientCount", {
                      count: ingredientCount,
                    })}
                  </div>
                </div>

                <div className="pb-1">
<DishIngredientsList
  compactMeal={true}
  dish={displayFood}
  foods={myFoods}
  hasDishDraftChanges={hasMealDishDraftChanges}
  onAddIngredient={(ingredientFood) => {
    addIngredientToMealDish(meal.id, item.id, ingredientFood);
  }}
  onSaveAsNewDish={() => {
  openMealDishAsNewBuilder(meal.id, item.id, displayFood);
}}
  onEditIngredient={(ingredientIndex, ing) => {
    handleEditDishIngredientWeight(
      meal.id,
      item.id,
      ingredientIndex,
      ing
    );
  }}
  onEditIngredientDetails={(ingredientIndex, ing) => {
    const sourceIngredient = (myFoods || []).find(
      (food) => String(food.id) === String(ing.id)
    );

    const grams = Math.max(1, Number(ing.grams || 100));

    const per100FromDish = {
      proteins: +((Number(ing.proteins || 0) / grams) * 100).toFixed(1),
      fats: +((Number(ing.fats || 0) / grams) * 100).toFixed(1),
      carbs: +((Number(ing.carbs || 0) / grams) * 100).toFixed(1),
      kcal: Math.round((Number(ing.kcal || 0) / grams) * 100),
    };

    setEditingMealDishIngredientFood({
      mealId: meal.id,
      itemId: item.id,
      ingredientIndex,
      food: {
        ...(sourceIngredient || {}),
        id: ing.id || sourceIngredient?.id || "",
        type: "ingredient",
        name: ing.name || "Ингредиент",
        defaultGrams: grams,
        per100: sourceIngredient?.per100 || per100FromDish,
        __editTitle: ing.name || "Редактировать ингредиент",
      },
    });
  }}
  onDeleteIngredient={(ingredientIndex, ing) => {
    deleteDishIngredientFromMeal(
      meal.id,
      item.id,
      ingredientIndex,
      ing
    );
  }}
/>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
)}

            </div>
          );
        })}
      </div>
      {/* system photo picker (iOS: Медиатека / Сделать снимок / Выбрать файл) */}
<input
  ref={photoInputRef}
  type="file"
  accept="image/*"
  className="hidden"
  onChange={async (e) => {
const file = e.target.files?.[0];

if (!file) {
  resetNativePhotoPickers();
  pendingPhotoMealIdRef.current = null;
  return;
}

    const mealId = pendingPhotoMealIdRef.current;
    pendingPhotoMealIdRef.current = null;
    if (!mealId) return;
    setPhotoTargetMealId(mealId);

setAddModal(null);

let base64 = "";

try {
  base64 = await compressPhotoForAnalysis(file);
} catch {
  setPhotoError(
    tr("ration.myFoods.photoFlow.prepareError")
  );
  e.target.value = "";
  return;
}

// 1) подготовить данные
setPhotoPick({ previewUrl: base64, base64 });
setPhotoResult(null);
setPhotoError(null);
setPhotoLimitNotice(null);

    // 2) закрыть попап выбора способа (если открыт)
    setAddModal(null);

    // 3) открыть экран "Добавить по фото"
    setAddFlow({ mealId, screen: "photo" });

    // 4) сразу запустить анализ
    analyzePhotoBase64(base64);

    // чтобы можно было выбрать тот же файл снова
    e.target.value = "";
  }}
/>

      {/* отдельные "страницы" после попапа выбора способа */}
{addFlow?.screen === "my" && (
  <Portal>
    <div
      className="fixed inset-0 z-[30000] overflow-hidden"
      style={{
        background: SHEET_PREMIUM_BG,
        overscrollBehavior: "none",
      }}
    >
      <div
        className="absolute inset-0 overflow-y-auto overscroll-contain"
        data-my-foods-scroll-root="1"
        style={{
          WebkitOverflowScrolling: "touch",
          background: SHEET_PREMIUM_BG,
          overscrollBehaviorY: "none",
          touchAction: "pan-y",
          scrollPaddingTop: "18px",
          scrollPaddingBottom: "132px",
          paddingBottom: 0,
          overflowAnchor: "none",
        }}
      >
        <div
          className="relative w-full max-w-[420px] mx-auto px-4 pt-[calc(env(safe-area-inset-top)+10px)]"
          style={{
            paddingBottom: "132px",
          }}
        >

<MyFoodsSheet
  open={true}
  onClose={closeGlobalMyFoodsPicker}
    initialTab={addFlow.initialTab || "ingredients"}
  mealTitle={meals.find((m) => m.id === addFlow.mealId)?.title || ""}
  foods={myFoods}
onConfirm={(foodsPicked) => {
  const picked = foodsPicked || [];

  if (addFlow?.chooseMealAfter) {
    setPendingMyFoodsAdd({ foods: picked });
    setPendingMyFoodsMealId("");
    return;
  }

  picked.forEach((food) => {
    addFoodToMealFromMyFoods(addFlow.mealId, food);
  });

  setExpandedIds((prev) =>
    prev.some((id) => String(id) === String(addFlow.mealId))
      ? prev
      : [...prev, addFlow.mealId]
  );

  setAddFlow(null);
}}
onCreateDish={async (dish) => {
  return await createDishInMyFoodsAndServer(dish);
}}
  onUpdateFood={(food) => {
    saveMyFoodAndSyncToday(food);
  }}
onCreateProduct={() => {
  const mealId = addFlow.mealId;

setAddFlow({
  mealId,
  screen: "manual",
  from: "my-foods",
  returnTab: "ingredients",
  chooseMealAfter: !!addFlow?.chooseMealAfter,
});

  resetForm();
}}
  onDelete={(foodOrId) => deleteMyFood(foodOrId)}
/>

        </div>
      </div>
    </div>
  </Portal>
)}
{pendingMyFoodsAdd && (
  <Portal>
   <div className="fixed inset-0 z-[90000] flex items-start justify-center px-3 pt-[90px] pb-3">
      <div
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={() => {
          setPendingMyFoodsAdd(null);
          setPendingMyFoodsMealId("");
        }}
      />

      <div className="relative w-full max-w-[420px] rounded-[28px] border border-white/15 bg-[#202b3d] p-4 shadow-[0_-18px_60px_rgba(0,0,0,0.7)]">
<div className="text-[17px] font-bold text-white">
  {tr("ration.myFoods.chooseMeal.title")}
</div>

<div className="mt-1 text-[13px] font-semibold leading-snug text-emerald-100/90">
  {(pendingMyFoodsAdd.foods || [])
    .map(
      (food) =>
        food.title ||
        food.name ||
        tr("ration.myFoods.chooseMeal.dishFallback")
    )
    .join(", ")}
</div>

<div className="mt-1 text-[13px] leading-snug text-white/65">
  {tr("ration.myFoods.chooseMeal.subtitle")}
</div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {(meals || []).map((meal) => {
            const active = String(pendingMyFoodsMealId) === String(meal.id);

            return (
              <button
                key={meal.id}
                type="button"
                onClick={() => setPendingMyFoodsMealId(meal.id)}
                className={
                  "h-11 rounded-2xl border px-3 text-[13px] font-semibold active:scale-[0.98] " +
                  (active
                    ? "border-emerald-200/70 bg-emerald-400/20 text-emerald-50"
                    : "border-white/10 bg-white/[0.06] text-white/75")
                }
              >
                {getRationMealTitle(meal.title)}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              setPendingMyFoodsAdd(null);
              setPendingMyFoodsMealId("");
            }}
            className="h-11 rounded-2xl border border-white/10 bg-white/[0.06] text-[13px] font-semibold text-white/75 active:scale-[0.98]"
          >
            {tr("actions.cancel")}
          </button>

          <button
            type="button"
            disabled={!pendingMyFoodsMealId}
            onClick={() => {
              const targetMealId = pendingMyFoodsMealId;

              (pendingMyFoodsAdd.foods || []).forEach((food) => {
                addFoodToMealFromMyFoods(targetMealId, food);
              });

              setExpandedIds((prev) =>
                prev.some((id) => String(id) === String(targetMealId))
                  ? prev
                  : [...prev, targetMealId]
              );

              setPendingMyFoodsAdd(null);
              setPendingMyFoodsMealId("");
              setAddFlow(null);
            }}
            className={
              "h-11 rounded-2xl text-[13px] font-bold active:scale-[0.98] " +
              (pendingMyFoodsMealId
                ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.45)]"
                : "bg-slate-700/60 text-slate-400")
            }
          >
            {tr("ration.myFoods.chooseMeal.confirm")}
          </button>
        </div>
      </div>
    </div>
  </Portal>
)}
{addFlow?.screen === "photo" && (
  <Portal>
<div
  className="fixed inset-0 z-[60000] overflow-hidden"
  style={{
    height: "var(--app-height, 100vh)",
    background: APP_BG,
    overscrollBehavior: "none",
  }}
>
      <div
        className="absolute inset-0 overflow-hidden"
        style={{
          height: "100%",
          maxHeight: "100%",
          overscrollBehavior: "none",
        }}
      >
        <div className="relative mx-auto flex h-full w-full max-w-[420px] flex-col px-3 pt-[calc(8px+env(safe-area-inset-top))] pb-[calc(8px+env(safe-area-inset-bottom,0px))]">
<div
  className="relative isolate flex flex-col overflow-hidden rounded-[28px] border border-white/20 bg-white/12 p-3 text-left shadow-[0_18px_55px_rgba(0,0,0,0.55)] backdrop-blur-xl"
  style={{
    maxHeight: "calc(var(--app-height, 100vh) - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
  }}
>
  <div className="mb-0 flex shrink-0 items-center justify-between gap-3">
    <div className="min-w-0 text-[18px] font-bold text-slate-50">
      {tr("ration.myFoods.photoFlow.title")}
    </div>

    <AppCloseButton
      className="h-8 w-8"
      onClick={() => setAddFlow(null)}
    />
  </div>

<div
  ref={addFlowScrollRef}
  data-photo-scroll-root="1"
  className="overflow-y-auto pr-1"
style={{
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "none",
    touchAction: "pan-y",
    // Не используем var(--kb) здесь.
    // Иначе при открытой клавиатуре появляется большая пустая прокрутка.
maxHeight:
  "calc(var(--app-height, 100vh) - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
    scrollPaddingBottom: "56px",
    overflowAnchor: "none",
  }}
>

         

          {/* Камера (скрытый input) */}
<input
  ref={cameraInputRef}
  type="file"
  accept="image/*"
  capture="environment"
  onChange={async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

let base64 = "";

try {
  base64 = await compressPhotoForAnalysis(file);
} catch {
  setPhotoError(
    tr("ration.myFoods.photoFlow.prepareError")
  );
  e.target.value = "";
  return;
}

setPhotoPick({ previewUrl: base64, base64 });
setPhotoResult(null);
setPhotoError(null);
setPhotoLimitNotice(null);

    // чтобы можно было выбрать тот же файл снова
    e.target.value = "";
  }}
  className="hidden"
/>

{/* Галерея (скрытый input) */}
<input
  ref={galleryInputRef}
  type="file"
  accept="image/*"
  onChange={async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

let base64 = "";

try {
  base64 = await compressPhotoForAnalysis(file);
} catch {
  setPhotoError(
    tr("ration.myFoods.photoFlow.prepareError")
  );
  e.target.value = "";
  return;
}

setPhotoPick({ previewUrl: base64, base64 });
setPhotoResult(null);
setPhotoError(null);

e.target.value = "";
  }}
  className="hidden"
/>


{/* Большая фотография остаётся и во время анализа, и после него */}
{photoPick?.previewUrl && (
  <div className="mt-1 overflow-hidden rounded-[22px] border border-white/10 bg-black/25">
    <img
      src={photoPick.previewUrl}
      alt={tr("ration.myFoods.photoFlow.imageAlt")}
      className="block h-[min(205px,27vh)] w-full object-cover"
    />
  </div>
)}

          {/* 3) кнопки анализа нужны только до результата */}
          {!photoResult && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setAddFlow(null)}
                className="flex-1 h-10 rounded-2xl border border-white/20 bg-slate-900/60 text-[13px] font-medium text-slate-100 hover:bg-slate-800 active:scale-[0.97]"
              >
                {tr("actions.cancel")}
              </button>

              <button
                type="button"
                disabled={!photoPick?.base64 || photoLoading}
                onClick={() => {
                  if (!photoPick?.base64 || photoLoading) return;
                  analyzePhotoBase64(photoPick.base64);
                }}
                className={
                  "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                  (!photoPick?.base64 || photoLoading
                    ? "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60"
                    : "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110 active:scale-[0.97]")
                }
              >
                <span className="block min-w-0 truncate px-1">
                  {photoLoading
                    ? photoLoadingText
                    : tr("ration.myFoods.photoFlow.recognize")}
                </span>
              </button>
            </div>
          )}

{/* 4) лимит / ошибки / результат */}
{photoLimitNotice && !photoResult && (
  <div className="mt-3 rounded-[22px] border border-[#18d7bd]/25 bg-[#051723]/86 px-4 py-3 shadow-[0_14px_34px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.055)] backdrop-blur-xl">
    <div className="text-center text-[14px] font-bold leading-snug tracking-[-0.01em] text-slate-50">
      {tr("ration.myFoods.photoFlow.limitReached")}
    </div>

    {photoLimitNotice.upgradeAvailable && (
      <button
        type="button"
        onClick={() => {
          setAddModal(null);
          setAddFlow(null);
          onPremiumRequired?.("photo_daily_limit_button");
        }}
        className="mt-3 h-10 w-full rounded-[16px] border border-[#18d7bd]/45 bg-[#18d7bd]/[0.105] px-4 text-[13px] font-bold text-[#35F3DF] shadow-[0_0_16px_rgba(24,215,189,0.12),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl active:scale-[0.98]"
      >
        {tr("ration.myFoods.photoFlow.upgradeLimit")}
      </button>
    )}
  </div>
)}

{photoError && !photoLimitNotice && (
  <div className="mt-3 rounded-[18px] border border-rose-300/20 bg-rose-950/20 px-3 py-2 text-[12px] font-medium leading-snug text-rose-100">
    {photoError}
  </div>
)}

{photoPick?.previewUrl && (
  <div
    ref={photoResultRef}
    data-photo-result-card="1"
    className="mt-2"
  >

{photoResult?.is_food === false ? (
  <div className="mt-3 overflow-hidden rounded-[22px] border border-[#35F3DF]/35 bg-[linear-gradient(135deg,rgba(24,215,189,0.18),rgba(5,23,35,0.78)_48%,rgba(14,165,233,0.12))] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-1px_0_rgba(24,215,189,0.08),0_0_18px_rgba(24,215,189,0.08)] backdrop-blur-xl">
    <div className="text-center text-[14px] font-bold leading-snug tracking-[-0.01em] text-slate-50">
      {tr("ration.myFoods.photoFlow.notFood")}
    </div>
  </div>
) : (
  <fieldset
    disabled={!photoResult || photoLoading || isAddingPhotoDish}
    className={cn(
      "m-0 min-w-0 border-0 p-0 transition-opacity duration-200",
      !photoResult || photoLoading
        ? "pointer-events-none opacity-[0.55]"
        : "opacity-100"
    )}
  >
    <div className="grid gap-2">
          {/* Название блюда */}
          <label className="text-[11px] text-slate-300">
            {tr("ration.myFoods.photoFlow.name")}
            <input
              type="text"
              value={photoResult?.meal_name ?? ""}
{...photoResultInputProps}              
              onChange={(e) =>
                setPhotoResult((prev) => ({
                  ...(prev || {}),
                  meal_name: e.target.value,
                }))
              }
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
              placeholder=""
            />
          </label>

          {/* Граммы: меняешь — пересчитываются ккал и БЖУ без “улёта” */}
          <label className="text-[11px] text-slate-300">
            {tr("ration.myFoods.photoFlow.servingGrams")}
            <input
              type="text"
              inputMode="decimal"
              value={photoResult?.total?.portion_grams ?? ""}
{...photoResultInputProps}
onChange={(e) => {
  const raw = sanitizePhotoNumberInput(e.target.value, { decimal: true });
  const nextGrams = readPhotoNumber(raw);

  setPhotoResult((prev) => {
    const t = prev?.total || {};

    // Пустое поле оставляем пустым, чтобы при удалении цифр не было прыжков.
    if (!Number.isFinite(nextGrams)) {
      return {
        ...prev,
        total: {
          ...t,
          portion_grams: raw,
        },
      };
    }

    const currentGrams = readPhotoNumber(t.portion_grams);

    const baseGrams =
      Number.isFinite(readPhotoNumber(t.base_grams)) &&
      readPhotoNumber(t.base_grams) > 0
        ? readPhotoNumber(t.base_grams)
        : Number.isFinite(currentGrams) && currentGrams > 0
        ? currentGrams
        : nextGrams;

    const baseP =
      t.base_p != null ? photoNumberOrZero(t.base_p) : photoNumberOrZero(t.proteins);
    const baseF =
      t.base_f != null ? photoNumberOrZero(t.base_f) : photoNumberOrZero(t.fats);
    const baseC =
      t.base_c != null ? photoNumberOrZero(t.base_c) : photoNumberOrZero(t.carbs);

    const baseKcal =
      t.base_kcal != null
        ? photoNumberOrZero(t.base_kcal)
        : t.kcal != null
        ? photoNumberOrZero(t.kcal)
        : Math.round(baseP * 4 + baseF * 9 + baseC * 4);

    const k = baseGrams > 0 ? nextGrams / baseGrams : 1;

    return {
      ...prev,
      total: {
        ...t,
        portion_grams: raw,
        proteins: +((baseP * k)).toFixed(1),
        fats: +((baseF * k)).toFixed(1),
        carbs: +((baseC * k)).toFixed(1),
        kcal: Math.round(baseKcal * k),

        base_grams: baseGrams,
        base_p: baseP,
        base_f: baseF,
        base_c: baseC,
        base_kcal: baseKcal,
      },
    };
  });
}}
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
              placeholder="150"
            />
          </label>

          {/* Ручная правка КБЖУ */}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-300">
              {tr("ration.myFoods.photoFlow.calories")}
              <input
                type="text"
                inputMode="numeric"
                value={photoResult?.total?.kcal ?? ""}
{...photoResultInputProps}
onChange={(e) => updatePhotoKcalInput(e.target.value)}
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
                placeholder="0"
              />
            </label>

            <label className="text-[11px] text-slate-300">
              {tr("ration.myFoods.photoFlow.proteinGrams")}
              <input
                type="text"
                inputMode="decimal"
                value={photoResult?.total?.proteins ?? ""}
{...photoResultInputProps}
               onChange={(e) => updatePhotoMacroInput("proteins", "base_p", e.target.value)}
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
                placeholder="0"
              />
            </label>

            <label className="text-[11px] text-slate-300">
              {tr("ration.myFoods.photoFlow.fatGrams")}
              <input
                type="text"
                inputMode="decimal"
                value={photoResult?.total?.fats ?? ""}
{...photoResultInputProps}
                onChange={(e) => updatePhotoMacroInput("fats", "base_f", e.target.value)}
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
                placeholder="0"
              />
            </label>

            <label className="text-[11px] text-slate-300">
              {tr("ration.myFoods.photoFlow.carbsGrams")}
              <input
                type="text"
                inputMode="decimal"
                value={photoResult?.total?.carbs ?? ""}
{...photoResultInputProps}
                onChange={(e) => updatePhotoMacroInput("carbs", "base_c", e.target.value)}
className="mt-1 block h-10 w-full rounded-2xl border border-cyan-300/20 bg-slate-950/65 px-3 text-[16px] leading-10 text-slate-50 outline-none placeholder:text-slate-500 focus:border-cyan-300/45 focus:ring-2 focus:ring-cyan-300/15"
                placeholder="0"
              />
            </label>
          </div>
        </div>

        {/* выбор приёма пищи для добавления */}
        <div className="mt-2">
          <div className="text-[11px] text-slate-300 mb-2">
            {tr("ration.myFoods.photoFlow.whereToAdd")}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {meals.map((m) => {
              const active = Number(photoTargetMealId ?? addFlow.mealId) === Number(m.id);


              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPhotoTargetMealId(Number(m.id))}

                  className={[
                    "shrink-0 h-8 px-3 rounded-2xl border text-[11px] font-semibold",
                    active
                      ? "border-emerald-200/50 bg-emerald-500/20 text-emerald-50"
                      : "border-white/10 bg-slate-900/60 text-slate-200",
                  ].join(" ")}
                >
                  {getRationMealTitle(m.title)}
                </button>
              );
            })}
          </div>
        </div>

        <button
  type="button"
  disabled={isAddingPhotoDish}
  onClick={async () => {
    if (isAddingPhotoDish) return;

    const targetMealId = Number(photoTargetMealId ?? addFlow.mealId);
    if (!targetMealId) return;

    setIsAddingPhotoDish(true);

    try {
    // 1) вычисляем mealSlot (breakfast/lunch/dinner/snack)
    const mealTitle = meals.find((m) => Number(m.id) === targetMealId)?.title || "";
    const mealSlot = mealSlotFromTitle(mealTitle);





// 2) данные из распознавания
// ВАЖНО: результат фото сохраняем как ПРОДУКТ, а не как блюдо.
// Поэтому у него не будет пустого состава в "Моих блюдах".
const title =
  (photoResult?.meal_name || "").trim() ||
  (photoResult?.dishes?.[0]?.name || "").trim() ||
  "Продукт";

const t = photoResult?.total || {};
const proteins = photoNumberOrZero(t.proteins);
const fats = photoNumberOrZero(t.fats);
const carbs = photoNumberOrZero(t.carbs);
const grams = Math.max(1, readPhotoNumber(t.portion_grams) || 100);
const kcal =
  t.kcal != null ? photoNumberOrZero(t.kcal) : Math.round(proteins * 4 + fats * 9 + carbs * 4);

// 3) Сначала сохраняем распознанное как продукт в user_foods (Мои продукты)
const per100k = grams > 0 ? 100 / grams : 1;

const savedProductResp = await apiUpsertUserFood({
  type: "ingredient",
  name: title,
  lastMeal: mealTitle,
  defaultGrams: grams,
  per100: {
    kcal: +((kcal || 0) * per100k).toFixed(1),
    proteins: +((proteins || 0) * per100k).toFixed(1),
    fats: +((fats || 0) * per100k).toFixed(1),
    carbs: +((carbs || 0) * per100k).toFixed(1),
  },
});

const savedProduct = savedProductResp?.data
  ? savedProductResp.data
  : savedProductResp;

const savedProductApp = {
  id: savedProduct?.id,
  name: savedProduct?.name ?? title,
  type: "ingredient",
  lastMeal: mealTitle,
  defaultGrams:
    savedProduct?.default_grams ??
    savedProduct?.defaultGrams ??
    grams,
  per100: {
    kcal: Number(
      savedProduct?.kcal_per100 ??
        savedProduct?.kcalPer100 ??
        (kcal || 0) * per100k
    ),
    proteins: Number(
      savedProduct?.p_per100 ??
        savedProduct?.pPer100 ??
        (proteins || 0) * per100k
    ),
    fats: Number(
      savedProduct?.f_per100 ??
        savedProduct?.fPer100 ??
        (fats || 0) * per100k
    ),
    carbs: Number(
      savedProduct?.c_per100 ??
        savedProduct?.cPer100 ??
        (carbs || 0) * per100k
    ),
  },
  updatedAt: Date.now(),
};

// Чтобы сразу появилось в UI “Мои продукты”
createMyFood(savedProductApp);

const savedProductId = isRealFoodId(savedProductApp.id)
  ? Number(savedProductApp.id)
  : null;

// 4) Потом сохраняем запись в рацион как продукт, без dish_snapshot/состава
const row = await apiAddFoodEntry({
  day: dayKey,
  meal_slot: mealSlot,
  entry_type: "ingredient",
  name: title,
  grams,
  kcal,
  proteins,
  fats,
  carbs,
  userFoodId: savedProductId,
  sourceUserFoodId: null,
  dishSnapshot: null,
});

const savedItem = {
  ...dbFoodEntryToItem(row),
  type: "ingredient",
  sourceMyFoodId: getMyFoodIdKey(savedProductApp),
  userFoodId: savedProductId,
  sourceUserFoodId: null,
  dishSnapshot: null,
  dish_snapshot: null,
  recipe: null,
  ingredients: [],
};
      setMeals((prev) =>
        prev.map((m) =>
          Number(m.id) !== Number(targetMealId)

            ? m
            : { ...m, items: [...(m.items || []), savedItem] }
        )
      );

      setExpandedIds((prev) =>
        prev.includes(targetMealId) ? prev : [...prev, targetMealId]
      );

      // 5) закрываем экран фото
       setAddFlow(null);
      setPhotoPick(null);
      setPhotoResult(null);
      setPhotoError(null);
    } catch (e) {
      console.error("photo save error:", e);
      setPhotoError(String(e?.message || e));
    } finally {
      setIsAddingPhotoDish(false);
    }
  }}
className={
  "mt-2 w-full h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
  (isAddingPhotoDish
    ? "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60"
: "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-none hover:brightness-105 active:brightness-110 active:scale-[0.99]")
}
>
  {isAddingPhotoDish
    ? tr("ration.myFoods.photoFlow.adding")
    : tr("ration.myFoods.photoFlow.add")}
</button>

  </fieldset>
)}
  </div>
)}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Portal>
)}

{addFlow?.screen === "manual" && (
  <Portal>
<div
  className="fixed inset-0 z-[80000] flex items-start justify-center overflow-hidden px-4 pt-[calc(72px+env(safe-area-inset-top,0px))]"
  style={{
    overscrollBehavior: "none",
    touchAction: "none",
  }}
  onTouchMove={(e) => {
    e.preventDefault();
  }}
>
      {addFlow?.from === "my-foods" && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: SHEET_PREMIUM_BG }}
        />
      )}

      <div className="pointer-events-none absolute inset-0 bg-black/38 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-[390px]"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >


<div
  data-onboarding="manual-form"
  className="rounded-[26px] border border-cyan-300/35 bg-slate-900/95 p-4 text-left shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl">
    <div className="mb-3 flex items-start justify-between gap-3">
  <div>
    <div className="text-[15px] font-bold text-slate-50">
      {tr("ration.myFoods.productForm.title")}
    </div>
    <div className="mt-0.5 text-[11px] font-medium text-white/55">
      {tr("ration.myFoods.productForm.macrosPer100")}
    </div>
  </div>

<AppCloseButton
  data-onboarding-cancel="1"
  onClick={() => {
    if (addFlow?.from === "my-foods") {
      setAddFlow({
        mealId: addFlow.mealId,
        screen: "my",
        initialTab: addFlow.returnTab || "ingredients",
        chooseMealAfter: !!addFlow?.chooseMealAfter,
      });
      return;
    }

    setAddFlow(null);
  }}
/>
</div>

          <input
            ref={nameRef}
            type="text"
            enterKeyHint="next"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={tr("ration.myFoods.productForm.namePlaceholder")}
            autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck={false}
            style={{
              caretColor: "#f8fafc",
              WebkitAppearance: "none",
              appearance: "none",
              lineHeight: "normal",
            }}
            className="block h-11 w-full rounded-2xl border border-cyan-300/35 bg-[#07111f] px-3 py-0 text-[16px] text-slate-50 placeholder:text-slate-300/55 outline-none shadow-[0_0_12px_rgba(45,230,230,0.04)] focus:border-cyan-300/55 focus:ring-2 focus:ring-cyan-300/20"
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              pRef.current?.focus?.();
            }}
          />

          {/* Ввод БЖУ на 100 г */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            <input
              ref={pRef}
              type="text"
              inputMode="decimal"
              enterKeyHint="next"
              value={formProteins}
              onChange={(e) => setFormProteins(e.target.value)}
              placeholder={tr("food.report.proteinShort")}
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              style={{
                caretColor: "#f8fafc",
                WebkitAppearance: "none",
                appearance: "none",
                lineHeight: "normal",
              }}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                fRef.current?.focus?.();
              }}
            />

            <input
              ref={fRef}
              type="text"
              inputMode="decimal"
              enterKeyHint="next"
              value={formFats}
              onChange={(e) => setFormFats(e.target.value)}
              placeholder={tr("food.report.fatShort")}
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              style={{
                caretColor: "#f8fafc",
                WebkitAppearance: "none",
                appearance: "none",
                lineHeight: "normal",
              }}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                cRef.current?.focus?.();
              }}
            />

            <input
              ref={cRef}
              type="text"
              inputMode="decimal"
              enterKeyHint="done"
              value={formCarbs}
              onChange={(e) => setFormCarbs(e.target.value)}
              placeholder={tr("food.report.carbsShort")}
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              style={{
                caretColor: "#f8fafc",
                WebkitAppearance: "none",
                appearance: "none",
                lineHeight: "normal",
              }}
className="block h-11 w-full rounded-2xl border border-transparent bg-[#07111f] px-3 py-0 text-center text-[16px] font-semibold text-slate-50 caret-slate-50 placeholder:text-slate-400 outline-none shadow-none focus:border-cyan-300/35 focus:ring-0"
onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                handleAddDishManual();
              }}
            />
          </div>

          {/* Автоподсчёт ккал */}
          <div className="mt-3 rounded-2xl border border-cyan-300/25 bg-[#07111f] px-3 py-2 text-[12px] font-semibold text-cyan-50 shadow-[0_0_10px_rgba(45,230,230,0.04)]">
            {(() => {
const p = parseMacro(formProteins);
const f = parseMacro(formFats);
const c = parseMacro(formCarbs);
const kcal = Math.round(p * 4 + f * 9 + c * 4);

              return (
                <>
{tr("ration.myFoods.productForm.per100")}{" "}
{tr("food.report.proteinShort")} {formatMacroPreview(p)} ·{" "}
{tr("food.report.fatShort")} {formatMacroPreview(f)} ·{" "}
{tr("food.report.carbsShort")} {formatMacroPreview(c)} ·{" "}
{Math.round(kcal)} {tr("units.kcal")}
                </>
              );
            })()}
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => {
                resetForm();

if (addFlow?.from === "my-foods") {
  setAddFlow({
    mealId: addFlow.mealId,
    screen: "my",
    initialTab: addFlow.returnTab || "ingredients",
    chooseMealAfter: !!addFlow?.chooseMealAfter,
  });
  return;
}

setAddFlow(null);
              }}
              className="flex-1 h-10 rounded-2xl border border-white/20 bg-slate-900/60 text-[13px] font-medium text-slate-100 hover:bg-slate-800 active:scale-[0.97]"
            >
              {tr("actions.cancel")}
            </button>

            <button
              type="button"
              disabled={isSavingManualFood}
              onClick={handleAddDishManual}
              className={
                "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                (isSavingManualFood
                  ? "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60"
                  : "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110 active:scale-[0.97]")
              }
            >
              {isSavingManualFood
                ? tr("ration.myFoods.productForm.saving")
                : tr("actions.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Portal>
)}


                        {/* модалка выбора способа добавления блюда — через портал */}
      {addModal &&
        addModal.step === "mode" &&
        typeof document !== "undefined" &&
        createPortal(
          <div
className="fixed inset-0 z-[60000] flex items-start justify-center overflow-y-auto px-4 pt-[calc(72px+env(safe-area-inset-top))] pb-4 bg-black/38 backdrop-blur-sm"
            onClick={() => {
              // 1) если открыта клава — сначала просто закрываем её
             
              // 2) если клавы нет — закрываем модалку
              closeAddModal();
            }}
          >
            <div
              className="relative w-full max-w-[390px]"
              onClick={(e) => e.stopPropagation()}
            >
<GlassCard dense className="!rounded-[26px] !border-cyan-300/35 !bg-slate-900/95 p-4 shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl">
      <div className="mb-3 flex items-center justify-between">
   <div>
  <div className="text-[15px] font-bold text-slate-50">
{tr("ration.myFoods.addMode.title")}
  </div>
  <div className="mt-0.5 text-[11px] font-medium text-white/55">
{tr("ration.myFoods.addMode.subtitle")}
  </div>
</div>
<AppCloseButton
  data-onboarding-cancel="1"
  onClick={closeAddModal}
/>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const mealId = addModal.mealId;
                    closeAddModal();
                                        setAddFlow({
                      mealId,
                      screen: "my",
                      initialTab: "ingredients",
                    });
                  }}
className="flex w-full items-center justify-between rounded-2xl border border-cyan-300/35 bg-cyan-400/[0.08] px-3.5 py-3 text-[13px] font-bold text-cyan-50 shadow-[0_0_14px_rgba(45,230,230,0.07),inset_0_1px_0_rgba(255,255,255,0.07)] active:scale-[0.99]"
           >
                  <span>{tr("ration.myFoods.addMode.myDishes")}</span>
                  <span className="text-[16px]">⭐</span>
                </button>

                <div className="mt-2 flex flex-col gap-2">
                  <button
  type="button"
  onClick={() => {
    const mealId = addModal.mealId;
    logEvent("meal:add:photo", { mealId });
    startPhotoPick(mealId); // открывает системный выбор (медиатека/камера/файл)
  }}
className="flex w-full items-center justify-between rounded-2xl border border-white/15 bg-white/10 px-3.5 py-3 text-[13px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] active:scale-[0.99]"
>
  <span>{tr("ration.myFoods.addMode.photo")}</span>
  <span className="text-[16px]">📷</span>
</button>


<button
  type="button"
  data-onboarding="manual-add"

  onClick={() => {
    const mealId =
      addModal.mealId;

    closeAddModal();

    setAddFlow({
      mealId,
      screen: "manual",
      from: "add-popup",
    });

    resetForm();

    emitAppEvent(
      "manual-form-opened",
      {
        mealId,
      }
    );
  }}
className="flex w-full items-center justify-between rounded-2xl border border-white/15 bg-white/10 px-3.5 py-3 text-[13px] font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] active:scale-[0.99]"
>
  <span>{tr("ration.myFoods.addMode.manual")}</span>
  <span className="text-[16px]">✍️</span>
</button>

                </div>
              </GlassCard>
            </div>
          </div>,
          document.body
        )}
{mealDishBuilderSeed && (
  <DishBuilderModal
    open={!!mealDishBuilderSeed}
    onClose={() => setMealDishBuilderSeed(null)}
    foods={myFoods}
    mealTitle={mealDishBuilderSeed.lastMeal || ""}
    initialDish={mealDishBuilderSeed}
    onUpdateFood={saveMyFoodAndSyncToday}
onCreateDish={async (dish) => {
  const sourceMealId = mealDishBuilderSeed?.__sourceMealId;
  const sourceItemId = mealDishBuilderSeed?.__sourceItemId;

  const savedDishApp = await createDishInMyFoodsAndServer(dish);

  if (sourceMealId != null && sourceItemId != null) {
    const oldMeals = meals;

    const grams = Math.max(
      1,
      Number(savedDishApp.defaultGrams ?? savedDishApp.weight ?? 100)
    );

    const per100 = savedDishApp.per100 || {};
    const k = grams / 100;

    const proteins = +((Number(per100.proteins || 0)) * k).toFixed(1);
    const fats = +((Number(per100.fats || 0)) * k).toFixed(1);
    const carbs = +((Number(per100.carbs || 0)) * k).toFixed(1);

    const kcalPer100 =
      per100.kcal ??
      calcKcalPer100(
        Number(per100.proteins || 0),
        Number(per100.fats || 0),
        Number(per100.carbs || 0)
      );

    const kcal = Math.round(Number(kcalPer100 || 0) * k);

const savedDishIdNumber = isRealFoodId(savedDishApp.id)
  ? Number(savedDishApp.id)
  : null;

const savedEntryDish = {
  ...savedDishApp,
  weight: grams,
  grams,
  proteins,
  fats,
  carbs,
  kcal,
};

const savedEntryPatch = makeMealDishEntryPatch(
  { sourceUserFoodId: savedDishIdNumber },
  savedEntryDish,
  false
);

setMeals((prev) =>
  prev.map((m) =>
    String(m.id) !== String(sourceMealId)
      ? m
      : {
          ...m,
          items: (m.items || []).map((it) =>
            String(it.id) !== String(sourceItemId)
              ? it
              : {
                  ...it,
                  type: "dish",
                  name: savedDishApp.name,
                  weight: grams,
                  proteins,
                  fats,
                  carbs,
                  kcal,
                  recipe: savedEntryPatch.dish_snapshot.recipe,
                  ingredients: savedEntryPatch.dish_snapshot.ingredients,
                  dishSnapshot: savedEntryPatch.dish_snapshot,
                  dish_snapshot: savedEntryPatch.dish_snapshot,
                  userFoodId: null,
                  sourceUserFoodId: savedDishIdNumber,
                  sourceMyFoodId: getMyFoodIdKey(savedDishApp),
                  changedFromSource: false,
                }
          ),
        }
  )
);

try {
  await apiUpdateFoodEntry(sourceItemId, savedEntryPatch);

  const key = getMealDishDraftKey(sourceMealId, sourceItemId);

  setChangedMealDishKeys((prev) => {
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
    } catch (e) {
      console.error("save new dish to meal entry failed:", e);
      setMeals(oldMeals);
      safeAlert(
        "Новое блюдо сохранилось, но не получилось заменить его в рационе дня.",
        "Ошибка"
      );
      throw e;
    }
  }

  setMealDishBuilderSeed(null);
}}
  />
)}
{editingMealDish && (
  <EditDishNameModal
    open={!!editingMealDish}
    dish={editingMealDish.dish}
    onClose={() => setEditingMealDish(null)}
    onSave={saveMealDishName}
  />
)}

{editingMealFood && (
  <EditMyFoodModal
    open={!!editingMealFood}
    food={editingMealFood.food}
    onClose={() => setEditingMealFood(null)}
    onSave={saveMealFoodDetails}
  />
)}

{editingMealDishIngredientFood && (
  <EditMyFoodModal
    open={!!editingMealDishIngredientFood}
    food={editingMealDishIngredientFood.food}
    onClose={() => setEditingMealDishIngredientFood(null)}
    onSave={saveMealDishIngredientDetails}
  />
)}
<FullNamePopup
  open={!!mealFullNamePopup}
  title={mealFullNamePopup?.title}
  name={mealFullNamePopup?.name}
  onClose={() => setMealFullNamePopup(null)}
/>
{/* модалка изменения граммовки */}
{weightModal && (
  <GramsModal
    open={!!weightModal}
    initial={weightModal.initial}
    onClose={() => setWeightModal(null)}
    onSubmit={(grams) => {
      if (weightModal.kind === "dishIngredient") {
        applyDishIngredientWeight(
          weightModal.mealId,
          weightModal.itemId,
          weightModal.ingredientIndex,
          grams
        );
      } else {
        applyNewWeight(weightModal.mealId, weightModal.itemId, grams);
      }

      setWeightModal(null);
    }}
  />
)}

    </div>
  );
}




function buildCalendar(
  baseDate,
  startOnMonday = true,
  locale = "ru-RU"
) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0).getDate();
  const weekStart = startOnMonday ? 1 : 0;
  const shift = (first.getDay() + 7 - weekStart) % 7;
  const cells = Array.from({ length: shift }).map(() => null);

  for (let d = 1; d <= last; d++) {
    cells.push(d);
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const monthName = baseDate.toLocaleDateString(
    locale,
    {
      month: "long",
    }
  );

  return {
    year,
    monthName,
    cells,
  };
}
function WeightModal({ open, onClose, onSubmit, initialCurrent, initialGoal, initialBaseline }) {
  const { t } = useTranslation();

  useFitmeupBottomNavHidden(open);

  const hasBaseline = initialBaseline !== null && initialBaseline !== undefined;

  const [current, setCurrent] = useState(String(initialCurrent ?? ""));
  const [goal, setGoal] = useState(String(initialGoal ?? ""));
  const [baseline, setBaseline] = useState(String(initialBaseline ?? initialCurrent ?? ""));

  const firstInputRef = useRef(null);

  const parse = (s) => {
    const n = Number((s || "").replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const canSave = parse(current) !== null && parse(goal) !== null;

  const trySubmit = () => {
    const c = parse(current);
    const g = parse(goal);
    const b = hasBaseline ? parse(baseline) : null;
    if (c === null || g === null) return;
    onSubmit({ current: c, goal: g, baseline: b });
    onClose();
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialCurrent, initialGoal, initialBaseline]);

  useEffect(() => {
    if (!open) return undefined;

    setCurrent(String(initialCurrent ?? ""));
    setGoal(String(initialGoal ?? ""));
    setBaseline(String(initialBaseline ?? initialCurrent ?? ""));

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialCurrent, initialGoal, initialBaseline]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
className="fixed inset-0 z-[60000] flex items-start justify-center overflow-y-auto px-4 pt-[calc(env(safe-area-inset-top)+4px)] pb-4 bg-transparent backdrop-blur-[10px]"
      onClick={onClose}
    >
      <div className="relative w-full max-w-[390px]" onClick={(e) => e.stopPropagation()}>
        <GlassCard
          dense
          className="!rounded-[26px] !border-cyan-300/35 !bg-slate-900/95 p-4 shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
        >
          <div className="mb-1 flex items-start justify-between gap-3">
            <div className="text-[16px] font-bold text-slate-50">
  {t("food.weight.title")}
</div>

            <AppCloseButton onClick={onClose} />
          </div>

          <div className="grid gap-2.5">
            <label className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("food.weight.currentLabel")}</span>
              <input
                ref={firstInputRef}
                inputMode="decimal"
                enterKeyHint="done"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                onFocus={(e) => focusToEndAndScroll(e, null)}
                onKeyDown={(e) => e.key === "Enter" && trySubmit()}
                className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
                placeholder={t("food.weight.currentPlaceholder")}
              />
            </label>

            <label className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("food.weight.goalLabel")}</span>
              <input
                inputMode="decimal"
                enterKeyHint="done"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                onFocus={(e) => focusToEndAndScroll(e, null)}
                onKeyDown={(e) => e.key === "Enter" && trySubmit()}
                className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
                placeholder={t("food.weight.goalPlaceholder")}
              />
            </label>

            {hasBaseline && (
              <label className="grid gap-1 text-[12px] font-semibold text-white/70">
                <span>{t("food.weight.baselineLabel")}</span>
                <input
                  inputMode="decimal"
                  enterKeyHint="done"
                  value={baseline}
                  onChange={(e) => setBaseline(e.target.value)}
                  onFocus={(e) => focusToEndAndScroll(e, null)}
                  onKeyDown={(e) => e.key === "Enter" && trySubmit()}
                  className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
                  placeholder={t("food.weight.baselinePlaceholder")}
                />
              </label>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-2xl border border-white/15 bg-white/[0.07] text-[13px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.97]"
            >
              {t("actions.cancel")}
            </button>

            <button
              type="button"
              onClick={trySubmit}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                (canSave
                  ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                  : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
              }
            >
              {t("actions.save")}
            </button>
          </div>
        </GlassCard>
      </div>
    </div>,
    document.body
  );
}

function SleepModal({ open, onClose, onSubmit, initialHours }) {
  const { t } = useTranslation();

  useFitmeupBottomNavHidden(open);

  const [value, setValue] = useState(
    initialHours !== null && initialHours !== undefined ? String(initialHours) : ""
  );
  const firstInputRef = useRef(null);

  const parse = (s) => {
    const n = Number((s || "").replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const canSave = parse(value) !== null;

  const trySubmit = () => {
    const h = parse(value);
    if (h === null) return;
    onSubmit(h);
    onClose();
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialHours]);

  useEffect(() => {
    if (!open) return undefined;

    setValue(initialHours !== null && initialHours !== undefined ? String(initialHours) : "");

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialHours]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
className="fixed inset-0 z-[60000] flex items-start justify-center overflow-y-auto px-4 pt-[calc(env(safe-area-inset-top)+4px)] pb-4 bg-transparent backdrop-blur-[10px]"
      onClick={onClose}
    >
      <div className="relative w-full max-w-[390px]" onClick={(e) => e.stopPropagation()}>
        <GlassCard
          dense
          className="!rounded-[26px] !border-cyan-300/35 !bg-slate-900/95 p-4 shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
        >
          <div className="mb-1 flex items-start justify-between gap-3">
            <div className="text-[16px] font-bold text-slate-50">
  {t("food.sleep.title")}
</div>

            <AppCloseButton onClick={onClose} />
          </div>

          <div className="grid gap-2.5">
            <label className="grid gap-1 text-[12px] font-semibold text-white/70">
              <span>{t("food.sleep.question")}</span>
              <input
                ref={firstInputRef}
                inputMode="decimal"
                enterKeyHint="done"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={(e) => focusToEndAndScroll(e, null)}
                onKeyDown={(e) => e.key === "Enter" && trySubmit()}
                className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
                placeholder={t("food.sleep.placeholder")}
              />
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-2xl border border-white/15 bg-white/[0.07] text-[13px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.97]"
            >
              {t("actions.cancel")}
            </button>

            <button
              type="button"
              onClick={trySubmit}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                (canSave
                  ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                  : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
              }
            >
              {t("actions.save")}
            </button>
          </div>
        </GlassCard>
      </div>
    </div>,
    document.body
  );
}

function FoodDayModal({ open, onClose, dayKey, loading, entries, totals, selectedIds, onToggleSelect, onAddSelected, addBusy, onChangeDay }) {
  const { t: tr, i18n } = useTranslation();

  const formatFoodTitleDate = (key) => {
    if (!key) return "";
    const parts = String(key).split("-");
    if (parts.length !== 3) return key;
  

    const y = Number(parts[0]);
    const m = Number(parts[1]) - 1;
    const d = Number(parts[2]);

    const dt = new Date(y, m, d); // важно: локальная дата, без сдвигов по UTC

    const s = dt.toLocaleDateString(
      i18n.resolvedLanguage === "ru"
        ? "ru-RU"
        : "en-GB",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    );

    return i18n.resolvedLanguage === "ru"
      ? s.replace(/\s?г\.\s*$/u, "")
      : s;
  };
  const entriesTotals = useMemo(() => {
    return (entries || []).reduce(
      (acc, item) => {
        acc.kcal += Number(item?.kcal || 0);
        acc.proteins += Number(item?.proteins || 0);
        acc.fats += Number(item?.fats || 0);
        acc.carbs += Number(item?.carbs || 0);
        return acc;
      },
      { kcal: 0, proteins: 0, fats: 0, carbs: 0 }
    );
  }, [entries]);

  const summaryTotals = totals || entriesTotals;

  const subtitle =
    !loading && dayKey
      ? `${tr("food.day.total")} ${Math.round(
          Number(summaryTotals?.kcal ?? 0)
        )} ${tr("units.kcal")} · ${tr("food.report.proteinShort")} ${Math.round(
          Number(summaryTotals?.proteins ?? 0)
        )} · ${tr("food.report.fatShort")} ${Math.round(
          Number(summaryTotals?.fats ?? 0)
        )} · ${tr("food.report.carbsShort")} ${Math.round(
          Number(summaryTotals?.carbs ?? 0)
        )}`
      : "";

  const groups = useMemo(() => {
    const map = {};
    for (const e of entries || []) {
      const slot = String(e.meal_slot || "meal");
      if (!map[slot]) map[slot] = [];
      map[slot].push(e);
    }


    return map;
  }, [entries]);
const [openDishIds, setOpenDishIds] = useState({});

useEffect(() => {
  if (!open) return;
  setOpenDishIds({});
}, [open, dayKey]);

const roundFoodDay = (value) => Math.round(Number(value || 0));

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const getFoodDayDishIngredients = (entry) => {
  const snapshot = parseMaybeJson(entry?.dish_snapshot) || parseMaybeJson(entry?.dishSnapshot);

  const fromSnapshot = Array.isArray(snapshot?.ingredients)
    ? snapshot.ingredients
    : [];

  const fromEntry = Array.isArray(entry?.ingredients)
    ? entry.ingredients
    : [];

  const fromRecipe = Array.isArray(entry?.recipe?.ingredients)
    ? entry.recipe.ingredients
    : [];

  return (fromSnapshot.length ? fromSnapshot : fromEntry.length ? fromEntry : fromRecipe)
    .filter(Boolean);
};

const getSlotMacros = (slot) => {
  const list = groups?.[slot] || [];

  return list.reduce(
    (acc, item) => {
      acc.kcal += Number(item?.kcal || 0);
      acc.proteins += Number(item?.proteins || 0);
      acc.fats += Number(item?.fats || 0);
      acc.carbs += Number(item?.carbs || 0);
      return acc;
    },
    { kcal: 0, proteins: 0, fats: 0, carbs: 0 }
  );
};
  const slotTitle = (slot) => {
  if (slot === "breakfast") return tr("food.day.breakfast");
  if (slot === "lunch") return tr("food.day.lunch");
  if (slot === "dinner") return tr("food.day.dinner");
  if (slot === "snack") return tr("food.day.snack");
  return slot;
};
const orderedSlots = useMemo(() => {
  const order = ["breakfast", "lunch", "dinner", "snack"];

  // сначала по фиксированному порядку
  const main = order.filter((s) => (groups?.[s]?.length ?? 0) > 0);

  // потом любые другие слоты (если вдруг появятся), стабильно по алфавиту
  const other = Object.keys(groups || {})
    .filter((s) => !order.includes(s))
    .sort((a, b) => a.localeCompare(b));

  return [...main, ...other];
}, [groups]);
const selectedCount = selectedIds?.size || 0;

const shiftFoodDay = (delta) => {
  if (!dayKey || typeof onChangeDay !== "function") return;

  const parts = String(dayKey).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return;

  const [y, m, d] = parts;
  const nextDate = new Date(y, m - 1, d + delta);
  onChangeDay(getLocalDayKey(nextDate));
};

  return (
    <FoodDayBaseModal
  open={open}
  onClose={onClose}
  title={
  dayKey
    ? tr("food.day.title", {
        date: formatFoodTitleDate(dayKey),
      })
    : tr("food.day.titleFallback")
}

  subtitle={subtitle}
>

      {dayKey && typeof onChangeDay === "function" ? (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.045] p-1.5">
          <button
            type="button"
            onClick={() => shiftFoodDay(-1)}
            disabled={loading}
            className="grid h-9 w-10 shrink-0 place-items-center rounded-2xl border border-white/10 bg-slate-950/35 text-[20px] font-black text-slate-100 active:scale-[0.96] disabled:opacity-45"
                        aria-label={tr("food.day.previousDayAria")}
          >
            ‹
          </button>

          <div className="min-w-0 flex-1 text-center text-[12px] font-semibold text-slate-200/85">
            {tr("food.day.otherDays")}
          </div>

          <button
            type="button"
            onClick={() => shiftFoodDay(1)}
            disabled={loading}
            className="grid h-9 w-10 shrink-0 place-items-center rounded-2xl border border-white/10 bg-slate-950/35 text-[20px] font-black text-slate-100 active:scale-[0.96] disabled:opacity-45"
                        aria-label={tr("food.day.nextDayAria")}
          >
            ›
          </button>
        </div>
      ) : null}

{loading ? (
  <div className="mb-3 inline-flex h-10 items-center rounded-2xl border border-white/10 bg-white/[0.035] px-3 text-sm font-semibold opacity-80">
    {tr("food.day.loading")}
  </div>
) : (entries?.length ? (
        <div className="grid gap-3">
          {orderedSlots.map((slot) => (

            <div key={slot} className="rounded-2xl border border-white/15 bg-white/5 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
  <div className="min-w-0 text-sm font-semibold opacity-90">
    {slotTitle(slot)}
  </div>

  {(() => {
    const totals = getSlotMacros(slot);

    return (
      <div className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold leading-none text-slate-200/85 tabular-nums">
        {roundFoodDay(totals.kcal)} {tr("units.kcal")} · {tr("food.report.proteinShort")} {roundFoodDay(totals.proteins)} · {tr("food.report.fatShort")} {roundFoodDay(totals.fats)} · {tr("food.report.carbsShort")} {roundFoodDay(totals.carbs)}
              </div>
    );
  })()}
</div>

              <div className="grid gap-2">
{groups[slot].map((e) => {
  const entryId = String(e.id);
  const dishIngredients = getFoodDayDishIngredients(e);
  const hasDishIngredients = dishIngredients.length > 0;
  const isDishOpen = Boolean(openDishIds[entryId]);

  return (
    <div
      key={e.id}
      className={
        "rounded-2xl border p-2 transition " +
        (selectedIds?.has(String(e.id))
          ? "border-emerald-400/35 bg-emerald-400/10 shadow-[0_0_0_1px_rgba(52,211,153,0.25),0_0_18px_rgba(16,185,129,0.12)]"
          : "border-white/10 bg-white/5")
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 text-[13px] font-semibold leading-snug">
          {e.name}
        </div>

        <button
          type="button"
          onClick={() => onToggleSelect(String(e.id))}
          className={
            "shrink-0 px-2 py-0.5 rounded-lg border text-[10px] font-medium active:scale-[0.97] " +
            (selectedIds?.has(String(e.id))
              ? "bg-emerald-400/15 border-emerald-400/40 text-emerald-50 shadow-[0_0_12px_rgba(16,185,129,0.16)]"
              : "bg-white/10 border-white/20 hover:bg-white/15")
          }
        >
          {selectedIds?.has(String(e.id))
            ? tr("food.day.selected")
            : tr("food.day.select")}
        </button>
      </div>

      <div className="mt-1 text-[12px] opacity-75 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
        {Math.round(Number(e.grams ?? 0))} {tr("units.gramsShort")} · {Math.round(Number(e.kcal ?? 0))} {tr("units.kcal")} · {tr("food.report.proteinShort")} {Math.round(Number(e.proteins ?? 0))} · {tr("food.report.fatShort")} {Math.round(Number(e.fats ?? 0))} · {tr("food.report.carbsShort")} {Math.round(Number(e.carbs ?? 0))}
      </div>

      {hasDishIngredients && (
        <button
          type="button"
          onClick={() => {
            setOpenDishIds((prev) => ({
              ...prev,
              [entryId]: !prev[entryId],
            }));
          }}
          className="mt-2 flex w-full items-center justify-between rounded-xl border border-white/10 bg-slate-950/25 px-3 py-2 text-left text-[12px] font-semibold text-slate-100/90 active:scale-[0.99]"
        >
                    <span>{tr("food.day.dishContents")}</span>
          <span className="text-[11px] text-slate-300/75">
            {dishIngredients.length} {isDishOpen ? "▲" : "▼"}
          </span>
        </button>
      )}

      {hasDishIngredients && isDishOpen && (
        <div className="mt-2 grid gap-1.5 rounded-2xl border border-white/10 bg-slate-950/25 p-2">
          {dishIngredients.map((ing, index) => {
            const ingName =
              ing?.name ||
              ing?.title ||
              ing?.foodName ||
              tr("food.day.ingredientFallback", {
                number: index + 1,
              });

            const grams = roundFoodDay(
              ing?.grams ??
                ing?.weight ??
                ing?.amount ??
                ing?.defaultGrams ??
                0
            );

            const kcal = roundFoodDay(ing?.kcal ?? ing?.calories ?? 0);
            const proteins = roundFoodDay(ing?.proteins ?? ing?.protein ?? ing?.p ?? 0);
            const fats = roundFoodDay(ing?.fats ?? ing?.fat ?? ing?.f ?? 0);
            const carbs = roundFoodDay(ing?.carbs ?? ing?.c ?? 0);

            return (
              <div
                key={`${entryId}-ing-${index}`}
                className="rounded-xl border border-white/10 bg-white/[0.045] px-2.5 py-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 text-[12px] font-semibold leading-snug text-slate-100">
                    {ingName}
                  </div>

                  {grams > 0 && (
                    <div className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-200/80">
   {grams} {tr("units.gramsShort")}
                    </div>
                  )}
                </div>

                <div className="mt-1 text-[11px] leading-none text-slate-300/75 tabular-nums">
                  {kcal} {tr("units.kcal")} · {tr("food.report.proteinShort")} {proteins} · {tr("food.report.fatShort")} {fats} · {tr("food.report.carbsShort")} {carbs}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
})}
              </div>
            </div>
          ))}
        </div>
      ) : (
                <div className="text-sm opacity-80">
          {tr("food.day.empty")}
        </div>
      ))}


 {selectedCount > 0 && (
  <div
    className="fixed left-1/2 z-[61000] -translate-x-1/2"
    style={{
      width: "min(360px, calc(100vw - 32px))",
      bottom: "calc(14px + env(safe-area-inset-bottom, 0px))",
    }}
  >
    <div className="rounded-[28px] border border-white/15 bg-slate-950/80 p-1.5 shadow-[0_16px_42px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-2xl">
      <button
        type="button"
        onClick={onAddSelected}
        disabled={addBusy}
        className={
          "h-12 w-full rounded-[24px] text-[14px] font-extrabold tracking-[-0.01em] transition-all active:scale-[0.98] " +
          (addBusy
            ? "bg-slate-700/70 text-slate-400 cursor-not-allowed"
            : "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-950 shadow-[0_0_24px_rgba(45,212,191,0.20)]")
        }
      >
        {addBusy
          ? tr("food.day.moving")
          : tr("food.day.addToday", {
              count: selectedCount,
            })}
      </button>
    </div>
  </div>
)}
    </FoodDayBaseModal>
  );
}
function WorkoutDayModal({ open, onClose, dayKey, workouts, onRepeat, onChangeDay }) {
  const { t, i18n } = useTranslation();

  const workoutDayLocale =
    i18n.resolvedLanguage === "ru"
      ? "ru-RU"
      : "en-GB";

  const [expandedExerciseKey, setExpandedExerciseKey] = useState(null);

  useEffect(() => {
    if (!open) return;
    setExpandedExerciseKey(null);
  }, [open, dayKey]);

  const getLocalizedWorkoutDayTitle = (workout) => {
    const customTitle = getWorkoutCustomTitle(workout);

    if (customTitle) {
      return customTitle;
    }

    if (workout?.mode === "cardio") {
      return t("workout.picker.cardio");
    }

    const groups = getWorkoutMuscleBadges(workout).filter(
      (group, index, arr) =>
        group &&
        arr.findIndex((item) => item?.key === group.key) === index
    );

    const names = groups
      .map((group) => t(`workout.muscles.${group.key}`))
      .filter(Boolean);

    return names.length
      ? names.join(" • ")
      : t("nav.workout");
  };

  const getLocalizedWorkoutDayMuscles = (workout) => {
    const groups = getWorkoutMuscleBadges(workout).filter(
      (group, index, arr) =>
        group &&
        arr.findIndex((item) => item?.key === group.key) === index
    );

    return groups
      .map((group) => t(`workout.muscles.${group.key}`))
      .filter(Boolean)
      .join(", ");
  };

  const getWorkoutDayExerciseName = (exercise, index) => {
    const rawName = String(exercise?.name || "").trim();

    const normalized = rawName
      .toLowerCase()
      .replace(/ё/g, "е");

    if (
      !rawName ||
      normalized === "упражнение" ||
      /^\d+\s+упражнение$/.test(normalized)
    ) {
      return t("workout.myWorkouts.exerciseFallback", {
        number: index + 1,
      });
    }

    return rawName;
  };

  const formatTitleDate = (key) => {
    if (!key) return "";
    const parts = String(key).split("-");
    if (parts.length !== 3) return key;

    const y = Number(parts[0]);
    const m = Number(parts[1]) - 1;
    const d = Number(parts[2]);
    const dt = new Date(y, m, d);

    const s = dt.toLocaleDateString(workoutDayLocale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    return s.replace(/\s?г\.\s*$/u, "");
  };

  const cleanSetsForExercise = (ex) => {
    return (Array.isArray(ex?.sets) ? ex.sets : []).filter((s) => {
      if (s?.prefill === true && s?.edited !== true) return false;

      const hasWeight = s?.weight != null && String(s.weight).trim() !== "";
      const hasReps = s?.reps != null && String(s.reps).trim() !== "";

      return hasWeight || hasReps;
    });
  };

  const formatWeight = (value) => {
    const n = Number(String(value ?? "").replace(",", "."));

    if (!Number.isFinite(n)) {
      return String(value ?? "").trim() || "—";
    }

    return `${formatKg(
      n,
      workoutDayLocale
    )} ${t("units.kilogramsShort")}`;
  };

  const formatReps = (value) => {
    const n = Number(String(value ?? "").replace(",", "."));
    if (!Number.isFinite(n)) return String(value ?? "").trim() || "—";
    return `${Math.round(n)}`;
  };

  const getSafeExercises = (w) => {
    return (Array.isArray(w?.exercises) ? w.exercises : []).filter((ex, idx) => {
      const name = String(ex?.name || "").trim();
      const sets = cleanSetsForExercise(ex);

      return name || sets.length > 0 || idx === 0;
    });
  };

  const getSafeCardioItems = (w) => {
    if (Array.isArray(w?.cardio) && w.cardio.length) return w.cardio;
    if (Array.isArray(w?.cardioItems) && w.cardioItems.length) return w.cardioItems;
    return [];
  };

  const getExerciseSummary = (ex) => {
    const sets = cleanSetsForExercise(ex);
    const count = sets.length;

    if (!count) {
      return t("workout.myWorkouts.noSets");
    }

    const range = getExerciseRangeLabel(
      { ...ex, sets },
      {
        locale: workoutDayLocale,
        weightUnit: t("units.kilogramsShort"),
        repsUnit: t("workout.myWorkouts.repetitions"),
      }
    );

    return `${count} ${t("workout.day.setNoun", {
      count,
    })} • ${range}`;
  };
const shiftWorkoutDay = (delta) => {
  if (!dayKey || typeof onChangeDay !== "function") return;

  const parts = String(dayKey).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return;

  const [y, m, d] = parts;
  const nextDate = new Date(y, m - 1, d + delta);
  onChangeDay(getLocalDayKey(nextDate));
};
  return (
    <FoodDayBaseModal
      open={open}
      onClose={onClose}
      title={
        dayKey
          ? t("workout.day.title", {
              date: formatTitleDate(dayKey),
            })
          : t("workout.day.titleFallback")
      }
subtitle=""
>
  {dayKey && typeof onChangeDay === "function" ? (
    <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.045] p-1.5">
      <button
        type="button"
        onClick={() => shiftWorkoutDay(-1)}
        className="grid h-9 w-10 shrink-0 place-items-center rounded-2xl border border-white/10 bg-slate-950/35 text-[20px] font-black text-slate-100 active:scale-[0.96]"
       aria-label={t("workout.day.previousDayAria")}
      >
        ‹
      </button>

<div className="min-w-0 flex-1 text-center text-[12px] font-semibold text-slate-200/85">
  {t("workout.day.otherDays")}
</div>

      <button
        type="button"
        onClick={() => shiftWorkoutDay(1)}
        className="grid h-9 w-10 shrink-0 place-items-center rounded-2xl border border-white/10 bg-slate-950/35 text-[20px] font-black text-slate-100 active:scale-[0.96]"
        aria-label={t("workout.day.nextDayAria")}
      >
        ›
      </button>
    </div>
  ) : null}

  {workouts?.length ? (
        <div className="grid gap-3">
          {(workouts || []).map((w, workoutIdx) => {
            const workoutKey = String(w?.id ?? `${dayKey || "day"}-${workoutIdx}`);
            const cardioItems = getSafeCardioItems(w);
            const cardioSummary = computeCardioSummary(w);

            const isCardio =
              (w?.mode === "cardio" ||
                /кардио/i.test(String(w?.musclesLabel || w?.title || w?.workoutTitle || ""))) &&
              cardioItems.length > 0;

            const exercises = getSafeExercises(w);
            const cleanExercises = exercises.map((ex) => ({
              ...ex,
              sets: cleanSetsForExercise(ex),
            }));

            const stats = computeWorkoutStats({ exercises: cleanExercises });
            const totalSets = cleanExercises.reduce(
              (sum, ex) => sum + (Array.isArray(ex?.sets) ? ex.sets.length : 0),
              0
            );

            const title = getLocalizedWorkoutDayTitle(w);
            const musclesLabel = getLocalizedWorkoutDayMuscles(w);

            return (
              <div
                key={workoutKey}
                className="relative overflow-hidden rounded-[26px] border border-[#18d7bd]/30 bg-[#051723]/78 p-3 shadow-[0_14px_36px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl"
              >
                <div className="pointer-events-none absolute -left-20 -top-24 h-44 w-44 rounded-full bg-[#18d7bd]/14 blur-3xl" />
                <div className="pointer-events-none absolute -right-24 bottom-[-70px] h-44 w-44 rounded-full bg-cyan-400/10 blur-3xl" />

                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
<div className="text-[17px] font-extrabold leading-snug tracking-[-0.025em] text-slate-50">
  {title || t("nav.workout")}
</div>

                      {musclesLabel && musclesLabel !== title ? (
                        <div className="mt-1 text-[12px] font-semibold text-slate-300/70">
                          {musclesLabel}
                        </div>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (typeof onClose === "function") onClose();
                        if (typeof onRepeat === "function") onRepeat(w);
                      }}
                      className="shrink-0 rounded-full border border-[#18d7bd]/35 bg-[#18d7bd]/[0.10] px-3 py-1.5 text-[12px] font-bold text-cyan-50 shadow-[0_0_16px_rgba(45,212,191,0.12)] active:scale-[0.97]"
title={t("workout.day.repeat")}
>
  🔄 {t("workout.day.repeat")}
</button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {isCardio ? (
                      <>
<span className="rounded-full border border-white/10 bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-slate-100">
  {t("workout.picker.cardio")}
</span>
<span className="rounded-full border border-white/10 bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-slate-100">
  {cardioSummary.totalMinutes
    ? t("workout.picker.minutesShort", {
        value: cardioSummary.totalMinutes,
      })
    : t("workout.myWorkouts.timeNotSpecified")}
</span>
                      </>
                    ) : (
                      <>
<span className="rounded-full border border-white/10 bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-slate-100">
  {exercises.length}{" "}
  {t("workout.myWorkouts.exerciseNoun", {
    count: exercises.length,
  })}
</span>

<span className="rounded-full border border-white/10 bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-slate-100">
  {totalSets}{" "}
  {t("workout.day.setNoun", {
    count: totalSets,
  })}
</span>

<span className="rounded-full border border-white/10 bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-slate-100">
  {formatKg(
    Math.round(Number(stats.tonnage) || 0),
    workoutDayLocale
  )}{" "}
  {t("units.kilogramsShort")}
</span>
                      </>
                    )}
                  </div>

                  {isCardio ? (
                    <div className="mt-3 grid gap-2">
                      {cardioItems.map((item, idx) => {
const rawTitle = String(item?.title || "").trim();

const title =
  rawTitle === "Кардио"
    ? t("workout.picker.cardio")
    : rawTitle ||
      t("workout.day.cardioItem", {
        number: idx + 1,
      });

const minutes =
  item?.minutes != null &&
  String(item.minutes).trim() !== ""
    ? t("workout.picker.minutesShort", {
        value: item.minutes,
      })
    : t("workout.myWorkouts.timeNotSpecified");
                        const comment = String(item?.comment || "").trim();

                        return (
                          <div
                            key={`${workoutKey}:cardio:${idx}`}
                            className="rounded-[20px] border border-white/10 bg-slate-950/35 p-3"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
<div className="text-[15px] font-extrabold tracking-[-0.02em] text-slate-50">
  {title}
</div>
                                <div className="mt-1 text-[12px] font-semibold text-slate-300/70">
                                  {minutes}
                                </div>
                              </div>

<div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl">
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M13.3 2.8L5.2 13.1H11L9.9 21.2L18.8 9.9H13L13.3 2.8Z"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</div>
                            </div>

                            {comment ? (
                              <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-[12px] font-semibold leading-snug text-slate-200/80">
                                {comment}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-2">
                      {exercises.length ? (
                        exercises.map((ex, idx) => {
                          const sets = cleanSetsForExercise(ex);
                          const exerciseKey = `${workoutKey}:${String(ex?.id ?? idx)}`;
                          const isExOpen = expandedExerciseKey === exerciseKey;

                          return (
                            <div
                              key={exerciseKey}
                              className={cn(
                                "overflow-hidden rounded-[21px] border bg-slate-950/30 transition-all",
                                isExOpen
                                  ? "border-[#18d7bd]/35 shadow-[0_0_20px_rgba(24,215,189,0.08)]"
                                  : "border-white/10"
                              )}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  setExpandedExerciseKey((prev) =>
                                    prev === exerciseKey ? null : exerciseKey
                                  );
                                }}
                                className="flex w-full items-center gap-3 p-3 text-left active:scale-[0.99]"
                              >
                                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#18d7bd]/35 bg-[#18d7bd]/[0.10] text-[12px] font-black text-[#35F3DF]">
                                  {idx + 1}
                                </div>

                                <div className="min-w-0 flex-1">
<div className="truncate text-[15px] font-extrabold leading-snug tracking-[-0.02em] text-slate-50">
  {getWorkoutDayExerciseName(ex, idx)}
</div>
                                  <div className="mt-1 line-clamp-1 text-[11px] font-semibold text-slate-300/65">
                                    {getExerciseSummary(ex)}
                                  </div>
                                </div>

<span
  className={cn(
    "grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition-transform duration-200",
    isExOpen ? "rotate-180" : "rotate-0"
  )}
>
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</span>
                              </button>

                              {isExOpen ? (
                                <div className="px-3 pb-3">
                                  {sets.length ? (
                                    <div className="grid gap-2">
                                      {sets.map((s, setIdx) => (
                                        <div
                                          key={`${exerciseKey}:set:${String(s?.id ?? setIdx)}`}
                                          className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-[17px] border border-white/10 bg-white/[0.055] px-3 py-2"
                                        >
                                          <div className="min-w-0 text-[12px] font-bold text-slate-300/78">
                                            {t("workout.myWorkouts.setLabel", {
  number: setIdx + 1,
})}
                                          </div>

                                          <div className="rounded-full border border-cyan-200/15 bg-cyan-300/[0.07] px-2.5 py-1 text-[11px] font-extrabold text-slate-50">
                                            {formatWeight(s?.weight)}
                                          </div>

                                          <div className="rounded-full border border-emerald-200/15 bg-emerald-300/[0.07] px-2.5 py-1 text-[11px] font-extrabold text-slate-50">
                                            × {formatReps(s?.reps)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
<div className="rounded-[17px] border border-white/10 bg-white/[0.045] px-3 py-2 text-[12px] font-semibold text-slate-300/65">
  {t("workout.myWorkouts.noSets")}
</div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      ) : (
<div className="rounded-[20px] border border-white/10 bg-slate-950/30 p-3 text-[12px] font-semibold text-slate-300/70">
  {t("workout.day.noExercises")}
</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
<div className="rounded-[20px] border border-[#18d7bd]/35 bg-[#051723]/72 p-4 text-center text-[13px] font-semibold text-slate-300/75">
  {t("workout.day.empty")}
</div>
      )}
    </FoodDayBaseModal>
  );
}


function StepsModal({ open, onClose, onSubmit, initialSteps }) {
  const { t } = useTranslation();

  useFitmeupBottomNavHidden(open);

  const [value, setValue] = useState(
    initialSteps !== null && initialSteps !== undefined ? String(initialSteps) : ""
  );
  const firstInputRef = useRef(null);

  const parse = (s) => {
    const cleaned = String(s || "").split(" ").join("");
    const n = Number(cleaned);
    return Number.isNaN(n) ? null : Math.max(0, Math.round(n));
  };

  const canSave = parse(value) !== null;

  const trySubmit = () => {
    const steps = parse(value);
    if (steps === null) return;
    onSubmit(steps);
    onClose();
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialSteps]);

  useEffect(() => {
    if (!open) return undefined;

    setValue(initialSteps !== null && initialSteps !== undefined ? String(initialSteps) : "");

    return focusModalInputAndOpenKeyboard(firstInputRef);
  }, [open, initialSteps]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
className="fixed inset-0 z-[60000] flex items-start justify-center overflow-y-auto px-4 pt-[calc(44px+env(safe-area-inset-top))] pb-4 bg-black/38 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative w-full max-w-[390px]" onClick={(e) => e.stopPropagation()}>
        <GlassCard
          dense
          className="!rounded-[26px] !border-cyan-300/35 !bg-slate-900/95 p-4 shadow-[0_22px_60px_rgba(0,0,0,0.52),0_0_18px_rgba(45,230,230,0.08),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-xl"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="text-[16px] font-bold text-slate-50">
  {t("body.overview.steps")}
</div>
            </div>

            <AppCloseButton onClick={onClose} />
          </div>

          <div className="grid gap-3">
            <label className="grid gap-1.5 text-[12px] font-semibold text-white/70">
<span>{t("body.steps.question")}</span>
<input
  ref={firstInputRef}
  inputMode="numeric"
  enterKeyHint="done"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  onFocus={(e) => focusToEndAndScroll(e, null)}
  onKeyDown={(e) => e.key === "Enter" && trySubmit()}
  className="h-11 rounded-2xl border border-cyan-200/20 bg-white/[0.07] px-3 text-[14px] font-semibold text-slate-50 placeholder:text-white/35 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-300/35"
  placeholder={t("body.steps.placeholder")}
/>
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-2xl border border-white/15 bg-white/[0.07] text-[13px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.97]"
>
  {t("actions.cancel")}
</button>

            <button
              type="button"
              onClick={trySubmit}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                (canSave
                  ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                  : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
              }
>
  {t("actions.save")}
</button>
          </div>
        </GlassCard>
      </div>
    </div>,
    document.body
  );
}

function MacroTargetsModal({ open, onClose, initialTargets, onSubmit }) {
  const { t: tr } = useTranslation();

  const firstInputRef = useRef(null);

  const [step, setStep] = useState("choice");

  // ручной ввод
  const [proteins, setProteins] = useState("");
  const [fats, setFats] = useState("");
  const [carbs, setCarbs] = useState("");

  // калькулятор
  const [sex, setSex] = useState("female");
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [activity, setActivity] = useState("medium");
  const [goal, setGoal] = useState("maintain");

  const toNum = (v) => {
    const x = parseFloat(String(v ?? "").replace(",", "."));
    return Number.isFinite(x) ? x : NaN;
  };

  const toInt = (v) => {
    const n = toNum(v);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.round(n));
  };

  // при открытии модалки заполняем поля и возвращаемся на шаг выбора
  useEffect(() => {
    if (!open) return;

    setStep("choice");

    setProteins(
      initialTargets?.proteins !== null && initialTargets?.proteins !== undefined
        ? String(initialTargets.proteins)
        : ""
    );
    setFats(
      initialTargets?.fats !== null && initialTargets?.fats !== undefined
        ? String(initialTargets.fats)
        : ""
    );
    setCarbs(
      initialTargets?.carbs !== null && initialTargets?.carbs !== undefined
        ? String(initialTargets.carbs)
        : ""
    );

let raf1 = 0;
let raf2 = 0;

const t = setTimeout(() => {
  const focusIt = () => {
    const el = firstInputRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
      const len = el.value.length;
      if (typeof el.setSelectionRange === "function") el.setSelectionRange(0, len);
    } catch {}
  };

  raf1 = requestAnimationFrame(() => {
    raf2 = requestAnimationFrame(focusIt);
  });
}, 0);

return () => {
  clearTimeout(t);
  cancelAnimationFrame(raf1);
  cancelAnimationFrame(raf2);
};


  }, [open, initialTargets]);

  const manualKcal = useMemo(() => {
    const p = toNum(proteins);
    const f = toNum(fats);
    const c = toNum(carbs);
    if (!Number.isFinite(p) || !Number.isFinite(f) || !Number.isFinite(c)) return NaN;
    return Math.round(p * 4 + c * 4 + f * 9);
  }, [proteins, fats, carbs]);

  const canSaveManual = useMemo(() => {
    const p = toInt(proteins);
    const f = toInt(fats);
    const c = toInt(carbs);
    return p !== null && f !== null && c !== null && p > 0 && f > 0 && c > 0;
  }, [proteins, fats, carbs]);

  const handleSaveManual = () => {
    const p = toInt(proteins);
    const f = toInt(fats);
    const c = toInt(carbs);
    if (p === null || f === null || c === null || p <= 0 || f <= 0 || c <= 0) return;

    const kcal = Math.round(p * 4 + c * 4 + f * 9);

    if (typeof onSubmit === "function") {
      onSubmit({ kcal, proteins: p, fats: f, carbs: c });
    }
  };

  const activityFactor = useMemo(() => {
    switch (activity) {
      case "low":
        return 1.2;
      case "light":
        return 1.375;
      case "medium":
        return 1.55;
      case "high":
        return 1.725;
      case "athlete":
        return 1.9;
      default:
        return 1.55;
    }
  }, [activity]);

  const goalFactor = useMemo(() => {
    switch (goal) {
      case "loss":
        return 0.85; // -15%
      case "maintain":
        return 1.0;
      case "gain":
        return 1.1; // +10%
      default:
        return 1.0;
    }
  }, [goal]);

  const calcResult = useMemo(() => {
    const a = toNum(age);
    const h = toNum(height);
    const w = toNum(weight);

    if (!Number.isFinite(a) || !Number.isFinite(h) || !Number.isFinite(w)) {
      return { kcal: 0, proteins: 0, fats: 0, carbs: 0 };
    }
    if (a <= 0 || h <= 0 || w <= 0) {
      return { kcal: 0, proteins: 0, fats: 0, carbs: 0 };
    }

    // Mifflin–St Jeor
    const bmr =
      sex === "male"
        ? 10 * w + 6.25 * h - 5 * a + 5
        : 10 * w + 6.25 * h - 5 * a - 161;

    const tdee = bmr * activityFactor;
    const kcal = Math.max(0, Math.round(tdee * goalFactor));

    // простое распределение макросов
    const proteins = Math.max(0, Math.round(w * (goal === "loss" ? 1.9 : 1.7)));
    const fats = Math.max(40, Math.round(w * 0.9));

    const rest = kcal - proteins * 4 - fats * 9;
    const carbs = Math.max(0, Math.round(rest / 4));

    return { kcal, proteins, fats, carbs };
  }, [sex, age, height, weight, activityFactor, goalFactor, goal]);

  const calcKcal = calcResult.kcal;
  const calcProteins = calcResult.proteins;
  const calcFats = calcResult.fats;
  const calcCarbs = calcResult.carbs;

  const canSaveCalc = calcKcal > 0;

  const handleSaveCalc = () => {
    if (!canSaveCalc) return;
    if (typeof onSubmit === "function") {
      onSubmit({
        kcal: calcKcal,
        proteins: calcProteins,
        fats: calcFats,
        carbs: calcCarbs,
      });
    }
  };

  if (!open) return null;

return (
    <BaseModal open={open} onClose={onClose} plain>
      <div
        className="flex w-full items-start justify-center overflow-hidden px-4"
        style={{
          height: "var(--vvh, var(--app-height, 100vh))",
          paddingTop: "calc(18px + env(safe-area-inset-top, 0px))",
          paddingBottom: "12px",
          overscrollBehavior: "none",
          touchAction: "none",
        }}
      >
        <div
          data-modal-scroll-root="1"
          className="relative w-full max-w-[360px] overflow-y-auto rounded-3xl border border-white/15 bg-slate-900/95 shadow-[0_18px_45px_rgba(0,0,0,0.8)] px-4 pt-4 pb-4"
          style={{
            maxHeight:
              "calc(var(--vvh, var(--app-height, 100vh)) - env(safe-area-inset-top, 0px) - 42px)",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ШАГ ВЫБОРА */}
          {step === "choice" && (
            <>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                  <div className="text-[15px] font-semibold text-slate-50">
                    {tr("food.macroTargets.title")}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-300/90">
                    {tr("food.macroTargets.chooseMethod")}
                  </div>
                </div>
<button
  type="button"
  onClick={onClose}
  className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.06] text-[14px] text-white/75 active:scale-[0.96]"
  aria-label={tr("actions.close")}
>
  ✕
</button>
              </div>

              <div className="grid gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (typeof logEvent === "function") logEvent("macro:calc:start", {});
                    setStep("calc");
                  }}
                  className="h-12 rounded-2xl border border-white/30 bg-slate-900/70 text-[13px] font-semibold text-slate-50 hover:bg-slate-800 active:scale-[0.97]"
                >
                  {tr("food.macroTargets.calculateForMe")}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (typeof logEvent === "function") logEvent("macro:manual:start", {});
                    setStep("manual");
                  }}
                  className="h-12 rounded-2xl border border-white/30 bg-slate-900/70 text-[13px] font-semibold text-slate-50 hover:bg-slate-800 active:scale-[0.97]"
                >
                  {tr("food.macroTargets.knowMyMacros")}
                </button>
              </div>
            </>
          )}

          {/* ШАГ РУЧНОГО ВВОДА */}
          {step === "manual" && (
            <>
              <div className="flex items-start justify-between gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setStep("choice")}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-full bg-slate-900/70 border border-white/30 text-[12px] font-medium text-slate-50 hover:bg-slate-800 active:scale-[0.97]"
                >
                  ← {tr("actions.back")}
                </button>

                <div className="text-[14px] font-semibold text-slate-50">
                  {tr("food.macroTargets.manualTitle")}
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-slate-900/70 text-slate-200 text-[13px] font-semibold hover:bg-slate-800 active:scale-[0.96]"
                  aria-label={tr("actions.close")}
                >
                  ✕
                </button>
              </div>

              <div className="grid gap-3 text-xs text-slate-200">
                <label className="grid gap-1">
                  <span className="opacity-80">{tr("food.macroTargets.proteinLabel")}</span>
                  <input
                    ref={firstInputRef}
                    autoFocus
                    type="number"
                    inputMode="decimal"
                    value={proteins}
                    onChange={(e) => setProteins(e.target.value)}
                    onFocus={(e) => (typeof focusToEndAndScroll === "function" ? focusToEndAndScroll(e, null) : null)}
                    className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                    placeholder={tr("food.macroTargets.example", { value: 110 })}
                  />
                </label>

                <label className="grid gap-1">
                  <span className="opacity-80">{tr("food.macroTargets.fatLabel")}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={fats}
                    onChange={(e) => setFats(e.target.value)}
                    onFocus={(e) => (typeof focusToEndAndScroll === "function" ? focusToEndAndScroll(e, null) : null)}
                    className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                    placeholder={tr("food.macroTargets.example", { value: 50 })}
                  />
                </label>

                <label className="grid gap-1">
                  <span className="opacity-80">{tr("food.macroTargets.carbsLabel")}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={carbs}
                    onChange={(e) => setCarbs(e.target.value)}
                    onFocus={(e) => (typeof focusToEndAndScroll === "function" ? focusToEndAndScroll(e, null) : null)}
                    className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                    placeholder={tr("food.macroTargets.example", { value: 180 })}
                  />
                </label>

                <div className="mt-1 rounded-2xl bg-slate-900/70 border border-white/15 px-3 py-2 flex items-baseline justify-between text-[12px]">
                  <span className="opacity-80">
                    {tr("food.macroTargets.formulaCalories")}
                  </span>
                  <span className="text-[13px] font-semibold text-emerald-300 tabular-nums">
                    {Number.isFinite(manualKcal) ? manualKcal : 0} {tr("units.kcal")}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-10 rounded-2xl border border-white/20 bg-slate-900/60 text-[13px] font-medium text-slate-100 hover:bg-slate-800 active:scale-[0.97]"
                >
                  {tr("actions.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveManual}
                  disabled={!canSaveManual}
                  aria-disabled={!canSaveManual}
                  className={
                    "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                    (canSaveManual
                      ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                      : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
                  }
                >
                  {tr("actions.save")}
                </button>
              </div>
            </>
          )}

          {/* ШАГ КАЛЬКУЛЯТОРА */}
          {step === "calc" && (
            <>
              <div className="flex items-start justify-between gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setStep("choice")}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-full bg-slate-900/70 border border-white/30 text-[12px] font-medium text-slate-50 hover:bg-slate-800 active:scale-[0.97]"
                >
                  ← {tr("actions.back")}
                </button>

                <div className="text-[14px] font-semibold text-slate-50">
                  {tr("food.macroTargets.calculatorTitle")}
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-slate-900/70 text-slate-200 text-[13px] font-semibold hover:bg-slate-800 active:scale-[0.96]"
                  aria-label={tr("actions.close")}
                >
                  ✕
                </button>
              </div>

              <div className="grid gap-3 text-xs text-slate-200">
                {/* пол */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSex("female")}
                    className={cn(
                      "h-9 rounded-2xl border text-[12px] font-medium",
                      sex === "female"
                        ? "border-emerald-300 bg-emerald-400/20 text-emerald-100"
                        : "border-white/25 bg-slate-900/70 text-slate-100"
                    )}
                    >
                      {tr("food.macroTargets.female")}
                    </button>
                  <button
                    type="button"
                    onClick={() => setSex("male")}
                    className={cn(
                      "h-9 rounded-2xl border text-[12px] font-medium",
                      sex === "male"
                        ? "border-emerald-300 bg-emerald-400/20 text-emerald-100"
                        : "border-white/25 bg-slate-900/70 text-slate-100"
                    )}
                    >
                      {tr("food.macroTargets.male")}
                    </button>
                </div>

                {/* возраст / рост / вес */}
                <div className="grid grid-cols-3 gap-2">
                  <label className="grid gap-1">
                    <span className="opacity-80">{tr("food.macroTargets.age")}</span>
                    <input
                      ref={firstInputRef}
                      inputMode="numeric"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                      placeholder="25"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="opacity-80">{tr("food.macroTargets.height")}</span>
                    <input
                      inputMode="numeric"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                      placeholder="170"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="opacity-80">{tr("food.macroTargets.weight")}</span>
                    <input
                      inputMode="decimal"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="w-full h-10 rounded-2xl border border-white/15 bg-slate-900/70 px-3 text-[13px] text-slate-50 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
                      placeholder="65"
                    />
                  </label>
                </div>

                {/* активность */}
                <div className="grid gap-1">
                  <span className="opacity-80">{tr("food.macroTargets.activityLevel")}</span>
                  <div className="grid gap-1">
                    <button
                      type="button"
                      onClick={() => setActivity("low")}
                      className={cn(
                        "h-9 rounded-2xl px-3 text-left text-[11px] border",
                        activity === "low"
                          ? "border-emerald-300 bg-emerald-400/15 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.activityLow")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivity("light")}
                      className={cn(
                        "h-9 rounded-2xl px-3 text-left text-[11px] border",
                        activity === "light"
                          ? "border-emerald-300 bg-emerald-400/15 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.activityLight")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivity("medium")}
                      className={cn(
                        "h-9 rounded-2xl px-3 text-left text-[11px] border",
                        activity === "medium"
                          ? "border-emerald-300 bg-emerald-400/15 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.activityMedium")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivity("high")}
                      className={cn(
                        "h-9 rounded-2xl px-3 text-left text-[11px] border",
                        activity === "high"
                          ? "border-emerald-300 bg-emerald-400/15 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.activityHigh")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivity("athlete")}
                      className={cn(
                        "h-9 rounded-2xl px-3 text-left text-[11px] border",
                        activity === "athlete"
                          ? "border-emerald-300 bg-emerald-400/15 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.activityAthlete")}
                    </button>
                  </div>
                </div>

                {/* цель */}
                <div className="grid gap-1">
                  <span className="opacity-80">{tr("food.macroTargets.goal")}</span>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setGoal("loss")}
                      className={cn(
                        "h-9 rounded-2xl border text-[11px] font-medium",
                        goal === "loss"
                          ? "border-emerald-300 bg-emerald-400/20 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.goalLoss")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setGoal("maintain")}
                      className={cn(
                        "h-9 rounded-2xl border text-[11px] font-medium",
                        goal === "maintain"
                          ? "border-emerald-300 bg-emerald-400/20 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.goalMaintain")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setGoal("gain")}
                      className={cn(
                        "h-9 rounded-2xl border text-[11px] font-medium",
                        goal === "gain"
                          ? "border-emerald-300 bg-emerald-400/20 text-emerald-100"
                          : "border-white/20 bg-slate-900/70 text-slate-100"
                      )}
                    >
                      {tr("food.macroTargets.goalGain")}
                    </button>
                  </div>
                </div>

                {/* результат */}
                <div className="mt-1 rounded-2xl bg-slate-900/70 border border-white/15 px-3 py-2 text-[12px] flex flex-col gap-1">
                  <div className="flex items-baseline justify-between">
                    <span className="opacity-80">
                      {tr("food.macroTargets.calories")}
                    </span>
                    <span className="text-[13px] font-semibold text-emerald-300 tabular-nums">
                      {calcKcal > 0 ? calcKcal : 0} {tr("units.kcal")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-85">
                    <span>{tr("food.macro.proteins")}</span>
                    <span className="tabular-nums">
                      {calcProteins || 0} {tr("units.gramsShort")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-85">
                    <span>{tr("food.macro.fats")}</span>
                    <span className="tabular-nums">
                      {calcFats || 0} {tr("units.gramsShort")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-85">
                    <span>{tr("food.macro.carbs")}</span>
                    <span className="tabular-nums">
                      {calcCarbs || 0} {tr("units.gramsShort")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-10 rounded-2xl border border-white/20 bg-slate-900/60 text-[13px] font-medium text-slate-100 hover:bg-slate-800 active:scale-[0.97]"
                >
                  {tr("actions.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveCalc}
                  disabled={!canSaveCalc}
                  aria-disabled={!canSaveCalc}
                  className={
                    "flex-1 h-10 rounded-2xl text-[13px] font-semibold transition-all duration-150 " +
                    (canSaveCalc
                      ? "bg-gradient-to-r from-emerald-400 to-cyan-300 text-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.55)] hover:brightness-105 active:brightness-110"
                      : "bg-slate-700/60 text-slate-400 cursor-not-allowed opacity-60")
                  }
                >
                  {tr("actions.save")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
}


function useKeyboardInset() {
  const baseHRef = React.useRef(0);
  const rafRef = React.useRef(0);
  const lastBottomRef = React.useRef(-1);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const root = document.documentElement;
    const vv = window.visualViewport;
    const tg =
      window.Telegram && window.Telegram.WebApp
        ? window.Telegram.WebApp
        : null;

    const rememberStableHeight = () => {
      const h = Math.round(window.innerHeight || 0);
      if (h > 0) baseHRef.current = Math.max(baseHRef.current || 0, h);
    };

    rememberStableHeight();

    const setBottom = (value) => {
      const bottom = Math.min(Math.max(Math.round(value || 0), 0), 430);

      if (Math.abs(bottom - lastBottomRef.current) < 2) return;
      lastBottomRef.current = bottom;

      root.style.setProperty("--kb", `${bottom}px`);
    };

    const calculateBottom = () => {
      const stableH = baseHRef.current || window.innerHeight || 0;

      let bottomVV = 0;
      if (vv) {
        const top = Number(vv.offsetTop || 0);
        const height = Number(vv.height || window.innerHeight || 0);
        bottomVV = Math.max(0, stableH - (top + height));
      } else {
        bottomVV = Math.max(0, stableH - (window.innerHeight || stableH));
      }

      let bottomTG = 0;
      if (
        tg &&
        typeof tg.viewportStableHeight === "number" &&
        typeof tg.viewportHeight === "number"
      ) {
        bottomTG = Math.max(
          0,
          (tg.viewportStableHeight || 0) - (tg.viewportHeight || 0)
        );
      }

      const nextBottom = Math.max(bottomVV, bottomTG);

      // Клавиатура закрыта: обновляем "нормальную" высоту экрана.
      if (nextBottom < 8) {
        baseHRef.current = Math.round(window.innerHeight || stableH);
        return 0;
      }

      return nextBottom;
    };

    const update = () => {
      cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
        const vvHeight = vv
          ? Math.max(0, Math.round(Number(vv.height || 0)))
          : Math.max(0, Math.round(window.innerHeight || 0));

        if (vvHeight > 0) {
          root.style.setProperty("--vvh", `${vvHeight}px`);
        }

        root.style.setProperty(
          "--vv-top",
          `${Math.round(Number(vv?.offsetTop || 0))}px`
        );

        setBottom(calculateBottom());
      });
    };

    const delayedUpdate = () => {
      update();
      window.setTimeout(update, 80);
      window.setTimeout(update, 260);
    };

    const onOrientationChange = () => {
      baseHRef.current = 0;
      rememberStableHeight();
      delayedUpdate();
    };

    update();

    if (vv) {
      // ВАЖНО: не слушаем visualViewport.scroll.
      // В Telegram/iOS он часто стреляет во время обычного скролла и дёргает модалку.
      vv.addEventListener("resize", update);
    }

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", onOrientationChange);

    document.addEventListener("focusin", delayedUpdate, true);
    document.addEventListener("focusout", delayedUpdate, true);

    if (tg && tg.onEvent) {
      tg.onEvent("viewportChanged", update);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);

      if (vv) {
        vv.removeEventListener("resize", update);
      }

      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", onOrientationChange);
      document.removeEventListener("focusin", delayedUpdate, true);
      document.removeEventListener("focusout", delayedUpdate, true);

      if (tg && tg.offEvent) {
        try {
          tg.offEvent("viewportChanged", update);
        } catch {}
      }

      root.style.setProperty("--kb", "0px");
      root.style.removeProperty("--vvh");
      root.style.removeProperty("--vv-top");
    };
  }, []);

  return 0;
}

function useStopEmptyRubberScroll(active = true) {
  useEffect(() => {
    if (!active || typeof window === "undefined" || typeof document === "undefined") return;

    // ВАЖНО:
    // НЕ трогаем общий [data-app-scroll-root="1"],
    // иначе начинает дёргаться обычная прокрутка экранов, например выбор групп мышц.
const SCROLL_ROOT_SELECTOR = [
  '[data-exercise-library-scroll="1"]',
  '[data-my-foods-scroll-root="1"]',
  '[data-modal-scroll-root="1"]',
].join(",");

    const state = {
      root: null,
      startX: 0,
      startY: 0,
      lastY: 0,
    };

    const findRoot = (target) => {
      if (!target || typeof target.closest !== "function") return null;
      return target.closest(SCROLL_ROOT_SELECTOR);
    };

    const onTouchStart = (e) => {
      state.root = null;

      if (!e.touches || e.touches.length !== 1) return;

      const root = findRoot(e.target);
      if (!root) return;

      const t = e.touches[0];

      state.root = root;
      state.startX = t.clientX;
      state.startY = t.clientY;
      state.lastY = t.clientY;
    };

    const onTouchMove = (e) => {
      if (!e.touches || e.touches.length !== 1 || !state.root) return;

      const t = e.touches[0];
      const root = state.root;

      const dx = Math.abs(t.clientX - state.startX);
      const fullDy = Math.abs(t.clientY - state.startY);

      // Горизонтальные жесты не блокируем.
      if (dx > fullDy + 6) return;

      const max = Math.max(0, root.scrollHeight - root.clientHeight);
      const current = root.scrollTop;
      const dy = t.clientY - state.lastY;

      state.lastY = t.clientY;

      if (max <= 1) {
        e.preventDefault();
        return;
      }

      // Верх списка: не даём тянуть пустоту вниз.
      if (current <= 0 && dy > 0) {
        e.preventDefault();
        root.scrollTop = 0;
        return;
      }

      // Низ списка: не даём тянуть пустоту вверх.
      if (current >= max - 1 && dy < 0) {
        e.preventDefault();
        root.scrollTop = max;
      }
    };

    const clear = () => {
      state.root = null;
    };

    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    document.addEventListener("touchend", clear, { capture: true, passive: true });
    document.addEventListener("touchcancel", clear, { capture: true, passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", clear, true);
      document.removeEventListener("touchcancel", clear, true);
    };
  }, [active]);
}



// --- user_foods (Мои продукты и блюда) в БД ---

function parseUserFoodJson(value, fallback) {
  if (value == null || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  return value;
}

function dbUserFoodToApp(row) {
  const proteins = Number(row.p_per100 ?? 0);
  const fats = Number(row.f_per100 ?? 0);
  const carbs = Number(row.c_per100 ?? 0);

  const savedKcal = Number(row.kcal_per100);

  const kcal =
    Number.isFinite(savedKcal) && savedKcal > 0
      ? savedKcal
      : Math.round(proteins * 4 + fats * 9 + carbs * 4);

  const type = row.type === "dish" ? "dish" : "ingredient";

  const parsedIngredients = parseUserFoodJson(
    row.ingredients,
    []
  );

  const parsedRecipe = parseUserFoodJson(
    row.recipe,
    null
  );

  const ingredients =
    type === "dish" && Array.isArray(parsedIngredients)
      ? parsedIngredients
      : [];

  const recipe =
    type === "dish" &&
    parsedRecipe &&
    typeof parsedRecipe === "object" &&
    !Array.isArray(parsedRecipe)
      ? parsedRecipe
      : null;

  return {
    id: row.id,
    name: row.name,
    type,
    defaultGrams: Number(row.default_grams ?? 100),

    per100: {
      kcal,
      proteins,
      fats,
      carbs,
    },

    ingredients,
    recipe,

    nameKey: row.name_key ?? null,
    lastMeal: row.last_meal ?? null,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

function appUserFoodToDb(food) {
  const type = food.type === "dish" ? "dish" : "ingredient";

  const ingredients =
    type === "dish" && Array.isArray(food.ingredients)
      ? food.ingredients
      : [];

  const recipe =
    type === "dish" &&
    food.recipe &&
    typeof food.recipe === "object" &&
    !Array.isArray(food.recipe)
      ? food.recipe
      : null;

  return {
    id: food.id ?? null,
    name: food.name,
    type,

    kcal_per100: Number(food.per100?.kcal ?? 0),
    p_per100: Number(food.per100?.proteins ?? 0),
    f_per100: Number(food.per100?.fats ?? 0),
    c_per100: Number(food.per100?.carbs ?? 0),

    default_grams: Number(food.defaultGrams ?? 100),
    last_meal: food.lastMeal ?? null,

    ingredients,
    recipe,
  };
}

async function apiListUserFoods() {
  const res = await tgFetch("/api/user-foods", { method: "GET" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiListUserFoods failed");
  return (json.data || []).map(dbUserFoodToApp);
}

async function apiUpsertUserFood(food) {
  const res = await tgFetch("/api/user-foods", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(appUserFoodToDb(food)),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiUpsertUserFood failed");
  return dbUserFoodToApp(json.data);
}
async function apiDeleteUserFood(foodOrId) {
  const isObject =
    foodOrId !== null &&
    typeof foodOrId === "object";

  const food = isObject ? normalizeMyFoodForList(foodOrId) : null;

  const rawId = isObject
    ? food?.id ??
      food?.userFoodId ??
      food?.user_food_id ??
      food?.sourceMyFoodId ??
      food?.myFoodId ??
      food?.__deleteId ??
      ""
    : foodOrId;

  const cleanId = String(rawId ?? "").trim();
  const numericId = Number(cleanId);
  const hasNumericId = Number.isFinite(numericId) && numericId > 0;

  const name = isObject
    ? String(
        food?.__deleteName ??
          food?.name ??
          food?.nameKey ??
          food?.name_key ??
          ""
      ).trim()
    : "";

  const nameKey = String(
    food?.nameKey ??
      food?.name_key ??
      food?.__deleteName ??
      food?.name ??
      name ??
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  const typeRaw = String(
    food?.__deleteType ??
      food?.type ??
      "ingredient"
  )
    .trim()
    .toLowerCase();

  const type = typeRaw === "dish" ? "dish" : "ingredient";

  if (!hasNumericId && !nameKey) {
    throw new Error("apiDeleteUserFood: id or name required");
  }

  const params = new URLSearchParams();

  if (hasNumericId) params.set("id", String(numericId));
  if (name) params.set("name", name);
  if (nameKey) params.set("name_key", nameKey);
  if (type) params.set("type", type);

  const body = {
    id: hasNumericId ? numericId : null,
    name,
    name_key: nameKey,
    type,
  };

  const res = await tgFetch(`/api/user-foods?${params.toString()}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  let json = null;

  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok || json?.ok === false) {
    throw new Error(json?.error || "apiDeleteUserFood failed");
  }

  return json?.data ?? {
    deleted: true,
  };
}
function ReportPeriodModal({ open, onClose, onPick }) {
  const { t } = useTranslation();

  return (
    <BaseModal open={open} onClose={onClose}>
      <div className="relative mx-auto w-full max-w-[390px] rounded-3xl border border-white/10 bg-slate-950/90 p-4">
      
        <div className="text-lg font-semibold">
          {t("food.report.periodTitle")}
        </div>
        <div className="text-xs opacity-70 mt-1">
          {t("food.report.choosePeriod")}
        </div>

        <div className="grid gap-2 mt-4">
          <button
            onClick={() => onPick?.("week")}
            className="h-11 rounded-2xl border border-white/15 bg-slate-900/60 text-slate-100 active:scale-[0.98]"
          >
            {t("food.report.week")}
          </button>
          <button
            onClick={() => onPick?.("month")}
            className="h-11 rounded-2xl border border-white/15 bg-slate-900/60 text-slate-100 active:scale-[0.98]"
          >
            {t("food.report.month")}
          </button>
          <button
            onClick={() => onPick?.("quarter")}
            className="h-11 rounded-2xl border border-white/15 bg-slate-900/60 text-slate-100 active:scale-[0.98]"
          >
            {t("food.report.quarter")}
          </button>
        </div>

        <button
          onClick={onClose}
          className="mt-4 h-11 w-full rounded-2xl border border-white/20 bg-slate-900/40 text-slate-200"
        >
          {t("actions.cancel")}
        </button>
      </div>
    </BaseModal>
  );
}
function StepsReportPeriodModal({ open, onClose }) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <BaseModal open={open} onClose={onClose}>
      <div className="relative mx-auto w-full max-w-[390px] rounded-3xl border border-white/10 bg-slate-950/90 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[20px] font-bold text-slate-50">
            {t("food.macro.report")}
          </div>

          <AppCloseButton
            onClick={onClose}
            aria-label={t("actions.close")}
          />
        </div>

        <div className="mt-5 rounded-[22px] border border-cyan-300/20 bg-cyan-300/[0.06] px-4 py-8 text-center">
          <div className="text-[15px] font-semibold leading-relaxed text-slate-200">
            {t("body.steps.reportInDevelopment")}
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
function ExerciseHistoryModal({ open, onClose, exercise, period }) {
  if (!open || !exercise) return null;

  const maxSets = Math.max(...exercise.history.map((item) => item.weights.length), 0);

  const periodLabel =
    period === "2weeks"
      ? "за 2 недели"
      : period === "month"
      ? "за месяц"
      : "за 3 месяца";

  return (
    <BaseModal open={open} onClose={onClose}>
      <div className="mx-auto w-full max-w-[430px]">
<div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(4,12,28,0.98)_0%,rgba(4,10,24,0.98)_100%)] px-4 pb-4 pt-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[22px] font-semibold leading-none tracking-[-0.03em] text-white">
                {exercise.title}
              </h2>
              <p className="mt-2 text-[13px] leading-[1.45] text-white/56">
  Сравнение подходов по дням {periodLabel}
</p>
            </div>
            <button
              onClick={onClose}
className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[24px] font-light text-white/80"            >
              ×
            </button>
          </div>

<div className="mt-4 rounded-[22px] bg-white/[0.035] p-3">
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `72px repeat(${exercise.history.length}, minmax(0, 1fr))` }}
            >
              <div className="flex items-center text-[11px] font-medium uppercase tracking-[0.04em] text-white/40">
                Подход
              </div>

              {exercise.history.map((item, idx) => (
                <div
                  key={`head-${idx}`}
                  className="rounded-xl bg-white/[0.035] px-2 py-2 text-center"
                >
                  <div className="text-[12px] font-medium text-white/85">{item.date}</div>
                  
                </div>
              ))}

              {Array.from({ length: maxSets }, (_, setIndex) => (
                <React.Fragment key={`set-${setIndex}`}>
                  <div className="flex min-h-[42px] items-center rounded-xl bg-white/[0.025] px-2 text-[12px] font-medium text-white/55">
                    {setIndex + 1} подход
                  </div>

                  {exercise.history.map((item, colIndex) => {
                    const value = item.weights[setIndex];
                    return (
                      <div
                        key={`cell-${setIndex}-${colIndex}`}
                        className={cn(
  "flex min-h-[42px] items-center justify-center rounded-xl px-2 text-center text-[13px] font-medium tabular-nums",
  value
    ? "bg-white/[0.04] text-white"
    : "bg-white/[0.02] text-white/22"
)}
                      >
                        {value || "—"}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

         
        </div>
      </div>
    </BaseModal>
  );
}
function StepsReportModal({ open, onClose, period }) {
  const [tab, setTab] = useState("period");
  const [showAllExercises, setShowAllExercises] = useState(false);
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  if (!open) return null;

  const periodLabel =
    period === "2weeks"
      ? "2 недели"
      : period === "month"
      ? "Месяц"
      : "3 месяца";

  const periodSubLabel =
    period === "2weeks"
      ? "за 2 недели"
      : period === "month"
      ? "за месяц"
      : "за 3 месяца";

  const outerCard =
  "rounded-[22px] bg-white/[0.035] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] backdrop-blur-sm";
const innerRow =
  "rounded-[18px] bg-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]";
  const exercises = [
    {
      title: "Жим лёжа",
      sub: "45 × 8 → 55 × 8",
      badge: "+10 кг",
      history: [
        { date: "18 мар", sets: "3", weights: ["45 × 8", "47.5 × 8", "50 × 8"] },
        { date: "23 мар", sets: "3", weights: ["47.5 × 8", "50 × 8", "52.5 × 8"] },
        { date: "30 мар", sets: "3", weights: ["50 × 8", "52.5 × 8", "55 × 8"] },
      ],
    },
    {
      title: "Тяга верхнего блока",
      sub: "50 × 10 → 60 × 10",
      badge: "+10 кг",
      history: [
        { date: "19 мар", sets: "3", weights: ["50 × 10", "52.5 × 10", "55 × 10"] },
        { date: "25 мар", sets: "3", weights: ["52.5 × 10", "55 × 10", "57.5 × 10"] },
        { date: "31 мар", sets: "3", weights: ["55 × 10", "57.5 × 10", "60 × 10"] },
      ],
    },
    {
      title: "Присед",
      sub: "60 × 8 → 70 × 8",
      badge: "+10 кг",
      history: [
        { date: "18 мар", sets: "4", weights: ["60 × 8", "62.5 × 8", "65 × 8", "65 × 8"] },
        { date: "24 мар", sets: "4", weights: ["62.5 × 8", "65 × 8", "67.5 × 8", "67.5 × 8"] },
        { date: "29 мар", sets: "4", weights: ["65 × 8", "67.5 × 8", "70 × 8", "70 × 8"] },
      ],
    },
    {
      title: "Жим ногами",
      sub: "120 × 12 → 130 × 12",
      badge: "+10 кг",
      history: [
        { date: "20 мар", sets: "3", weights: ["120 × 12", "122.5 × 12", "125 × 12"] },
        { date: "26 мар", sets: "3", weights: ["122.5 × 12", "125 × 12", "127.5 × 12"] },
        { date: "31 мар", sets: "3", weights: ["125 × 12", "127.5 × 12", "130 × 12"] },
      ],
    },
    {
      title: "Румынская тяга",
      sub: "70 × 10 → 77.5 × 10",
      badge: "+7.5 кг",
      history: [
        { date: "21 мар", sets: "3", weights: ["70 × 10", "72.5 × 10", "72.5 × 10"] },
        { date: "27 мар", sets: "3", weights: ["72.5 × 10", "75 × 10", "75 × 10"] },
        { date: "31 мар", sets: "3", weights: ["75 × 10", "77.5 × 10", "77.5 × 10"] },
      ],
    },
    {
      title: "Сгибание на бицепс",
      sub: "18 × 10 → 22 × 10",
      badge: "+4 кг",
      history: [
        { date: "18 мар", sets: "3", weights: ["18 × 10", "18 × 10", "20 × 10"] },
        { date: "24 мар", sets: "3", weights: ["18 × 10", "20 × 10", "20 × 10"] },
        { date: "29 мар", sets: "3", weights: ["20 × 10", "22 × 10", "22 × 10"] },
      ],
    },
    {
      title: "Разгибание трицепса",
      sub: "25 × 12 → 32 × 12",
      badge: "+7 кг",
      history: [
        { date: "19 мар", sets: "3", weights: ["25 × 12", "27 × 12", "27 × 12"] },
        { date: "25 мар", sets: "3", weights: ["27 × 12", "30 × 12", "30 × 12"] },
        { date: "31 мар", sets: "3", weights: ["30 × 12", "32 × 12", "32 × 12"] },
      ],
    },
  ];

  const TabButton = ({ id, children }) => {
    const active = tab === id;
    return (
      <button
        onClick={() => setTab(id)}
        className={cn(
          "relative h-9 rounded-full px-4 text-[13px] font-medium transition whitespace-nowrap",
          active ? "bg-white text-slate-900 shadow" : "bg-white/5 text-white/70 hover:bg-white/10"
        )}
      >
        {children}
      </button>
    );
  };

  const Badge = ({ children, tone = "green" }) => {
    const toneClass =
      tone === "pink"
        ? "border-[#7f5268] bg-[#3d2632]/70 text-[#f6d7e4]"
        : tone === "yellow"
        ? "border-[#7f6a33] bg-[#3f3419]/70 text-[#f3df9a]"
        : "border-[#2e6b63] bg-[#173a3a]/70 text-[#b9ead7]";

    return (
      <div className={cn("rounded-full border px-3 py-1.5 text-[12px] font-medium leading-none", toneClass)}>
        {children}
      </div>
    );
  };

  const SmallStat = ({ label, value, sub }) => (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3">
      <div className="text-[11px] text-white/50">{label}</div>
      <div className="mt-1 text-[18px] font-semibold text-white tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] text-white/55">{sub}</div>
    </div>
  );

const BigRow = ({ title, sub, badge, tone = "green", onClick }) => {
  const openedRef = React.useRef(false);

  return (
    <button
      type="button"
      onPointerDown={(e) => {
        if (!onClick) return;
        openedRef.current = true;
        e.stopPropagation();
        onClick();
        setTimeout(() => {
          openedRef.current = false;
        }, 300);
      }}
      onClick={(e) => {
        if (!onClick) return;
        if (openedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick();
      }}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "block w-full text-left select-none",
        onClick ? "cursor-pointer" : "cursor-default"
      )}
      style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
    >
      <div
        className={cn(
          innerRow,
          "flex items-center justify-between gap-3 px-3.5 py-3.5 transition",
          onClick ? "active:scale-[0.995]" : ""
        )}
      >
        <div className="min-w-0 pr-2">
          <div className="text-[15px] font-medium tracking-[-0.02em] text-white">
            {title}
          </div>
          <div className="mt-1 text-[12px] text-white/50">{sub}</div>
        </div>
        <div className="shrink-0">
          <Badge tone={tone}>{badge}</Badge>
        </div>
      </div>
    </button>
  );
};
  const Chip = ({ children }) => (
    <div className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 text-[13px] leading-none text-white/78">
      {children}
    </div>
  );

  return (
    <>
      <BaseModal open={open} onClose={onClose}>
        <div className="mx-auto w-full max-w-[430px]">
<div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(4,12,28,0.98)_0%,rgba(4,10,24,0.98)_100%)] px-4 pb-4 pt-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-[24px] font-semibold leading-none tracking-[-0.03em] text-white">
                  Отчёт
                </h1>
                <p className="mt-3 max-w-[280px] text-[14px] leading-[1.45] text-white/56">
                  Тренировки, прогресс, замеры и активность за период
                </p>
              </div>
              <button
                onClick={onClose}
className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[24px] font-light text-white/80"              >
                ×
              </button>
            </div>

<div className="mt-4" />
            <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1">
  <button
    type="button"
    onClick={() => setTab("period")}
    className={`h-8 px-3 rounded-full border text-[12px] font-semibold whitespace-nowrap active:scale-[0.99] ${
      tab === "period"
        ? "bg-white/20 border-white/30 text-white"
        : "bg-white/10 border-white/15 text-white/75"
    }`}
  >
    Период
  </button>

  <button
    type="button"
    onClick={() => setTab("training")}
    className={`h-8 px-3 rounded-full border text-[12px] font-semibold whitespace-nowrap active:scale-[0.99] ${
      tab === "training"
        ? "bg-white/20 border-white/30 text-white"
        : "bg-white/10 border-white/15 text-white/75"
    }`}
  >
    Тренировки
  </button>

  <button
    type="button"
    onClick={() => setTab("body")}
    className={`h-8 px-3 rounded-full border text-[12px] font-semibold whitespace-nowrap active:scale-[0.99] ${
      tab === "body"
        ? "bg-white/20 border-white/30 text-white"
        : "bg-white/10 border-white/15 text-white/75"
    }`}
  >
    Форма
  </button>
</div>

<div className="mt-4" />
            {tab === "period" && (
              <div className="mt-4 space-y-4">
                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[13px] text-white/50">Период</div>
                  <div className="mt-1 text-[22px] font-semibold text-white">{periodLabel}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <SmallStat label="Тренировки" value="7" sub={periodSubLabel} />
                    <SmallStat label="Подходы" value="94" sub="всего" />
                    <SmallStat label="Тоннаж" value="21.6 т" sub="рабочий" />
                    <SmallStat label="Активных дней" value="11" sub="со спортом" />
                  </div>
                </section>

                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[15px] font-medium tracking-[-0.02em] text-white">
                    Шаги и активность
                  </div>
                  <div className="mt-3.5 grid grid-cols-3 gap-2.5">
                    <SmallStat label="Лучший день" value="13 440" sub="по шагам" />
                    <SmallStat label="Среднее" value="10 240" sub="шагов / день" />
                    <SmallStat label="Серия" value="5" sub="дней подряд" />
                  </div>
                </section>

                <section className={cn(outerCard, "p-3.5")}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[15px] font-medium tracking-[-0.02em] text-white">
                      Любимые упражнения
                    </div>
                    <div className="text-[11px] text-white/40">по частоте</div>
                  </div>

                  <div className="mt-3 space-y-2.5">
                    {[
                      {
                        title: "Жим лёжа",
                        count: 4,
                        note: "чаще всего в периоде",
                        history: [
                          { date: "18 мар", sets: "3", weights: ["45 × 8", "47.5 × 8", "50 × 8"] },
                          { date: "23 мар", sets: "3", weights: ["47.5 × 8", "50 × 8", "52.5 × 8"] },
                          { date: "30 мар", sets: "3", weights: ["50 × 8", "52.5 × 8", "55 × 8"] },
                        ],
                      },
                      {
                        title: "Присед",
                        count: 3,
                        note: "стабильно в программе",
                        history: [
                          { date: "18 мар", sets: "4", weights: ["60 × 8", "62.5 × 8", "65 × 8", "65 × 8"] },
                          { date: "24 мар", sets: "4", weights: ["62.5 × 8", "65 × 8", "67.5 × 8", "67.5 × 8"] },
                          { date: "29 мар", sets: "4", weights: ["65 × 8", "67.5 × 8", "70 × 8", "70 × 8"] },
                        ],
                      },
                      {
                        title: "Тяга верхнего блока",
                        count: 3,
                        note: "часто повторяется",
                        history: [
                          { date: "19 мар", sets: "3", weights: ["50 × 10", "52.5 × 10", "55 × 10"] },
                          { date: "25 мар", sets: "3", weights: ["52.5 × 10", "55 × 10", "57.5 × 10"] },
                          { date: "31 мар", sets: "3", weights: ["55 × 10", "57.5 × 10", "60 × 10"] },
                        ],
                      },
                      {
                        title: "Жим ногами",
                        count: 2,
                        note: "реже встречается",
                        history: [
                          { date: "20 мар", sets: "3", weights: ["120 × 12", "122.5 × 12", "125 × 12"] },
                          { date: "26 мар", sets: "3", weights: ["122.5 × 12", "125 × 12", "127.5 × 12"] },
                        ],
                      },
                      {
                        title: "Разгибание трицепса",
                        count: 2,
                        note: "точечная добивка",
                        history: [
                          { date: "19 мар", sets: "3", weights: ["25 × 12", "27 × 12", "27 × 12"] },
                          { date: "25 мар", sets: "3", weights: ["27 × 12", "30 × 12", "30 × 12"] },
                        ],
                      },
                    ]
                      .slice(0, showAllFavorites ? undefined : 3)
                      .map((item, index) => (
                        <button
  key={item.title}
  type="button"
onClick={() => setSelectedExercise({ title: item.title, history: item.history })}
  className="block w-full rounded-[18px] bg-white/[0.03] px-3 py-3 text-left active:scale-[0.995]"
>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[14px] font-medium text-white">
                                {index + 1}. {item.title}
                              </div>
                              <div className="mt-0.5 text-[11px] text-white/45">{item.note}</div>
                            </div>
                            <div className="shrink-0 rounded-full bg-white/[0.04] px-2.5 py-1 text-[11px] text-white/70">
  {item.count}×
</div>
                          </div>

                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                            <div
                              className="h-full rounded-full bg-[linear-gradient(90deg,rgba(94,234,212,0.95)_0%,rgba(52,211,153,0.95)_100%)]"
                              style={{ width: `${Math.max(24, item.count * 24)}%` }}
                            />
                          </div>
                        </button>
                      ))}

<button
  onClick={() => setShowAllFavorites((v) => !v)}
  className="mt-3 w-full rounded-full bg-white/[0.04] py-2.5 text-[13px] text-white/72 hover:bg-white/[0.07]"
>
                      {showAllFavorites ? "Скрыть" : "Показать все"}
                    </button>
                  </div>
                </section>
              </div>
            )}

            {tab === "training" && (
              <div className="mt-4 space-y-4">
                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[15px] font-medium tracking-[-0.02em] text-white">Группы мышц</div>
                  <div className="mt-3.5 flex flex-wrap gap-2">
                    <Chip>Спина · 5</Chip>
                    <Chip>Ноги · 4</Chip>
                    <Chip>Грудь · 4</Chip>
                    <Chip>Бицепс · 3</Chip>
                    <Chip>Трицепс · 3</Chip>
                    <Chip>Плечи · 2</Chip>
                    <Chip>Пресс · 2</Chip>
                  </div>
                </section>

                <section className={cn(outerCard, "p-3.5")}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[15px] font-medium tracking-[-0.02em] text-white">Прогресс по весам</div>
                    <div className="text-[11px] text-white/36">Нажми на упражнение</div>
                  </div>
                  <div className="mt-3.5 space-y-2.5">
{exercises.slice(0, showAllExercises ? exercises.length : 5).map((ex) => (
  <button
    key={ex.title}
    type="button"
    onClick={() => setSelectedExercise({
      title: ex.title,
      history: ex.history,
    })}
    className="block w-full rounded-[18px] bg-white/[0.03] px-3 py-3 text-left active:scale-[0.995]"
    style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 pr-2">
        <div className="text-[15px] font-medium tracking-[-0.02em] text-white">
          {ex.title}
        </div>
        <div className="mt-1 text-[12px] text-white/50">{ex.sub}</div>
      </div>

      <div className="shrink-0">
        <div className="rounded-full border border-[#2e6b63] bg-[#173a3a]/70 px-3 py-1.5 text-[12px] font-medium leading-none text-[#b9ead7]">
          {ex.badge}
        </div>
      </div>
    </div>
  </button>
))}
                  </div>
                  <button
                    onClick={() => setShowAllExercises((v) => !v)}
                    className="mt-3.5 w-full rounded-full border border-white/10 bg-white/[0.04] py-2.5 text-[13px] text-white/72 hover:bg-white/[0.07]"
                  >
                    {showAllExercises ? "Скрыть упражнения" : "Показать все упражнения"}
                  </button>
                </section>

                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[15px] font-medium tracking-[-0.02em] text-white">Кардио</div>
                  <div className="mt-3.5 space-y-2.5">
                    <BigRow title="Бег" sub="3 сессии · 18 км" badge="+ выносливость" />
                    <BigRow title="Велотренажёр" sub="2 сессии · 45 мин" badge="+ кардио" />
                  </div>
                </section>
              </div>
            )}

            {tab === "body" && (
              <div className="mt-4 space-y-4">
                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[15px] font-medium tracking-[-0.02em] text-white">Замеры тела</div>
                  <div className="mt-3.5 space-y-2.5">
                    <BigRow title="Талия" sub="69 см → 68 см" badge="−1 см" />
                    <BigRow title="Плечи" sub="103 см → 104 см" badge="+1 см" />
                    <BigRow title="Бёдра" sub="95.5 см → 96 см" badge="+0.5 см" tone="pink" />
                  </div>
                </section>

                <section className={cn(outerCard, "p-3.5")}>
                  <div className="text-[15px] font-medium tracking-[-0.02em] text-white">Добавки</div>
                  <div className="mt-3.5 space-y-2.5">
                    <BigRow title="Омега-3" sub="28 из 31 дней" badge="90%" />
                    <BigRow title="Магний" sub="31 из 31 дней" badge="100%" />
                    <BigRow title="Витамин D3" sub="22 из 31 дней" badge="71%" tone="yellow" />
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </BaseModal>

<ExerciseHistoryModal
  open={!!selectedExercise}
  onClose={() => setSelectedExercise(null)}
  exercise={selectedExercise}
  period={period}
/>
    </>
  );
}
function FoodReportModal({ open, onClose, period }) {
  const { t: tr, i18n } = useTranslation();

  const [tab, setTab] = useState("overview");

  const periodLabel =
    period === "week"
      ? tr("food.report.periodWeekLabel")
      : period === "month"
      ? tr("food.report.periodMonthLabel")
      : tr("food.report.periodQuarterLabel");
  
    const formatDateDMY = (iso) => {
  if (!iso) return "—";

  const d = new Date(String(iso));

  if (Number.isNaN(d.getTime())) {
    return String(iso);
  }

  return d.toLocaleDateString(
    i18n.resolvedLanguage === "ru"
      ? "ru-RU"
      : "en-GB",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  );
};


 // --- РЕАЛЬНЫЕ ДАННЫЕ ДЛЯ ОТЧЁТА ---
const [seriesA, setSeriesA] = useState([]);
const [reportLoading, setReportLoading] = useState(false);
const [reportError, setReportError] = useState("");
const [reportLoadingText, setReportLoadingText] = useState(
  () => tr("food.report.loadingInitial")
);
useEffect(() => {
  let cancelled = false;
  let timers = [];
  (async () => {
    try {
      setReportLoading(true);
      setReportError("");
      setReportLoadingText(
        tr("food.report.loadingDays")
      );

      timers.push(
        setTimeout(
          () =>
            setReportLoadingText(
              tr("food.report.loadingMacros")
            ),
          900
        )
      );

      timers.push(
        setTimeout(
          () =>
            setReportLoadingText(
              tr("food.report.loadingSleepWater")
            ),
          1700
        )
      );

      timers.push(
        setTimeout(
          () =>
            setReportLoadingText(
              tr("food.report.loadingSummary")
            ),
          2600
        )
      );

      timers.push(
        setTimeout(
          () =>
            setReportLoadingText(
              tr("food.report.loadingAlmost")
            ),
          3800
        )
      );
      const qs = new URLSearchParams({ period: String(period || "week") });

// ВАЖНО: используем tgFetch, чтобы улетал x-telegram-init-data
const res = await tgFetch(
  `/api/food-month?mode=report&period=${encodeURIComponent(String(period || "week"))}`,
  {
    method: "GET",
    headers: { Accept: "application/json" },
  }
);


      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      if (!cancelled) {
  const s = Array.isArray(data.series) ? data.series : [];

  debugLog("[FOOD REPORT] period =", period);
  debugLog("[FOOD REPORT] len =", s.length);
  debugLog("[FOOD REPORT] first =", s[0]?.date, "last =", s[s.length - 1]?.date);
  debugLog("[FOOD REPORT] water sample =", s.slice(0, 10).map(x => x?.water));
  debugLog("[FOOD REPORT] weight sample =", s.slice(0, 10).map(x => x?.weight));
  debugLog("[FOOD REPORT] sleep sample =", s.slice(0, 10).map(x => x?.sleepHours));

  setSeriesA(s);
}

    } catch (e) {
      if (!cancelled) {
        setReportError(
  e?.message
    ? String(e.message)
    : tr("food.report.loadingError")
);
        setSeriesA([]);
      }
        } finally {
      timers.forEach(clearTimeout);
      timers = [];
      if (!cancelled) setReportLoading(false);
    }
  })();

    return () => {
    cancelled = true;
    timers.forEach(clearTimeout);
    timers = [];
  };
}, [period]);

const summarize = (series) => {
  if (!Array.isArray(series) || series.length === 0) {
    return {
      days: 0,
      total: { kcal: 0, protein: 0, fat: 0, carb: 0, water: 0, moodScore: 0, sleepHours: 0 },
      avg: { kcal: 0, protein: 0, fat: 0, carb: 0, water: 0, moodScore: 0, weight: 0, sleepHours: 0 },
      range: {
        kcal: { min: 0, max: 0 },
        protein: { min: 0, max: 0 },
        fat: { min: 0, max: 0 },
        carb: { min: 0, max: 0 },
        water: { min: 0, max: 0 },
        weight: { min: 0, max: 0 },
        sleepHours: { min: 0, max: 0 },
      },
      weightToday: 0,
    };
  }

  const n = series.length;
// дни, где реально есть еда (чтобы "среднее" не портилось пропусками)
const foodN = series.filter((x) => {
  const kcal = Number(x?.kcal) || 0;
  const p = Number(x?.protein ?? x?.proteins) || 0;
  const f = Number(x?.fat ?? x?.fats) || 0;
  const c = Number(x?.carb ?? x?.carbs) || 0;
  return kcal > 0 || p > 0 || f > 0 || c > 0;
}).length;

// дни, где реально есть вода/сон/настроение (чтобы средние были честные)
const waterN = series.filter((x) => (Number(x?.water) || 0) > 0).length;
const sleepN = series.filter((x) => (Number(x?.sleepHours) || 0) > 0).length;
const moodN  = series.filter((x) => (Number(x?.moodScore) || 0) > 0).length;
  const total = series.reduce(
    (acc, x) => {
      acc.kcal += Number(x?.kcal) || 0;
      acc.protein += Number(x?.protein) || 0;
      acc.fat += Number(x?.fat) || 0;
      acc.carb += Number(x?.carb) || 0;
      acc.water += Number(x?.water) || 0;
      acc.moodScore += Number(x?.moodScore) || 0;
      acc.sleepHours += Number(x?.sleepHours) || 0;
      return acc;
    },
    { kcal: 0, protein: 0, fat: 0, carb: 0, water: 0, moodScore: 0, sleepHours: 0 }
  );

  const weights = series
    .map((x) => Number(x?.weight))
    .filter((v) => Number.isFinite(v) && v > 0);

  const weightToday = (() => {
    for (let i = series.length - 1; i >= 0; i--) {
      const v = Number(series[i]?.weight);
      if (Number.isFinite(v) && v > 0) return Math.round(v * 10) / 10;
    }
    return 0;
  })();

  const weightMode = (() => {
    if (!weights.length) return 0;
    const freq = new Map();
    for (const w of weights) {
      const key = Math.round(w * 10) / 10;
      freq.set(key, (freq.get(key) || 0) + 1);
    }
    let bestVal = 0;
    let bestCnt = -1;
    for (const [val, cnt] of freq.entries()) {
      if (cnt > bestCnt) {
        bestCnt = cnt;
        bestVal = val;
      }
    }
    return bestVal;
  })();

  const minMax = (arr) => {
    const vals = arr.filter((v) => Number.isFinite(v));
    if (!vals.length) return { min: 0, max: 0 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  };

  const range = {
    kcal: minMax(series.map((x) => Number(x?.kcal) || 0)),
    protein: minMax(series.map((x) => Number(x?.protein) || 0)),
    fat: minMax(series.map((x) => Number(x?.fat) || 0)),
    carb: minMax(series.map((x) => Number(x?.carb) || 0)),
    water: minMax(series.map((x) => Number(x?.water) || 0)),
    sleepHours: minMax(series.map((x) => Number(x?.sleepHours) || 0)),
    weight: weights.length ? { min: Math.min(...weights), max: Math.max(...weights) } : { min: 0, max: 0 },
  };

const avg = {
  kcal: foodN ? Math.round(total.kcal / foodN) : 0,
  protein: foodN ? Math.round(total.protein / foodN) : 0,
  fat: foodN ? Math.round(total.fat / foodN) : 0,
  carb: foodN ? Math.round(total.carb / foodN) : 0,

  water: waterN ? Math.round((total.water / waterN) * 10) / 10 : 0,
  moodScore: moodN ? Math.round((total.moodScore / moodN) * 10) / 10 : 0,

  // вес средний оставим как было (мода), ты просил менять только "набрал/сбросил"
  weight: weightMode,

  sleepHours: sleepN ? Math.round((total.sleepHours / sleepN) * 10) / 10 : 0,
};

  return { days: n, total, avg, range, weightToday };
};

  const buildInsights = (series, summary) => {
    const insights = [];

    if (summary.avg.protein < 95) {
      insights.push({
        title: "Белка маловато",
        text: "Средний белок ниже условной цели. Можно добавить белковый продукт в каждый приём пищи.",
      });
    } else {
      insights.push({
        title: "Белок ок",
        text: "В среднем белка достаточно — хороший фундамент для сытости.",
      });
    }

    const spread = summary.range.kcal.max - summary.range.kcal.min;
    if (spread > 450) {
      insights.push({
        title: "Качели по калориям",
        text: "Есть дни с заметно разной калорийностью — посмотри дни-пики.",
      });
    } else {
      insights.push({
        title: "Калории довольно ровные",
        text: "Разброс по калориям умеренный — это помогает стабильности.",
      });
    }

    if (summary.avg.water < 1.7) {
      insights.push({
        title: "Воды можно больше",
        text: "Среднее потребление воды ниже 1.7 л/день.",
      });
    }

    const sorted = [...series].sort((a, b) => b.kcal - a.kcal);
    const top = sorted.slice(0, Math.min(3, sorted.length));
    if (top.length) {
      insights.push({
        title: "Топ калорийные дни",
text: top.map((x) => `${formatDateDMY(x.date)}: ${x.kcal} ккал`).join(" • "),
      });
    }

    return insights;
  };

  
  const summaryA = useMemo(() => summarize(seriesA), [seriesA]);
  const insightsA = useMemo(() => buildInsights(seriesA, summaryA), [seriesA, summaryA]);

  const moodEmoji = (score) => {
    if (score >= 4.5) return "😁";
    if (score >= 3.5) return "🙂";
    if (score >= 2.5) return "😐";
    return "🙁";
  };

  // зелёная если "растёт в среднем", красная если падает
  const trendArrow = (deltaAvg) => {
    if (deltaAvg > 0.05) return { icon: "↗", cls: "text-emerald-300" };
    if (deltaAvg < -0.05) return { icon: "↘", cls: "text-rose-300" };
    return { icon: "→", cls: "text-white/60" };
  };

  const computeTrend = (series, key) => {
    const n = series?.length || 0;
    if (!n) return { avgFrom: 0, avgTo: 0, deltaAvg: 0 };

    const win = Math.max(2, Math.min(n >= 14 ? 7 : 3, Math.floor(n / 2)));
    const first = series.slice(0, win);
    const last = series.slice(-win);

    const avgOf = (arr) => {
      const s = arr.reduce((a, x) => a + (Number(x?.[key]) || 0), 0);
      return arr.length ? s / arr.length : 0;
    };

    const avgFrom = Math.round(avgOf(first) * 10) / 10;
    const avgTo = Math.round(avgOf(last) * 10) / 10;
    const deltaAvg = Math.round((avgTo - avgFrom) * 10) / 10;

    return { avgFrom, avgTo, deltaAvg };
  };


  return (
    <BaseModal open={open} onClose={onClose}>
<div className="mx-auto w-full max-w-[390px] rounded-3xl border border-white/10 bg-slate-950/90 p-4">
{reportLoading && (
  <div className="absolute inset-0 z-[999] flex items-center justify-center rounded-3xl bg-black/50 backdrop-blur-sm">
    <div className="w-[90%] max-w-[320px] rounded-2xl border border-white/10 bg-slate-950/90 p-4 shadow-xl">
      <div className="flex items-center gap-3">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/25 border-t-white" />
        <div>
          <div className="text-[14px] font-semibold text-white">
            {tr("food.report.loadingTitle")}
          </div>
          <div className="mt-0.5 text-[12px] text-white/70">
            {reportLoadingText}
          </div>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-white/50">
        {tr("food.report.loadingHint")}
      </div>
    </div>
  </div>
)}
<div className="relative flex items-center">
  <button
    type="button"
    onClick={onClose}
    className="inline-flex h-8 items-center gap-1 rounded-full border border-white/15 bg-white/10 px-3 text-[12px] font-semibold text-white/85 active:scale-[0.99]"
  >
    ← {tr("actions.back")}
  </button>

  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-lg font-semibold">
    {tr("food.report.title")}
  </div>

<div className="ml-auto text-xs px-2 py-1 rounded-full bg-slate-900/60 border border-white/10 opacity-80">
  {periodLabel}
</div>
</div>





        {/* вкладки */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTab("overview")}
            className={`h-8 px-3 rounded-full border text-[12px] font-semibold active:scale-[0.99] ${
              tab === "overview"
                ? "bg-white/20 border-white/30 text-white"
                : "bg-white/10 border-white/15 text-white/75"
            }`}
          >
            {tr("food.report.overviewTab")}
          </button>
          <button
            type="button"
            onClick={() => setTab("days")}
            className={`h-8 px-3 rounded-full border text-[12px] font-semibold active:scale-[0.99] ${
              tab === "days"
                ? "bg-white/20 border-white/30 text-white"
                : "bg-white/10 border-white/15 text-white/75"
            }`}
          >
            {tr("food.report.daysTab")}
          </button>

        </div>

        {/* СВОДКА */}
        {tab === "overview" && (
          <div className="mt-3 grid gap-3">
            {/* 1-я строка: Калории + Сон */}
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <div className="text-[12px] text-white/70">
  {tr("food.report.caloriesAndSleep")}
</div>

              <div className="mt-2 grid grid-cols-2 gap-2">
{/* Калории */}
<div className="rounded-xl bg-black/20 p-2">
  {(() => {
    const total7 = Math.round(summaryA.total.kcal || 0);

    const min = summaryA.range.kcal.min;
    const max = summaryA.range.kcal.max;

    const t = computeTrend(seriesA, "kcal");
    const a = trendArrow(t.deltaAvg);

    return (
      <>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] text-white/60">
  {tr("food.report.calories")}
</div>

            <div className="mt-1 flex items-baseline justify-between gap-2">
              <div className="text-[16px] font-bold text-white tabular-nums">{total7}</div>
              <div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
            </div>
          </div>

<div className={`text-[18px] font-bold ${a.cls}`} title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}>
            {a.icon}
          </div>
        </div>

        <div className="mt-2 rounded-xl bg-black/20 p-2">
          <div className="text-[10.5px] text-white/60">
            {tr("food.report.average")}
          </div>
          <div className="text-[13px] font-semibold text-white tabular-nums">
            {summaryA.avg.kcal} {tr("units.kcal")}/{tr("food.report.dayUnit")}
          </div>
          <div className="mt-1 text-[11px] text-white/60 tabular-nums">
            {tr("food.report.range", { min, max })}
          </div>
        </div>
      </>
    );
  })()}
</div>


{/* Сон */}
<div className="rounded-xl bg-black/20 p-2">
  {(() => {
    const total7 = Math.round((summaryA.total.sleepHours || 0) * 10) / 10;


    const t = computeTrend(seriesA, "sleepHours");
    const ar = trendArrow(t.deltaAvg);

    return (
      <>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] text-white/60">
  {tr("food.report.sleep")}
</div>

            <div className="mt-1 flex items-baseline justify-between gap-2">
<div className="text-[16px] font-bold text-white tabular-nums">
  {total7} {tr("units.hoursShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
            </div>
          </div>

          <div
            className={`text-[18px] font-bold ${ar.cls}`}
            title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}
          >
            {ar.icon}
          </div>
        </div>

        <div className="mt-2 rounded-xl bg-black/20 p-2">
          <div className="text-[10.5px] text-white/60">
            {tr("food.report.average")}
          </div>
          <div className="text-[13px] font-semibold text-white tabular-nums">
            {summaryA.avg.sleepHours} {tr("units.hoursShort")}/{tr("food.report.dayUnit")}
          </div>
          <div className="mt-1 text-[11px] text-white/60 tabular-nums">
            {tr("food.report.range", {
              min: summaryA.range.sleepHours.min,
              max: summaryA.range.sleepHours.max,
            })}
          </div>
        </div>
      </>
    );
  })()}
</div>

              </div>
            </div>

            {/* 2-я строка: Вода + Б/Ж/У */}
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <div className="text-[12px] text-white/70">
  {tr("food.report.periodAverages")}
</div>



              <div className="mt-2 grid grid-cols-2 gap-2">
                {/* Вода */}
<div className="rounded-xl bg-black/20 p-2">
  {(() => {
    const total7 = Math.round((summaryA.total.water || 0) * 10) / 10;

    const min = summaryA.range.water.min;
    const max = summaryA.range.water.max;
    const t = computeTrend(seriesA, "water");
    const a = trendArrow(t.deltaAvg);

    return (
      <>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] text-white/60">
  {tr("food.report.water")}
</div>

            <div className="mt-1 flex items-baseline justify-between gap-2">
<div className="text-[16px] font-bold text-white tabular-nums">
  {total7} {tr("units.litersShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
            </div>
          </div>

          <div className={`text-[18px] font-bold ${a.cls}`} title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}>
            {a.icon}
          </div>
        </div>

<div className="mt-2 rounded-xl bg-black/20 p-2">
          <div className="text-[10.5px] text-white/60">
            {tr("food.report.average")}
          </div>

          <div className="text-[13px] font-semibold text-white tabular-nums">
            {summaryA.avg.water} {tr("units.litersShort")}/{tr("food.report.dayUnit")}
          </div>
          <div className="mt-1 text-[11px] text-white/60 tabular-nums">
            {tr("food.report.range", { min, max })}
          </div>
        </div>
      </>
    );
  })()}
</div>


                {/* Белки */}
                <div className="rounded-xl bg-black/20 p-2">
                  {(() => {
                    const total7 = Math.round(summaryA.total.protein || 0);
const min = summaryA.range.protein.min;
const max = summaryA.range.protein.max;

                    const t = computeTrend(seriesA, "protein");
                    const a = trendArrow(t.deltaAvg);

                    return (
                      <>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-[11px] text-white/60">
  {tr("food.report.protein")}
</div>
                            <div className="mt-1 flex items-baseline justify-between gap-2">
<div className="text-[16px] font-bold text-white tabular-nums">
  {total7} {tr("units.gramsShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
</div>

                          </div>
                          <div className={`text-[18px] font-bold ${a.cls}`} title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}>
                            {a.icon}
                          </div>
                        </div>

<div className="mt-2 rounded-xl bg-black/20 p-2">
                          <div>
<div className="text-[10.5px] text-white/60">
  {tr("food.report.average")}
</div>

<div className="text-[13px] font-semibold text-white tabular-nums">
  {summaryA.avg.protein} {tr("units.gramsShort")}/{tr("food.report.dayUnit")}
</div>
<div className="mt-1 text-[11px] text-white/60 tabular-nums">
  {tr("food.report.range", { min, max })}
</div>

                          </div>

                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Жиры */}
                <div className="rounded-xl bg-black/20 p-2">
                  {(() => {
                    const total7 = Math.round(summaryA.total.fat || 0);
const min = summaryA.range.fat.min;
const max = summaryA.range.fat.max;

                    const t = computeTrend(seriesA, "fat");
                    const a = trendArrow(t.deltaAvg);

                    return (
                      <>
                        <div className="flex items-start justify-between">
                          <div>
                          <div className="text-[11px] text-white/60">
  {tr("food.report.fat")}
</div>
                            <div className="mt-1 flex items-baseline justify-between gap-2">
<div className="text-[16px] font-bold text-white tabular-nums">
  {total7} {tr("units.gramsShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
</div>

                          </div>
                          <div className={`text-[18px] font-bold ${a.cls}`} title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}>
                            {a.icon}
                          </div>
                        </div>

<div className="mt-2 rounded-xl bg-black/20 p-2">
                          <div>
<div className="text-[10.5px] text-white/60">
  {tr("food.report.average")}
</div>

<div className="text-[13px] font-semibold text-white tabular-nums">
  {summaryA.avg.fat} {tr("units.gramsShort")}/{tr("food.report.dayUnit")}
</div>
<div className="mt-1 text-[11px] text-white/60 tabular-nums">
  {tr("food.report.range", { min, max })}
</div>

                          </div>

                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Углеводы */}
                <div className="rounded-xl bg-black/20 p-2">
                  {(() => {
                    const total7 = Math.round(summaryA.total.carb || 0);
const min = summaryA.range.carb.min;
const max = summaryA.range.carb.max;

                    const t = computeTrend(seriesA, "carb");
                    const a = trendArrow(t.deltaAvg);

                    return (
                      <>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-[11px] text-white/60">
  {tr("food.report.carbs")}
</div>
                            <div className="mt-1 flex items-baseline justify-between gap-2">
<div className="text-[16px] font-bold text-white tabular-nums">
  {total7} {tr("units.gramsShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.total")}
</div>
</div>

                          </div>
                          <div className={`text-[18px] font-bold ${a.cls}`} title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}>
                            {a.icon}
                          </div>
                        </div>

<div className="mt-2 rounded-xl bg-black/20 p-2">
                          <div>
<div className="text-[10.5px] text-white/60">
  {tr("food.report.average")}
</div>

<div className="text-[13px] font-semibold text-white tabular-nums">
  {summaryA.avg.carb} {tr("units.gramsShort")}/{tr("food.report.dayUnit")}
</div>
<div className="mt-1 text-[11px] text-white/60 tabular-nums">
  {tr("food.report.range", { min, max })}
</div>

                          </div>

                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* 3-я строка: Вес + Настроение */}
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <div className="text-[12px] text-white/70">
  {tr("food.report.weightAndMood")}
</div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                {/* Вес */}
                <div className="rounded-xl bg-black/20 p-2">
                  <div className="flex items-start justify-between">
                    <div className="text-[11px] text-white/60">
  {tr("food.report.weight")}
</div>
                    {(() => {
                      const t = computeTrend(seriesA, "weight");
                      const a = trendArrow(t.deltaAvg);
                      return (
                        <div
                          className={`text-[18px] font-bold ${a.cls}`}
                          title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}
                        >
                          {a.icon}
                        </div>
                      );
                    })()}
                  </div>

                  {(() => {
                    const now = summaryA.weightToday || 0;

                    return (
                      <div className="mt-1 flex items-baseline gap-2">

<div className="text-[16px] font-bold text-white tabular-nums">
  {now} {tr("units.kilogramsShort")}
</div>
<div className="text-[10.5px] text-white/55">
  {tr("food.report.today")}
</div>
                      </div>
                    );
                  })()}

                  <div className="mt-2 rounded-xl bg-black/20 p-2">

                    <div className="text-[10.5px] text-white/60">
                      {tr("food.report.average")}
                    </div>
                    <div className="text-[13px] font-semibold text-white tabular-nums">
                      {summaryA.avg.weight} {tr("units.kilogramsShort")}
                    </div>
                    <div className="mt-1 text-[11px] text-white/60 tabular-nums">
                      {tr("food.report.range", {
                        min: summaryA.range.weight.min,
                        max: summaryA.range.weight.max,
                      })}
                    </div>

                    {(() => {
const start = (() => {
  for (let i = 0; i < (seriesA?.length || 0); i++) {
    const v = Number(seriesA[i]?.weight);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return 0;
})();

const end = (() => {
  for (let i = (seriesA?.length || 0) - 1; i >= 0; i--) {
    const v = Number(seriesA[i]?.weight);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return 0;
})();

if (!start || !end) {
  return (
    <div className="mt-1 text-[11px] text-white/60 tabular-nums">
      {tr("food.report.noData")}
    </div>
  );
}

const delta = Math.round((end - start) * 10) / 10;
const abs = Math.abs(delta);

const text =
  abs < 0.05
    ? tr("food.report.noChanges")
    : delta < 0
    ? tr("food.report.lostWeight", {
        value: abs,
      })
    : tr("food.report.gainedWeight", {
        value: abs,
      });

                      const cls =
                        abs < 0.05 ? "text-white/60" : delta < 0 ? "text-emerald-300" : "text-rose-300";

                      return (
                        <div className={`mt-1 text-[11px] ${cls} tabular-nums`}>
                                                    {tr("food.report.result")} {text}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Настроение */}
                <div className="rounded-xl bg-black/20 p-2">
                  <div className="flex items-start justify-between">
                    <div className="text-[11px] text-white/60">
  {tr("food.report.mood")}
</div>
                    {(() => {
                      const t = computeTrend(seriesA, "moodScore");
                      const a = trendArrow(t.deltaAvg);
                      return (
                        <div
                          className={`text-[18px] font-bold ${a.cls}`}
                          title={tr("food.report.trendTitle", {
  from: t.avgFrom,
  to: t.avgTo,
})}
                        >
                          {a.icon}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="mt-1 flex items-baseline gap-2">

                    <div className="flex items-center gap-2">
                      <div className="text-[16px] font-bold text-white tabular-nums">
                        {summaryA.avg.moodScore}
                      </div>
                      <div className="text-[20px]">{moodEmoji(summaryA.avg.moodScore)}</div>
                    </div>
                    <div className="text-[10.5px] text-white/55">
  {tr("food.report.averageShort")}
</div>
                  </div>

                  <div className="mt-2 rounded-xl bg-black/20 p-2">

                    <div className="text-[10.5px] text-white/60">
  {tr("food.report.average")}
</div>
                    <div className="text-[13px] font-semibold text-white tabular-nums">
                      {summaryA.avg.moodScore}
                    </div>

                    {(() => {
                      const min = Math.min(...seriesA.map((x) => x.moodScore));
                      const max = Math.max(...seriesA.map((x) => x.moodScore));

                      const start = seriesA?.[0]?.moodScore ?? 0;
                      const end = seriesA?.[seriesA.length - 1]?.moodScore ?? 0;
                      const delta = Math.round((end - start) * 10) / 10;
                      const abs = Math.abs(delta);

const text =
  abs < 0.05
    ? tr("food.report.noChanges")
    : delta > 0
    ? tr("food.report.betterMood", {
        value: abs,
      })
    : tr("food.report.worseMood", {
        value: abs,
      });

                      const cls =
                        abs < 0.05 ? "text-white/60" : delta > 0 ? "text-emerald-300" : "text-rose-300";

                      return (
                        <>
                          <div className="mt-1 text-[11px] text-white/60 tabular-nums">
                                                        {tr("food.report.range", { min, max })}
                          </div>
                          <div className={`mt-1 text-[11px] ${cls} tabular-nums`}>
                                                        {tr("food.report.result")} {text}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

       </div>
)}


        {/* ПО ДНЯМ */}
        {tab === "days" && (
          <div className="mt-3 min-h-0">
            <div className="text-[12px] text-white/70">
  {tr("food.report.daysList")}
</div>
            <div
              data-modal-scroll-root="1"
              className="mt-2 overflow-y-auto overflow-x-hidden rounded-2xl border border-white/15 bg-white/10"
              style={{
                height: "min(520px, calc(var(--app-height, 100vh) - 220px))",
                maxHeight: "calc(var(--app-height, 100vh) - 220px)",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "contain",
                touchAction: "pan-y",
              }}
            >
              <div className="divide-y divide-white/10">
                {seriesA
                  .slice()
                  .reverse()
                  .map((d) => (
                    <div key={d.date} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="text-[12.5px] font-semibold text-white/90 tabular-nums">{formatDateDMY(d.date)}</div>

                        <div className="text-[12.5px] font-semibold text-white tabular-nums">
  {d.kcal} {tr("units.kcal")}
</div>
                      </div>

<div className="mt-1 grid grid-cols-3 gap-2 text-[10.5px] text-white/70">
  <div className="rounded-xl bg-black/15 px-2 py-1">
    {tr("food.report.proteinShort")}: {d.protein}{tr("units.gramsShort")}
  </div>
  <div className="rounded-xl bg-black/15 px-2 py-1">
    {tr("food.report.fatShort")}: {d.fat}{tr("units.gramsShort")}
  </div>
  <div className="rounded-xl bg-black/15 px-2 py-1">
    {tr("food.report.carbsShort")}: {d.carb}{tr("units.gramsShort")}
  </div>
</div>

<div className="mt-1 grid grid-cols-3 gap-2 text-[10.5px] text-white/70">
  <div className="rounded-xl bg-black/15 px-2 py-1">
    💧 {d.water}{tr("units.litersShort")}
  </div>
  <div className="rounded-xl bg-black/15 px-2 py-1">
    ⚖️ {d.weight}{tr("units.kilogramsShort")}
  </div>
  <div className="rounded-xl bg-black/15 px-2 py-1">
    😴 {d.sleepHours}{tr("units.hoursShort")}
  </div>
</div>

<div className="mt-1 text-[10px] text-white/60">
  {tr("food.report.mood")}: {moodEmoji(d.moodScore)} ({d.moodScore})
</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        


        <button
          type="button"
          onClick={onClose}
          className="mt-4 h-11 w-full rounded-2xl border border-white/20 bg-slate-900/40 text-slate-200"
        >
          {tr("food.report.done")}
        </button>
      </div>
    </BaseModal>
  );
}

function PremiumStarsPage({
  open,
  onClose,
  onPaid,
  userAccess,
  onStartOnboarding,
}) {
const { t: tr, i18n } = useTranslation();

const currentLanguage =
  i18n.resolvedLanguage === "ru"
    ? "ru"
    : "en";

useFitmeupBottomNavHidden(open);

  const [selectedPlanId, setSelectedPlanId] = useState("month");

  if (!open || typeof document === "undefined") return null;

  const TRIBUTE_STARS_URL = "https://t.me/tribute/app?startapp=plsg";

  // Потом сюда вставишь свою ссылку на поддержку
  const SUPPORT_URL = "https://t.me/FitMeUp_support";

const plans = [
  {
    id: "month",
    title: tr("premium.monthTitle"),
    price: "149",
    period: tr("premium.monthPeriod"),
    note: tr("premium.monthNote"),
    badge: "",
  },
  {
    id: "quarter",
    title: tr("premium.quarterTitle"),
    price: "379",
    period: tr("premium.quarterPeriod"),
    note: tr("premium.quarterNote"),
    badge: "-15%",
  },
  {
    id: "year",
    title: tr("premium.yearTitle"),
    price: "1 390",
    period: tr("premium.yearPeriod"),
    note: tr("premium.yearNote"),
    badge: "-22%",
  },
];
  const selectedPlan =
    plans.find((plan) => plan.id === selectedPlanId) || plans[0];
const premiumUntilValue = userAccess?.premiumUntil || null;
const trialUntilValue = userAccess?.trialUntil || null;

const formatAccessDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) return "";

  const locale =
    i18n.resolvedLanguage === "ru"
      ? "ru-RU"
      : "en-GB";

  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return date.toLocaleDateString(locale);
  }
};

const premiumUntilText = formatAccessDate(premiumUntilValue);
const trialUntilText = formatAccessDate(trialUntilValue);

const premiumIsActive = Boolean(userAccess?.premiumActive);
const trialIsActive = Boolean(userAccess?.trialActive);
  const openTelegramUrl = (url) => {
    try {
      if (!url) {
        safeAlert("Сюда позже добавим ссылку.", "Подписка и связь с нами");
        return;
      }

      const tg = getTg();

      if (tg?.openTelegramLink && String(url).startsWith("https://t.me/")) {
        tg.openTelegramLink(url);
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      try {
        window.location.href = url;
      } catch {}
    }
  };

  const payPremium = async () => {
    try {
      const tg = getTg();



      const res = await tgFetch("/api/create-stars-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: selectedPlan.id,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok || !json?.invoiceUrl) {
        safeAlert(
          json?.error || "Сервер оплаты пока не настроен. Мы подключим его следующим шагом.",
          "Оплата недоступна"
        );
        return;
      }


      if (!tg?.initData || !tg?.openInvoice) {
        window.location.assign(json.invoiceUrl);
        return;
      }

      tg.openInvoice(json.invoiceUrl, (status) => {

        if (status === "paid") {
          safeAlert(
            "Оплата прошла. Premium включится автоматически через несколько секунд.",
            "Спасибо!"
          );

          window.setTimeout(() => onPaid?.(), 1200);
          window.setTimeout(() => onPaid?.(), 5000);

          return;
        }

        if (status === "cancelled") {
          safeAlert("Оплата отменена.", "Telegram Stars");
          return;
        }

        if (status === "failed") {
          safeAlert("Оплата не прошла. Попробуй ещё раз.", "Ошибка оплаты");
          return;
        }

        safeAlert(
          "Оплата обрабатывается. Если Premium не включился сразу, перезапусти приложение через несколько секунд.",
          "Telegram Stars"
        );
      });
    } catch (e) {
      console.error("payPremium error:", e);
      safeAlert(
        "Не удалось открыть оплату. Проверь интернет и попробуй ещё раз.",
        "Ошибка оплаты"
      );
    }
  };

  const CheckIcon = () => (
    <svg
      viewBox="0 0 24 24"
      className="h-[17px] w-[17px]"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m7.3 12.4 3.15 3.15 6.4-7"
        stroke="currentColor"
        strokeWidth="2.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const HelpIcon = () => (
    <svg
      viewBox="0 0 28 28"
      className="h-[31px] w-[31px]"
      fill="none"
      aria-hidden="true"
      style={{
        color: "#35F3DF",
        filter: "drop-shadow(0 0 10px rgba(53,243,223,0.36))",
      }}
    >
      <circle
        cx="14"
        cy="14"
        r="10.1"
        stroke="currentColor"
        strokeWidth="2.2"
      />
      <path
        d="M10.9 11.25c.45-1.55 1.75-2.55 3.4-2.55 1.95 0 3.35 1.25 3.35 3.05 0 1.55-.82 2.28-2.05 3.05-.96.6-1.32 1.1-1.32 2.02v.38"
        stroke="currentColor"
        strokeWidth="2.05"
        strokeLinecap="round"
      />
      <path
        d="M14.25 20.9h.01"
        stroke="currentColor"
        strokeWidth="3.1"
        strokeLinecap="round"
      />
    </svg>
  );

  const PlanCard = ({ plan }) => {
    const active = selectedPlanId === plan.id;

    return (
      <button
        type="button"
        onClick={() => setSelectedPlanId(plan.id)}
        className="relative flex w-full items-center text-left transition active:scale-[0.987]"
        style={{
          minHeight: 88,
          borderRadius: 22,
          border: active
            ? "1.2px solid rgba(53,243,223,0.86)"
            : "1px solid rgba(148,163,184,0.20)",
          background: active
            ? "radial-gradient(circle at 10% 18%, rgba(53,243,223,0.115), transparent 39%), linear-gradient(180deg, rgba(8,29,46,0.84), rgba(4,16,29,0.88))"
            : "linear-gradient(180deg, rgba(13,29,45,0.70), rgba(4,15,27,0.79))",
          boxShadow: active
            ? "0 0 0 1px rgba(53,243,223,0.055), 0 14px 42px rgba(0,0,0,0.31), 0 0 14px rgba(53,243,223,0.10), inset 0 1px 0 rgba(255,255,255,0.07)"
            : "0 12px 34px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)",
          padding: "14px 15px",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
        }}
      >
        <div className="min-w-0 flex-1 pr-3">
          <div className="flex items-center justify-between gap-3">
            <span
              className="font-black text-white"
              style={{
                fontSize: 20,
                lineHeight: 1,
                letterSpacing: "-0.045em",
              }}
            >
              {plan.title}
            </span>

            {plan.badge ? (
              <span
                style={{
                  height: 22,
                  padding: "0 9px",
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(53,243,223,0.12)",
                  color: "#35F3DF",
                  fontSize: 12.5,
                  lineHeight: 1,
                  fontWeight: 850,
                  letterSpacing: "-0.03em",
                }}
              >
                {plan.badge}
              </span>
            ) : null}
          </div>

          <div className="mt-3 flex items-end gap-2 whitespace-nowrap">
            <span
              className="font-black text-white"
              style={{
                fontSize: "clamp(35px, 9.2vw, 45px)",
                lineHeight: 0.9,
                letterSpacing: "-0.065em",
                textShadow: "0 10px 18px rgba(0,0,0,0.38)",
              }}
            >
              {plan.price}
            </span>

            <span
              style={{
                fontSize: "clamp(25px, 6.5vw, 31px)",
                lineHeight: 0.92,
                filter: "drop-shadow(0 5px 10px rgba(245,158,11,0.30))",
              }}
            >
              ⭐
            </span>

            <span
              style={{
                paddingBottom: 4,
                fontSize: 16.5,
                lineHeight: 1,
                fontWeight: 820,
                color: "rgba(226,232,240,0.76)",
                letterSpacing: "-0.045em",
              }}
            >
              {plan.period}
            </span>
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 12.6,
              lineHeight: 1.16,
              fontWeight: 630,
              color: "rgba(203,213,225,0.69)",
              letterSpacing: "-0.025em",
            }}
          >
            {plan.note}
          </div>
        </div>

        <span
          className="grid shrink-0 place-items-center"
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            border: active
              ? "2px solid rgba(53,243,223,0.95)"
              : "2px solid rgba(148,163,184,0.62)",
            background: active ? "#35F3DF" : "transparent",
            color: active ? "#04111D" : "transparent",
            boxShadow: active ? "0 0 15px rgba(53,243,223,0.40)" : "none",
          }}
        >
          {active ? <CheckIcon /> : null}
        </span>
      </button>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 overflow-hidden"
      style={{
        zIndex: 50000,
        background:
          "radial-gradient(circle at 0% -8%, rgba(53,243,223,0.13), transparent 33%), radial-gradient(circle at 100% 18%, rgba(14,165,233,0.10), transparent 34%), linear-gradient(180deg, #020814 0%, #03111E 46%, #020814 100%)",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', Inter, system-ui, sans-serif",
      }}
    >
      <div
        className="absolute inset-x-0 bottom-0 overflow-y-auto overflow-x-hidden"
        style={{
          top: hasTgAuth()
            ? 0
            : "env(safe-area-inset-top, 0px)",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        <div
          className="mx-auto min-h-full"
          style={{
            width: "min(100vw, 430px)",
            padding: hasTgAuth()
              ? "calc(24px + env(safe-area-inset-top, 0px)) 18px calc(24px + env(safe-area-inset-bottom, 0px))"
              : "24px 18px calc(24px + env(safe-area-inset-bottom, 0px))",
          }}
        >
        <div
          className="flex items-center justify-between gap-4"
          style={{ marginTop: 4 }}
        >
          <div className="min-w-0">
            <div
              className="font-black text-white"
              style={{
                fontSize: 34,
                lineHeight: 0.98,
                letterSpacing: "-0.068em",
                textShadow: "0 10px 20px rgba(0,0,0,0.40)",
              }}
            >
              {tr("premium.title")}
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 15.5,
                lineHeight: 1.1,
                fontWeight: 650,
                letterSpacing: "-0.025em",
                color: "rgba(203,213,225,0.72)",
              }}
            >
              {tr("premium.subtitle")}
            </div>
          </div>

          <AppCloseButton
            onClick={onClose}
            aria-label={tr("actions.close")}
            style={{
              width: 40,
              height: 40,
              fontSize: 20,
            }}
          />
        </div>
        {premiumIsActive ? (
          <div
            style={{
              marginTop: 22,
              position: "relative",
              overflow: "hidden",
              minHeight: 84,
              borderRadius: 24,
              border: "1px solid rgba(53,243,223,0.46)",
              background:
                "radial-gradient(circle at 8% 50%, rgba(53,243,223,0.14), transparent 30%), radial-gradient(circle at 92% -35%, rgba(34,211,238,0.09), transparent 42%), linear-gradient(180deg, rgba(4,23,33,0.92), rgba(3,11,20,0.98))",
              boxShadow:
                "0 16px 38px rgba(0,0,0,0.32), 0 0 20px rgba(53,243,223,0.08), inset 0 1px 0 rgba(255,255,255,0.055)",
              padding: "12px 14px",
              backdropFilter: "blur(22px)",
              WebkitBackdropFilter: "blur(22px)",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "linear-gradient(110deg, rgba(53,243,223,0.045) 0%, transparent 34%, transparent 55%, rgba(53,243,223,0.05) 56%, transparent 82%)",
              }}
            />

            <div
              style={{
                position: "absolute",
                right: 44,
                top: -105,
                width: 310,
                height: 310,
                pointerEvents: "none",
                borderRadius: 999,
                border: "1px solid rgba(53,243,223,0.045)",
              }}
            />

            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                minHeight: 60,
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 15,
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                  background:
                    "linear-gradient(180deg, rgba(6,45,55,0.60), rgba(4,18,28,0.88))",
                  border: "1px solid rgba(53,243,223,0.52)",
                  boxShadow:
                    "0 0 18px rgba(53,243,223,0.16), inset 0 1px 0 rgba(255,255,255,0.08)",
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    lineHeight: 1,
                    fontWeight: 950,
                    color: "#35F3DF",
                    textShadow: "0 0 14px rgba(53,243,223,0.52)",
                    transform: "translateY(-1px)",
                  }}
                >
                  ✓
                </div>
              </div>

              <div
                style={{
                  minWidth: 0,
                  flex: 1,
                  paddingRight: 94,
                }}
              >
                <div
                  style={{
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: 17,
                      lineHeight: 1.05,
                      fontWeight: 950,
                      letterSpacing: "-0.05em",
                      color: "#F8FAFC",
                      textShadow: "0 7px 16px rgba(0,0,0,0.32)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tr("premium.activeTitle")}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 16,
                      lineHeight: 1.08,
                      fontWeight: 900,
                      letterSpacing: "-0.045em",
                      color: "#35F3DF",
                      textShadow: "0 0 14px rgba(53,243,223,0.22)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tr("premium.activeUntil", {
                      date: premiumUntilText,
                    })}
                  </div>
                </div>
              </div>

              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: 34,
                  padding: "0 13px",
                  borderRadius: 15,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(180deg, rgba(53,243,223,0.14), rgba(53,243,223,0.055))",
                  border: "1px solid rgba(53,243,223,0.24)",
                  boxShadow:
                    "0 0 14px rgba(53,243,223,0.10), inset 0 1px 0 rgba(255,255,255,0.06)",
                  color: "#35F3DF",
                  fontSize: 13.5,
                  lineHeight: 1,
                  fontWeight: 950,
                  letterSpacing: "-0.025em",
                  textShadow: "0 0 10px rgba(53,243,223,0.22)",
                }}
              >
                {tr("premium.activeBadge")}
              </div>
            </div>
          </div>
        ) : null}
        <div className="grid" style={{ marginTop: 28, gap: 10 }}>
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        <button
          type="button"
          onClick={payPremium}
          className="relative flex w-full items-center justify-center active:scale-[0.987]"
          style={{
            marginTop: 16,
            height: 62,
            borderRadius: 19,
border: "1px solid rgba(53,243,223,0.56)",
outline: "none",
appearance: "none",
WebkitAppearance: "none",
background:
  "linear-gradient(180deg, rgba(13,33,48,0.96), rgba(4,14,26,0.98))",
boxShadow:
  "0 18px 44px rgba(0,0,0,0.40), 0 0 0 1px rgba(53,243,223,0.08), 0 0 18px rgba(53,243,223,0.22), 0 0 42px rgba(53,243,223,0.16), inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -1px 0 rgba(53,243,223,0.16)",
color: "#F8FAFC",
            fontSize: 19,
            lineHeight: 1,
            fontWeight: 920,
            letterSpacing: "-0.05em",
            overflow: "hidden",
            transform: "translateZ(0)",
          }}
        >
          <span style={{ position: "relative", zIndex: 2 }}>
            {tr("premium.pay", {
              price: selectedPlan.price,
              period: selectedPlan.period,
            })}
          </span>

          <span
            className="absolute"
            style={{
              right: 18,
              top: "50%",
              zIndex: 2,
              transform: "translateY(-53%)",
              color: "rgba(53,243,223,0.72)",
              fontSize: 31,
              fontWeight: 360,
              lineHeight: 1,
            }}
          >
            ›
          </span>
        </button>

<button
  type="button"
  onClick={() => openTelegramUrl(TRIBUTE_STARS_URL)}
  className="flex w-full items-center text-left active:scale-[0.987]"
  style={{
    marginTop: 13,
    height: 54,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.15)",
    background:
      "linear-gradient(180deg, rgba(7,22,38,0.64), rgba(4,13,25,0.66))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    padding: "0 18px",
  }}
>
  <span
    className="min-w-0 flex-1"
    style={{
      fontSize: 17,
      lineHeight: 1,
      fontWeight: 780,
      letterSpacing: "-0.035em",
      color: "rgba(248,250,252,0.92)",
    }}
  >
    {tr("premium.buyStars")}
  </span>

  <span
    style={{
      fontSize: 30,
      lineHeight: 1,
      color: "rgba(148,163,184,0.56)",
      fontWeight: 300,
    }}
  >
    ›
  </span>
</button>

<div
  style={{
    marginTop: 18,
  }}
>
  <div
    style={{
      marginBottom: 9,
      fontSize: 14,
      lineHeight: 1,
      fontWeight: 750,
      letterSpacing: "-0.025em",
      color: "rgba(203,213,225,0.72)",
    }}
  >
    Language
  </div>

  <div className="grid h-11 grid-cols-2 gap-1 rounded-[18px] border border-cyan-300/50 bg-slate-950/24 p-1 shadow-[0_0_12px_rgba(45,230,230,0.04)]">
    <button
      type="button"
      onClick={() => {
        setAppLanguage("ru");

        void tgFetch("/api/auth", {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
body: JSON.stringify({
  appLanguage: "ru",
  appLanguageSource: "manual",
}),
        }).catch((e) => {
          console.error(
            "[language] sync ru error:",
            e
          );
        });
      }}
      className={
        "h-full rounded-[14px] px-4 text-[13px] font-semibold transition active:scale-[0.99] " +
        (currentLanguage === "ru"
          ? "border border-cyan-200/70 bg-cyan-400/[0.16] text-white shadow-[0_0_18px_rgba(45,230,230,0.18),inset_0_1px_0_rgba(255,255,255,0.12)]"
          : "border border-transparent bg-transparent text-white/70")
      }
    >
      Русский
    </button>

    <button
      type="button"
      onClick={() => {
        setAppLanguage("en");

        void tgFetch("/api/auth", {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
body: JSON.stringify({
  appLanguage: "en",
  appLanguageSource: "manual",
}),
        }).catch((e) => {
          console.error(
            "[language] sync en error:",
            e
          );
        });
      }}
      className={
        "h-full rounded-[14px] px-4 text-[13px] font-semibold transition active:scale-[0.99] " +
        (currentLanguage === "en"
          ? "border border-cyan-200/70 bg-cyan-400/[0.16] text-white shadow-[0_0_18px_rgba(45,230,230,0.18),inset_0_1px_0_rgba(255,255,255,0.12)]"
          : "border border-transparent bg-transparent text-white/70")
      }
    >
      English
    </button>
  </div>
</div>

<button
  type="button"
  onClick={() => openTelegramUrl(SUPPORT_URL)}
  className="flex w-full items-center text-left active:scale-[0.987]"
  style={{
    marginTop: 11,
    minHeight: 64,
    gap: 13,
    borderRadius: 19,
    border: "1px solid rgba(148,163,184,0.18)",
    background:
      "radial-gradient(circle at 0% 0%, rgba(24,215,189,0.08), transparent 44%), linear-gradient(180deg, rgba(8,23,38,0.84), rgba(4,13,25,0.92))",
    boxShadow:
      "0 14px 38px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.055)",
    padding: "0 16px",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    appearance: "none",
    WebkitAppearance: "none",
  }}
>
  <span
    className="grid shrink-0 place-items-center"
    style={{
      width: 40,
      height: 40,
      borderRadius: 14,
      border: "1px solid rgba(53,243,223,0.28)",
      background: "rgba(53,243,223,0.075)",
      boxShadow:
        "0 0 14px rgba(53,243,223,0.08), inset 0 1px 0 rgba(255,255,255,0.05)",
    }}
  >
    <svg
      viewBox="0 0 32 32"
      className="h-[25px] w-[25px]"
      fill="none"
      aria-hidden="true"
      style={{
        color: "#35F3DF",
        filter: "drop-shadow(0 0 8px rgba(53,243,223,0.28))",
      }}
    >
      <path
        d="M8.4 8.6h15.2a4.2 4.2 0 0 1 4.2 4.2v6.9a4.2 4.2 0 0 1-4.2 4.2h-7.4l-5.1 3.05c-.72.43-1.62-.08-1.62-.92v-2.18A4.2 4.2 0 0 1 4.2 19.7v-6.9a4.2 4.2 0 0 1 4.2-4.2Z"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinejoin="round"
      />
      <path
        d="M10.4 14.1h11.2M10.4 18.1h7.4"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
      />
    </svg>
  </span>

  <span className="min-w-0 flex-1">
    <span
      className="block"
      style={{
        fontSize: 17.5,
        lineHeight: 1,
        fontWeight: 850,
        letterSpacing: "-0.04em",
        color: "rgba(248,250,252,0.94)",
      }}
    >
      {tr("premium.supportTitle")}
    </span>

    <span
      className="mt-1.5 block"
      style={{
        fontSize: 13,
        lineHeight: 1.1,
        fontWeight: 650,
        letterSpacing: "-0.025em",
        color: "rgba(203,213,225,0.62)",
      }}
    >
      {tr("premium.supportSubtitle")}
    </span>
  </span>

  <span
    style={{
      fontSize: 28,
      lineHeight: 1,
      color: "rgba(148,163,184,0.56)",
      fontWeight: 350,
    }}
  >
    ›
  </span>
</button>
<button
  type="button"
  onClick={() =>
    onStartOnboarding?.()
  }
  className="relative flex w-full items-center text-left transition active:scale-[0.987]"
  style={{
    marginTop: 11,
    minHeight: 64,
    gap: 13,

    borderRadius: 19,

    border:
      "1px solid rgba(53,243,223,0.30)",

    background:
      "radial-gradient(circle at 0% 0%, rgba(53,243,223,0.12), transparent 45%), linear-gradient(180deg, rgba(7,28,42,0.92), rgba(3,14,25,0.96))",

    boxShadow:
      "0 14px 38px rgba(0,0,0,0.30), 0 0 20px rgba(53,243,223,0.07), inset 0 1px 0 rgba(255,255,255,0.06)",

    padding: "0 16px",

    backdropFilter:
      "blur(20px)",

    WebkitBackdropFilter:
      "blur(20px)",
  }}
>
  <span
    className="grid shrink-0 place-items-center"
    style={{
      width: 40,
      height: 40,

      borderRadius: 14,

      border:
        "1px solid rgba(53,243,223,0.38)",

      background:
        "rgba(53,243,223,0.10)",

      color: "#35F3DF",

      boxShadow:
        "0 0 16px rgba(53,243,223,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
    }}
  >
    <svg
      viewBox="0 0 32 32"
      className="h-[24px] w-[24px]"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="16"
        cy="16"
        r="11"
        stroke="currentColor"
        strokeWidth="2"
      />

      <path
        d="M13.5 11.8 21 16l-7.5 4.2v-8.4Z"
        fill="currentColor"
      />
    </svg>
  </span>

  <span className="min-w-0 flex-1">
    <span
      className="block"
      style={{
        fontSize: 17.5,
        lineHeight: 1,
        fontWeight: 850,

        letterSpacing:
          "-0.04em",

        color:
          "rgba(248,250,252,0.96)",
      }}
    >
      {tr("premium.onboardingTitle")}
    </span>

    <span
      className="mt-1.5 block"
      style={{
        fontSize: 13,
        lineHeight: 1.1,
        fontWeight: 650,

        letterSpacing:
          "-0.025em",

        color:
          "rgba(203,213,225,0.64)",
      }}
    >
      {tr("premium.onboardingSubtitle")}
    </span>
  </span>

  <span
    style={{
      fontSize: 28,
      lineHeight: 1,

      color:
        "rgba(53,243,223,0.62)",

      fontWeight: 350,
    }}
  >
    ›
  </span>
</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function OnboardingOffer({
  open,
  onStart,
  onSkip,
  busy = false,
}) {
  const { t: tr } = useTranslation();

  useFitmeupBottomNavHidden(open);

  if (
    !open ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return (
    <Portal>
      <style>{`
        @keyframes fitmeupOfferIn {
          from {
            opacity: 0;
            transform:
              translateY(-18px)
              scale(0.97);
          }

          to {
            opacity: 1;
            transform:
              translateY(0)
              scale(1);
          }
        }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-offer-title"
        className="fixed inset-0"
        style={{
          zIndex: 140000,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "rgba(2, 8, 23, 0.66)",

            backdropFilter:
              "blur(13px) saturate(125%)",

            WebkitBackdropFilter:
              "blur(13px) saturate(125%)",
          }}
        />

        <div
          className="absolute inset-x-0 top-0 flex justify-center px-4"
          style={{
            paddingTop:
              "calc(22px + env(safe-area-inset-top))",
          }}
        >
          <div
            className="relative w-full max-w-[420px] overflow-hidden"
            style={{
              borderRadius: 28,

              border:
                "1px solid rgba(53, 243, 223, 0.34)",

              background:
                "radial-gradient(circle at 0% 0%, rgba(53,243,223,0.16), transparent 48%), linear-gradient(180deg, rgba(7,28,42,0.97), rgba(3,12,23,0.99))",

              boxShadow:
                "0 24px 80px rgba(0,0,0,0.58), 0 0 34px rgba(53,243,223,0.10), inset 0 1px 0 rgba(255,255,255,0.08)",

              backdropFilter:
                "blur(24px)",

              WebkitBackdropFilter:
                "blur(24px)",

              padding:
                "24px 20px 18px",

              animation:
                "fitmeupOfferIn 280ms cubic-bezier(.2,.8,.2,1) both",
            }}
          >
            <div
              className="pointer-events-none absolute inset-x-8 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(165,243,252,0.65), transparent)",
              }}
            />

            <div
              className="mb-4 inline-flex items-center rounded-full"
              style={{
                minHeight: 28,
                padding: "0 11px",

                border:
                  "1px solid rgba(53,243,223,0.28)",

                background:
                  "rgba(53,243,223,0.09)",

                color:
                  "rgba(165,243,252,0.92)",

                fontSize: 11,
                lineHeight: 1,
                fontWeight: 800,

                letterSpacing:
                  "0.08em",

                textTransform:
                  "uppercase",
              }}
            >
              {tr("onboarding.offer.badge")}
            </div>

            <h2
              id="onboarding-offer-title"
              className="m-0 text-white"
              style={{
                fontSize: 28,
                lineHeight: 1.08,
                fontWeight: 900,

                letterSpacing:
                  "-0.045em",
              }}
            >
              {tr("onboarding.offer.title")}
            </h2>

            <p
              className="m-0"
              style={{
                marginTop: 13,

                color:
                  "rgba(203,213,225,0.76)",

                fontSize: 15,
                lineHeight: 1.48,
                fontWeight: 550,

                letterSpacing:
                  "-0.015em",
              }}
            >
{tr("onboarding.offer.text")}
          
            </p>

            <button
              type="button"
              onClick={onStart}
              className="mt-6 flex w-full items-center justify-center transition active:scale-[0.98]"
              style={{
                minHeight: 52,
                borderRadius: 18,

                border:
                  "1px solid rgba(153,246,228,0.68)",

                background:
                  "linear-gradient(135deg, #35F3DF, #18D7BD)",

                color: "#031018",

                boxShadow:
                  "0 14px 34px rgba(24,215,189,0.24), 0 0 22px rgba(53,243,223,0.16), inset 0 1px 0 rgba(255,255,255,0.48)",

                fontSize: 16,
                fontWeight: 900,

                letterSpacing:
                  "-0.025em",
              }}
            >
              {tr("onboarding.offer.start")}
            </button>

            <button
              type="button"
              onClick={onSkip}
              className="mt-2 flex w-full items-center justify-center transition active:scale-[0.98]"
              style={{
                minHeight: 44,

                border: 0,
                background:
                  "transparent",

                color:
                  "rgba(203,213,225,0.68)",

                fontSize: 14,
                fontWeight: 700,

                letterSpacing:
                  "-0.015em",
              }}
            >
              {tr("onboarding.offer.skip")}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default function TelegramMiniAppPreview() {
  const { t, i18n } = useTranslation();
  
  // id пользователя именно из БД (users.id)
const {
  dbUserId,
  dbUserLoading,
  dbUser,
  userAccess,
  reloadDbUser,
} = useDbUserId();

  // ✅ Дата "дня" по локальному времени пользователя (не UTC)
  const getLocalDayKey = (d = new Date()) => d.toLocaleDateString("en-CA"); // YYYY-MM-DD

  // текущий "день" в приложении
   const [dayKey, setDayKey] = useState(getLocalDayKey());

  // Отдельная дата только для экрана «Рацион».
  // dayKey по-прежнему отвечает за воду, шаги, сон, настроение и добавки.
  const [rationDayKey, setRationDayKey] = useState(getLocalDayKey());
const [
  onboardingOfferOpen,
  setOnboardingOfferOpen,
] = useState(false);

const [
  onboardingOfferSaving,
  setOnboardingOfferSaving,
] = useState(false);

useEffect(() => {
  if (
    dbUserLoading ||
    !dbUserId ||
    !dbUser
  ) {
    return undefined;
  }

  const alreadyDecided =
    Boolean(
      dbUser
        .food_onboarding_choice
    ) ||
    Boolean(
      dbUser
        .food_onboarding_choice_at
    ) ||
    Boolean(
      dbUser
        .food_onboarding_completed_at
    );

  if (alreadyDecided) {
    setOnboardingOfferOpen(
      false
    );

    return undefined;
  }

  const timer =
    window.setTimeout(() => {
      setOnboardingOfferOpen(
        true
      );
    }, 1500);

  return () => {
    window.clearTimeout(timer);
  };
}, [
  dbUserLoading,
  dbUserId,
  dbUser,
]);


  
  // 🔍 кто сейчас пользователь
  useEffect(() => {
    const tg = getTg();
    debugLog('=== TG DEBUG ===');
    debugLog('tg =', tg);
    debugLog('initDataUnsafe =', tg?.initDataUnsafe);
    debugLog('getUid() =', getUid());
  }, []);
async function apiSaveDayStats(
  dbUserId,
  {
    day,
    steps,
    water,
    weight,
    mood,
    sleep,
    startWeight,
    goalWeight,
  }
) {
  const payload = {
    day,
    steps,
    water,
    weight,
    mood,
    sleep,
    startWeight,
    goalWeight,
  };

  debugLog("[dayStats] POST /api/day-stats payload =", payload);

  const res = await tgFetch("/api/day-stats", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = await res.json();

  debugLog("[dayStats] POST response =", json);

  if (!json.ok) {
    console.error("[dayStats] Ошибка сохранения дня:", json.error);
    throw new Error(json.error || "Ошибка сохранения дня");
  }

  return json.data;
}
async function apiLoadWeightSummary() {
  const res = await tgFetch("/api/day-stats?mode=weight-summary", {
    method: "GET",
  });

  const json = await res.json();

  if (!json.ok) {
    throw new Error(json.error || "Ошибка загрузки веса");
  }

  return json.data || null;
}
async function apiSaveSupplementDay(dbUserId, { day, supplementId, taken }) {
  const res = await tgFetch("/api/supplements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      day,
      supplementId,
      taken,
    }),
  });

  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "Ошибка сохранения добавок");
  return json.data;
}
async function apiSaveBodyMeasurement({ metricKey, value, date }) {
  const res = await tgFetch("/api/measurements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "save",
      metricKey,
      value,
      date, // ✅ совпадает с backend
    }),
  });

  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiSaveBodyMeasurement failed");
  return json;
}


// --- supplements (список добавок) в БД ---
async function apiListSupplements() {
  const res = await tgFetch("/api/supplements", { method: "GET" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiListSupplements failed");
  return json.data || [];
}

async function apiCreateSupplement({ name, perDay }) {
  const res = await tgFetch("/api/supplements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, perDay }),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiCreateSupplement failed");
  return json.data; // { id, name, perDay }
}

async function apiDeleteSupplement(id) {
  const res = await tgFetch(`/api/supplements?id=${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiDeleteSupplement failed");
  return json.deleted;
}

// --- supplement-days (сколько выпито за день) из БД ---
async function apiLoadSupplementTaken(day) {
  const res = await tgFetch(`/api/supplements?day=${encodeURIComponent(day)}`, { method: "GET" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiLoadSupplementTaken failed");
  return json.data || {}; // { [supplementId]: taken }
}

// --- body measurements (Замеры) в БД ---
async function apiLoadMeasurements() {
  const res = await tgFetch("/api/measurements", { method: "GET" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiLoadMeasurements failed");
  return json;
}

async function apiToggleBodyMetric(metricKey, active) {
  const res = await tgFetch("/api/measurements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "toggle", metricKey, active }),
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiToggleBodyMetric failed");
  return true;
}

async function apiSaveBodyMetric(metricKey, value, date) {
  const res = await tgFetch("/api/measurements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "save", metricKey, value, date }),
  });

  const json = await res.json();

  if (!json.ok) {
    throw new Error(json.error || "apiSaveBodyMetric failed");
  }

  const row = json.data || {};

  return {
    id: row.id ?? null,
    value: Number(row.value ?? value),
    date: row.measured_at
      ? new Date(row.measured_at).toISOString()
      : date,
  };
}

async function apiDeleteBodyMeasurement(metricKey, measurement) {
  const id = Number(measurement?.id);

  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("measurement id required");
  }

  const res = await tgFetch("/api/measurements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "delete",
      metricKey,
      id,
    }),
  });

  const json = await res.json();

  if (!json.ok) {
    throw new Error(json.error || "apiDeleteBodyMeasurement failed");
  }

  return true;
}


async function apiLoadDayStats(dbUserId, day) {
  const params = new URLSearchParams({ day });
  const url = `/api/day-stats?${params.toString()}`;
  debugLog("[dayStats] GET", url);

  try {
    const res = await tgFetch(url);
    const json = await res.json();
    debugLog("[dayStats] GET response =", json);
    if (!json.ok) {
      console.error("[dayStats] Ошибка загрузки дня:", json.error);
      return null;
    }

    const row = json.data;
    if (!row) return null;

    const data = {
      userId: row.userId ?? row.user_id ?? dbUserId,
      day: row.day,
      steps: row.steps != null ? Number(row.steps) : null,
      water: row.water != null ? Number(row.water) : null,
      weight: row.weight != null ? Number(row.weight) : null,
      mood: row.mood != null ? Number(row.mood) : null,
      sleep: row.sleep != null ? Number(row.sleep) : null,
    };

    debugLog("[dayStats] normalized data =", data);
    return data;
  } catch (e) {
    console.error("[dayStats] FETCH ERROR GET /api/day-stats:", e);
    return null;
  }
}
// --- macro targets (цели КБЖУ) ---
async function apiLoadMacroTargets() {
  const res = await tgFetch("/api/macro-targets", { method: "GET" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiLoadMacroTargets failed");
  return json.data; // { kcal, carbs, proteins, fats }
}

async function apiSaveMacroTargets(targets) {
  const res = await tgFetch("/api/macro-targets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      kcal: targets.kcal,
      carbs: targets.carbs,
      proteins: targets.proteins,
      fats: targets.fats,
    }),
  });

  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiSaveMacroTargets failed");
  return json.data;
}

// --- workouts (Мои тренировки) в БД ---
async function apiListWorkouts() {
  const res = await tgFetch("/api/workouts", { method: "GET" });
  const json = await res.json().catch(() => null);

  if (res.status === 402 || json?.code === "WORKOUT_ACCESS_REQUIRED") {
    return [];
  }

  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || "apiListWorkouts failed");
  }

return (json.data || []).map((r) => {
  const d = r.data || {};
  const durationSeconds = Number(d.durationSeconds || d.duration_seconds || 0) || 0;
  const title = String(r.title || d.title || d.workoutTitle || d.musclesLabel || "Тренировка").trim();

  return {
    id: Number(r.client_id),
    date: d.date || null,
    mode: d.mode || null,
    title,
    workoutTitle: title,
    muscles: d.muscles || [],
    musclesLabel: d.musclesLabel || title || "Тренировка",
    exercises: d.exercises || [],
    cardio: d.cardio || d.cardioItems || [],
    durationSeconds,
    durationText: d.durationText || (durationSeconds ? formatWorkoutDuration(durationSeconds) : ""),
    timer: d.timer || null,
  };
});
}

async function apiUpsertWorkout(workout) {
  const res = await tgFetch("/api/workouts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
body: JSON.stringify({
  client_id: Number(workout.id),
  title: workout.title || workout.workoutTitle || workout.musclesLabel || "Тренировка",
  data: workout,
}),
  });

  const json = await res.json().catch(() => null);

  if (res.status === 402 || json?.code === "WORKOUT_ACCESS_REQUIRED") {
    throw new Error("WORKOUT_ACCESS_REQUIRED");
  }

  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || "apiUpsertWorkout failed");
  }

  return json.data;
}

async function apiDeleteWorkout(clientId) {
  const params = new URLSearchParams({ client_id: String(clientId) });
  const res = await tgFetch(`/api/workouts?${params.toString()}`, { method: "DELETE" });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "apiDeleteWorkout failed");
  return json.deleted;
}
async function apiReplaceCardioForWorkout(workoutClientId, mode, items) {
  const res = await tgFetch("/api/cardio", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      workout_client_id: Number(workoutClientId),
      mode,
      items: items || [],
    }),
  });

  const json = await res.json().catch(() => null);

  if (res.status === 402 || json?.code === "WORKOUT_ACCESS_REQUIRED") {
    throw new Error("WORKOUT_ACCESS_REQUIRED");
  }

  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || "apiReplaceCardioForWorkout failed");
  }

  return json;
}

async function apiListFoodEntries(day) {
  const params = new URLSearchParams({ day });
  const url = `/api/food-entries?${params.toString()}`;
  const res = await tgFetch(url);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "Ошибка загрузки еды");
  return json.data || [];
}
async function apiFoodMonth(from, to) {
  const params = new URLSearchParams({ from, to });
  const res = await tgFetch(`/api/food-month?${params.toString()}`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "Ошибка загрузки календаря еды");
  return json.data || {};
}


  useTelegramInit();
  useTelegramInitSafe();


    const isInputFocused = useInputFocusFlag();
const keyboardInset = useKeyboardInset();
useStopEmptyRubberScroll(true);
const { scale, innerWidth } = useScaleFactor(isInputFocused);
const noBackdropBlur = React.useMemo(() => {
  try {
    const tg = getTg();
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const tgIOS = tg && String(tg.platform || "").toLowerCase() === "ios";
    return Boolean(isIOS || tgIOS);
  } catch {
    return false;
  }
}, []);




const [tab, setTab] = useState("food");

// отдельно для "Активность"
const [activityCalOffset, setActivityCalOffset] = useState(0);

// отдельно для "Календарь еды"
const [foodCalOffset, setFoodCalOffset] = useState(0);

// ===== Календарь еды: суммы по дням месяца (для колец) =====
const [foodMonthMap, setFoodMonthMap] = useState({}); // { "YYYY-MM-DD": {kcal, proteins, fats, carbs} }
const [foodMonthLoading, setFoodMonthLoading] = useState(false);
const [foodMonthReloadTick, setFoodMonthReloadTick] = useState(0);


// ===== Попап "Еда за день" =====
const [foodDayOpen, setFoodDayOpen] = useState(false);
const [foodDayKey, setFoodDayKey] = useState("");
const [foodDayEntries, setFoodDayEntries] = useState([]);
const [foodDayLoading, setFoodDayLoading] = useState(false);
const [foodDaySelectedIds, setFoodDaySelectedIds] = useState(() => new Set());
const [foodDayAddBusy, setFoodDayAddBusy] = useState(false);
const [premiumOpen, setPremiumOpen] = useState(false);
const canUsePremiumFeatures = Boolean(userAccess?.active);

const openPremiumPaywall = (source = "premium") => {
  logEvent("premium:paywall:open", {
    source,
    access: userAccess || null,
  });

  setPremiumOpen(true);
};
async function openFoodDay(dayKey) {
  try {
    setFoodDayLoading(true);
    setFoodDayKey(dayKey);
    setFoodDayOpen(true);
    setFoodDaySelectedIds(new Set());


    const entries = await apiListFoodEntries(dayKey);
    setFoodDayEntries(entries);
  } catch (e) {
    console.error("openFoodDay failed:", e);
    setFoodDayEntries([]);
  } finally {
    setFoodDayLoading(false);
  }
}
function toggleFoodDaySelect(id) {
  setFoodDaySelectedIds((prev) => {
    const next = new Set(prev);
    const key = String(id);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
}

async function addSelectedFoodDayToToday() {
  // ✅ защита от двойного нажатия
  if (foodDayAddBusy) return;

  const selected = Array.from(foodDaySelectedIds || []);
  if (!selected.length) return;

  const todayKey = getLocalDayKey();

  try {
    setFoodDayAddBusy(true);

    for (const id of selected) {
      const entry = (foodDayEntries || []).find((e) => String(e.id) === String(id));
      if (!entry) continue;

      await apiAddFoodEntry({
        day: todayKey,
        mealSlot: String(entry.meal_slot || "other"),
        userFoodId: entry.user_food_id ?? null,
        name: entry.name,
        grams: entry.grams,
        kcal: entry.kcal,
        proteins: entry.proteins,
        fats: entry.fats,
        carbs: entry.carbs,
      });
    }

    // закрываем попап и сбрасываем выбор
    setFoodDayOpen(false);
    setFoodDaySelectedIds(new Set());

    // обновляем основной рацион
    setDayKey(todayKey);
    setRationDayKey(todayKey);
    setFoodReloadTick((x) => x + 1);

    // обновляем маленькие кольца месяца
    try {
      setFoodMonthLoading(true);

      const base = new Date();
      base.setDate(1);
      base.setMonth(base.getMonth() + foodCalOffset);


      const year = base.getFullYear();
      const month = base.getMonth();

      const fromDate = new Date(year, month, 1);
      const toDate = new Date(year, month + 1, 0);

      const from = toDayKey(fromDate);
      const to = toDayKey(toDate);

      const data = await apiFoodMonth(from, to);
      setFoodMonthMap(data);
    } catch (e) {
      console.error("refresh food-month failed:", e);
    } finally {
      setFoodMonthLoading(false);
    }
  } catch (e) {
    console.error("addSelectedFoodDayToToday failed:", e);
  } finally {
    setFoodDayAddBusy(false);
  }
}



const toDayKey = (d) => d.toLocaleDateString("en-CA"); // YYYY-MM-DD

    // экран еды: обзор / детали
  const [foodView, setFoodView] = useState("overview");

  // приёмы пищи и добавленные блюда
  const MEAL_TITLES = ["Завтрак", "Обед", "Ужин", "Перекус"];

  const makeEmptyMeals = () =>
    MEAL_TITLES.map((title, idx) => ({
      id: idx + 1,
      title,
      items: [],
    }));

  const [meals, setMeals] = useState(makeEmptyMeals);
  const [foodReloadTick, setFoodReloadTick] = useState(0);

useEffect(() => {
  if (!dbUserId) return undefined;

  let cancelled = false;

  // Сразу убираем содержимое прошлого выбранного дня,
  // чтобы оно не показывалось под новой датой во время загрузки.
  setMeals(makeEmptyMeals());

  (async () => {
    try {
      const rows = await apiListFoodEntries(rationDayKey);

      // Если пользователь быстро пролистал ещё один день,
      // старый ответ уже не должен попадать на экран.
      if (cancelled) return;

      const grouped = {
        breakfast: [],
        lunch: [],
        dinner: [],
        snack: [],
        other: [],
      };

      for (const r of rows) {
        const slot = String(r.meal_slot || "other");
        (grouped[slot] || grouped.other).push(dbFoodEntryToItem(r));
      }

      setMeals(
        MEAL_TITLES.map((title, idx) => {
          const slot = mealSlotFromTitle(title);

          return {
            id: idx + 1,
            title,
            items: grouped[slot] || [],
          };
        })
      );
    } catch (e) {
      if (cancelled) return;

      console.error("load food entries error:", e);
      setMeals(makeEmptyMeals());
    }
  })();

  return () => {
    cancelled = true;
  };
}, [dbUserId, rationDayKey, foodReloadTick]);

useEffect(() => {
  if (tab !== "food") return;

  (async () => {
    try {
      setFoodMonthLoading(true);

      const base = new Date();
      base.setDate(1);
      base.setMonth(base.getMonth() + foodCalOffset);

      const year = base.getFullYear();
      const month = base.getMonth();

      const fromDate = new Date(year, month, 1);
      const toDate = new Date(year, month + 1, 0);

      const from = toDayKey(fromDate);
      const to = toDayKey(toDate);

      const data = await apiFoodMonth(from, to);
      setFoodMonthMap(data);
    } catch (e) {
      console.error("food-month load failed:", e);
      setFoodMonthMap({});
    } finally {
      setFoodMonthLoading(false);
    }
  })();
}, [tab, foodCalOffset, foodMonthReloadTick]);



  // ✅ итоги за день (КБЖУ + ккал) — нужно для overview и для details
  const totals = useMemo(() => {
    const t = calcAllTotals(meals);
    return {
      proteins: +t.proteins.toFixed(1),
      fats: +t.fats.toFixed(1),
      carbs: +t.carbs.toFixed(1),
      kcal: Math.round(t.kcal),
    };
  }, [meals]);

  // ✅ интро-режим: если ещё ничего не внесли за день
  const macroIntro =
    (totals.carbs || 0) === 0 &&
    (totals.proteins || 0) === 0 &&
    (totals.fats || 0) === 0 &&
    (totals.kcal || 0) === 0;
// ✅ серия (streak): сколько дней подряд добавляли блюда
const [foodStreak, setFoodStreak] = useState(1);

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const toKey = getLocalDayKey();
      const toDate = new Date(`${toKey}T00:00:00`);
      const fromDate = new Date(toDate);
      fromDate.setDate(fromDate.getDate() - 120); // запас, чтобы серия могла тянуться через месяцы
      const fromKey = getLocalDayKey(fromDate);

      const map = await apiFoodMonth(fromKey, toKey);

      const hasMeals = (v) =>
        !!v &&
        ((v.kcal || 0) > 0 ||
          (v.proteins || 0) > 0 ||
          (v.fats || 0) > 0 ||
          (v.carbs || 0) > 0);

      const todayHas = hasMeals(map[toKey]) || (Array.isArray(meals) && meals.length > 0);

      let streak = 1; // по умолчанию всегда "день 1"

      // Если сегодня есть блюда — сегодня считается как 1-й день серии,
      // а дальше добираем только ПРЕДЫДУЩИЕ дни подряд (вчера, позавчера, ...)
      if (todayHas) {
        streak = 1;
        const d = new Date(toDate);
        d.setDate(d.getDate() - 1); // стартуем со вчера

        while (true) {
          const key = getLocalDayKey(d);
          if (!hasMeals(map[key])) break;
          streak += 1;
          d.setDate(d.getDate() - 1);
        }
      }


      if (!cancelled) setFoodStreak(streak);
    } catch (e) {
      console.error("food streak load failed:", e);
      if (!cancelled) setFoodStreak(1);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [foodMonthReloadTick, meals]);

  const [steps, setSteps] = useState(null);
  const [stepsModalOpen, setStepsModalOpen] = useState(false);
  // === Emotions widget state & logic ===
const [selectedEmotion, setSelectedEmotion] = useState(null);
const [showEmotionHint, setShowEmotionHint] = useState(false);
const infoRef = useRef(null);
const popRef  = useRef(null);

  // === Общая авто-сохраналка дня в БД ===
    async function autoSaveDay(overrides = {}) {
        if (dbUserLoading || !dbUserId) return;
    const today = dayKey;


    const currentWeight = Number(weight?.current);

    const base = {
      steps: steps ?? null,
      water: cupsCount * 250,
      weight: Number.isFinite(currentWeight) && currentWeight > 0 ? currentWeight : null,
      mood: selectedEmotion ?? null,
      sleep: sleepHours ?? null,
    };

    const payload = {
      day: today,
      ...base,
      ...overrides,
    };

    debugLog("AUTO_SAVE_DAY payload =", payload, "dbUserId =", dbUserId);

    try {
      await apiSaveDayStats(dbUserId, payload);
      debugLog("AUTO_SAVE_DAY OK");
    } catch (e) {
      console.error("AUTO_SAVE_DAY ERROR:", e);
    }
  }


    // экран внутри вкладки "Тренировки": основной / добавки / замеры тела / выбор мышц / сессия
  const [trainScreen, setTrainScreen] = useState("main"); // "main" | "supplements" | "body" | "trainGroups" | "session"

  // состояния для выбора групп мышц
const [selectedMuscles, setSelectedMuscles] = useState([]);
const [pickerMuscles, setPickerMuscles] = useState([]);
  // состояние тренировки (упражнения + подходы)
  const [exercises, setExercises] = useState(() => createInitialExercises());
  const [isSavingWorkout, setIsSavingWorkout] = useState(false);

  const [trainMode, setTrainMode] = useState("strength"); // "strength" | "cardio"
const [workoutTitle, setWorkoutTitle] = useState("");
const [workoutTitleModalOpen, setWorkoutTitleModalOpen] = useState(false);
const [cardioItems, setCardioItems] = useState([]);
const [cardioModal, setCardioModal] = useState(null);
// { title: "", minutes: "", comment: "" }
const [workoutTimer, setWorkoutTimer] = useState(() => createEmptyWorkoutTimer());
const [timerNow, setTimerNow] = useState(() => Date.now());

const workoutElapsedMs = useMemo(
  () => getWorkoutTimerElapsedMs(workoutTimer, timerNow),
  [workoutTimer, timerNow]
);

const workoutDurationSeconds = Math.floor(workoutElapsedMs / 1000);

useEffect(() => {
  if (trainScreen !== "session" || !workoutTimer.isRunning) return;

  setTimerNow(Date.now());

  const id = window.setInterval(() => {
    setTimerNow(Date.now());
  }, 1000);

  return () => window.clearInterval(id);
}, [trainScreen, workoutTimer.isRunning]);

const resetWorkoutTimer = () => {
  setWorkoutTimer(createEmptyWorkoutTimer());
  setTimerNow(Date.now());
};

const toggleWorkoutTimer = () => {
  const now = Date.now();
  setTimerNow(now);

  setWorkoutTimer((prev) => {
    const t = normalizeWorkoutTimer(prev);

    if (t.isRunning) {
      return {
        isRunning: false,
        startedAt: null,
        elapsedMs: getWorkoutTimerElapsedMs(t, now),
      };
    }

    return {
      isRunning: true,
      startedAt: now,
      elapsedMs: t.elapsedMs,
    };
  });
};
const startWorkoutTimerIfNeeded = () => {
  const now = Date.now();

  setTimerNow(now);

  setWorkoutTimer((prev) => {
    const t = normalizeWorkoutTimer(prev);

    // уже идёт — ничего не делаем
    if (t.isRunning) return t;

    // если пользователь уже сам запускал и потом поставил на паузу —
    // не запускаем повторно автоматически
    if (getWorkoutTimerElapsedMs(t, now) > 0) return t;

    return {
      isRunning: true,
      startedAt: now,
      elapsedMs: 0,
    };
  });
};

  const [exerciseModal, setExerciseModal] = useState(null); // {id, name}
  const [setModal, setSetModal] = useState(null); // {exerciseId, setId, weight, reps, label}
  const [showTrainInfoHint, setShowTrainInfoHint] = useState(false);
  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [expandedWorkoutId, setExpandedWorkoutId] = useState(null);
const [expandedWorkoutExerciseId, setExpandedWorkoutExerciseId] = useState(null);
const [selectedWorkoutTitleFilter, setSelectedWorkoutTitleFilter] = useState("Все");
// дни, когда была сохранена тренировка
const workoutDayMarkerMap = useMemo(() => {
  const map = new Map();

  (savedWorkouts || []).forEach((w) => {
    if (!w?.date) return;

    const d = new Date(w.date);
    if (Number.isNaN(d.getTime())) return;

    const dayKey = getLocalDayKey(d);
    const cardioSummary = computeCardioSummary(w);

    const isCardio =
      (w?.mode === "cardio" ||
        /кардио/i.test(String(w?.musclesLabel || w?.title || w?.workoutTitle || ""))) &&
      cardioSummary.itemsCount > 0;

    const prev = map.get(dayKey);

    // Если в один день есть силовая и кардио — оставляем обычный огонь.
    if (prev === "strength") return;

    map.set(dayKey, isCardio ? "cardio" : "strength");
  });

  return map;
}, [savedWorkouts]);

const workoutDaysSet = useMemo(
  () => new Set(workoutDayMarkerMap.keys()),
  [workoutDayMarkerMap]
);
// ===== Попап "Тренировки за день" (по огоньку в календаре) =====
const [workoutDayOpen, setWorkoutDayOpen] = useState(false);
const [workoutDayKey, setWorkoutDayKey] = useState("");
const [workoutDayItems, setWorkoutDayItems] = useState([]); // массив тренировок за выбранный день

function openWorkoutDay(dayKey) {
  try {
    setWorkoutDayKey(dayKey);
    setWorkoutDayOpen(true);

    // достаём из уже загруженных "Мои тренировки"
    const list = (savedWorkouts || []).filter((w) => {
      if (!w?.date) return false;
      const d = new Date(w.date);
      if (Number.isNaN(d.getTime())) return false;
      return getLocalDayKey(d) === dayKey;
    });

    setWorkoutDayItems(list);
  } catch (e) {
    console.error("openWorkoutDay failed:", e);
    setWorkoutDayItems([]);
  }
}


const [draftWorkout, setDraftWorkout] = useState(null);
const [workoutConflictOpen, setWorkoutConflictOpen] = useState(false);

const [namesModal, setNamesModal] = useState(null); // { title: string, names: string[] }
const getDefaultWorkoutTitle = () => {
  if (trainMode === "cardio") return "Кардио";
  const title = selectedMuscles.map(getMuscleLabel).filter(Boolean).join(", ");
  return title || "Тренировка";
};
  const setsContainersRef = useRef({});

const toggleMuscleGroup = (key) => {
  setPickerMuscles((prev) =>
    prev.includes(key)
      ? prev.filter((k) => k !== key)
      : [...prev, key]
  );
};
  const musclesTitle =
    selectedMuscles.length > 0
      ? selectedMuscles.map(getMuscleLabel).join(", ")
      : "Тренировка";
const sessionTitle = String(workoutTitle || "").trim() || getDefaultWorkoutTitle();

const buildCurrentWorkoutDraft = () => ({
  trainMode,
  selectedMuscles,
  workoutTitle: String(workoutTitle || "").trim(),
  exercises,
  cardioItems,
  workoutTimer,
  updatedAt: Date.now(),
});

const getWorkoutDraftTitle = (d) => {
  const customTitle = String(d?.workoutTitle || d?.title || "").trim();
  if (customTitle) return customTitle;

  if (d?.trainMode === "cardio") {
    return t("workout.picker.cardio");
  }

  const muscles = Array.isArray(d?.selectedMuscles)
    ? d.selectedMuscles
    : [];

  const title = muscles
    .map((key) => t(`workout.muscles.${key}`))
    .filter(Boolean)
    .join(", ");

  return title || t("nav.workout");
};

const openWorkoutTitle = getWorkoutDraftTitle(
  trainScreen === "session" ? buildCurrentWorkoutDraft() : draftWorkout
);
// ✅ Для экрана выбора мышц:
// если тренировка уже идёт и пользователь ничего нового не выбрал,
// нижняя кнопка возвращает его в текущую тренировку.
// Как только он выбирает новые мышцы — панель снова становится обычной,
// а старая защита warnIfWorkoutAlreadyOpen() покажет экран с выбором действия.
const hasWorkoutToResumeFromGroups = hasMeaningfulDraftWorkout(draftWorkout);
const showResumeWorkoutInGroups =
  trainScreen === "trainGroups" &&
  hasWorkoutToResumeFromGroups &&
  pickerMuscles.length === 0;

const activeWorkoutTitleInGroups = showResumeWorkoutInGroups
  ? getWorkoutDraftTitle(draftWorkout)
  : "";
const persistCurrentWorkoutDraft = () => {
  const d = buildCurrentWorkoutDraft();

  if (!hasMeaningfulDraftWorkout(d)) return null;

  storageSet(DRAFT_WORKOUT_KEY, d);
  setDraftWorkout(d);

  return d;
};
const resumeOpenWorkout = () => {
  const d = draftWorkout;

  if (d && hasMeaningfulDraftWorkout(d)) {
    const mode = d.trainMode === "cardio" ? "cardio" : "strength";

setTrainMode(mode);
setSelectedMuscles(d.selectedMuscles || []);
setWorkoutTitle(String(d.workoutTitle || d.title || "").trim());
setExercises(d.exercises || createInitialExercises());
    setCardioItems(d.cardioItems || d.cardio || []);
    setWorkoutTimer(normalizeWorkoutTimer(d.workoutTimer));
    setTimerNow(Date.now());
  }

  setWorkoutConflictOpen(false);
  setTrainScreen("session");
};
const hasOpenWorkout = () => {
  if (trainScreen === "session") {
    return hasMeaningfulDraftWorkout(buildCurrentWorkoutDraft());
  }

  return hasMeaningfulDraftWorkout(draftWorkout);
};

const warnIfWorkoutAlreadyOpen = () => {
  if (!hasOpenWorkout()) return false;

  if (trainScreen === "session") {
    persistCurrentWorkoutDraft();
  }

  setWorkoutConflictOpen(true);
  return true;
};

const [expandedSessionRecords, setExpandedSessionRecords] = useState({});
const [exerciseActionChoice, setExerciseActionChoice] = useState(null);

const [draggingExerciseId, setDraggingExerciseId] = useState(null);

const exerciseDragRef = useRef({
  id: null,
  pointerId: null,
  startY: 0,
  lastY: 0,
  lastSwapAt: 0,
  lastInsertIndex: null,
  clone: null,
  originalCard: null,
});

const exerciseDragBodyStyleRef = useRef(null);
const EXERCISE_DRAG_EVENT_OPTIONS = {
  passive: false,
  capture: true,
};
const getSessionExerciseCards = () => {
  if (typeof document === "undefined") return [];

  return Array.from(
    document.querySelectorAll("[data-session-exercise-card='1']")
  ).filter((card) => !card.hasAttribute("data-session-drag-clone"));
};

const getSessionExerciseCardRects = () => {
  const cards = getSessionExerciseCards();

  return new Map(
    cards.map((card) => [
      card.getAttribute("data-session-exercise-id"),
      card.getBoundingClientRect(),
    ])
  );
};

const animateSessionExerciseCardsFrom = (beforeRects) => {
  if (typeof document === "undefined" || !beforeRects) return;

  requestAnimationFrame(() => {
    const cards = getSessionExerciseCards();

    cards.forEach((card) => {
      const id = card.getAttribute("data-session-exercise-id");
      if (!id || id === exerciseDragRef.current.id) return;

      const before = beforeRects.get(id);
      if (!before) return;

      const after = card.getBoundingClientRect();
      const dy = before.top - after.top;

      if (Math.abs(dy) < 1) return;

      card.style.transition = "none";
      card.style.transform = `translate3d(0, ${dy}px, 0)`;
      card.style.willChange = "transform";

      requestAnimationFrame(() => {
        card.style.transition =
          "transform 220ms cubic-bezier(0.18, 0.9, 0.22, 1)";
        card.style.transform = "translate3d(0, 0, 0)";

        window.setTimeout(() => {
          card.style.transition = "";
          card.style.transform = "";
          card.style.willChange = "";
        }, 270);
      });
    });
  });
};

const moveDragClone = (clientY) => {
  const drag = exerciseDragRef.current;
  const clone = drag.clone;

  if (!clone) return;

  const dy = clientY - drag.startY;

  // Без transition по transform: карточка должна быть прямо "приклеена" к пальцу.
  clone.style.transform = `translate3d(0, ${dy}px, 0) scale(1.035) rotate(-0.35deg)`;
};

const autoScrollDuringExerciseDrag = (clientY) => {
  if (typeof document === "undefined") return;

  const root =
    document.querySelector("[data-app-scroll-root='1']") ||
    document.querySelector("[data-session-scroll-root='1']") ||
    document.scrollingElement ||
    document.documentElement;

  if (!root) return;

  const isPage =
    root === document.scrollingElement ||
    root === document.documentElement ||
    root === document.body;

  const rect = isPage
    ? { top: 0, bottom: window.innerHeight }
    : root.getBoundingClientRect();

  const edge = 92;
  let delta = 0;

  if (clientY < rect.top + edge) {
    delta = -Math.round((1 - (clientY - rect.top) / edge) * 16);
  } else if (clientY > rect.bottom - edge) {
    delta = Math.round((1 - (rect.bottom - clientY) / edge) * 16);
  }

  if (!delta) return;

  try {
    if (isPage) {
      window.scrollBy(0, delta);
    } else {
      root.scrollTop += delta;
    }
  } catch {}
};

const removeDragClone = () => {
  if (typeof document === "undefined") return;

  const currentClone = exerciseDragRef.current.clone;

  const clones = Array.from(
    document.querySelectorAll("[data-session-drag-clone='1']")
  );

  clones.forEach((clone) => {
    if (clone === currentClone) {
      try {
        clone.style.transition =
          "opacity 140ms ease, transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1)";
        clone.style.opacity = "0";
        clone.style.transform += " scale(0.985)";
      } catch {}

      window.setTimeout(() => {
        try {
          clone.remove();
        } catch {}
      }, 160);

      return;
    }

    // Страховка: если старая копия почему-то осталась от прошлого drag —
    // удаляем сразу.
    try {
      clone.remove();
    } catch {}
  });
};

const cleanupExerciseDrag = () => {
  try {
  window.removeEventListener(
    "pointermove",
    handleExerciseDragMove,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.removeEventListener(
    "pointerup",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.removeEventListener(
    "pointercancel",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.removeEventListener(
    "mouseup",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.removeEventListener(
    "touchend",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.removeEventListener(
    "touchcancel",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );

  window.removeEventListener("blur", cleanupExerciseDrag);
  window.removeEventListener("pagehide", cleanupExerciseDrag);
  document.removeEventListener("visibilitychange", cleanupExerciseDrag);
} catch {}

  removeDragClone();

  exerciseDragRef.current = {
    id: null,
    pointerId: null,
    startY: 0,
    lastY: 0,
    lastSwapAt: 0,
    lastInsertIndex: null,
    clone: null,
    originalCard: null,
  };

  setDraggingExerciseId(null);

  try {
    const saved = exerciseDragBodyStyleRef.current;

    if (saved) {
      document.body.style.userSelect = saved.userSelect;
      document.body.style.WebkitUserSelect = saved.webkitUserSelect;
      document.body.style.cursor = saved.cursor;
      document.body.style.touchAction = saved.touchAction;
      document.documentElement.style.overscrollBehavior =
        saved.htmlOverscrollBehavior;
    }

    exerciseDragBodyStyleRef.current = null;
  } catch {}
};

const startExerciseDrag = (e, exerciseId) => {
  e.preventDefault();
  e.stopPropagation();

  // Страховка: если прошлый drag по какой-то причине не очистился,
  // перед новым захватом убираем старую копию.
  removeDragClone();

  const id = String(exerciseId);
  const card = e.currentTarget.closest?.("[data-session-exercise-card='1']");

  if (!card || typeof document === "undefined") return;

  const rect = card.getBoundingClientRect();
  const clone = card.cloneNode(true);

  clone.setAttribute("data-session-drag-clone", "1");

  clone.style.position = "fixed";
  clone.style.left = `${rect.left}px`;
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.style.zIndex = "999999";
  clone.style.pointerEvents = "none";
  clone.style.margin = "0";
  clone.style.opacity = "0.99";
  clone.style.transform = "translate3d(0, 0, 0) scale(1.035) rotate(-0.35deg)";
  clone.style.transformOrigin = "center";
  clone.style.willChange = "transform";
  clone.style.borderColor = "rgba(32,234,212,0.95)";
  clone.style.background = "rgba(8,44,54,0.98)";
  clone.style.boxShadow =
    "0 28px 70px rgba(0,0,0,0.55), 0 0 38px rgba(32,234,212,0.28), inset 0 1px 0 rgba(255,255,255,0.12)";
  clone.style.transition =
    "opacity 120ms ease, box-shadow 120ms ease, border-color 120ms ease";
  clone.style.borderRadius = "18px";

  document.body.appendChild(clone);

  exerciseDragRef.current = {
    id,
    pointerId: e.pointerId,
    startY: e.clientY,
    lastY: e.clientY,
    lastSwapAt: 0,
    lastInsertIndex: null,
    clone,
    originalCard: card,
  };

  setDraggingExerciseId(id);
  protectNextTap(1000);

  try {
    exerciseDragBodyStyleRef.current = {
      userSelect: document.body.style.userSelect,
      webkitUserSelect: document.body.style.WebkitUserSelect,
      cursor: document.body.style.cursor,
      touchAction: document.body.style.touchAction,
      htmlOverscrollBehavior: document.documentElement.style.overscrollBehavior,
    };

    document.body.style.userSelect = "none";
    document.body.style.WebkitUserSelect = "none";
    document.body.style.cursor = "grabbing";
    document.body.style.touchAction = "none";
    document.documentElement.style.overscrollBehavior = "none";
  } catch {}

try {
  window.addEventListener(
    "pointermove",
    handleExerciseDragMove,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.addEventListener(
    "pointerup",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.addEventListener(
    "pointercancel",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );

  // Дополнительные страховки для мобильных WebView / Telegram.
  window.addEventListener(
    "mouseup",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.addEventListener(
    "touchend",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );
  window.addEventListener(
    "touchcancel",
    stopExerciseDrag,
    EXERCISE_DRAG_EVENT_OPTIONS
  );

  window.addEventListener("blur", cleanupExerciseDrag);
  window.addEventListener("pagehide", cleanupExerciseDrag);
  document.addEventListener("visibilitychange", cleanupExerciseDrag);
} catch {}

  try {
    getTg()?.HapticFeedback?.impactOccurred?.("medium");
  } catch {}
};

const reorderExerciseByPointerY = (clientY) => {
  const drag = exerciseDragRef.current;
  const dragId = drag.id;

  if (!dragId) return;

  const now = Date.now();

  // Небольшой лимит, чтобы не было дрожания, но перенос оставался отзывчивым.
  if (now - drag.lastSwapAt < 70) return;

  const allCards = getSessionExerciseCards()
    .map((card) => ({
      id: card.getAttribute("data-session-exercise-id"),
      rect: card.getBoundingClientRect(),
    }))
    .filter((item) => item.id)
    .sort((a, b) => a.rect.top - b.rect.top);

  const otherCards = allCards.filter((item) => item.id !== dragId);

  if (!otherCards.length) return;

  let insertIndex = otherCards.length;

  for (let i = 0; i < otherCards.length; i += 1) {
    const centerY = otherCards[i].rect.top + otherCards[i].rect.height / 2;

    if (clientY < centerY) {
      insertIndex = i;
      break;
    }
  }

  const currentDomIndex = allCards.findIndex((item) => item.id === dragId);
  const currentInsertIndex =
    currentDomIndex < 0
      ? null
      : allCards
          .slice(0, currentDomIndex)
          .filter((item) => item.id !== dragId).length;

  if (currentInsertIndex === insertIndex) return;
  if (drag.lastInsertIndex === insertIndex) return;

  const beforeRects = getSessionExerciseCardRects();

  exerciseDragRef.current.lastSwapAt = now;
  exerciseDragRef.current.lastInsertIndex = insertIndex;

  setExercises((prev) => {
    const list = Array.isArray(prev) ? [...prev] : [];
    const fromIndex = list.findIndex((ex) => String(ex?.id) === String(dragId));

    if (fromIndex < 0) return prev;

    const moved = list[fromIndex];
    const rest = list.filter((ex) => String(ex?.id) !== String(dragId));
    const safeInsertIndex = Math.max(0, Math.min(insertIndex, rest.length));

    rest.splice(safeInsertIndex, 0, moved);

    return rest;
  });

  animateSessionExerciseCardsFrom(beforeRects);

  try {
    getTg()?.HapticFeedback?.selectionChanged?.();
  } catch {}
};

const handleExerciseDragMove = (e) => {
  const drag = exerciseDragRef.current;
  const dragId = drag.id;

  if (!dragId) return;

  if (drag.pointerId != null && e.pointerId != null && e.pointerId !== drag.pointerId) {
    return;
  }

  e.preventDefault?.();
  e.stopPropagation?.();

  exerciseDragRef.current.lastY = e.clientY;

  moveDragClone(e.clientY);
  autoScrollDuringExerciseDrag(e.clientY);
  reorderExerciseByPointerY(e.clientY);
};

const stopExerciseDrag = (e) => {
  if (!exerciseDragRef.current.id) return;

  e?.preventDefault?.();
  e?.stopPropagation?.();

  cleanupExerciseDrag();
  protectNextTap(500);
};
const cancelCurrentWorkout = () => {
  storageSet(DRAFT_WORKOUT_KEY, null);
  setDraftWorkout(null);
  setPickerMuscles([]);
  setSelectedMuscles([]);
  setWorkoutTitle("");
  setExercises(createInitialExercises());
  setCardioItems([]);
  setTrainMode("strength");
  resetWorkoutTimer();
  setWorkoutConflictOpen(false);
  setExerciseActionChoice(null);

  setTrainScreen("trainGroups");
};

const confirmAndCancelCurrentWorkout = async () => {
  const ok = await safeConfirm(
    t("workout.session.cancelConfirm")
  );

  if (!ok) return;

  cancelCurrentWorkout();
};

const closeSessionToGroups = () => {
  persistCurrentWorkoutDraft();
  setExerciseActionChoice(null);
  setTrainScreen("trainGroups");
};

const deleteExercise = async (exerciseId) => {
  const ok = await safeConfirm(
    t("workout.session.deleteExerciseConfirm")
  );
  if (!ok) return;

  setExpandedSessionRecords((prev) => {
    const next = { ...(prev || {}) };
    delete next[exerciseId];
    return next;
  });

  setExercises((prev) => {
    const next = (prev || []).filter((ex) => ex.id !== exerciseId);
    return next.length ? next : createInitialExercises();
  });
};

const deleteLastSetFromExercise = async (exerciseId) => {
  const target = (exercises || []).find((ex) => ex.id === exerciseId);
  const sets = Array.isArray(target?.sets) ? target.sets : [];

  if (sets.length <= 1) {
    safeAlert("В упражнении должен остаться хотя бы один подход", "Нельзя удалить");
    return;
  }

const ok = await safeConfirm(
  t("workout.session.deleteLastSetConfirm")
);
if (!ok) return;

  setExercises((prev) =>
    (prev || []).map((ex) => {
      if (ex.id !== exerciseId) return ex;

      const currentSets = Array.isArray(ex.sets) ? ex.sets : [];
      if (currentSets.length <= 1) return ex;

      return {
        ...ex,
        sets: currentSets.slice(0, -1),
      };
    })
  );
};

const deleteSetFromExercise = async (exerciseId, setId, setIndex) => {
  const target = (exercises || []).find((ex) => ex.id === exerciseId);
  const sets = Array.isArray(target?.sets) ? target.sets : [];

  if (sets.length <= 1) {
    safeAlert("В упражнении должен остаться хотя бы один подход", "Нельзя удалить");
    return;
  }

const ok = await safeConfirm(
  t("workout.session.deleteSetConfirm", {
    number: Number(setIndex) + 1,
  })
);

  if (!ok) return;

  setExercises((prev) =>
    (prev || []).map((ex) => {
      if (ex.id !== exerciseId) return ex;

      const currentSets = Array.isArray(ex.sets) ? ex.sets : [];

      if (currentSets.length <= 1) return ex;

      const nextSets = currentSets
        .filter((_, idx) => idx !== setIndex)
        .map((set, idx) => ({
          ...set,
          id: idx + 1,
          label: `Подход ${idx + 1}`,
        }));

      return {
        ...ex,
        sets: nextSets,
      };
    })
  );
};

const openExerciseDeleteChoice = (exerciseId) => {
  const target = (exercises || []).find((ex) => ex.id === exerciseId);
  if (!target) return;

  setExerciseActionChoice({ exerciseId });
};

const addSetToExercise = (exerciseId) => {
  setExercises((prev) =>
    prev.map((e) => {
      if (e.id !== exerciseId) return e;

      const sets = Array.isArray(e.sets) ? e.sets : [];
      if (sets.length >= 150) return e;

      const nextId = sets.length
        ? Number(sets[sets.length - 1].id || sets.length) + 1
        : 1;

      return {
        ...e,
        sets: [
          ...sets,
          {
            id: nextId,
            label: `Подход ${nextId}`,
            weight: null,
            reps: null,
            edited: true,
            prefill: false,
          },
        ],
      };
    })
  );

  setTimeout(() => {
    const el = setsContainersRef.current[exerciseId];
    if (!el) return;

    try {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } catch {
      el.scrollLeft = el.scrollWidth;
    }
  }, 0);
};

const repeatWorkout = (w) => {
  if (warnIfWorkoutAlreadyOpen()) return;

  const cardioList = (w?.cardio || w?.cardioItems || []).slice();
  const isCardio =
    (w?.mode === "cardio" || /кардио/i.test(String(w?.musclesLabel || ""))) &&
    cardioList.length > 0;

  if (isCardio) {
    const it = cardioList[0] || {};

    setCardioModal({
      title: String(it.title || "").trim(),
      minutes: it.minutes != null ? String(it.minutes) : "",
      comment: String(it.comment || "").trim(),
      kind: "repeat",
    });

    return;
  }

  const repeatedExercises = (
    w?.exercises && w.exercises.length ? w.exercises : createInitialExercises()
  ).map((ex, i) => ({
    ...ex,
    id: String(ex.id || `ex-${i}`) + "-r-" + String(Date.now()),
    name: String(ex.name || `${i + 1} упражнение`).trim(),
    sets: (ex.sets || []).map((s, j) => ({
      ...s,
      id: typeof s.id === "number" ? s.id : j + 1,
      label: s.label || `Подход ${j + 1}`,

      // вес/повторы переносим как подсказку,
      // но подход НЕ считается выполненным, пока пользователь не сохранит его заново
      edited: false,
      prefill: true,
    })),
  }));

  setTrainMode("strength");
  setWorkoutTitle(String(w?.title || w?.workoutTitle || w?.musclesLabel || "").trim());
  setCardioItems([]);
  setSelectedMuscles((w?.muscles || []).slice());
  setExercises(repeatedExercises.length ? repeatedExercises : createInitialExercises());

  // длительность и общий вес не переносятся
  resetWorkoutTimer();
  setTimerNow(Date.now());

  setExpandedSessionRecords({});
  setExerciseActionChoice(null);
  setTrainScreen("session");
};
// Удаляем пустые подходы, а пустые упражнения сохраняем ТОЛЬКО если юзер что-то поменял
const cleanExercisesForSave = (list) => {
  const toNum = (v) => {
    if (v === null || v === undefined) return NaN;
    const s = String(v).trim().replace(",", ".");
    if (!s) return NaN;
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

const isSetFilled = (set) => {
  // Если тренировка была повторена, старые подходы являются только подсказкой.
  // Они не сохраняются как выполненные, пока пользователь не нажал на подход
  // и не сохранил вес/повторы заново.
  if (set?.prefill === true && set?.edited !== true) return false;

  const w = toNum(set?.weight);
  const r = toNum(set?.reps);

  // Подход заполнен, если есть хотя бы вес ИЛИ повторы
  return !(Number.isNaN(w) && Number.isNaN(r));
};

  const isPlaceholderName = (name) => {
    const n = String(name || "").trim().toLowerCase();
    // "1 упражнение", "2 упражнение", ...
    return /^\d+\s+упражнение$/.test(n);
  };

  const isDefaultSet = (set, idx) => {
    const label = String(set?.label || "").trim();
    const defaultLabel = `Подход ${idx + 1}`;
    const w = set?.weight;
    const r = set?.reps;
    return label === defaultLabel && (w === null || w === "" || w === undefined) && (r === null || r === "" || r === undefined);
  };

  const isExerciseChanged = (ex) => {
    // Считаем "изменённым", если:
    // 1) имя НЕ плейсхолдер ("4 упражнение") ИЛИ
    // 2) количество подходов НЕ 3 ИЛИ
    // 3) подписи подходов отличаются от дефолтных
    const name = String(ex?.name || "").trim();
    if (name && !isPlaceholderName(name)) return true;

    const sets = Array.isArray(ex?.sets) ? ex.sets : [];
    if (sets.length !== 3) return true;

    for (let i = 0; i < sets.length; i++) {
      if (!isDefaultSet(sets[i], i)) return true;
    }
    return false;
  };

  return (list || [])
    .map((ex) => {
      const name = String(ex?.name || "").trim();
      const sets = Array.isArray(ex?.sets) ? ex.sets : [];

      const cleanedSets = sets.filter(isSetFilled);

      // если подходов нет, но упражнение "изменено" — сохраняем его БЕЗ подходов
      if (cleanedSets.length === 0) {
        if (isExerciseChanged(ex)) {
          return { ...ex, name, sets: [] };
        }
        // не изменено и пустое → удаляем
        return null;
      }

      // если есть заполненные подходы — сохраняем только их
      return { ...ex, name, sets: cleanedSets };
    })
    .filter(Boolean);
};
const openCardioModal = (initial = null) => {
  // ✅ Открываем Кардио в виде попапа
  // initial можно передать, если нужно открыть с уже заполненными полями
  setCardioModal({
    title: String(initial?.title || ""),
    minutes: initial?.minutes != null ? String(initial.minutes) : "",
    comment: String(initial?.comment || ""),
    kind: initial?.kind || "draft",
  });
};





const addCardioItem = ({ title, minutes, comment }) => {
  setCardioItems((prev) => [
    ...prev,
    {
      id: `cardio-${Date.now()}-${prev.length + 1}`,
      title: String(title || "").trim() || "Кардио",
      minutes: Number(minutes) || 0,
      comment: String(comment || "").trim(),
      createdAt: new Date().toISOString(),
    },
  ]);
};
// Быстрое сохранение "чистого кардио" из попапа на экране выбора мышц.
// Не переводит на экран тренировки — просто пишет в БД и обновляет "Мои тренировки".
const saveStandaloneCardio = async ({ title, minutes, comment }) => {
  if (!canUsePremiumFeatures) {
    openPremiumPaywall("save_standalone_cardio");
    return;
  }

  try {
    const t = String(title || "").trim() || "Кардио";
    const m = Number(String(minutes ?? "").replace(",", "."));
    const c = String(comment || "").trim();

    if (!Number.isFinite(m) || m <= 0) {
      safeAlert("Укажи время кардио в минутах (например 20).", "Кардио");
      return;
    }

const item = {
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `cardio-${Date.now()}-1`,
  title: t,
  minutes: m,
  comment: c,
  createdAt: new Date().toISOString(),
};

const cardioDurationSeconds = Math.round(m * 60);

const workoutId = Date.now();
const workout = {
  id: workoutId,
  date: new Date().toISOString(),
  mode: "cardio",
  title: "Кардио",
  workoutTitle: "Кардио",
  muscles: [],
  musclesLabel: "Кардио",
  exercises: [],
  cardio: [item],
  durationSeconds: cardioDurationSeconds,
  durationText: formatWorkoutDuration(cardioDurationSeconds),
  timer: {
    isRunning: false,
    startedAt: null,
    elapsedMs: cardioDurationSeconds * 1000,
  },
};

    // сразу обновим список в UI
    setSavedWorkouts((prev) => [workout, ...(prev || [])]);

    // сохраним в БД
    await apiUpsertWorkout(workout);
    await apiReplaceCardioForWorkout(workoutId, "standalone", [item]);

    safeAlert("Кардио сохранено ✅", "Готово");
    setCardioModal(null);
  } catch (e) {
    console.error("saveStandaloneCardio error", e);
    safeAlert("Не удалось сохранить кардио. Проверь интернет и попробуй ещё раз.", "Ошибка");
  }
};

const saveWorkout = async () => {
  if (!canUsePremiumFeatures) {
    openPremiumPaywall("save_workout");
    return;
  }

  if (!canSaveWorkout || isSavingWorkout) return;
  setIsSavingWorkout(true);

  const cleanedExercises = cleanExercisesForSave(exercises);
const timerDurationSeconds = Math.floor(
  getWorkoutTimerElapsedMs(workoutTimer, Date.now()) / 1000
);

const cardioDurationSeconds = Math.round(
  (cardioItems || []).reduce((sum, item) => {
    const minutes = Number(String(item?.minutes ?? "").replace(",", "."));
    return sum + (Number.isFinite(minutes) ? minutes : 0);
  }, 0) * 60
);

const durationSeconds =
  trainMode === "cardio" && cardioDurationSeconds > 0
    ? cardioDurationSeconds
    : timerDurationSeconds;

const workout = {
  id: Date.now(),
  date: new Date().toISOString(),
  mode: trainMode,
  title: sessionTitle,
  workoutTitle: sessionTitle,
  muscles: trainMode === "cardio" ? [] : selectedMuscles,
  musclesLabel: getDefaultWorkoutTitle(),
  exercises: trainMode === "cardio" ? [] : cleanedExercises,
  cardio: cardioItems,
  durationSeconds,
  durationText: formatWorkoutDuration(durationSeconds),
  timer: {
    isRunning: false,
    startedAt: null,
    elapsedMs: durationSeconds * 1000,
  },
};

  const next = [workout, ...savedWorkouts];
  setSavedWorkouts(next);

  try {
    // 1) сохранить тренировку
    await apiUpsertWorkout(workout);

    // 2) сохранить кардио (для отчётов)
    const mode = trainMode === "cardio" ? "standalone" : "inline";
    await apiReplaceCardioForWorkout(workout.id, mode, cardioItems);

    // ✅ успех
    safeAlert("Тренировка сохранена ✅", "Готово");
  } catch (e) {
    console.error("save workout error", e);
    safeAlert("Не удалось сохранить тренировку. Проверь интернет и попробуй ещё раз.", "Ошибка");
    return;
  } finally {
    setIsSavingWorkout(false);
  }

  // очистка черновика и состояния
storageSet(DRAFT_WORKOUT_KEY, null);
setDraftWorkout(null);
setWorkoutConflictOpen(false);

setSelectedMuscles([]);
setWorkoutTitle("");
setExercises(createInitialExercises());
setCardioItems([]);
setTrainMode("strength");
resetWorkoutTimer();

setTrainScreen("trainGroups");
};


const deleteWorkout = async (workoutOrId) => {
  const workout =
    typeof workoutOrId === "object"
      ? workoutOrId
      : (savedWorkouts || []).find((w) => String(w.id) === String(workoutOrId));

  const workoutId = workout?.id ?? workoutOrId;
  if (workoutId == null) return;

  const customTitle = getWorkoutCustomTitle(workout);

  const workoutGroups = getWorkoutMuscleBadges(workout).filter(
    (group, index, arr) =>
      group &&
      arr.findIndex((item) => item?.key === group.key) === index
  );

  const localizedDefaultTitle =
    workout?.mode === "cardio"
      ? t("workout.picker.cardio")
      : workoutGroups
          .map((group) => t(`workout.muscles.${group.key}`))
          .filter(Boolean)
          .join(" • ");

  const title =
    customTitle ||
    localizedDefaultTitle ||
    t("nav.workout");

  const ok = await safeConfirm(
    t("workout.day.deleteWorkoutConfirm", {
      name: title,
    })
  );

  if (!ok) return;

  const before = savedWorkouts || [];

  setSavedWorkouts((prev) =>
    (prev || []).filter((w) => String(w.id) !== String(workoutId))
  );

  if (String(expandedWorkoutId) === String(workoutId)) {
    setExpandedWorkoutId(null);
    setExpandedWorkoutExerciseId(null);
  }

  try {
    await apiDeleteWorkout(workoutId);
    safeAlert("Тренировка удалена", "Готово");
  } catch (e) {
    console.error("delete workout error", e);
    setSavedWorkouts(before);
    safeAlert("Не удалось удалить тренировку. Проверь интернет и попробуй ещё раз.", "Ошибка");
  }
};

  const addExercise = () => {
    setExercises((prev) => {
      const nextIndex = prev.length + 1;
      return [
        ...prev,
        {
          id: `ex-${Date.now()}-${nextIndex}`,
          name: `${nextIndex} упражнение`,
          sets: [
            { id: 1, label: "Подход 1", weight: null, reps: null },
            { id: 2, label: "Подход 2", weight: null, reps: null },
            { id: 3, label: "Подход 3", weight: null, reps: null },
          ],
        },
      ];
    });
  };
const canSaveWorkout = useMemo(() => {
  // если есть кардио — сохранять можно
  if ((cardioItems || []).length > 0) return true;

  // иначе — старая логика силовой
  return exercises.some((ex, idx) => {
    const renamed = ex.name && ex.name !== `${idx + 1} упражнение`;
    const hasOneSet = ex.sets.some((s) => s.weight != null && s.reps != null);
    return renamed && hasOneSet;
  });
}, [exercises, cardioItems]);



  // === Замеры тела: выбранные метрики, история и инпуты ===
const [bodySelected, setBodySelected] = useState([]);
const [bodyHistory, setBodyHistory] = useState(createEmptyHistory());
const [bodyInputs, setBodyInputs] = useState(createEmptyInputs());
const [metricsCollapsed, setMetricsCollapsed] = useState(false);
const [bodyKeyboardOpen, setBodyKeyboardOpen] = useState(false);
  
  const bodyScreenRef = useRef(null);
useEffect(() => {
  if (trainScreen !== "body") return;

  setMetricsCollapsed(false);
  document.documentElement.style.setProperty("--kb", "0px");
}, [trainScreen]);

  const toggleBodyMetric = (key) => {
  setBodySelected((prev) => {
    const isActive = prev.includes(key);
    const next = isActive ? prev.filter((k) => k !== key) : [...prev, key];

    // ✅ пишем активность в БД (история НЕ удаляется)
    if (!dbUserLoading && dbUserId) {
      apiToggleBodyMetric(key, !isActive).catch((e) =>
        console.error("toggle body metric error", e)
      );
    }

    return next;
  });
};
const handleRemoveBodyMetric = async (key) => {
  const label = t(
    `body.measurements.metrics.${key}`
  );

  const ok = await safeConfirm(
    t("body.measurements.removeConfirm", {
      name: label,
    }),
    t("body.measurements.confirmTitle")
  );

  if (!ok) return;

  const beforeSelected = bodySelected;
  const beforeInput = bodyInputs[key] ?? "";

  setBodySelected((prev) => prev.filter((k) => k !== key));
  setBodyInputs((prev) => ({ ...prev, [key]: "" }));

  if (!dbUserLoading && dbUserId) {
    try {
      await apiToggleBodyMetric(key, false);
      safeAlert(`"${label}" убрано из выбранных`, "Готово");
    } catch (e) {
      console.error("remove body metric error", e);
      setBodySelected(beforeSelected);
      setBodyInputs((prev) => ({ ...prev, [key]: beforeInput }));
      safeAlert("Не удалось убрать часть тела. Проверь интернет и попробуй ещё раз.", "Ошибка");
    }
  } else {
    safeAlert(`"${label}" убрано из выбранных`, "Готово");
  }
};

const isSameBodyMeasurement = (a, b) => {
  if (a?.id != null && b?.id != null) {
    return String(a.id) === String(b.id);
  }

  return (
    String(a?.date ?? "") === String(b?.date ?? "") &&
    String(a?.value ?? "") === String(b?.value ?? "")
  );
};

const handleDeleteBodyMeasurement = async (key, measurement) => {
  if (!measurement) return;

const label = t(
  `body.measurements.metrics.${key}`
);

const measurementLocale =
  i18n.resolvedLanguage === "ru"
    ? "ru-RU"
    : "en-GB";

const measurementText =
  `${formatDateShort(measurement.date, measurementLocale)} · ` +
  `${measurement.value} ${t("units.centimetersShort")}`;

const ok = await safeConfirm(
  t("body.measurements.deleteConfirm", {
    name: label,
    measurement: measurementText,
  }),
  t("body.measurements.confirmTitle")
);

  if (!ok) return;

  const beforeHistory = bodyHistory[key] ?? [];

  setBodyHistory((prev) => ({
    ...prev,
    [key]: (prev[key] ?? []).filter((h) => !isSameBodyMeasurement(h, measurement)),
  }));

  if (!dbUserLoading && dbUserId) {
    try {
      await apiDeleteBodyMeasurement(key, measurement);
      safeAlert("Замер удалён", "Готово");
    } catch (e) {
      console.error("delete body measurement error", e);
      setBodyHistory((prev) => ({ ...prev, [key]: beforeHistory }));
      safeAlert("Не удалось удалить замер. Проверь интернет и попробуй ещё раз.", "Ошибка");
    }
  } else {
    safeAlert("Замер удалён", "Готово");
  }
};

const handleSaveBodyMetric = async (key) => {
  const raw = bodyInputs[key];
  const n = parseNumber(raw);

  if (n == null) return;

  const now = new Date().toISOString();
  const optimisticMeasurement = {
    id: `tmp-${Date.now()}`,
    value: n,
    date: now,
  };

  setBodyHistory((prev) => ({
    ...prev,
    [key]: [...(prev[key] || []), optimisticMeasurement],
  }));

  setBodyInputs((prev) => ({ ...prev, [key]: "" }));

  if (!dbUserLoading && dbUserId) {
    try {
      const savedMeasurement = await apiSaveBodyMetric(key, n, now);

      setBodyHistory((prev) => ({
        ...prev,
        [key]: (prev[key] || []).map((h) =>
          String(h.id) === String(optimisticMeasurement.id)
            ? savedMeasurement
            : h
        ),
      }));
    } catch (e) {
      console.error("save body metric error", e);

      setBodyHistory((prev) => ({
        ...prev,
        [key]: (prev[key] || []).filter(
          (h) => String(h.id) !== String(optimisticMeasurement.id)
        ),
      }));

      setBodyInputs((prev) => ({ ...prev, [key]: raw }));

      safeAlert("Не удалось сохранить замер. Проверь интернет и попробуй ещё раз.", "Ошибка");
    }
  }
};




  // список добавок
  const [supplements, setSupplements] = useState([]);
// ✅ сколько выпито сегодня (по dayKey)
const [suppTaken, setSuppTaken] = useState({}); // { [supplementId]: number }

const getTaken = (id) => Number(suppTaken?.[id] || 0);

    // форма новой добавки
  const [suppName, setSuppName] = useState("");
  const [suppPerDay, setSuppPerDay] = useState("2");
  const [suppFormCollapsed, setSuppFormCollapsed] = useState(false);


  // === Water widget state & logic ===

const VISIBLE_SLOTS = 7;                     // 7 видимых стаканов по ширине
const [cupsCount, setCupsCount] = useState(0);            // всего выпито (без лимита)
  


  // цели КБЖУ, которые можно редактировать
  const [macroTargets, setMacroTargets] = useState({
    kcal: 1500,
    carbs: 160,
    proteins: 75,
    fats: 30,
  });
  useEffect(() => {
  if (!dbUserId) return;

  (async () => {
    try {
      const data = await apiLoadMacroTargets();
      setMacroTargets((prev) => ({
        kcal: Number(data?.kcal ?? prev.kcal),
        carbs: Number(data?.carbs ?? prev.carbs),
        proteins: Number(data?.proteins ?? prev.proteins),
        fats: Number(data?.fats ?? prev.fats),
      }));
    } catch (e) {
      console.error("load macro targets error:", e);
    }
  })();
}, [dbUserId]);

  const [macroModalOpen, setMacroModalOpen] = useState(false);
// ✅ Отчёт по еде (период → отчёт)
const [foodReportPeriodOpen, setFoodReportPeriodOpen] = useState(false);
const [foodReportOpen, setFoodReportOpen] = useState(false);
const [foodReportPeriod, setFoodReportPeriod] = useState("week"); // week | month | quarter
// ✅ Отчёт по шагам / активности
const [stepsReportPeriodOpen, setStepsReportPeriodOpen] = useState(false);
const [stepsReportOpen, setStepsReportOpen] = useState(false);
const [stepsReportPeriod, setStepsReportPeriod] = useState("month"); // 2weeks | month | quarter

// ====== LOAD FOOD REPORT (REAL DATA) ======
const [foodReportSeries, setFoodReportSeries] = useState([]);
const [foodReportLoading, setFoodReportLoading] = useState(false);

useEffect(() => {
  if (!foodReportOpen) return;      // грузим только когда открыт отчёт
  if (!dbUserId) return;            // ждём пока определится пользователь

  (async () => {
    try {
      setFoodReportLoading(true);

      debugLog("[REPORT] fetching... period =", foodReportPeriod);

      const res = await tgFetch(
        `/api/food-month?mode=report&period=${encodeURIComponent(String(foodReportPeriod || "week"))}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );

const data = await res.json().catch(() => ({}));
debugLog("[REPORT] status =", res.status, "data =", data);

if (res.status === 401) {
  safeAlert("Авторизация устарела. Закрой мини-апп и открой заново из Telegram.", "Нужно обновить вход");
  setFoodReportSeries([]);
  return;
}

if (!res.ok || !data?.ok) {
  safeAlert(data?.error || `Ошибка отчёта (HTTP ${res.status})`, "Ошибка");
  setFoodReportSeries([]);
  return;
}

      setFoodReportSeries(Array.isArray(data.series) ? data.series : []);
    } catch (e) {
      console.error("[REPORT] load error:", e);
      setFoodReportSeries([]);
    } finally {
      setFoodReportLoading(false);
    }
  })();
}, [foodReportOpen, foodReportPeriod, dbUserId]);


const [, setAnimFillSlot] = useState(null);   // какой видимый стакан заливаем
const [, setAnimDrainSlot] = useState(null);  // какой видимый стакан опустошаем


const waterMl = cupsCount * 250;
const formatWater = (ml) =>
  ml >= 1000
    ? `${Number((ml / 1000).toFixed((ml / 1000) % 1 === 0 ? 0 : 2))}\u00A0${t("units.litersShort")}`
    : `${ml}\u00A0${t("units.millilitersShort")}`; // неразрывный пробел — не переносит единицу

const addCup = () => {
  setCupsCount((prev) => {
    const next = prev + 1;
    logEvent("water:add", { from: prev, to: next });

    const slot = Math.min(next - 1, VISIBLE_SLOTS - 1);
    setAnimFillSlot(slot);
    setTimeout(() => setAnimFillSlot(null), 280);

    // авто-сохранение новой воды (next стаканов → мл)
    autoSaveDay({ water: next * 250 });

    return next;
  });
};

const removeCup = () => {
  setCupsCount((prev) => {
    if (prev <= 0) {
      logEvent("water:remove", { from: prev, to: prev });
      return prev;
    }

    const next = prev - 1;
    logEvent("water:remove", { from: prev, to: next });

    const overflow = prev > VISIBLE_SLOTS;
    const slot = overflow ? VISIBLE_SLOTS - 1 : prev - 1;
    setAnimDrainSlot(slot);
    setTimeout(() => setAnimDrainSlot(null), 280);

    // авто-сохранение новой воды
    autoSaveDay({ water: next * 250 });

    return next;
  });
};



 
/// Вес: текущий/цель/стартовый. Обнуляем как при первом заходе
const [weight, setWeight] = useState({
  goal: 0,
  current: 0,
  baseline: null,
});

// Показывает, что старые данные веса уже прочитаны из localStorage.
// До этого момента нельзя записывать начальные нули обратно в хранилище.
const [weightStorageLoaded, setWeightStorageLoaded] = useState(false);

const [weightModalOpen, setWeightModalOpen] = useState(false);

// Сон: часы за текущий день
const [sleepHours, setSleepHours] = useState(null);
const [sleepModalOpen, setSleepModalOpen] = useState(false);

  // вес оставляем как есть: weight.current лежит внутри объекта weight

    // Авто-сохранение шагов (через общий autoSaveDay)
  function handleStepsChange(newSteps) {
    setSteps(newSteps);
    autoSaveDay({ steps: newSteps });
  }


// == Загрузка сохранённых данных при старте ==
useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const savedWeight = await storageGet(k("weight"), null);

      if (
        !cancelled &&
        savedWeight &&
        typeof savedWeight === "object"
      ) {
        const savedGoal = Number(savedWeight.goal);
        const savedCurrent = Number(savedWeight.current);
        const savedBaseline = Number(savedWeight.baseline);

        setWeight({
          goal:
            Number.isFinite(savedGoal) && savedGoal > 0
              ? savedGoal
              : 0,

          current:
            Number.isFinite(savedCurrent) && savedCurrent > 0
              ? savedCurrent
              : 0,

          baseline:
            Number.isFinite(savedBaseline) && savedBaseline > 0
              ? savedBaseline
              : null,
        });
      }

      const savedCups = await storageGet(k("cupsCount"), null);

      if (!cancelled && typeof savedCups === "number") {
        setCupsCount(savedCups);
      }

      const savedSleep = await storageGet(k("sleepHours"), null);

      if (!cancelled && typeof savedSleep === "number") {
        setSleepHours(savedSleep);
      }
    } finally {
      if (!cancelled) {
        setWeightStorageLoaded(true);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);
// ✅ Загружаем список добавок из БД
useEffect(() => {
  if (!dbUserId) return;

  (async () => {
    try {
      const list = await apiListSupplements();
      setSupplements(list);
    } catch (e) {
      console.error("load supplements error:", e);
    }
  })();
}, [dbUserId]);

// ✅ Загружаем замеры (выбранные метрики + история) из БД
useEffect(() => {
  if (!dbUserId) return;

  (async () => {
    try {
      const json = await apiLoadMeasurements();
      if (Array.isArray(json.selected)) setBodySelected(json.selected);
      if (json.history && typeof json.history === "object") {
        setBodyHistory((prev) => ({ ...prev, ...json.history }));
      }
    } catch (e) {
      console.error("load measurements error:", e);
    }
  })();
}, [dbUserId]);

// ✅ Загружаем "сколько выпито сегодня" из БД (зависит от dayKey)
useEffect(() => {
  if (!dbUserId) return;

  (async () => {
    try {
      const map = await apiLoadSupplementTaken(dayKey);
      setSuppTaken(map);
    } catch (e) {
      console.error("load supplement taken error:", e);
      setSuppTaken({});
    }
  })();
}, [dbUserId, dayKey]);
// ✅ Загружаем настройки веса из БД.
// Сначала ждём localStorage, чтобы не потерять старые значения
// пользователей, которые были сохранены до появления полей в users.
useEffect(() => {
  if (!dbUserId || !weightStorageLoaded) return;

  let cancelled = false;

  const toPositiveWeight = (value) => {
    const number = Number(value);

    return Number.isFinite(number) && number > 0
      ? number
      : null;
  };

  (async () => {
    try {
      const summary = await apiLoadWeightSummary();

      if (cancelled || !summary) return;

      const dbBaseline = toPositiveWeight(summary.baseline);
      const dbCurrent = toPositiveWeight(summary.current);
      const dbGoal = toPositiveWeight(summary.goal);

      const hasSavedBaseline =
        summary.hasSavedBaseline === true;

      const hasSavedGoal =
        summary.hasSavedGoal === true;

      setWeight((prev) => {
        const localBaseline = toPositiveWeight(prev?.baseline);
        const localCurrent = toPositiveWeight(prev?.current);
        const localGoal = toPositiveWeight(prev?.goal);

        return {
          ...prev,

          // Если стартовый вес уже отдельно сохранён в users,
          // значение БД является главным.
          // Для старых пользователей пока сохраняем локальное значение.
          baseline: hasSavedBaseline
            ? dbBaseline
            : localBaseline ?? dbBaseline,

          // Аналогично с желаемым весом.
          goal: hasSavedGoal
            ? dbGoal ?? 0
            : localGoal ?? dbGoal ?? 0,

          // Текущий вес берём из последней записи day_stats.
          current: dbCurrent ?? localCurrent ?? 0,
        };
      });
    } catch (e) {
      console.error("load weight summary error:", e);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [dbUserId, weightStorageLoaded]);

/// Загружаем данные дня с сервера при старте (когда знаем dbUserId)
useEffect(() => {
  if (!dbUserId) return; // ждём, пока придёт id из БД

  (async () => {
    const today = dayKey;
    const data = await apiLoadDayStats(dbUserId, today);
    if (!data) {
  // новый день — показываем пустые значения
  setSteps(null);
  setCupsCount(0);
  setSelectedEmotion(null);
  setSleepHours(null);
  return;
}


    // ВСЁ приводим к числам, даже если пришли строки
    if (data.steps != null) setSteps(Number(data.steps));

    if (data.water != null) {
      const ml = Number(data.water);
      setCupsCount(Math.round(ml / 250)); // 1 стакан = 250 мл
    }

    if (data.sleep != null) setSleepHours(Number(data.sleep));

if (data.weight != null) {
  const currentWeight = Number(data.weight);

  if (
    Number.isFinite(currentWeight) &&
    currentWeight > 0
  ) {
    setWeight((prev) => ({
      ...prev,
      current: currentWeight,
    }));
  }
}

    if (data.mood != null) setSelectedEmotion(Number(data.mood));

    debugLog("Данные дня из БД:", data);
  })();
}, [dbUserId, dayKey]);
// ✅ Загружаем "Мои тренировки" из БД
useEffect(() => {
  if (!dbUserId || dbUserLoading) return;

  if (!userAccess?.active) {
    setSavedWorkouts([]);
    return;
  }

  (async () => {
    try {
      const list = await apiListWorkouts();
      setSavedWorkouts(list);
    } catch (e) {
      console.error("load workouts error:", e);
    }
  })();
}, [dbUserId, dbUserLoading, userAccess?.active]);
// ✅ Загружаем черновик тренировки из localStorage
useEffect(() => {
  (async () => {
    const d = await storageGet(DRAFT_WORKOUT_KEY, null);

    if (d && hasMeaningfulDraftWorkout(d)) {
      setDraftWorkout(d);

      // держим данные черновика в состоянии, чтобы кнопка "Сохранить" из предупреждения тоже работала
setTrainMode(d.trainMode === "cardio" ? "cardio" : "strength");
setSelectedMuscles(d.selectedMuscles || []);
setWorkoutTitle(String(d.workoutTitle || d.title || "").trim());
setExercises(d.exercises || createInitialExercises());
      setCardioItems(d.cardioItems || d.cardio || []);
      setWorkoutTimer(normalizeWorkoutTimer(d.workoutTimer));
      setTimerNow(Date.now());
    } else {
      setDraftWorkout(null);
    }
  })();
}, []);


// ✅ Автосохраняем черновик, пока пользователь в тренировке
useEffect(() => {
  if (trainScreen !== "session") return;

const d = buildCurrentWorkoutDraft();

storageSet(DRAFT_WORKOUT_KEY, d);
setDraftWorkout(d);
}, [trainScreen, trainMode, selectedMuscles, workoutTitle, exercises, cardioItems, workoutTimer]);


// ✅ В 00:00 по локальному времени переключаем день и сбрасываем дневные показатели
useEffect(() => {
  const now = new Date();
  const nextMidnight = new Date(now);
  nextMidnight.setHours(24, 0, 0, 0);

  const ms = nextMidnight.getTime() - now.getTime();

  const t = setTimeout(() => {
    const newDay = getLocalDayKey();
    debugLog("[day] midnight ->", newDay);

        // Если в рационе был открыт сегодняшний день,
    // после полуночи переводим его на новый сегодняшний день.
    // Если пользователь специально редактировал старый день — не выбрасываем его.
    setRationDayKey((currentRationDay) =>
      currentRationDay === dayKey
        ? newDay
        : currentRationDay
    );

    setDayKey(newDay);

    // сброс дневных показателей в UI
    setSteps(null);
    setCupsCount(0);
    setSelectedEmotion(null);
    setSleepHours(null);
  }, ms + 500); // +0.5с чтобы гарантированно после 00:00

  return () => clearTimeout(t);
}, [dayKey]);




// == Автосохранение при изменениях ==
useEffect(() => {
  if (!weightStorageLoaded) return;

  storageSet(k("weight"), weight);
}, [weight, weightStorageLoaded]);

useEffect(() => {
  storageSet(k("cupsCount"), cupsCount);
}, [cupsCount]);

useEffect(() => {
  storageSet(k("sleepHours"), sleepHours);
}, [sleepHours]);



  // Автоподсказка по редактированию тренировки — первый заход на экран сессии
  useEffect(() => {
    if (trainScreen !== "session") return;
    try {
      const key = "fitmeup:trainSessionInfoShown";
      const already =
        typeof window !== "undefined" && window.localStorage
          ? window.localStorage.getItem(key)
          : null;
      if (!already) {
        setShowTrainInfoHint(true);
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(key, "1");
        }
      }
    } catch {}
  }, [trainScreen]);


const openWeightModal = () => setWeightModalOpen(true);
const closeWeightModal = () => setWeightModalOpen(false);

const submitWeight = ({ current, goal, baseline }) => {
  const nextCurrent = Number(current);
  const nextGoal = Number(goal);

  const submittedBaseline = Number(baseline);
  const previousBaseline = Number(weight?.baseline);

  const nextBaseline =
    Number.isFinite(submittedBaseline) &&
    submittedBaseline > 0
      ? submittedBaseline
      : Number.isFinite(previousBaseline) &&
          previousBaseline > 0
        ? previousBaseline
        : nextCurrent;

  const next = {
    current: nextCurrent,
    goal: nextGoal,
    baseline: nextBaseline,
  };

  setWeight((prev) => {
    logEvent("weight:update", {
      prev,
      next,
    });

    return next;
  });

  // Текущий вес сохраняется в day_stats,
  // стартовый и желаемый — отдельно в users.
  autoSaveDay({
    weight: nextCurrent,
    startWeight: nextBaseline,
    goalWeight: nextGoal,
  });
};

const openSleepModal = () => setSleepModalOpen(true);
const closeSleepModal = () => setSleepModalOpen(false);

const submitSleep = (hours) => {
  setSleepHours(hours);
  logEvent("sleep:update", { hours });
  autoSaveDay({ sleep: hours });
};


const fmt = (n) => {
  const value = String(n);

  return i18n.resolvedLanguage === "ru"
    ? value.replace(".", ",")
    : value.replace(",", ".");
};
const formatSleepLabel = (h) => {
  if (h == null) return "";

  return t("food.sleep.hours", {
    value: fmt(h),
  });
};


  // ⚠️ В Telegram WebView window.alert часто даёт «чёрный экран»/зависание.
// Вместо alert — просто логируем (или можно сделать noop).
const click = (id) => () => {
  try { logEvent("ui:click", { id }); } catch {}
};

  const toggleTab = () => {
  setTab(prev => {
    const next = prev === "food" ? "train" : "food";
    logEvent('ui:toggleTab', { from: prev, to: next });  // ← ДОБАВЛЕНО
    return next;
  });
};

// Стартовый набор упражнений для сессии
function createInitialExercises() {
  return Array.from({ length: 4 }).map((_, idx) => ({
    id: `ex-${idx + 1}`,
    name: `${idx + 1} упражнение`,
    sets: [
      { id: 1, label: "Подход 1", weight: null, reps: null },
      { id: 2, label: "Подход 2", weight: null, reps: null },
      { id: 3, label: "Подход 3", weight: null, reps: null },
    ],
  }));
}

// Открыть/закрыть экран добавок
const openSupplements = () => {
  logEvent("supp:open", {});
  setTrainScreen("supplements");
};


// Логика добавок (создание/изменение/удаление)
const addSupplementRow = () => {
  const trimmed = suppName.trim();
  const per = parseInt(suppPerDay, 10) || 0;
  if (!trimmed || per <= 0) return;

  // если юзер ещё не определён — просто не даём добавить (чтобы не было кривых id)
  if (!dbUserId) {
    debugWarn("No dbUserId yet, cannot create supplement");
    return;
  }

  (async () => {
    try {
      const created = await apiCreateSupplement({ name: trimmed, perDay: per });
      setSupplements((prev) => [...prev, created]);

      logEvent("supp:add", { name: trimmed, perDay: per });
      setSuppName("");
      setSuppPerDay("2");
    } catch (e) {
      console.error("add supplement error:", e);
    }
  })();
};


const setSupplementTaken = (id, newTaken) => {
  const s = supplements.find((x) => x.id === id);
  const perDay = s?.perDay || 0;

  const next = Math.max(0, Math.min(perDay, newTaken));

  setSuppTaken((prev) => {
    const from = Number(prev?.[id] || 0);
    logEvent("supp:taken", { id, from, to: next, day: dayKey });
    return { ...(prev || {}), [id]: next };
  });
  if (!dbUserLoading && dbUserId) {
  apiSaveSupplementDay(dbUserId, {
    day: dayKey,
    supplementId: id,
    taken: next,
  }).catch((e) => console.error("save supplement day error", e));
}

};


const deleteSupplement = async (id, name) => {
  const title =
    String(
      name ||
        t("body.supplements.deleteFallbackName")
    ).trim() ||
    t("body.supplements.deleteFallbackName");

  const ok = await safeConfirm(
    t("body.supplements.deleteConfirm", {
      name: title,
    }),
    t("body.supplements.deleteTitle")
  );

  if (!ok) return;

  setSupplements((prev) => prev.filter((s) => s.id !== id));

  setSuppTaken((prev) => {
    const next = { ...(prev || {}) };
    delete next[id];
    return next;
  });

  logEvent("supp:remove", { id, name });

  if (dbUserId) {
    apiDeleteSupplement(id).catch((e) =>
      console.error("delete supplement db error:", e)
    );
  }
};

// Краткое резюме по добавкам для маленькой карточки
const supplementsSummary = (() => {
  if (!supplements.length) return null;
  const total = supplements.reduce(
    (acc, s) => {
      const per = s.perDay || 0;
      const taken = getTaken(s.id);
      acc.planned += per;
      acc.done += Math.min(per, taken);
      return acc;
    },
    { planned: 0, done: 0 }
  );
  return total.planned > 0 ? total : null;
})();

// Закрывать подсказку кликом вне, но не закрывать при клике по i и по самой подсказке
useEffect(() => {
  if (!showEmotionHint) return;
  const onDocClick = (e) => {
    const t = e.target;
    if (infoRef.current?.contains(t) || popRef.current?.contains(t)) return;
    setShowEmotionHint(false);
  };
  document.addEventListener('click', onDocClick, { capture: true });
  return () => document.removeEventListener('click', onDocClick, { capture: true });
}, [showEmotionHint]);
// === Sleep widget hint ===
const [showSleepHint, setShowSleepHint] = useState(false);
const sleepInfoRef = useRef(null);
const sleepPopRef = useRef(null);

useEffect(() => {
  if (!showSleepHint) return;
  const onDocClick = (e) => {
    const t = e.target;
    if (sleepInfoRef.current?.contains(t) || sleepPopRef.current?.contains(t)) return;
    setShowSleepHint(false);
  };
  document.addEventListener("click", onDocClick, { capture: true });
  return () => document.removeEventListener("click", onDocClick, { capture: true });
}, [showSleepHint]);

const scrollAppRootToTop = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const run = () => {
    const root = document.querySelector("[data-app-scroll-root='1']");

    try {
      if (root && typeof root.scrollTo === "function") {
        root.scrollTo({ top: 0, left: 0, behavior: "auto" });
        return;
      }

      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      if (root) root.scrollTop = 0;
      else window.scrollTo(0, 0);
    }
  };

  run();
  requestAnimationFrame(run);
  window.setTimeout(run, 80);
};
const bottomNavActive = (() => {
  const workoutIsOpen =
    trainScreen === "session"
      ? hasMeaningfulDraftWorkout(buildCurrentWorkoutDraft())
      : hasMeaningfulDraftWorkout(draftWorkout);

  if (workoutIsOpen) {
    return "workout";
  }

  if (tab === "food") {
    return foodView === "details" ? "ration" : "food";
  }

  if (tab === "train") {
    if (
      trainScreen === "trainGroups" ||
      trainScreen === "session" ||
      trainScreen === "myWorkouts"
    ) {
      return "workout";
    }

    return "body";
  }

  return "food";
})();

const resetRationToToday = () => {
  const todayKey = getLocalDayKey();

  if (rationDayKey === todayKey) return;

  setMeals(makeEmptyMeals());
  setRationDayKey(todayKey);
};

const goBottomFood = () => {
  logEvent("bottomNav:food", {});

  resetRationToToday();

  // После изменений прошлого дня обновляем кольца календаря.
  setFoodMonthReloadTick((x) => x + 1);

  setTab("food");
  setFoodView("overview");
  scrollAppRootToTop();
};

const goBottomRation = () => {
  logEvent("bottomNav:ration", {});

  resetRationToToday();

  setTab("food");
  setFoodView("details");
  scrollAppRootToTop();
};

const goBottomBody = () => {
  logEvent("bottomNav:body", {});
  setTab("train");
  setFoodView("overview");
  setTrainScreen("main");
  scrollAppRootToTop();
};

const goBottomWorkout = () => {
  logEvent("bottomNav:workout", {});
  scrollAppRootToTop();

  if (dbUserLoading) {
    safeAlert(
      "Проверяем доступ. Попробуй ещё раз через пару секунд.",
      "Тренировка"
    );
    return;
  }

  if (!canUsePremiumFeatures) {
    setTab("train");
    setFoodView("overview");
    openPremiumPaywall("bottom_nav_workout");
    return;
  }

  setTab("train");
  setFoodView("overview");

  if (trainScreen === "session") {
    return;
  }

  if (hasMeaningfulDraftWorkout(draftWorkout)) {
    resumeOpenWorkout();
    return;
  }

  setPickerMuscles([]);
  setTrainScreen("trainGroups");
};

const bottomNavItems = [
  {
    key: "food",
    label: t("nav.food"),
    icon: "food",
    onClick: goBottomFood,
  },
  {
    key: "ration",
    label: t("nav.ration"),
    icon: "ration",
    onClick: goBottomRation,
  },
  {
    key: "body",
    label: t("nav.body"),
    icon: "body",
    onClick: goBottomBody,
  },
  {
    key: "workout",
    label: t("nav.workout"),
    icon: "workout",
    onClick: goBottomWorkout,
  },
];
  return (
    <>
          <style>{`
        /* iOS Telegram WebView: backdrop-filter вызывает "мигание/перерисовку" при setState */
        [data-no-blur="1"] [class*="backdrop-blur"] {
          -webkit-backdrop-filter: none !important;
          backdrop-filter: none !important;
        }

        /* иногда помогает ещё убрать тяжёлые эффекты на этих же стеклянных блоках */
        [data-no-blur="1"] [class*="backdrop-blur"] {
          transform: translateZ(0);
        }
          /* Убирает "резинку"/overscroll (на Android работает отлично; на iOS эффект может быть частичный) */
html, body {
  overscroll-behavior-y: none;
}
/* Убираем “полоску” скролла справа */
* {
  scrollbar-width: none;         /* Firefox */
  -ms-overflow-style: none;      /* старый Edge */
}
*::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}

/* На всякий: чтобы не появлялась горизонтальная */
html, body {
  overflow-x: hidden;
  margin: 0;
  min-height: var(--app-height, 100vh);
  background: #020817;
}

#root {
  min-height: var(--app-height, 100vh);
  background: #020817;
}

      `}</style>

      <div
        aria-hidden="true"
        data-fitmeup-fullscreen-bg="1"
        style={FULLSCREEN_APP_BG_STYLE}
      />

      {/* ВНЕШНИЙ контейнер: контент поверх общего фона */}
      <div
      data-no-blur={noBackdropBlur ? "1" : "0"}
className="relative z-[1] w-full max-w-[100svw] box-border overflow-x-hidden flex justify-center px-0"
style={{
  fontFamily: "Verdana, system-ui, sans-serif",
  background: "transparent",
  color: "#f9fafb",
  height: "var(--app-height, 100vh)",
  overflowY: "hidden",
  boxSizing: "border-box",
  paddingTop: hasTgAuth()
    ? 0
    : "env(safe-area-inset-top, 0px)",
  paddingBottom: 0,
}}
>


        {/* SIZER: задаёт РЕАЛЬНЫЕ габариты под результат масштабирования */}
<div
  data-app-scroll-root="1"
  className="mx-auto w-full overflow-x-hidden px-4"
  style={{
    width: "100%",
    maxWidth: "none",
    height: "100%",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box",
    paddingTop: "18px",
    paddingBottom: "calc(92px + env(safe-area-inset-bottom, 0px))",
    scrollPaddingBottom: "calc(100px + env(safe-area-inset-bottom, 0px))",
  }}
>

  {/* SCALED: визуально уменьшаем, но НЕ даём расползаться по ширине */}
<div
  className="origin-top mx-auto box-border"
  style={{
    width: "100%",
    maxWidth: "388px",
    transform: "none",
    transformOrigin: "top center",
    transition: "none",
  }}


  >





                {tab === "train" ? (
        trainScreen === "main" ? (
(() => {
const targetSteps = 12000;
const stepsNumber = Number(steps);
const hasStepsValue = Number.isFinite(stepsNumber) && stepsNumber > 0;

const stepsValue = hasStepsValue ? stepsNumber : 0;
const stepsProgress = hasStepsValue
  ? Math.max(0, Math.min(100, Math.round((stepsValue / targetSteps) * 100)))
  : null;

const bodyLocale =
  i18n.resolvedLanguage === "ru"
    ? "ru-RU"
    : "en-GB";

const allBodyHistory = Object.values(bodyHistory || {})
  .flat()
  .filter((item) => item && item.date);

allBodyHistory.sort(
  (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
);

const lastBody = allBodyHistory[allBodyHistory.length - 1] || null;

const lastBodyDate = lastBody
  ? new Date(lastBody.date).toLocaleDateString(bodyLocale, {
      day: "numeric",
      month: "long",
    })
  : "";

const lastBodyLabel = lastBody
  ? t("body.overview.lastMeasurement", {
      date: lastBodyDate,
    })
  : t("body.overview.noMeasurements");

const formCards = [
  {
    key: "measurements",
    title: t("body.overview.measurements"),
    value: "90 · 60 · 90",
    sub: lastBodyLabel,
    icon: measurementsImg,
    onClick: () => setTrainScreen("body"),
  },
  {
    key: "steps",
    title: t("body.overview.steps"),
    value: hasStepsValue
      ? t("body.overview.stepsValue", {
          value: stepsValue.toLocaleString(bodyLocale),
        })
      : t("body.overview.stepsGoal"),
    sub: hasStepsValue
      ? t("body.overview.stepsGoalWithColon")
      : t("body.overview.addStepsToday"),
    icon: stepsImg,
    progress: hasStepsValue ? stepsProgress : null,
    onClick: () => setStepsModalOpen(true),
  },
  {
    key: "supplements",
    title: t("body.overview.supplements"),
    value: supplementsSummary
      ? t("body.overview.supplementsProgress", {
          done: supplementsSummary.done,
          planned: supplementsSummary.planned,
        })
      : t("body.overview.supplementsEmpty"),
    sub: t("body.overview.supplementsSubtitle"),
    icon: supplementsImg,
    onClick: openSupplements,
  },
];

  return (
    <div className="mx-auto w-full max-w-[420px] grid gap-4">
      <div className="grid gap-3">
{formCards.map((card) => (
  <button
    key={card.key}
    type="button"
    onClick={card.onClick}
    className="group relative isolate flex min-h-[92px] w-full items-center gap-3.5 overflow-hidden rounded-[24px] border border-[#18d7bd]/30 bg-[#051723]/78 px-3.5 py-3 text-left shadow-[0_0_0_1px_rgba(24,215,189,0.025),0_14px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl transition active:scale-[0.99] outline-none focus:outline-none focus:ring-0"
    style={{ WebkitTapHighlightColor: "transparent" }}
  >
<div
  className="pointer-events-none absolute inset-0 rounded-[24px] opacity-70"
  style={{
    background:
      "radial-gradient(circle at 0% 0%, rgba(45,212,191,0.10), transparent 42%)",
  }}
/>

<div className="relative grid h-[58px] w-[58px] shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl">
  <img
    src={card.icon}
    alt=""
    aria-hidden="true"
    draggable="false"
    loading="eager"
    decoding="async"
    className="h-[44px] w-[44px] object-contain"
  />
</div>

            <div className="relative min-w-0 flex-1">
<div className="text-[16px] font-extrabold leading-tight tracking-[0.01em] text-white/92">
                {card.title}
              </div>
              <div className="mt-1.5 text-[14px] font-semibold leading-none tracking-[0.01em] text-[#35F3DF]">
                {card.value}
              </div>
              <div className="mt-2 text-[12px] font-medium leading-none text-slate-300/62">
                {card.sub}
              </div>

              {card.progress != null ? (
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-slate-800/92">
                    <div
                      className="h-full rounded-full bg-[#35F3DF] shadow-[0_0_14px_rgba(53,243,223,0.30)]"
                      style={{ width: `${card.progress}%` }}
                    />
                  </div>
                  <div className="min-w-[42px] text-right text-[13px] font-semibold text-[#35F3DF]">
                    {card.progress}%
                  </div>
                </div>
              ) : null}
            </div>

<div className="relative shrink-0 text-[31px] font-light leading-none text-white/72 transition group-active:translate-x-0.5">
              ›
            </div>
          </button>
        ))}
      </div>

      <div
        className="box-border w-full min-h-[360px] rounded-[30px] border border-[#243342]/80 bg-[#07111e]/92 p-4 text-left shadow-[0_18px_52px_rgba(0,0,0,0.36),inset_0_1px_0_rgba(255,255,255,0.055)]"
        onClick={click("Календарь активности")}
      >
        {(() => {
          const base = new Date();
          base.setDate(1);
          base.setMonth(base.getMonth() + activityCalOffset);
const { year, monthName, cells } = buildCalendar(
  base,
  true,
  bodyLocale
);

const week = [
  t("body.overview.weekdays.mon"),
  t("body.overview.weekdays.tue"),
  t("body.overview.weekdays.wed"),
  t("body.overview.weekdays.thu"),
  t("body.overview.weekdays.fri"),
  t("body.overview.weekdays.sat"),
  t("body.overview.weekdays.sun"),
];

          return (
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between gap-3">
                <div>
<div className="text-[17px] font-extrabold tracking-[0.01em] text-white/92">
  {t("body.overview.activity")}
</div>
                  <div className="mt-2 text-[14px] font-medium text-slate-300/62 capitalize">
                    {monthName} {year}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStepsReportPeriodOpen(true);
                    }}
                    className="inline-flex h-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.055] px-5 text-[13px] font-semibold tracking-wide text-[#35F3DF] shadow-[inset_0_1px_0_rgba(255,255,255,0.055)] active:scale-[0.98]"
>
  {t("food.macro.report")}
</button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivityCalOffset((v) => v - 1);
                    }}
                    className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.055] text-[22px] font-light text-white/82 shadow-[inset_0_1px_0_rgba(255,255,255,0.055)] active:scale-[0.97]"
                  >
                    ‹
                  </button>

                  {activityCalOffset < 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActivityCalOffset((v) => v + 1);
                      }}
                      className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.055] text-[22px] font-light text-white/82 shadow-[inset_0_1px_0_rgba(255,255,255,0.055)] active:scale-[0.97]"
                    >
                      ›
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-7 grid grid-cols-7 gap-x-2 gap-y-1 text-center text-[13px]">
                {week.map((d) => (
                  <div key={d} className="font-semibold text-slate-300/72">
                    {d}
                  </div>
                ))}
                <div className="col-span-7" />
              </div>

              <div className="mt-2 grid grid-cols-7 gap-x-2 gap-y-2 text-center text-[15px] tabular-nums text-white/86 flex-1">
                {cells.map((d, i) => {
                  if (!d) {
                    return (
                      <div key={i} className="py-1">
                        <div>&nbsp;</div>
                      </div>
                    );
                  }

                  const cellDate = new Date(base.getFullYear(), base.getMonth(), d);
const cellDayKey = getLocalDayKey(cellDate);
const workoutMarker = workoutDayMarkerMap.get(cellDayKey);
const hasWorkout = Boolean(workoutMarker);

                  return (
                    <div key={i} className="py-1">
                      <button
                        type="button"
                        onClick={() => hasWorkout && openWorkoutDay(cellDayKey)}
                        className={
                          "relative mx-auto flex h-7 w-7 items-center justify-center rounded-md " +
                          (hasWorkout ? "cursor-pointer hover:bg-white/10 active:scale-[0.98]" : "")
                        }
                      >
{hasWorkout && (
  <span
    className={
      "pointer-events-none absolute inset-0 flex items-center justify-center " +
      (workoutMarker === "cardio"
        ? "text-[23px] opacity-90 -translate-y-[4px]"
        : "text-[23px] opacity-70 -translate-y-[4px]")
    }
    style={
      workoutMarker === "cardio"
        ? {
            filter:
              "hue-rotate(165deg) saturate(0.9) brightness(0.86)",
          }
        : undefined
    }
  >
    🔥
  </span>
)}
                        <span className="relative z-10">{d}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      <button
        type="button"
        onPointerDown={rememberTapStart}
        onPointerUp={(e) => {
          runIfRealTap(e, () => setPremiumOpen(true), 450);
        }}
        onPointerCancel={clearTapStart}
        onPointerLeave={clearTapStart}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className="-mt-1 flex min-h-[54px] w-full items-center justify-center rounded-[24px] border border-[#18d7bd]/30 px-4 text-center backdrop-blur-xl active:scale-[0.985]"
        style={{
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          background:
            "radial-gradient(circle at 0% 0%, rgba(24,215,189,0.10), transparent 46%), linear-gradient(180deg, rgba(7,22,36,0.88), rgba(3,12,23,0.94))",
          boxShadow:
            "0 0 0 1px rgba(24,215,189,0.025), 0 14px 36px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.055)",
        }}
      >
<span className="text-[16px] font-extrabold leading-none tracking-[-0.035em] text-slate-50">
  {t("premium.entry")}
</span>
      </button>
    </div>
  );
})()
                   ) : trainScreen === "supplements" ? (
  <div className="mx-auto w-full max-w-[420px] grid gap-4">

    {/* Карточка только с заголовком и формой новой добавки */}
    <GlassCard solid className={cn("space-y-3 p-3.5", FOOD_COMPACT_CARD)}>
      {/* шапка */}
<div className="flex items-center justify-between gap-3">
  <div className="min-w-0 text-[20px] font-extrabold leading-none tracking-[0.01em] text-white">
    {t("body.overview.supplements")}
  </div>

  <AppCloseButton
    onClick={() => setTrainScreen("main")}
    className="h-9 w-9 text-[19px]"
    aria-label={t("actions.close")}
  />
</div>

      {/* Блок "Новая добавка" */}
      <div className="mt-1 rounded-[20px] border border-[#18d7bd]/20 bg-[#061723]/70 px-3 py-2">
        <button
          type="button"
          onClick={() => setSuppFormCollapsed((v) => !v)}
          className="flex w-full items-center justify-between gap-2"
        >
<div className="text-[14px] font-semibold tracking-wide opacity-90">
  {t("body.supplements.newSupplement")}
</div>
<div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition active:scale-[0.94]">
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    className={cn(
      "transition-transform duration-200",
      suppFormCollapsed ? "rotate-0" : "rotate-180"
    )}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</div>
        </button>

        {!suppFormCollapsed && (
          <div className="mt-3 space-y-3">
            <div className="relative grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-3 items-end">
            

              {/* Название */}
<div className="flex flex-col gap-1">
  <span className="text-[11px] opacity-75">
    {t("body.supplements.name")}
  </span>
  <input
    value={suppName}
    onChange={(e) => setSuppName(e.target.value)}
    placeholder={t("body.supplements.namePlaceholder")}
    className="h-9 rounded-2xl border border-white/25 bg-slate-900/40 px-3 text-[13px] text-slate-50 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-400/70 focus:border-emerald-300/70"
  />
</div>

              {/* Количество */}
<div className="flex flex-col gap-1 min-w-0">
  <span className="text-[11px] opacity-75">
    {t("body.supplements.quantity")}
  </span>
  <div className="flex items-center gap-2">
<div className="relative">
  <select
    value={suppPerDay}
    onChange={(e) => setSuppPerDay(e.target.value)}
    className="h-9 w-[54px] appearance-none rounded-2xl border border-[#18d7bd]/35 bg-[#061723]/90 pl-3 pr-6 text-center text-[13px] font-bold text-[#35F3DF] outline-none shadow-[0_0_14px_rgba(24,215,189,0.10)] focus:border-[#35F3DF]/70 focus:ring-2 focus:ring-cyan-300/25"
  >
    {[1, 2, 3, 4, 5].map((n) => (
      <option key={n} value={n} className="bg-[#06121c] text-slate-50">
        {n}
      </option>
    ))}
  </select>

  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#35F3DF]"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</div>
<span className="text-[11px] opacity-60 whitespace-nowrap pr-[2px]">
  {t("body.supplements.timesPerDayShort")}
</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                addSupplementRow();
              }}
              disabled={!suppName.trim()}
              className="w-full h-9 rounded-2xl bg-gradient-to-r from-emerald-400 to-cyan-300 text-[13px] font-semibold text-slate-900 shadow-[0_8px_24px_rgba(16,185,129,0.55)] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
>
  {t("body.supplements.add")}
</button>
          </div>
        )}
      </div>
    </GlassCard>

    {/* список добавок — отдельные виджеты под формой */}
    <div className="mt-2 space-y-2">
      {supplements.length === 0 ? (
<div className="text-xs opacity-70 text-center w-full flex justify-center">
  {t("body.supplements.empty")}
</div>
      ) : (
        supplements.map((s) => (
          <div
            key={s.id}
            className={cn("relative flex flex-col gap-2 px-3 py-2.5", FOOD_COMPACT_CARD)}
          >
            {/* крестик удалить */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteSupplement(s.id, s.name);
              }}
className="absolute right-2 top-2 grid h-7 w-7 shrink-0 place-items-center rounded-full border border-cyan-200/35 bg-cyan-300/[0.10] text-[14px] font-extrabold leading-none text-cyan-50 shadow-[0_0_16px_rgba(45,212,191,0.13),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl transition active:scale-[0.96]"
aria-label={t("body.supplements.deleteAria")}
            >
              ✕
            </button>

            <div className="flex items-center justify-between gap-2 pr-7">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate">
                  {s.name}
                </div>
<div className="text-[11px] opacity-70">
  {t("body.supplements.perDay", {
    count: s.perDay || 0,
  })}
</div>
              </div>
              <div className="text-right text-[11px] opacity-80">
<div>
  {t("body.supplements.today", {
    taken: getTaken(s.id),
    planned: s.perDay || 0,
  })}
</div>
{s.perDay - getTaken(s.id) > 0 && (
  <div className="opacity-70">
    {t("body.supplements.remaining", {
      count: s.perDay - getTaken(s.id),
    })}
  </div>
)}

              </div>
            </div>

            {/* кнопки приёмов */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSupplementTaken(s.id, getTaken(s.id) - 1);
                }}
                disabled={getTaken(s.id) <= 0}
className="h-8 w-8 grid place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-sm font-bold text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                −
              </button>

              <div className="flex-1 flex gap-1">
                {Array.from({ length: s.perDay || 0 }).map((_, idx) => {
                  const filled = idx < getTaken(s.id);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const current = getTaken(s.id);
                        const next = idx < current ? idx : idx + 1;
                        setSupplementTaken(s.id, next);
                      }}
                      className={cn(
                        "flex-1 h-7 rounded-2xl border text-[11px] font-semibold transition",
                        filled
                          ? "border-emerald-300 bg-gradient-to-r from-emerald-400/80 to-cyan-300/80 text-slate-900 shadow-[0_0_18px_rgba(45,212,191,0.8)]"
                          : "border-white/25 bg-slate-900/40 text-slate-100/80"
                      )}
                    >
                      #{idx + 1}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSupplementTaken(s.id, getTaken(s.id) + 1);
                }}
                disabled={getTaken(s.id) >= (s.perDay || 0)}
className="h-8 w-8 grid place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-sm font-bold text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                +
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
) : trainScreen === "trainGroups" ? (
  <div
    className="mx-auto w-full max-w-[430px] px-2"
    style={{
paddingBottom: "80px",
}}
  >
    {/* Верх блока */}
    <div className="mb-2 pt-0">
      <div className="flex items-center justify-between gap-3">
        <div className="-ml-1 flex min-w-0 flex-1 items-center gap-2">
<h1 className="whitespace-nowrap text-[26px] font-extrabold leading-none tracking-[0.01em] text-white drop-shadow-[0_8px_22px_rgba(0,0,0,0.42)]">
  {t("workout.picker.title")}
</h1>

          <div className="h-px min-w-5 flex-1 bg-gradient-to-r from-[#22d3bd]/35 to-transparent" />
        </div>


      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
<div className="min-w-0 text-[13px] font-normal tracking-[0.01em] text-slate-200/65">
  {t("workout.picker.subtitle")}
</div>

        <button
          type="button"
          onClick={() => setTrainScreen("myWorkouts")}
          className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full border border-[#1f5c57]/70 bg-[#06131d]/95 px-2.5 text-[11.5px] font-medium text-white shadow-[0_0_18px_rgba(20,184,166,0.10)] active:scale-[0.97]"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            className="text-[#25d8b8]"
            aria-hidden="true"
          >
            <path
              d="M7 3v3M17 3v3M5 9h14M6.5 5h11A2.5 2.5 0 0 1 20 7.5v10A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-10A2.5 2.5 0 0 1 6.5 5Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 14h6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>

          <span>{t("workout.picker.myWorkouts")}</span>
        </button>
      </div>
    </div>

    {/* Карточки мышц */}
    <div className="grid grid-cols-2 gap-x-2 gap-y-2.5">
      {ALL_MUSCLE_GROUPS.map((g) => {
        const active = pickerMuscles.includes(g.key);
        const isBiceps =
          g.key === "biceps" || String(g.label).toLowerCase().includes("бицепс");

        return (
          <button
            key={g.key}
            type="button"
            onClick={() => toggleMuscleGroup(g.key)}
            className={cn(
              "relative h-[118px] overflow-hidden rounded-[16px] border text-left transition-all duration-200 active:scale-[0.985]",
              "bg-[#06121c] shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]",
              active
                ? "border-[#25d8b8]/95 shadow-[0_0_18px_rgba(37,216,184,0.18),inset_0_0_24px_rgba(37,216,184,0.07)]"
                : "border-[#2b4b55]/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]"
            )}
          >
<img
  src={g.img}
  alt={t(`workout.muscles.${g.key}`)}
  className={cn(
                "absolute inset-0 h-full w-full object-cover transition duration-200",
                isBiceps ? "scale-[1.02]" : "scale-[1.0]",
                active
                  ? "brightness-[1.04] contrast-[1.1] saturate-[1.08]"
                  : "brightness-[0.86] contrast-[1.06] saturate-[1.0] opacity-100"
              )}
              style={
                isBiceps
                  ? { objectPosition: "22% center" }
                  : { objectPosition: "center" }
              }
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(3,10,18,0.58) 0%, rgba(3,10,18,0.20) 52%, rgba(3,10,18,0.02) 100%)",
              }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-[#020915]/52 via-transparent to-transparent" />

<div className="absolute bottom-3 left-3 text-[14px] font-medium tracking-[0.01em] text-white/92 drop-shadow-[0_2px_9px_rgba(0,0,0,0.9)]">
  {t(`workout.muscles.${g.key}`)}
</div>

            <div
              className={cn(
                "absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border text-[15px] transition",
                active
                  ? "border-[#26e2bf] bg-[#05151d]/72 text-white shadow-[0_0_16px_rgba(38,226,191,0.34)]"
                  : "border-transparent bg-transparent text-transparent"
              )}
            >
              ✓
            </div>
          </button>
        );
      })}
    </div>

    {/* Кардио */}
    <button
      type="button"
onClick={() => {
  if (warnIfWorkoutAlreadyOpen()) return;

  logEvent("trainGroups:cardio");
  openCardioModal();
}}
      className="mt-3 flex h-[72px] w-full items-center justify-between rounded-[18px] border border-[#213b46]/85 bg-[#06121c] px-5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] active:scale-[0.985]"
    >
      <div className="flex items-center gap-4">
        <div className="grid h-11 w-11 place-items-center rounded-full border border-[#1b695e]/80 bg-[#0a2a29] text-[#31e0bf] shadow-[0_0_18px_rgba(37,216,184,0.13)]">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 12h3l2-5 4 10 2-5h5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

<div>
  <div className="text-[17px] font-semibold leading-tight text-white">
    {t("workout.picker.cardio")}
  </div>

  <div className="mt-1 text-[13px] font-normal leading-snug text-slate-300/68">
    {t("workout.picker.cardioSubtitle")}
  </div>
</div>
      </div>

      <div className="text-[34px] leading-none text-slate-300/72">›</div>
    </button>

    {cardioItems.map((c) => (
      <GlassCard key={c.id} className="mt-3 cursor-default p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            <div className="min-w-0 truncate text-[13px] font-semibold">
              {c.title}
            </div>

<div className="shrink-0 text-[12px] text-white/70 tabular-nums">
  {c.minutes
    ? t("workout.picker.minutesShort", {
        value: c.minutes,
      })
    : "—"}
</div>

            {c.comment ? (
              <div className="min-w-0 truncate text-[12px] text-white/55">
                {c.comment}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() =>
              setCardioItems((prev) => prev.filter((x) => x.id !== c.id))
            }
            className="grid h-8 w-8 place-items-center rounded-full border border-white/20 bg-white/10 text-white/80 active:scale-[0.97]"
            title={t("workout.picker.delete")}
          >
            ×
          </button>
        </div>
      </GlassCard>
    ))}

    {/* Нижняя панель */}
<div
  className="fixed inset-x-0 z-[70] mx-auto w-[92%] max-w-[390px]"
  style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
>
      <div className="rounded-[17px] border border-[#213b46] bg-[#06121c] px-2.5 py-2 shadow-[0_-10px_28px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.04)]">
<div
  className={cn(
    "grid items-center gap-2",
    showResumeWorkoutInGroups
      ? "grid-cols-[minmax(0,1fr)_138px]"
      : "grid-cols-[68px_minmax(0,1fr)_138px]"
  )}
>
  {showResumeWorkoutInGroups ? (
    <div className="min-w-0 px-1">
<div className="text-[11.5px] font-semibold leading-tight text-cyan-100/78">
  {t("workout.picker.workoutInProgress")}
</div>
      <div className="mt-0.5 truncate text-[14px] font-extrabold leading-tight text-white">
        {activeWorkoutTitleInGroups}
      </div>
    </div>
  ) : (
    <>
      <div className="min-w-0">
<div className="text-[11.5px] font-normal leading-tight text-slate-300/72">
  {t("workout.picker.selected")}
</div>

<div className="flex items-end gap-1">
  <span className="text-[28px] font-bold leading-none text-[#22d3bd] drop-shadow-[0_0_12px_rgba(34,211,189,0.24)]">
    {pickerMuscles.length}
  </span>

  <span className="pb-0.5 text-[11.5px] text-slate-300/72">
    {t("workout.picker.groupNoun", {
      count: pickerMuscles.length,
    })}
  </span>
</div>
      </div>

      <div className="flex min-w-0 items-center overflow-hidden pl-1">
        {pickerMuscles.slice(0, 2).map((key) => {
          const g = ALL_MUSCLE_GROUPS.find((x) => x.key === key);

          if (!g) return null;

          return (
            <div
              key={key}
              className="-ml-1.5 first:ml-0 h-6 w-6 shrink-0 overflow-hidden rounded-full border border-[#25d8b8]/70 bg-slate-950 shadow-[0_0_10px_rgba(37,216,184,0.14)]"
            >
<img
  src={g.img}
  alt={t(`workout.muscles.${g.key}`)}
  className="h-full w-full object-cover brightness-[0.92] contrast-[1.08] saturate-[1.02]"
/>
            </div>
          );
        })}

        {pickerMuscles.length > 2 ? (
          <div className="-ml-1.5 grid h-6 min-w-6 shrink-0 place-items-center rounded-full border border-[#25d8b8]/70 bg-[#07131d] px-1.5 text-[9px] font-semibold text-white shadow-[0_0_10px_rgba(37,216,184,0.14)]">
            +{pickerMuscles.length - 2}
          </div>
        ) : null}
      </div>
    </>
  )}

  <button
    type="button"
    onClick={() => {
      if (showResumeWorkoutInGroups) {
        resumeOpenWorkout();
        return;
      }

      if (warnIfWorkoutAlreadyOpen()) return;

      setTrainMode("strength");
      setWorkoutTitle("");
      setCardioItems([]);
      setSelectedMuscles(pickerMuscles);

      logEvent("trainGroups:confirm", {
        muscles: pickerMuscles,
        labels: pickerMuscles.map(getMuscleLabel),
      });

      setExercises(createInitialExercises());
      resetWorkoutTimer();
      setTrainScreen("session");
      setPickerMuscles([]);
    }}
    disabled={!showResumeWorkoutInGroups && pickerMuscles.length === 0}
    className="flex h-10 w-full items-center justify-center rounded-[13px] bg-gradient-to-r from-[#24d7b5] to-[#117e88] px-2 text-[13px] font-semibold text-white shadow-[0_8px_20px_rgba(36,215,181,0.22),inset_0_1px_0_rgba(255,255,255,0.15)] transition active:scale-[0.97] disabled:opacity-45 disabled:shadow-none"
  >
    <span className="whitespace-nowrap">
  {t("workout.picker.continue")}
</span>
  </button>
</div>
      </div>
    </div>
  </div>
) : trainScreen === "myWorkouts" ? (
(() => {
const allWorkoutFilterLabel = "Все";

const workoutLocale =
  i18n.resolvedLanguage === "ru"
    ? "ru-RU"
    : "en-GB";

const getLocalizedWorkoutDisplayTitle = (workout) => {
  const customTitle = getWorkoutCustomTitle(workout);

  if (customTitle) {
    return customTitle;
  }

  if (workout?.mode === "cardio") {
    return t("workout.myWorkouts.cardio");
  }

  const groups = getWorkoutMuscleBadges(workout).filter(
    (group, index, arr) =>
      group &&
      arr.findIndex((item) => item?.key === group.key) === index
  );

  const names = groups
    .map((group) => t(`workout.muscles.${group.key}`))
    .filter(Boolean);

  if (names.length) {
    return names.join(" • ");
  }

  return getWorkoutDisplayTitle(workout);
};

const allSavedWorkouts = savedWorkouts || [];

const workoutTitleCounts = new Map();

allSavedWorkouts.forEach((item) => {
  const title = getLocalizedWorkoutDisplayTitle(item);
  if (!title) return;

  workoutTitleCounts.set(title, (workoutTitleCounts.get(title) || 0) + 1);
});

const workoutTitleFilters = [
  {
    name: allWorkoutFilterLabel,
    count: allSavedWorkouts.length,
  },
  ...Array.from(workoutTitleCounts.entries())
    .sort(
  (a, b) =>
    b[1] - a[1] ||
    a[0].localeCompare(b[0], workoutLocale)
)
    .map(([name, count]) => ({
      name,
      count,
    })),
];

const workoutTitleFilterNames = workoutTitleFilters.map((item) => item.name);

const activeWorkoutTitleFilter = workoutTitleFilterNames.includes(
  selectedWorkoutTitleFilter
)
  ? selectedWorkoutTitleFilter
  : allWorkoutFilterLabel;

    const visibleSavedWorkouts =
      activeWorkoutTitleFilter === allWorkoutFilterLabel
        ? allSavedWorkouts
: allSavedWorkouts.filter(
    (item) =>
      getLocalizedWorkoutDisplayTitle(item) ===
      activeWorkoutTitleFilter
  );

    return (
      <div
        className="mx-auto w-full max-w-none px-1.5 pt-2"
        style={{
paddingBottom: "0px",
}}
      >
        <div className="mb-3">
          <div className="flex items-center justify-between gap-3">
<div className="min-w-0 truncate text-[20px] font-extrabold leading-none tracking-[0.07em] text-slate-50">
  {t("workout.myWorkouts.title")}
</div>

<AppCloseButton
  onClick={() => {
    persistCurrentWorkoutDraft();
    setPickerMuscles([]);
    setTrainScreen("trainGroups");
  }}
  className="shrink-0"
  aria-label={t("actions.close")}
/>
          </div>

<div className="mt-2.5 min-w-0 overflow-x-auto pt-1.5 pb-1.5">
  <div className="flex w-max min-w-full items-center gap-1.5 pr-2">
{workoutTitleFilters.map((filter) => {
const filterName = filter.name;
const filterCount = filter.count;

const filterDisplayName =
  filterName === allWorkoutFilterLabel
    ? t("workout.myWorkouts.all")
    : filterName;

const isFilterActive = activeWorkoutTitleFilter === filterName;

  return (
    <button
      key={filterName}
      type="button"
      onClick={() => setSelectedWorkoutTitleFilter(filterName)}
title={`${filterDisplayName} · ${t(
  "workout.myWorkouts.workoutCount",
  {
    count: filterCount,
  }
)}`}
      className={cn(
        "relative h-8 max-w-[132px] shrink-0 rounded-full border px-3 pr-5 text-[12px] font-semibold transition active:scale-[0.97]",
        isFilterActive
          ? "border-[#17dec3]/70 bg-[#0bb99f]/20 text-slate-50 shadow-[0_0_18px_rgba(24,215,189,0.18)]"
          : "border-white/10 bg-[#06131d]/58 text-slate-300/80"
      )}
      aria-pressed={isFilterActive}
    >
      <span className="block max-w-[86px] truncate">
        {filterName === allWorkoutFilterLabel
  ? t("workout.myWorkouts.all")
  : getOneWordWorkoutFilterLabel(
      filterDisplayName,
      allWorkoutFilterLabel
    )}
      </span>

      <span
        className={cn(
          "absolute -right-1 -top-1 grid h-[15px] min-w-[15px] place-items-center rounded-full border px-[4px] text-[8.5px] font-extrabold leading-none shadow-[0_5px_12px_rgba(0,0,0,0.28)]",
          isFilterActive
            ? "border-[#8fffee]/55 bg-[#19dfc4] text-[#03211f]"
            : "border-[#18d7bd]/35 bg-[#08212b] text-cyan-50"
        )}
      >
        {filterCount}
      </span>
    </button>
  );
})}
            </div>
          </div>
        </div>

        <div className="grid gap-2.5">
          {visibleSavedWorkouts.map((w, workoutIndex) => {
            const workoutKey = String(w?.id ?? `${w?.date || "workout"}-${workoutIndex}`);
            const workoutFullName = getLocalizedWorkoutDisplayTitle(w);
            const exercisesList = Array.isArray(w?.exercises) ? w.exercises : [];
            const st = computeWorkoutStats(w);
            const cs = computeCardioSummary(w);

const isCardioWorkout =
  (w?.mode === "cardio" ||
    /кардио/i.test(String(w?.musclesLabel || workoutFullName || ""))) &&
  cs.itemsCount > 0;

const cardioDurationSeconds = Math.round(Number(cs.totalMinutes || 0) * 60);

const workoutDurationSeconds =
  isCardioWorkout && cardioDurationSeconds > 0
    ? cardioDurationSeconds
    : Number(w?.durationSeconds || w?.duration_seconds || 0) || 0;

            const exerciseCount = isCardioWorkout ? cs.itemsCount : exercisesList.length;

            const muscleGroups = getWorkoutMuscleBadges(w).filter(
              (badge, badgeIndex, arr) =>
                badge && arr.findIndex((item) => item?.key === badge.key) === badgeIndex
            );

            const badges = muscleGroups.slice(0, 3);
            const workoutFullMuscleNames = muscleGroups.map((g) =>
  t(`workout.muscles.${g.key}`)
);

const workoutCustomTitle = getWorkoutCustomTitle(w);

const workoutName =
  workoutCustomTitle ||
  (workoutFullMuscleNames.length > 0
    ? getCompactWorkoutTitleFromLabels(workoutFullMuscleNames, workoutFullName)
    : getCompactWorkoutFilterLabel(workoutFullName, allWorkoutFilterLabel));

const isOpen = expandedWorkoutId === workoutKey;

const toggleCard = () => {
  // При открытии тренировки упражнения остаются закрытыми.
  // Это убирает авторазворот первого упражнения и странный скачок карточки.
  setExpandedWorkoutExerciseId(null);

  setExpandedWorkoutId((prev) =>
    prev === workoutKey ? null : workoutKey
  );
};

            return (
              <div
                key={workoutKey}
                className="overflow-hidden rounded-[19px] border border-[#18d7bd]/50 bg-[#051723]/78 p-2.5 shadow-[0_0_0_1px_rgba(24,215,189,0.05),0_14px_36px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-xl"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-start gap-2">
                                    <div className="min-w-0 pt-0.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();

setNamesModal({
title: workoutFullMuscleNames.length
  ? t("workout.myWorkouts.muscleGroupsTitle", {
      count: workoutFullMuscleNames.length,
    })
  : t("workout.myWorkouts.workoutNameTitle"),
  names: workoutFullMuscleNames.length
    ? workoutFullMuscleNames
    : [workoutFullName],
  customTitle:
    workoutCustomTitle && workoutFullMuscleNames.length
      ? workoutCustomTitle
      : "",
});
                      }}
                      className="block max-w-full truncate text-left text-[20px] font-extrabold leading-[1.05] tracking-[0.01em] text-slate-50 active:scale-[0.99]"
                      title={workoutFullMuscleNames.length ? workoutFullMuscleNames.join(", ") : workoutFullName}
                    >
                      {workoutName}
                    </button>

                    <div className="mt-1 text-[13px] font-medium leading-none text-slate-300/78">
                      {formatWorkoutCardDate(w?.date, workoutLocale)}
                    </div>
                  </div>

                  {badges.length ? (
                    <div className="flex items-center gap-1.5 pt-0.5">
                      {badges.map((badge, badgeIndex) => (
                        <div
                          key={`${badge.key}-${badgeIndex}`}
                          className="h-[34px] w-[34px] overflow-hidden rounded-full border border-[#16e0c2]/75 bg-[#07131d] shadow-[0_0_13px_rgba(24,215,189,0.18)]"
                        >
                          <img
                            src={badge.img}
                            alt={t(`workout.muscles.${badge.key}`)}
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid h-[34px] w-[34px] place-items-center rounded-full border border-[#16e0c2]/55 bg-[#07131d] text-[15px] shadow-[0_0_13px_rgba(24,215,189,0.15)]">
                      ⚡
                    </div>
                  )}

<button
  type="button"
  onPointerDown={(e) => {
    e.stopPropagation();
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCard();
  }}
  className="grid h-[34px] w-[34px] place-items-center rounded-full border border-white/18 bg-[#07111f]/64 text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] active:scale-[0.96]"
  aria-label={
  isOpen
    ? t("workout.myWorkouts.collapseAria")
    : t("workout.myWorkouts.expandAria")
}
>
                    <svg
                      viewBox="0 0 24 24"
                      className={cn(
                        "h-[14px] w-[14px] transition-transform duration-200",
                        isOpen ? "rotate-180" : ""
                      )}
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M6.5 9.5L12 15l5.5-5.5"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
<AppCloseButton
  onClick={(e) => {
    e.stopPropagation();
    deleteWorkout(w);
  }}
  className="h-[25px] w-[25px] -mr-1.5 -mt-1.5 text-[13px]"
aria-label={t("workout.myWorkouts.deleteWorkoutAria")}
title={t("workout.myWorkouts.deleteWorkoutAria")}
/>
                </div>

                <div className="my-2.5 h-px bg-white/10" />

                <div className="grid grid-cols-3 divide-x divide-white/10 text-center">
                  <div className="min-w-0 px-0.5">
                    <div className="flex items-center justify-center gap-1 text-[#26e6c8]">
                      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px]" fill="none" aria-hidden="true">
                        <path
                          d="M4 10v4M8 7v10M16 7v10M20 10v4M8 12h8"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>

                      <span className="whitespace-nowrap text-[13px] font-extrabold tabular-nums text-slate-50">
                        {exerciseCount}
                      </span>
                    </div>

                    <div className="mt-0.5 text-[10.5px] leading-tight text-slate-300/78">
{t("workout.myWorkouts.exerciseNoun", {
  count: exerciseCount,
})}
                    </div>
                  </div>

                  <div className="min-w-0 px-0.5">
                    <div className="flex items-center justify-center gap-1 text-[#26e6c8]">
                      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px]" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
                        <path
                          d="M12 7v5l3 2"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span className="whitespace-nowrap text-[13px] font-extrabold tabular-nums text-slate-50">
                        {formatWorkoutDuration(workoutDurationSeconds)}
                      </span>
                    </div>

                    <div className="mt-0.5 text-[10.5px] leading-tight text-slate-300/78">
                      {t("workout.myWorkouts.duration")}
                    </div>
                  </div>

                  <div className="min-w-0 px-0.5">
                    <div className="flex items-center justify-center gap-1 text-[#26e6c8]">
                      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px]" fill="none" aria-hidden="true">
                        <path
                          d="M7 9h10l1 10H6L7 9Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9 9a3 3 0 0 1 6 0"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>

                      <span className="whitespace-nowrap text-[13px] font-extrabold tabular-nums text-slate-50">
                        {isCardioWorkout
                          ? "—"
                          : `${formatKg(
    Math.round(Number(st.tonnage) || 0),
    workoutLocale
  )}\u00A0${t("units.kilogramsShort")}`}
                      </span>
                    </div>

                    <div className="mt-0.5 text-[10.5px] leading-tight text-slate-300/78">
                      {t("workout.myWorkouts.totalWeight")}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => repeatWorkout(w)}
className="mt-3 flex h-[38px] w-full items-center justify-center gap-2 overflow-hidden rounded-[12px] bg-[linear-gradient(90deg,#0eb99f_0%,#0ba993_35%,#08756c_72%,#063f43_100%)] px-3 text-[14px] font-extrabold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.18),0_10px_22px_rgba(0,0,0,0.18)] active:scale-[0.985]"
>
                  <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0" fill="none" aria-hidden="true">
                    <path
                      d="M20 12a8 8 0 0 1-13.66 5.66M4 12A8 8 0 0 1 17.66 6.34"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 18H4v3M17 6h3V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                 <span className="min-w-0 truncate">
  {t("workout.myWorkouts.repeatWorkout")}
</span>
                </button>
                {isOpen && workoutFullMuscleNames.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {workoutFullMuscleNames.map((name) => (
                      <div
                        key={name}
                        className="rounded-full border border-[#18d7bd]/28 bg-[#06131d]/72 px-2.5 py-1 text-[11.5px] font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]"
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                ) : null}
                {isOpen && !isCardioWorkout ? (
                  <div className="mt-2.5 grid gap-2">
                    {exercisesList.map((ex, exerciseIndex) => {
                      const exerciseKey = `${workoutKey}:${String(ex?.id ?? exerciseIndex)}`;
                      const exerciseOpen = expandedWorkoutExerciseId === exerciseKey;
                      const sets = Array.isArray(ex?.sets) ? ex.sets : [];

                      return (
                        <div
                          key={exerciseKey}
                          className={cn(
                            "overflow-hidden rounded-[15px] border border-white/10 bg-[#071923]/72 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]",
                            exerciseOpen ? "border-[#18d7bd]/28 bg-[#071c27]/86" : ""
                          )}
                        >
                          <button
                            type="button"
onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();

  setExpandedWorkoutExerciseId((prev) =>
    prev === exerciseKey ? null : exerciseKey
  );
}}
                            className="grid w-full grid-cols-[32px_minmax(0,1fr)_26px] items-center gap-2 px-2 py-2 text-left active:bg-white/[0.03]"
                          >
                            <div className="grid h-8 w-8 place-items-center rounded-full border border-[#18d7bd]/32 bg-[#04131c] text-[14px] font-medium text-slate-50 shadow-[0_0_12px_rgba(24,215,189,0.08)]">
                              {exerciseIndex + 1}
                            </div>

                            <div className="min-w-0">
                              <div className="truncate text-[14px] font-medium leading-tight text-slate-50">
                                {ex?.name ||
  t("workout.myWorkouts.exerciseFallback", {
    number: exerciseIndex + 1,
  })}
                              </div>

                              <div className="mt-0.5 truncate text-[11.5px] font-medium leading-tight text-slate-300/70">
                                {getExerciseRangeLabel(ex, {
  locale: workoutLocale,
  weightUnit: t("units.kilogramsShort"),
  repsUnit: t("workout.myWorkouts.repetitions"),
})}
                              </div>
                            </div>

                            <div className="grid h-6 w-6 place-items-center rounded-full border border-white/10 bg-[#07111f]/68 text-slate-50">
                              <svg
                                viewBox="0 0 24 24"
                                className={cn(
                                  "h-[13px] w-[13px] transition-transform duration-200",
                                  exerciseOpen ? "rotate-180" : ""
                                )}
                                fill="none"
                                aria-hidden="true"
                              >
                                <path
                                  d="M6.5 9.5L12 15l5.5-5.5"
                                  stroke="currentColor"
                                  strokeWidth="2.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </button>

                          {exerciseOpen ? (
                            <div className="px-2.5 pb-2 pl-[44px]">
                              <div className="divide-y divide-white/[0.07] border-t border-white/[0.07]">
                                {sets.length ? (
                                  sets.map((set, setIndex) => {
                                    const weight = set?.weight ?? "—";
                                    const reps = set?.reps ?? "—";

                                    return (
                                      <div
                                        key={set?.id ?? setIndex}
                                        className="grid grid-cols-[minmax(0,1fr)_64px_78px] items-center gap-2 py-1.5 text-[12px] text-slate-200/88"
                                      >
                                        <div className="flex min-w-0 items-center gap-2">
                                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#18d7bd] shadow-[0_0_9px_rgba(24,215,189,0.8)]" />
                                          <span className="truncate">
                                            {t("workout.myWorkouts.setLabel", {
  number: setIndex + 1,
})}
                                          </span>
                                        </div>

                                        <div className="whitespace-nowrap text-right tabular-nums text-slate-50/90">
                                          {weight} {t("units.kilogramsShort")}
                                        </div>

                                        <div className="whitespace-nowrap text-right tabular-nums text-slate-50/90">
                                          {reps} {t("workout.myWorkouts.repsShort")}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="py-2 text-[12px] text-slate-300/65">
                                    {t("workout.myWorkouts.noSets")}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {isOpen && isCardioWorkout ? (
                  <div className="mt-2.5 rounded-[15px] border border-white/10 bg-[#071923]/72 p-3 text-[13px] text-slate-200/82">
                    <div className="text-[15px] font-semibold text-slate-50">
                      {cs.mainTitle || t("workout.myWorkouts.cardio")}
                    </div>

                    <div className="mt-1 text-slate-300/70">
                      {cs.totalMinutes
  ? t("workout.picker.minutesShort", {
      value: cs.totalMinutes,
    })
  : t("workout.myWorkouts.timeNotSpecified")}
                      {cs.comment ? ` • ${cs.comment}` : ""}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {visibleSavedWorkouts.length === 0 && (
<div className="rounded-[20px] border border-[#18d7bd]/35 bg-[#051723]/72 p-4 text-center text-[13px] text-slate-300/75">
  {t("workout.myWorkouts.empty")}
</div>
          )}
        </div>
      </div>
    );
  })()

) : trainScreen === "session" ? (
  (() => {
    const sessionLocale =
      i18n.resolvedLanguage === "ru"
        ? "ru-RU"
        : "en-GB";

    const localizedDefaultSessionTitle =
      trainMode === "cardio"
        ? t("workout.picker.cardio")
        : selectedMuscles
            .map((key) => t(`workout.muscles.${key}`))
            .filter(Boolean)
            .join(", ") || t("nav.workout");

    const sessionDisplayTitle =
      String(workoutTitle || "").trim() ||
      localizedDefaultSessionTitle;

    const shownExercises = Array.isArray(exercises) ? exercises : [];
    const shownCardioItems = Array.isArray(cardioItems) ? cardioItems : [];

    const isPlaceholderExerciseName = (value) => {
      const n = String(value || "").trim().toLowerCase().replace(/ё/g, "е");
      return /^\d+\s+упражнение$/.test(n) || n === "упражнение";
    };

    const completedExercisesForStats = shownExercises.map((ex) => ({
      ...ex,
      sets: (Array.isArray(ex?.sets) ? ex.sets : []).filter((set) => {
        if (set?.prefill === true && set?.edited !== true) return false;

        const hasWeight = set?.weight != null && String(set.weight).trim() !== "";
        const hasReps = set?.reps != null && String(set.reps).trim() !== "";

        return hasWeight && hasReps;
      }),
    }));

    const liveWorkoutStats = computeWorkoutStats({
      exercises: completedExercisesForStats,
    });

    const exercisesCount =
      trainMode === "cardio"
        ? Math.max(shownCardioItems.length, 1)
        : shownExercises.length;

    // стартуем с 00:00, а не с прочерка
    const durationText = formatWorkoutDuration(workoutDurationSeconds);

const totalWeightText =
  trainMode === "cardio"
    ? "0"
    : formatKg(
        Math.round(Number(liveWorkoutStats.tonnage) || 0),
        sessionLocale
      );

    const normalizeExerciseRecordName = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/ё/g, "е")
        .replace(/\s+/g, " ");

const getExerciseRecord = (exerciseName) => {
  const targetName = normalizeExerciseRecordName(exerciseName);

  if (!targetName || isPlaceholderExerciseName(targetName)) {
    return null;
  }

  const rows = [];

  const collectFromExercises = (workoutExercises) => {
    (Array.isArray(workoutExercises) ? workoutExercises : []).forEach((exercise) => {
      const name = normalizeExerciseRecordName(exercise?.name);
      if (!name || name !== targetName) return;

      const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];

      sets.forEach((set) => {
        if (set?.prefill === true && set?.edited !== true) return;

        const weight = Number(String(set?.weight ?? "").replace(",", "."));
        const reps = Number(String(set?.reps ?? "").replace(",", "."));

        if (!Number.isFinite(weight) || !Number.isFinite(reps)) return;
        if (weight <= 0 || reps <= 0) return;

        rows.push({ weight, reps });
      });
    });
  };

  // Старые сохранённые тренировки
  (savedWorkouts || []).forEach((workout) => {
    collectFromExercises(workout?.exercises);
  });

  // Текущая тренировка прямо сейчас, ещё до сохранения
  collectFromExercises(shownExercises);

  if (!rows.length) return null;

  // Рекорд = самый большой вес.
  // Если вес одинаковый — берём больше повторений.
  return rows
    .slice()
    .sort((a, b) => b.weight - a.weight || b.reps - a.reps)[0];
};

const formatRecordSet = (record) => {
  if (!record) return "—";

  return `${formatKg(
    record.weight,
    sessionLocale
  )} ${t("units.kilogramsShort")} × ${Math.round(record.reps)}`;
};

    const GripDots = () => (
      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 opacity-75">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-200/70" />
      </div>
    );

    const RecordChevronIcon = ({ expanded }) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn(
          "shrink-0 transition-transform duration-200",
          expanded ? "rotate-180" : ""
        )}
      >
        <path
          d="M7 10l5 5 5-5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    const PencilIcon = ({ className = "" }) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={className || "h-3.5 w-3.5"}
      >
        <path
          d="M4 20h4.2L18.7 9.5a2 2 0 0 0 0-2.8l-1.4-1.4a2 2 0 0 0-2.8 0L4 15.8V20Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.5 6.5l4 4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );

    const WorkoutStatIcon = ({ type }) => {
      if (type === "exercise") {
        return (
          <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" aria-hidden="true">
            <path
              d="M4 10v4M8 7v10M16 7v10M20 10v4M8 12h8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        );
      }

      if (type === "time") {
        return (
          <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M12 7v5l3 2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }

      return (
        <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" aria-hidden="true">
          <path
            d="M7 9h10l1 10H6L7 9Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 9a3 3 0 0 1 6 0"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    };

    const actionExercise = exerciseActionChoice
      ? shownExercises.find((ex) => ex.id === exerciseActionChoice.exerciseId)
      : null;

    const actionSets = Array.isArray(actionExercise?.sets)
      ? actionExercise.sets
      : [];

    return (
      <>
        <div className="mx-auto w-full max-w-[420px] px-3 pb-3 pt-1 text-slate-50">
          {/* Верх */}
          <div className="grid grid-cols-[minmax(0,1fr)_106px_42px] items-center gap-2 px-1">
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setWorkoutTitleModalOpen(true);
              }}
className="min-w-0 active:scale-[0.99]"
title={t("workout.session.editWorkoutTitle")}
>
              <div className="flex min-w-0 items-center justify-start gap-1.5 pl-1">
                <div className="truncate text-left text-[25px] font-bold leading-tight tracking-[-0.04em] text-slate-50">
                  {sessionDisplayTitle}
                </div>

                <span className="shrink-0 text-[#28ead3]">
                  <PencilIcon className="h-[15px] w-[15px]" />
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={saveWorkout}
              disabled={!canSaveWorkout || isSavingWorkout}
              className={cn(
                "h-10 rounded-full border px-2 text-[14px] font-medium transition active:scale-[0.97]",
                canSaveWorkout && !isSavingWorkout
                  ? "border-[#20ead4]/70 bg-[#071923]/70 text-[#5df7e6] shadow-[0_0_20px_rgba(32,234,212,0.20),inset_0_0_16px_rgba(32,234,212,0.07)]"
                  : "border-[#20ead4]/22 bg-[#071923]/35 text-[#5df7e6]/42"
              )}
            >
              {isSavingWorkout
  ? t("workout.session.saving")
  : t("actions.save")}
            </button>

            <AppCloseButton
              onClick={closeSessionToGroups}
              className="h-10 w-10"
              aria-label={t("workout.session.closeWorkoutAria")}
            />
          </div>

          {/* Статистика */}
          <div className="mt-5 grid grid-cols-[1fr_1.18fr_1fr] items-center px-0.5">
            <div className="flex min-w-0 items-center justify-center gap-2">
              <div className="shrink-0 text-[#26e6c8]">
                <WorkoutStatIcon type="exercise" />
              </div>

              <div className="min-w-0">
                <div className="text-[17px] font-medium leading-none tabular-nums text-slate-50">
                  {exercisesCount}
                </div>
<div className="mt-1.5 whitespace-nowrap text-[11px] leading-none text-slate-300/70">
  {t("workout.myWorkouts.exerciseNoun", {
    count: exercisesCount,
  })}
</div>
              </div>
            </div>

            <div className="flex min-w-0 items-center justify-center gap-1.5 border-x border-white/14 px-1.5">
              <button
                type="button"
                onClick={toggleWorkoutTimer}
                className={cn(
                  "grid h-[27px] w-[27px] shrink-0 place-items-center rounded-full border text-[#26e6c8] transition active:scale-[0.95]",
                  workoutTimer.isRunning
                    ? "border-amber-200/70 bg-amber-300/15 text-amber-200 shadow-[0_0_14px_rgba(252,211,77,0.22)]"
                    : "border-[#22e8d2]/55 bg-[#06202a]/55 shadow-[0_0_14px_rgba(34,232,210,0.16)]"
                )}
                title={
  workoutTimer.isRunning
    ? t("workout.session.pause")
    : t("workout.session.start")
}
              >
                <WorkoutStatIcon type="time" />
              </button>

              <div className="min-w-0">
                <div className="whitespace-nowrap text-[17px] font-medium leading-none tabular-nums text-slate-50">
                  {durationText}
                </div>
<div className="mt-1.5 whitespace-nowrap text-[9.5px] leading-none text-slate-300/70">
  {t("workout.myWorkouts.duration")}
</div>
              </div>
            </div>

            <div className="flex min-w-0 items-center justify-center gap-2">
              <div className="shrink-0 text-[#26e6c8]">
                <WorkoutStatIcon type="weight" />
              </div>

              <div className="min-w-0">
<div className="whitespace-nowrap text-[17px] font-medium leading-none tabular-nums text-slate-50">
  {totalWeightText} {t("units.kilogramsShort")}
</div>
<div className="mt-1.5 whitespace-nowrap text-[11px] leading-none text-slate-300/70">
  {t("workout.myWorkouts.totalWeight")}
</div>
              </div>
            </div>
          </div>

          {/* Упражнения */}
          {trainMode === "strength" ? (
            <div className="mt-5 grid gap-2.5">
              {shownExercises.map((ex, index) => {
                const sets = Array.isArray(ex?.sets) ? ex.sets : [];
                const rawExerciseName = ex?.name || `${index + 1} упражнение`;
                const isPlaceholderName = isPlaceholderExerciseName(rawExerciseName);
                const exerciseName = isPlaceholderName
  ? t("workout.session.exercisePlaceholder")
  : rawExerciseName;

                const record = getExerciseRecord(rawExerciseName);
                const recordOpen = !!expandedSessionRecords?.[ex.id];

                return (
<div
  key={ex?.id ?? index}
  data-session-exercise-card="1"
  data-session-exercise-id={String(ex.id)}
  className={cn(
    "relative rounded-[18px] border border-[#1f6f75]/60 bg-[#06101f]/78 px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_12px_30px_rgba(0,0,0,0.16)] transition-[transform,box-shadow,border-color,background-color,opacity] duration-200 ease-[cubic-bezier(0.18,0.9,0.22,1)]",
    draggingExerciseId === String(ex.id)
      ? "scale-[0.985] border-[#20ead4]/25 bg-[#06101f]/35 opacity-25 shadow-none"
      : draggingExerciseId
        ? "opacity-95"
        : ""
  )}
>
<button
  type="button"
  onClick={async (e) => {
    stopAndProtect(e, 700);
    await deleteExercise(ex.id);
  }}
  className="absolute right-1.5 top-1.5 z-30 grid h-6 w-6 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] p-0 text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition active:scale-[0.94]"
aria-label={t("workout.session.deleteExerciseAria")}
title={t("workout.session.deleteExerciseAria")}
>
  <svg
    viewBox="0 0 16 16"
    className="h-3 w-3"
    aria-hidden="true"
    fill="none"
  >
    <path
      d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
</button>

                    <div
                      className="grid items-stretch gap-2"
                      style={{
                        gridTemplateColumns: "112px minmax(0, 1fr)",
                      }}
                    >
                      <div className="grid min-w-0 grid-cols-[30px_minmax(0,1fr)] gap-1.5">
                        <div className="flex flex-col items-center justify-between py-1">
<div className="grid h-[27px] w-[27px] place-items-center rounded-full border border-[#22e8d2]/35 bg-[#061924]/65 text-[12px] font-medium text-slate-100">
  {index + 1}
</div>

<div className="pb-1">
<button
  type="button"
  onPointerDown={(e) => startExerciseDrag(e, ex.id)}
    
    className={cn(
      "grid h-8 w-8 place-items-center border-0 bg-transparent p-0 text-cyan-100/75 outline-none transition active:scale-[0.94]",
      draggingExerciseId === String(ex.id) ? "text-cyan-50" : ""
    )}
    style={{
      touchAction: "none",
      cursor: draggingExerciseId === String(ex.id) ? "grabbing" : "grab",
    }}
aria-label={t("workout.session.dragExerciseAria")}
title={t("workout.session.dragExerciseAria")}
  >
    <GripDots />
  </button>
</div>
                        </div>

                        <div className="min-w-0 py-1">
                          <button
                            type="button"
                            onClick={() =>
                              setExerciseModal({
                                id: ex.id,
                                name: isPlaceholderName ? "" : rawExerciseName,
                              })
                            }
                            className="block w-full min-w-0 max-w-full overflow-hidden rounded-[12px] text-left active:scale-[0.99]"
                            title={t("workout.session.editExercise")}
                          >
                            <div className="flex w-full min-w-0 max-w-full items-start gap-1 overflow-hidden">
<div
    className="min-h-[50px] min-w-0 max-w-full flex-1 break-words pt-[3px] pb-[3px] text-[13px] font-medium leading-[1.22] tracking-[-0.03em] text-slate-50"
  style={{
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  }}
>
  {exerciseName}
</div>

                              {isPlaceholderName ? (
                                <span className="mt-[1px] shrink-0 text-[#28ead3]/90">
                                  <PencilIcon className="h-3 w-3" />
                                </span>
                              ) : null}
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setExpandedSessionRecords((prev) => ({
                                ...(prev || {}),
                                [ex.id]: !prev?.[ex.id],
                              }))
                            }
                            className="mt-1.5 flex items-center gap-1.5 text-left text-[12px] text-slate-300/78 active:scale-[0.99]"
title={t("workout.session.showRecord")}
>
  <span>{t("workout.session.record")}</span>
                            <span className="grid h-4 w-4 place-items-center text-cyan-100/75">
                              <RecordChevronIcon expanded={recordOpen} />
                            </span>
                          </button>

                          {recordOpen ? (
                            <div className="mt-1.5 text-[10.5px] leading-[1.15] text-slate-300/72">
                              <div className="tabular-nums">
                                {formatRecordSet(record)}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div
                        className="min-w-0 overflow-x-auto pr-5"
                        ref={(el) => {
                          if (el) setsContainersRef.current[ex.id] = el;
                        }}
                        style={{
                          WebkitOverflowScrolling: "touch",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        <div className="grid w-max grid-flow-col auto-cols-[48px] gap-1.5">
                          {sets.map((set, setIndex) => {
                            const hasNumbers =
                              set.weight != null &&
                              set.reps != null &&
                              String(set.weight).trim() !== "" &&
                              String(set.reps).trim() !== "";

                            const isPrefill = !!set.prefill;
                            const isEdited = set.edited === true;
                            const showGreen =
                              hasNumbers && (isEdited || !isPrefill);

return (
  <div
    key={set?.id ?? setIndex}
    className={cn(
      "relative flex h-[76px] w-[48px] flex-col rounded-[11px] border px-1.5 py-1.5 text-left transition active:scale-[0.97]",
      showGreen
        ? "border-[#20ead4]/90 bg-[#092b2d]/88 text-[#54f8e7] shadow-[0_0_18px_rgba(32,234,212,0.23),inset_0_0_14px_rgba(32,234,212,0.08)]"
        : "border-white/18 bg-[#0a1322]/88 text-slate-300/78"
    )}
  >
    {/* Клик по карточке открывает ввод веса/повторов */}
    <button
      type="button"
      onClick={() =>
setSetModal({
  exerciseId: ex.id,
  setId: set.id,
  weight: set.weight ?? "",
  reps: set.reps ?? "",
  label: t("workout.myWorkouts.setLabel", {
    number: setIndex + 1,
  }),
})
      }
      className="absolute inset-0 z-10 rounded-[11px] outline-none focus:outline-none focus:ring-0"
      aria-label={t("workout.session.openSetAria", {
  number: setIndex + 1,
})}
    />

    {/* Маленький фирменный крестик удаления подхода */}
    <button
      type="button"
      onClick={(e) => {
        stopAndProtect(e, 700);
        deleteSetFromExercise(ex.id, set.id, setIndex);
      }}
      className="absolute right-1 top-1 z-20 grid h-4 w-4 place-items-center rounded-full border border-[#18d7bd]/40 bg-[#18d7bd]/[0.085] p-0 text-[#35F3DF] shadow-[0_0_8px_rgba(24,215,189,0.10)] transition active:scale-[0.92]"
aria-label={t("workout.session.deleteSetAria", {
  number: setIndex + 1,
})}
title={t("workout.session.deleteSetAria", {
  number: setIndex + 1,
})}
    >
      <svg
        viewBox="0 0 16 16"
        className="h-2.5 w-2.5"
        aria-hidden="true"
        fill="none"
      >
        <path
          d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>

    <div className="pointer-events-none relative z-0 flex items-start justify-between gap-1 pr-4">
      <span className="text-[10.5px] font-medium leading-none tabular-nums">
        {setIndex + 1}
      </span>
    </div>

    <div className="pointer-events-none relative z-0 mt-auto pb-1.5 text-center">
      {hasNumbers ? (
        <>
          <div className="whitespace-nowrap text-[11px] font-medium leading-none tabular-nums">
            {set.weight} {t("units.kilogramsShort")}
          </div>

          <div className="mt-1.5 whitespace-nowrap text-[11px] font-medium leading-none tabular-nums">
            × {set.reps}
          </div>
        </>
      ) : (
<div className="whitespace-nowrap text-[9px] font-medium leading-none text-slate-300/68">
  {t("workout.session.setShort")}
</div>
      )}
    </div>
  </div>
);
                          })}

                          <button
                            type="button"
                            onClick={() => addSetToExercise(ex.id)}
                            className="flex h-[76px] w-[52px] flex-col items-center justify-center rounded-[11px] border border-dashed border-[#20ead4]/45 bg-[#06111f]/60 text-[#45f2df] transition active:scale-[0.97]"
aria-label={t("workout.session.addSet")}
title={t("workout.session.addSet")}
                          >
                            <span className="text-[25px] font-light leading-none">
                              +
                            </span>
<span className="mt-1.5 whitespace-nowrap text-[9.5px] font-medium leading-none">
  {t("workout.session.setShort")}
</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-5 grid gap-2.5">
              {shownCardioItems.length > 0 ? (
                shownCardioItems.map((item, index) => (
                  <div
                    key={item?.id ?? index}
                    className="rounded-[18px] border border-[#1f6f75]/55 bg-[#06101f]/78 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_12px_30px_rgba(0,0,0,0.16)]"
                  >
<div className="text-[18px] font-semibold text-slate-50">
  {String(item?.title || "").trim() === "Кардио" ||
  !String(item?.title || "").trim()
    ? t("workout.picker.cardio")
    : item.title}
</div>

<div className="mt-2 text-[14px] text-slate-300/75">
  {item?.minutes
    ? t("workout.picker.minutesShort", {
        value: item.minutes,
      })
    : t("workout.myWorkouts.timeNotSpecified")}
  {item?.comment ? ` • ${item.comment}` : ""}
</div>
                  </div>
                ))
              ) : (
<div className="rounded-[18px] border border-[#1f6f75]/55 bg-[#06101f]/78 p-5 text-center text-[14px] text-slate-300/75">
  {t("workout.session.cardioEmpty")}
</div>
              )}
            </div>
          )}

          {/* Низ */}
          {trainMode === "strength" ? (
            <div className="mt-3 grid gap-3">
<button
  type="button"
  onClick={addExercise}
  className="flex h-[48px] w-full items-center justify-center gap-3 rounded-[19px] border border-[#20ead4]/70 bg-[#051923]/55 text-[16px] font-medium text-[#54f8e7] shadow-[0_0_22px_rgba(32,234,212,0.11),inset_0_0_16px_rgba(32,234,212,0.05)] active:scale-[0.985]"
  title={t("workout.session.addExercise")}
>
  <span className="text-[28px] font-light leading-none">+</span>
  <span>{t("workout.session.addExercise")}</span>
</button>

              <div className="flex justify-end">
<button
  type="button"
  onClick={confirmAndCancelCurrentWorkout}
  className="h-[34px] rounded-full border border-rose-400/35 bg-rose-500/8 px-4 text-[13px] font-medium text-rose-300/90 active:scale-[0.97]"
  title={t("workout.session.cancelWorkout")}
>
  {t("workout.session.cancelWorkout")}
</button>
              </div>
            </div>
          ) : (
            <div className="mt-3 grid gap-3">
<button
  type="button"
  onClick={openCardioModal}
  className="flex h-[48px] w-full items-center justify-center gap-3 rounded-[19px] border border-[#20ead4]/70 bg-[#051923]/55 text-[16px] font-medium text-[#54f8e7] shadow-[0_0_22px_rgba(32,234,212,0.11),inset_0_0_16px_rgba(32,234,212,0.05)] active:scale-[0.985]"
  title={t("workout.session.addCardio")}
>
  <span className="text-[28px] font-light leading-none">+</span>
  <span>{t("workout.session.addCardio")}</span>
</button>

<div className="flex justify-end">
  <button
    type="button"
    onClick={confirmAndCancelCurrentWorkout}
    className="h-[34px] rounded-full border border-rose-400/35 bg-rose-500/8 px-4 text-[13px] font-medium text-rose-300/90 active:scale-[0.97]"
    title={t("workout.session.cancelWorkout")}
  >
    {t("workout.session.cancelWorkout")}
  </button>
</div>
            </div>
          )}
        </div>

        <BaseModal
          open={!!exerciseActionChoice}
          onClose={() => setExerciseActionChoice(null)}
        >
<div className="flex min-h-[calc(var(--app-height,100vh)-32px)] items-start justify-center pt-[72px]">
<div className="w-full max-w-[360px] overflow-hidden rounded-[30px] border border-[#20ead4]/30 bg-white/[0.09] text-slate-50 shadow-[0_24px_80px_rgba(0,0,0,0.48),0_0_34px_rgba(32,234,212,0.14)] backdrop-blur-2xl">
              <div className="relative overflow-hidden p-4">
                <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-[#20ead4]/18 blur-3xl" />
                <div className="pointer-events-none absolute -right-20 bottom-0 h-48 w-48 rounded-full bg-[#0ea5e9]/12 blur-3xl" />

                <div className="relative flex items-start justify-between gap-3">
                  <div>
<div className="text-[18px] font-bold tracking-[-0.02em] text-slate-50">
  {t("workout.session.deleteChoiceTitle")}
</div>
<div className="mt-1 text-[12px] leading-snug text-slate-300/72">
  {t("workout.session.deleteChoiceSubtitle")}
</div>
                  </div>

                  <AppCloseButton onClick={() => setExerciseActionChoice(null)} />
                </div>

                <div className="relative mt-5 grid gap-2.5">
                  <button
                    type="button"
                    onClick={async () => {
                      const id = exerciseActionChoice?.exerciseId;
                      setExerciseActionChoice(null);
                      if (!id) return;
                      await deleteExercise(id);
                    }}
                    className="group flex min-h-[54px] items-center justify-between rounded-[22px] border border-rose-300/30 bg-rose-500/10 px-4 text-left text-[14px] font-bold text-rose-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.98]"
                  >
                    <span>{t("workout.session.deleteExercise")}</span>
                    <span className="text-[20px] text-rose-200/75 transition group-active:translate-x-0.5">
                      →
                    </span>
                  </button>

                  <button
                    type="button"
                    disabled={actionSets.length <= 1}
                    onClick={async () => {
                      const id = exerciseActionChoice?.exerciseId;
                      setExerciseActionChoice(null);
                      if (!id) return;
                      await deleteLastSetFromExercise(id);
                    }}
                    className={cn(
                      "group flex min-h-[54px] items-center justify-between rounded-[22px] border px-4 text-left text-[14px] font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] active:scale-[0.98]",
                      actionSets.length > 1
                        ? "border-[#20ead4]/40 bg-[#06202a]/72 text-cyan-50"
                        : "border-white/10 bg-white/[0.04] text-slate-500"
                    )}
                  >
                    <span>{t("workout.session.deleteLastSet")}</span>
                    <span className="text-[20px] text-cyan-100/70 transition group-active:translate-x-0.5">
                      →
                    </span>
                  </button>
                </div>

                {actionSets.length <= 1 ? (
<div className="relative mt-3 rounded-[16px] border border-white/10 bg-white/[0.05] px-3 py-2 text-[12px] leading-snug text-slate-300/70">
  {t("workout.session.lastSetRequired")}
</div>
                ) : null}
              </div>
            </div>
          </div>
        </BaseModal>
      </>
    );
  })()
) : (

// ЭКРАН ЗАМЕРОВ ТЕЛА
<div
  ref={bodyScreenRef}
  className="pb-4 min-h-0"
>
  {/* Выбор метрик */}
<GlassCard
  solid
  className={cn(
    "cursor-default w-full",
    metricsCollapsed ? "p-3" : "p-4",
    FOOD_COMPACT_CARD
  )}
>
  <div className={cn("px-1 pt-0", metricsCollapsed ? "pb-0" : "pb-1")}>
    {/* Заголовок внутри окна */}
    <div
      className={cn(
        "flex items-center justify-between gap-3",
        metricsCollapsed ? "mb-0" : "mb-3"
      )}
    >
<div className="flex min-w-0 items-center gap-2">
  <div className="text-[15px] font-semibold tracking-[-0.02em]">
    {t("body.measurements.title")}
  </div>

  <button
    type="button"
    onClick={() => setMetricsCollapsed((v) => !v)}
className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl transition active:scale-[0.94]"
    aria-label={
      metricsCollapsed
        ? t("body.measurements.expandAria")
        : t("body.measurements.collapseAria")
    }
  >
          
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              className={cn(
                "transition-transform duration-200",
                metricsCollapsed ? "rotate-0" : "rotate-180"
              )}
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <AppCloseButton onClick={() => setTrainScreen("main")} />
      </div>

      {/* Теги с метриками без поиска */}
      {!metricsCollapsed && (
        <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
          {METRICS.map((m) => {
            const active = bodySelected.includes(m.key);

            return (
              <button
                key={m.key}
                type="button"
                onClick={() => toggleBodyMetric(m.key)}
                className={cn(
                  "px-3 py-1.5 rounded-full border transition-all text-[12px]",
                  active
                    ? "border-emerald-300 bg-gradient-to-r from-emerald-400/80 to-cyan-300/80 text-slate-900 shadow-[0_0_18px_rgba(45,212,191,0.6)]"
                    : "border-white/25 bg-slate-900/40 text-slate-100/80 hover:bg-slate-900/70"
                )}
>
  {t(`body.measurements.metrics.${m.key}`)}
</button>
            );
          })}
        </div>
      )}
    </div>
  </GlassCard>


{/* Виджеты для выбранных метрик */}
<div className="grid gap-2 mt-3">
  {bodySelected.length === 0 ? (
<div className="text-xs opacity-70 text-center">
  {t("body.measurements.emptySelected")}
</div>
  ) : (
    bodySelected.map((key) => {
      const metric = METRICS.find((m) => m.key === key);
      if (!metric) return null;

      const history = bodyHistory[key] ?? [];
      const valueInput = bodyInputs[key] ?? "";

      return (
<MetricCard
  key={key}
  metric={metric}
  history={history}
  valueInput={valueInput}
  onChange={(val) =>
    setBodyInputs((prev) => ({ ...prev, [key]: val }))
  }
  onSave={() => handleSaveBodyMetric(key)}
  onRemoveMetric={() => handleRemoveBodyMetric(key)}
  onDeleteMeasurement={(measurement) =>
    handleDeleteBodyMeasurement(key, measurement)
  }
/>
      );
    })
  )}
</div>
</div>

    )) :   
foodView === "details" ? (
<MealsDetails
  dbUserId={dbUserId}
  dayKey={rationDayKey}
  onChangeDay={(nextDayKey) => {
    setMeals(makeEmptyMeals());
    setRationDayKey(nextDayKey);
    scrollAppRootToTop();
  }}
  meals={meals}
  setMeals={setMeals}
  onBack={() => setFoodView("overview")}
  onEditMacros={() => setMacroModalOpen(true)}
  setTrainScreen={setTrainScreen}
  bumpFoodMonth={() => setFoodMonthReloadTick((x) => x + 1)}
  macroTargets={macroTargets}
  premiumAccessLoading={dbUserLoading}
  canUsePremiumFeatures={canUsePremiumFeatures}
  onPremiumRequired={openPremiumPaywall}
/>



  ) : (
    <div className="mx-auto w-full max-w-[390px] grid gap-4 pt-0 pb-0">

      <div className="w-full">
  {/* ✅ totals должен быть объявлен ДО return (выше в компоненте), а тут просто используем */}
  <MacroIntakeCard
   introMode={macroIntro}
    consumed={{
      carbs: totals.carbs,
      proteins: totals.proteins,
      fats: totals.fats,
      kcal: totals.kcal,
    }}
    targets={macroTargets}
    day={foodStreak}

    onEdit={() => {
  setMacroModalOpen(true);

  emitAppEvent(
    "macro-modal-opened"
  );
}}
    onOpenDetails={() => {
  resetRationToToday();
  setFoodView("details");
}}
      onOpenReport={() => setFoodReportPeriodOpen(true)}

  />
</div>

{/* ✅ Модалка выбора периода */}
<ReportPeriodModal
  open={foodReportPeriodOpen}
  onClose={() => setFoodReportPeriodOpen(false)}
  onPick={(p) => {
    setFoodReportPeriod(p);
    setFoodReportPeriodOpen(false);
    setFoodReportOpen(true);
  }}
/>

{/* ✅ Модалка отчёта */}
<FoodReportModal
  open={foodReportOpen}
  onClose={() => setFoodReportOpen(false)}
  period={foodReportPeriod}
/>





<div
  data-onboarding="wellness-block"
  className="grid gap-3 rounded-[26px]"
>
  <div className="grid grid-cols-2 gap-3">
    {/* Вода */}
    <GlassCard solid onClick={addCup} className={FOOD_WIDGET_CARD}>
    {(() => {
      const targetMl = 2000;
      const progress = Math.max(
        0,
        Math.min(100, Math.round((Number(waterMl || 0) / targetMl) * 100))
      );


      return (
  <div className="relative z-10 h-full">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={FOOD_WIDGET_ICON}>
            <FoodDropIcon />
          </span>

          <div className="text-[19px] font-extrabold tracking-[0.08em] text-white">
            {t("food.water.title")}
          </div>
        </div>
      </div>
    </div>

    <div className="mt-2 flex items-end justify-between gap-2">
<div className="min-w-0 max-w-[106px] truncate text-[20px] font-extrabold leading-none tracking-[0.04em] text-white">
  {formatWater(waterMl)}
</div>

      <div className="shrink-0 text-[14px] font-extrabold text-[#35F3DF]">
        {progress}%
      </div>
    </div>

    <div className="mt-2 text-[11px] font-medium tracking-[0.02em] text-slate-300/68">
      {t("food.water.goal")}
    </div>

    <div className="mt-3 flex items-center gap-2">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          removeCup();
        }}
        className={FOOD_WIDGET_SMALL_BTN}
      >
        −
      </button>

      <div className="h-[8px] flex-1 overflow-hidden rounded-full bg-black/45 shadow-[inset_0_1px_4px_rgba(0,0,0,0.55)]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#12b8ad] to-[#35F3DF] shadow-[0_0_14px_rgba(53,243,223,0.34)]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          addCup();
        }}
        className={FOOD_WIDGET_SMALL_BTN}
      >
        +
      </button>
    </div>
  </div>
);
        
    })()}
  </GlassCard>

  {/* Вес */}
<GlassCard
  solid
  onClick={(e) => {
    e.preventDefault();
    openWeightModal();
  }}
  className={FOOD_WIDGET_CARD}
>
    {(() => {
      const goal = weight.goal;
      const current = weight.current;
      const baseline = weight.baseline;
      const diff = baseline == null ? 0 : +(current - baseline).toFixed(1);
      const isGain = diff > 0;
      const arrow = isGain ? "↑" : "↓";
      const diffAbs = Math.abs(diff).toFixed(1);

      return (
        <div className="relative z-10 h-full overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={FOOD_WIDGET_ICON}>
                <FoodScaleIcon />
              </span>

<div
  className={
    (i18n.resolvedLanguage === "ru"
      ? "text-[19px]"
      : "text-[16px]") +
    " font-extrabold tracking-[0.08em] text-white"
  }
>
  {t("food.weight.title")}
</div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openWeightModal();
              }}
              className={FOOD_WIDGET_SMALL_BTN}
              aria-label={t("food.weight.openAria")}
            >
              +
            </button>
          </div>

<div className="mt-2 text-[25px] font-extrabold leading-none tracking-[0.06em] text-white">
  {fmt(current)} <span className="text-[14px] text-white/80">{t("units.kilogramsShort")}</span>
</div>

          <div className="mt-2 text-[11px] font-medium tracking-[0.02em] text-slate-300/68">
            {t("food.weight.goal")} {fmt(goal)} {t("units.kilogramsShort")}{" "}
            <span className="text-slate-400">
              {arrow} {diffAbs} {t("units.kilogramsShort")}
            </span>
          </div>

          <div className="mt-2 text-[12px] font-medium tracking-[0.02em] text-slate-300/76">
            {t("food.weight.start")} —{" "}
            <span className="font-bold text-white">
              {fmt(baseline ?? current)} {t("units.kilogramsShort")}
            </span>
          </div>

        </div>
      );
    })()}
  </GlassCard>
</div>

<div className="grid grid-cols-2 gap-3">
  {/* Настроение */}
  <GlassCard solid className={FOOD_WIDGET_CARD}>
    <div className="relative z-10">
      <button
        ref={infoRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowEmotionHint((v) => !v);
        }}
className={FOOD_WIDGET_INFO_BTN}
        aria-label={t("food.mood.hintAria")}
      >
        i
      </button>

<div className="pr-10 text-[17px] font-extrabold leading-none tracking-[0.02em] text-white">
  {t("food.mood.title")}
</div>
      <div className="mt-2 text-[12px] font-medium leading-snug tracking-[0.02em] text-slate-300/70">
        {t("food.mood.question")}
      </div>

      <div className="mt-4 flex items-center justify-between gap-1.5">
        {[0, 1, 2, 3].map((type) => {
          const active = selectedEmotion === type;

          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                setSelectedEmotion(type);
                logEvent("mood:set", { valueIndex: type });
                autoSaveDay({ mood: type });
              }}
className={cn(
  "grid h-9 w-9 place-items-center rounded-full border transition active:scale-[0.94]",
  active
    ? "border-[#35F3DF]/75 bg-[#35F3DF]/18 shadow-[0_0_18px_rgba(53,243,223,0.40)]"
    : "border-[#35F3DF]/20 bg-[#35F3DF]/[0.035] text-[#78989E] shadow-[inset_0_1px_0_rgba(53,243,223,0.055)]"
)}
              aria-pressed={active}
            >
              <MoodFace type={type} active={active} />
            </button>
          );
        })}
      </div>

      {showEmotionHint && (
        <div
          ref={popRef}
          className="absolute left-0 right-0 bottom-[100%] translate-y-1 z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative rounded-2xl border border-white/40 bg-gradient-to-b from-white/95 to-[#f6f5ff]/95 p-3 text-[12px] leading-relaxed text-slate-800 shadow-[0_12px_34px_rgba(0,0,0,0.12)]">
            {t("food.mood.hint")}
          </div>
        </div>
      )}
    </div>
  </GlassCard>

  {/* Сон */}
  <GlassCard solid onClick={openSleepModal} className={FOOD_WIDGET_CARD}>
    <div className="relative z-10">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={FOOD_WIDGET_ICON}>
            <FoodMoonIcon />
          </span>

          <div className="text-[19px] font-extrabold tracking-[0.08em] text-white">
            {t("food.sleep.title")}
          </div>
        </div>

        <button
          ref={sleepInfoRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowSleepHint((v) => !v);
          }}
className={FOOD_WIDGET_INFO_BTN}
          aria-label={t("food.sleep.hintAria")}
        >
          i
        </button>
      </div>

<div className="mt-2 text-[21px] font-extrabold leading-none tracking-[0.04em] text-white">
  {sleepHours != null
    ? formatSleepLabel(sleepHours)
    : t("food.sleep.zeroHours")}
</div>

      <div className="mt-3 grid grid-cols-7 items-end gap-1.5">
        {[
          t("food.sleep.weekdays.mon"),
          t("food.sleep.weekdays.tue"),
          t("food.sleep.weekdays.wed"),
          t("food.sleep.weekdays.thu"),
          t("food.sleep.weekdays.fri"),
          t("food.sleep.weekdays.sat"),
          t("food.sleep.weekdays.sun"),
        ].map((day, i) => {
          const sleepValues = [8, 6, 4, 10, 7, 6, 7];
          const totalHeight = 40;
          const h = Math.round(((sleepValues[i] ?? 6) / 12) * totalHeight);

          return (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="relative h-[42px] w-[10px] overflow-hidden rounded-full bg-white/[0.075]">
                <div
                  className="absolute bottom-0 left-0 right-0 rounded-full bg-gradient-to-t from-[#11998f] to-[#35F3DF] shadow-[0_0_10px_rgba(53,243,223,0.28)]"
                  style={{ height: `${h}px` }}
                />
              </div>

              <div className="text-[9px] font-semibold leading-none text-slate-300/66">
                {day}
              </div>
            </div>
          );
        })}
      </div>

      {showSleepHint && (
        <div
          ref={sleepPopRef}
          className="absolute left-0 right-0 bottom-[100%] translate-y-1 z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative rounded-2xl border border-white/40 bg-gradient-to-b from-white/95 to-[#f6f5ff]/95 p-3 text-[12px] leading-relaxed text-slate-800 shadow-[0_12px_34px_rgba(0,0,0,0.12)]">
            {t("food.sleep.hint")}
          </div>
        </div>
      )}
    </div>
  </GlassCard>
</div>
</div>
<GlassCard
  solid
  data-onboarding="food-calendar"
  className="box-border w-full min-h-[316px] rounded-[24px] border border-[#18d7bd]/30 bg-[#051723]/78 p-3 shadow-[0_0_0_1px_rgba(24,215,189,0.025),0_14px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl"
  onClick={click("Календарь еды")}
>
  {(() => {
    const base = new Date();
    base.setDate(1);
    base.setMonth(base.getMonth() + foodCalOffset);

    const {
      year,
      monthName,
      cells,
    } = buildCalendar(
      base,
      true,
      i18n.resolvedLanguage === "ru"
        ? "ru-RU"
        : "en-GB"
    );

    const week = [
      t("food.calendar.weekdays.mon"),
      t("food.calendar.weekdays.tue"),
      t("food.calendar.weekdays.wed"),
      t("food.calendar.weekdays.thu"),
      t("food.calendar.weekdays.fri"),
      t("food.calendar.weekdays.sat"),
      t("food.calendar.weekdays.sun"),
    ];

    return (
      <div className="h-full flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <svg viewBox="0 0 24 24" className="mt-1 h-8 w-8 shrink-0 text-[#35F3DF]" fill="none">
              <rect x="3.5" y="5" width="17" height="15.5" rx="3.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M7.5 3.5v4M16.5 3.5v4M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>

            <div>
              <div className="text-[21px] font-extrabold tracking-[0.06em] text-white">
                {t("food.calendar.title")}
              </div>

              <div className="mt-1 text-[14px] font-medium capitalize tracking-[0.04em] text-slate-300/72">
                {monthName} {year}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFoodCalOffset((v) => v - 1);
              }}
className="grid h-9 w-9 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[22px] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl outline-none focus:outline-none focus:ring-0 active:scale-[0.96]"
            >
              ‹
            </button>

            {foodCalOffset < 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFoodCalOffset((v) => v + 1);
                }}
className="grid h-9 w-9 place-items-center rounded-full border border-[#18d7bd]/45 bg-[#18d7bd]/[0.095] text-[22px] text-[#35F3DF] shadow-[0_0_14px_rgba(24,215,189,0.14)] backdrop-blur-xl outline-none focus:outline-none focus:ring-0 active:scale-[0.96]"
              >
                ›
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-7 gap-y-2 text-center">
          {week.map((d) => (
            <div key={d} className="text-[12px] font-semibold tracking-[0.03em] text-slate-300/68">
              {d}
            </div>
          ))}

          {cells.map((d, i) => (
            <div key={i} className="flex h-[34px] items-center justify-center">
              {d ? (() => {
                const monthIndex = base.getMonth();
                const dayKey = toDayKey(new Date(year, monthIndex, d));
                const m = foodMonthMap?.[dayKey] || null;

                const carbsRatio = m
                  ? Number(m.carbs ?? 0) / Math.max(1, Number(macroTargets?.carbs ?? 0))
                  : 0;

                const proteinsRatio = m
                  ? Number(m.proteins ?? 0) / Math.max(1, Number(macroTargets?.proteins ?? 0))
                  : 0;

                const fatsRatio = m
                  ? Number(m.fats ?? 0) / Math.max(1, Number(macroTargets?.fats ?? 0))
                  : 0;

                return (
                  <button
                    type="button"
                    onPointerDown={rememberTapStart}
                    onPointerUp={(e) => {
                      runIfRealTap(e, () => openFoodDay(dayKey), 450);
                    }}
                    onPointerCancel={clearTapStart}
                    onPointerLeave={clearTapStart}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="mx-auto relative flex h-7 w-7 items-center justify-center rounded-full active:scale-[0.94]"
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    {m && (
                      <MiniRings
                        carbsRatio={carbsRatio}
                        proteinsRatio={proteinsRatio}
                        fatsRatio={fatsRatio}
                      />
                    )}

                    <span
                      className={cn(
                        "relative z-10 mx-auto flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold tabular-nums",
                        m ? "bg-[#06101f]/92 text-white" : "text-slate-200/88"
                      )}
                    >
                      {d}
                    </span>
                  </button>
                );
              })() : (
                <div className="h-7 w-7" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  })()}
</GlassCard>

<button
  type="button"
  onPointerDown={rememberTapStart}
  onPointerUp={(e) => {
    runIfRealTap(e, () => setPremiumOpen(true), 450);
  }}
  onPointerCancel={clearTapStart}
  onPointerLeave={clearTapStart}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
className="mt-2 flex min-h-[54px] w-full items-center justify-center rounded-[24px] border border-[#18d7bd]/30 px-4 text-center backdrop-blur-xl active:scale-[0.985]"
  style={{
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    background:
      "radial-gradient(circle at 0% 0%, rgba(24,215,189,0.10), transparent 46%), linear-gradient(180deg, rgba(7,22,36,0.88), rgba(3,12,23,0.94))",
    boxShadow:
      "0 0 0 1px rgba(24,215,189,0.025), 0 14px 36px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.055)",
  }}
>
  <span className="text-[16px] font-extrabold leading-none tracking-[-0.035em] text-slate-50">
    {t("premium.entry")}
  </span>
</button>

        </div>
      )}
       </div> {/* закрываем ВНУТРЕННИЙ контейнер */}
<WorkoutTitleModal
  open={workoutTitleModalOpen}
  initialTitle={workoutTitle || ""}
  defaultTitle={getDefaultWorkoutTitle()}
  displayDefaultTitle={
    trainMode === "cardio"
      ? t("workout.picker.cardio")
      : selectedMuscles
          .map((key) => t(`workout.muscles.${key}`))
          .filter(Boolean)
          .join(", ") || t("nav.workout")
  }
  onClose={() => setWorkoutTitleModalOpen(false)}
  onSave={(newTitle) => setWorkoutTitle(String(newTitle || "").trim())}
/>
<ExerciseNameModal
  open={!!exerciseModal}
  initialName={exerciseModal?.name || ""}
  savedWorkouts={savedWorkouts}
  onClose={() => setExerciseModal(null)}
  onSave={(newName) => {
    const normalizedName =
      String(newName || "")
        .trim()
        .toLowerCase()
        .replace(/ё/g, "е")
        .replace(/\s+/g, " ");

    const latestExercise = (
      Array.isArray(savedWorkouts)
        ? [...savedWorkouts]
        : []
    )
      .sort((a, b) => {
        const aTime =
          Date.parse(a?.date || "") ||
          Number(a?.id) ||
          0;

        const bTime =
          Date.parse(b?.date || "") ||
          Number(b?.id) ||
          0;

        return bTime - aTime;
      })
      .map((workout) =>
        (Array.isArray(workout?.exercises)
          ? workout.exercises
          : []
        ).find((exercise) => {
          const exerciseName =
            String(exercise?.name || "")
              .trim()
              .toLowerCase()
              .replace(/ё/g, "е")
              .replace(/\s+/g, " ");

          return (
            exerciseName === normalizedName
          );
        })
      )
      .find(Boolean);

    const latestSets =
      Array.isArray(latestExercise?.sets)
        ? latestExercise.sets
        : [];

    setExercises((prev) =>
      prev.map((e) => {
        if (
          !exerciseModal ||
          e.id !== exerciseModal.id
        ) {
          return e;
        }

        return {
          ...e,

          name: String(
            newName || ""
          ).trim(),

          sets:
            latestSets.length > 0
              ? latestSets.map(
                  (set, index) => ({
                    ...set,

                    id: index + 1,

                    label:
                      `Подход ${index + 1}`,

                    /*
                    Это подсказка из
                    последнего выполнения.

                    Пока пользователь
                    сам не сохранит подход,
                    он не считается
                    выполненным.
                    */
                    edited: false,
                    prefill: true,
                  })
                )
              : e.sets,
        };
      })
    );
  }}
/>

      <SetModal
        open={!!setModal}
        initialWeight={setModal?.weight}
        initialReps={setModal?.reps}
        label={setModal?.label}
        onClose={() => setSetModal(null)}
        onSave={({ weight, reps }) => {
            startWorkoutTimerIfNeeded();
          setExercises((prev) =>
            prev.map((e) => {
              if (!setModal || e.id !== setModal.exerciseId) return e;
              return {
                ...e,
                sets: e.sets.map((s) =>
  s.id === setModal.setId
    ? {
        ...s,
        weight,
        reps,

        // ✅ теперь это "реально изменено пользователем"
        edited: true,
        prefill: false,
      }
    : s
),

              };
            })
          );
        }}
      />
     

<NamesListModal
  open={!!namesModal}
  title={namesModal?.title}
  names={namesModal?.names}
  customTitle={namesModal?.customTitle}
  onClose={() => setNamesModal(null)}
/>


            <WeightModal
  open={weightModalOpen}
  onClose={closeWeightModal}
  onSubmit={submitWeight}
  initialCurrent={weight.current || ""}
  initialGoal={weight.goal || ""}
  initialBaseline={weight.baseline}
/>


      <SleepModal
  open={sleepModalOpen}
  onClose={closeSleepModal}
  onSubmit={submitSleep}
  initialHours={sleepHours}
/>
<FoodDayModal
  open={foodDayOpen}
  onClose={() => setFoodDayOpen(false)}
  dayKey={foodDayKey}
  loading={foodDayLoading}
  entries={foodDayEntries}
  totals={foodMonthMap?.[foodDayKey]}
  selectedIds={foodDaySelectedIds}
  onToggleSelect={toggleFoodDaySelect}
  onAddSelected={addSelectedFoodDayToToday}
    addBusy={foodDayAddBusy}
  onChangeDay={openFoodDay}
/>

<OnboardingOffer
  open={onboardingOfferOpen}

  onStart={async () => {
    if (
      onboardingOfferSaving
    ) {
      return;
    }

    setOnboardingOfferSaving(
      true
    );

    try {
      await apiUpdateFoodOnboarding(
        "offer_start"
      );

      setOnboardingOfferOpen(
        false
      );

      await reloadDbUser();

      window.setTimeout(() => {
        openOnboardingMenu();
      }, 0);
    } catch (error) {
      console.error(
        "onboarding offer start:",
        error
      );

      safeAlert(
        "Не удалось сохранить выбор. Попробуйте ещё раз.",
        "Ошибка"
      );
    } finally {
      setOnboardingOfferSaving(
        false
      );
    }
  }}

  onSkip={async () => {
    if (
      onboardingOfferSaving
    ) {
      return;
    }

    setOnboardingOfferSaving(
      true
    );

    try {
      await apiUpdateFoodOnboarding(
        "offer_skip"
      );

      setOnboardingOfferOpen(
        false
      );

      await reloadDbUser();
    } catch (error) {
      console.error(
        "onboarding offer skip:",
        error
      );

      safeAlert(
        "Не удалось сохранить выбор. Попробуйте ещё раз.",
        "Ошибка"
      );
    } finally {
      setOnboardingOfferSaving(
        false
      );
    }
  }}
/>
<PremiumStarsPage
  open={premiumOpen}
  onClose={() =>
    setPremiumOpen(false)
  }
  onPaid={reloadDbUser}
  userAccess={userAccess}
  onStartOnboarding={() => {
    setPremiumOpen(false);
    openOnboardingMenu();
  }}
/>
<WorkoutDayModal
  open={workoutDayOpen}
  onClose={() => setWorkoutDayOpen(false)}
  dayKey={workoutDayKey}
  workouts={workoutDayItems}
  onRepeat={repeatWorkout}
  onChangeDay={openWorkoutDay}
/>

<WorkoutConflictModal
  open={workoutConflictOpen}
  onClose={() => setWorkoutConflictOpen(false)}
  title={openWorkoutTitle}
  canSave={canSaveWorkout || hasMeaningfulDraftWorkout(draftWorkout)}
  saving={isSavingWorkout}
  onSave={saveWorkout}
  onCancelWorkout={cancelCurrentWorkout}
  onResume={resumeOpenWorkout}
/>
<CardioModal
  open={!!cardioModal}
  initial={cardioModal}
  onClose={() => setCardioModal(null)}
  onAdd={({ title, minutes, comment }) => {
    saveStandaloneCardio({ title, minutes, comment });
  }}
/>




      <StepsModal
        open={stepsModalOpen}
        onClose={() => setStepsModalOpen(false)}
        onSubmit={handleStepsChange}
        initialSteps={steps}
      />

<MacroTargetsModal
  open={macroModalOpen}

  onClose={() => {
    setMacroModalOpen(false);

    emitAppEvent(
      "macros:closed"
    );
  }}
  initialTargets={macroTargets}
  onSubmit={async (next) => {
  try {
    await apiSaveMacroTargets(next);

    setMacroTargets(next);

    logEvent(
      "macro:targets:update",
      next
    );

    setMacroModalOpen(false);

    emitAppEvent(
      "macros-saved",
      {
        targets: next,
      }
    );
  } catch (e) {
    console.error(e);
    safeAlert("Не удалось сохранить цели. Попробуй ещё раз.");
  }
}}

  setTrainScreen={setTrainScreen}
/>


<StepsReportPeriodModal
  open={stepsReportPeriodOpen}
  onClose={() => setStepsReportPeriodOpen(false)}
  onSelect={(p) => {
    setStepsReportPeriod(p);
    setStepsReportPeriodOpen(false);
    setStepsReportOpen(true);
  }}
/>

<StepsReportModal
  open={stepsReportOpen}
  onClose={() => setStepsReportOpen(false)}
  period={stepsReportPeriod}
/>

    </div> {/* SIZER */}
  </div> {/* ВНЕШНИЙ контейнер */}
<AppOnboardingBridge
  onFoodOnboardingStart={() => {
    void apiUpdateFoodOnboarding(
      "start"
    )
      .then(() => {
        return reloadDbUser();
      })
      .catch((error) => {
        console.error(
          "food onboarding start:",
          error
        );
      });
  }}

onFoodOnboardingComplete={(
  completion = {}
) => {
  const payload =
    Object.prototype.hasOwnProperty.call(
      completion,
      "reminderHours"
    )
      ? {
          reminderHours:
            completion.reminderHours,
        }
      : {};

  void apiUpdateFoodOnboarding(
    "complete",
    payload
  )
.then(() => {
  safeAlert("Готово");

  return reloadDbUser();
})
    .catch((error) => {
      console.error(
        "food onboarding complete:",
        error
      );

      safeAlert(
        "Онбординг завершён, но не удалось сохранить результат. Откройте приложение ещё раз.",
        "Ошибка"
      );
    });
}}
  openFoodOverview={({
    scrollTop = false,
  } = {}) => {
    setTab("food");
    setFoodView("overview");

    if (scrollTop) {
      window.setTimeout(
        scrollAppRootToTop,
        0
      );
    }
  }}

  openRationToday={() => {
    setTab("food");

    setRationDayKey(
      getLocalDayKey()
    );

    setFoodView("details");

    window.setTimeout(
      scrollAppRootToTop,
      0
    );
  }}

  closeTransientScreens={() => {
    setMacroModalOpen(false);
  }}

  onLocked={() => {
    safeAlert(
      "Сначала пройдите обзор приложения.",
      "Онбординг"
    );
  }}
/>
<FitmeupBottomNav
  active={bottomNavActive}
  hidden={isInputFocused}
  items={bottomNavItems}
/>
</>
  );
}

